import {
  IComponent,
  CommonHelperFunctions,
  EEditabilityType,
  ELayoutPosition,
  EScreenType,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IAuthorizationDto,
  ICodeGenerationContext,
  IDisplayItem,
  IDisplayItemProperty,
  IFillStyle,
  IKuikaStyle,
  IProjectRole,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  ISymbol,
  SpecialDIIds,
  StyleHelper,
  StyleState
} from "../..";
import type { IEffectsEntryAnimation, ILoopingAnimationItem, ILoopingKeyframeStop, ITransformItem } from "../..";
import type {
  BreadcrumbOptions,
  CalendarOptions,
  ChartAdditionalSeries,
  IValidation,
  MenuOptions,
  MixedChartBarOptions,
  MixedChartLineOptions,
  MixedChartOptions,
  MixedChartPointOptions,
  NodeTreeNodeOptions,
  RadialChartLevelOptions,
  RadialChartOptions,
  TimelineOptions
} from "../models/builder.dtos";
import type {
  IBorderStateStyles,
  IEffectsStateStyles,
  IFillStateStyles,
  IPositionStateStyles,
  IShadowStateStyles,
  ITypographyStateStyles
} from "../models/state-styles";

/**
 * L1 React Native emitter: converts a simple IScreenDesign (Container + Label + Button)
 * into a self-contained Expo/React Native TSX screen file.
 *
 * Sibling of ServerSideElementGenerator (the React/web emitter) but intentionally NOT
 * derived from BaseElementGenerator: that base carries web-only concerns (Form.Item
 * wrapping, HTML tags, master-screen layout composition) that have no RN counterpart yet.
 * Scope is the L1 component set only — unsupported components are skipped with a TSX
 * comment marker so gaps are visible in the generated output.
 */

// "Icon" is a logical tag (not a raw RN element): it routes the Icon widget to the emitIcon strategy,
// which emits the KIcon runtime component. It never reaches the react-native import (usedTags stays []).
// "Switch" is a logical tag: it routes to emitSwitch which emits KSwitch — same pattern as Icon.
// "Image" is a logical tag: it routes to emitImage which emits KImage (backed by expo-image) — same pattern.
// "Checkbox" is a logical tag: it routes to emitCheckbox which emits KCheckbox (custom Pressable — same pattern as Switch).
// "Panel" is a logical tag: it routes to emitPanel which emits KPanel (container with scrolling/hover/dynamic bg) — same pattern.
// "Container" is a logical tag: the Row/Col layout box, emitted as KContainer — a plain View until the designer's
// onClick lands on its kuikaid slot, at which point KContainer becomes that click's press surface. Emitted for
// EVERY Row/Col, not just clickable ones, because this leg never looks at a display item's events (see
// IComponentEmitContext.actionMarker); the tag has to be able to take a press before anyone knows it will get one.
// "Spacer" is a logical tag: it routes to emitSpacer which emits KSpacer — a container that spaces children.
// "Badge" is a logical tag: it routes to emitBadge which emits KBadge — a count/dot/icon-pill indicator.
// "Markdown" is a logical tag: it routes to emitMarkdown which emits KMDViewer — a read-only markdown view.
// "Divider" is a logical tag: it routes to emitDivider which emits KDivider — a horizontal/vertical separator line.
// "Rating" is a logical tag: it routes to emitRating which emits KRating — a controlled star-rating input (same pattern as Switch/Checkbox, but numeric).
// "ProgressBar" is a logical tag: it routes to emitProgressBar which emits KProgressBar — a read-only completion bar (presentational, no state).
// "ProgressCircle" is a logical tag: it routes to emitProgressCircle which emits KProgressCircle — a read-only circular completion ring (presentational, no state).
// "Countdown" is a logical tag: it routes to emitCountdown which emits KCountdown — a self-ticking countdown timer (its onFinish/onChange actions land on the tag's kuikaid slot, resolved C#-side).
// "CountUp" is a logical tag: it routes to emitCountUp which emits KCountUp — a self-ticking elapsed-time stopwatch (its onChange actions land on the tag's kuikaid slot, resolved C#-side).
// "Table" is a logical tag: it routes to emitTable which emits KTable — the data-bound table (a data repeater:
// it consumes its own TableHeader/TableRow/column display items instead of emitting them as nodes).
// "Gallery" / "FlexGrid" are logical tags: they route to emitGallery / emitFlexGrid which emit KGallery / KFlexGrid —
// the list-shaped data repeaters (their own children are the item template, lowered into a `renderItem` closure).
// "Carousel" is a logical tag: it routes to emitCarousel which emits KCarousel — the pager data repeater shared by
// the designer's Carousel and HorizontalGrid (one web component in `carousel` mode, one tagName).
// "Select" is a logical tag: it routes to emitSelect which emits KSelectBox / KMultiSelect / KAutoComplete — the
// select family, one web component (`KSelectBox`) wrapped twice, so one strategy serves all three.
// "ScrollView" is used by containers when overflow:scroll is detected; it IS a raw RN import.
type TReactNativeTag =
  | "View"
  | "Container"
  | "Text"
  | "Pressable"
  | "TextInput"
  | "Icon"
  | "ScrollView"
  | "Switch"
  | "Image"
  | "WebView"
  | "Checkbox"
  | "Panel"
  | "Spacer"
  | "Badge"
  | "Markdown"
  | "Divider"
  | "Rating"
  | "Slider"
  | "RangeSlider"
  | "ProgressBar"
  | "ProgressCircle"
  | "QRRenderer"
  | "BarcodeRenderer"
  | "Thumbnail"
  | "Timeline"
  | "Tree"
  | "ColorPicker"
  | "MDEditor"
  | "Marquee"
  | "LanguageSelector"
  | "RadioButtonGroup"
  | "RadioButton"
  | "Calendar"
  | "Stepper"
  | "StepperItem"
  | "PivotTable"
  | "NodeTree"
  | "BigFileUpload"
  | "Signature"
  | "Recaptcha"
  | "PaInteractionButtons"
  | "ProcessAutomation"
  | "Countdown"
  | "CountUp"
  | "Stopwatch"
  | "ContentMenu"
  | "ContentMenuItem"
  | "ContentMenuPanel"
  | "VideoPlayer"
  | "MicroApp"
  | "Modal"
  | "Tab"
  | "TabItem"
  | "Table"
  | "Gallery"
  | "FlexGrid"
  | "Carousel"
  | "Chat"
  | "Chart"
  | "GaugeChart"
  | "RadialChart"
  | "MixedChart"
  | "Select"
  | "DatePicker"
  | "YearPicker"
  | "DateRange"
  | "RadioGroup"
  | "LocationPicker"
  | "LocationSearch"
  | "Maps"
  | "Tooltip"
  | "FloatButton"
  | "Collapse"
  | "CollapsePanel"
  | "FloatPanel"
  | "Breadcrumb"
  | "Pagination"
  | "Drawer"
  | "Menu"
  | "MenuItem"
  | "SubMenu";

interface IEmittedNode {
  jsx: string;
  usedTags: TReactNativeTag[];
  /**
   * Names of Kuika runtime components this node (or its subtree) references, imported from
   * `@/components/kuika` rather than `react-native` — e.g. `["KLabel"]`. Behavior-carrying
   * components live in the generated app's runtime library, so the generator emits a thin
   * reference here instead of inlining their markup. Absent/empty when the node uses only RN tags.
   */
  runtimeComponents?: string[];
  /**
   * The JSX is not a bare element: it is either a `{… && (…)}` **expression container** (a designer-visibility or
   * role-authorization guard) or a JSX comment container (an unsupported component). Both forms are valid wherever a
   * JSX *child* is expected, which is most positions a component is emitted into — but NOT an expression position
   * such as a repeater's `render: (row) => ( … )` or a master shell's `drawerContent={… return ( … ); }`, where the
   * leading brace reads as an object literal and the file stops parsing. Every emitter targeting such a position
   * asks for this flag and wraps the node in a fragment; see {@link expressionPositionBody}.
   */
  isExpressionContainer?: boolean;
}

interface IStyleSheetEntry {
  name: string;
  style: Record<string, string | number>;
}

/** How one node renders "disabled" — see {@link ReactNativeElementGenerator.resolveDisableRendering}. */
interface IDisableRendering {
  /** The tag's own disable prop, spliced into its opening tag (` editable={…}` / ` disabled={…}`), or "". */
  prop: string;
  /**
   * The bare "this node is disabled right now" expression (`!isAuthorized_di5 || isEnabled_di5 === "disabled"`),
   * whichever form the tag itself uses — or "" when nothing disables the node. It exists for the ONE case where
   * neither {@link prop} nor {@link boundaryExpr} can land on the node's outermost element: a tooltip-wrapped
   * component, where `KTooltip` is the opening tag and owns the press surface. See
   * {@link IComponentEmitContext.disableWrapperProp}.
   */
  disabledExpr: string;
  /**
   * The expression that is true while the node is disabled, for a tag with no prop of its own whose web
   * counterpart blocks the interaction through the element's style. "" when the tag carries {@link prop}
   * instead, when nothing disables the node, or when web does not block it either.
   */
  boundaryExpr: string;
}

/**
 * The style delta a {@link IDisableRendering.boundaryExpr} merges into a disabled node's own style — the RN
 * spelling of what web's `with-common-events` HOC applies (`pointerEvents: "none"`, `opacity: 0.7`).
 *
 * <p>A style rather than a wrapper component on purpose: `pointerEvents` is a `ViewStyle` key in React Native,
 * so the delta rides in on the prop the component already forwards to its root view and blocks that view's whole
 * subtree — with no extra `<View>` in the tree to perturb the layout the designer authored. It is registered as
 * one shared StyleSheet entry per screen ({@link DISABLED_STYLE_ENTRY_NAME}), so every disabled node on a screen
 * references the same object and the array identity stays stable across renders.</p>
 */
const DISABLED_STYLE_ENTRY_NAME: string = "kuikaDisabled";
const DISABLED_STYLE: Record<string, string | number> = { opacity: 0.7, pointerEvents: "none" };

/**
 * The designer's Fill → Image, as the RN runtime needs it. Not a style value: React Native has no
 * `background-image`, so this becomes a `<KBackgroundImage />` element rather than a StyleSheet key.
 */
interface IFillImage {
  /** A Kuika resource GUID or an absolute address — whichever spelling the fill carried. */
  value: string;
  /** `cover` | `contain` | `stretch`, folded from the panel's sizing switches. */
  fit: string;
  repeat: boolean;
  /** The panel's `bgImagePosition`, or "" when it set none. */
  position: string;
}

/**
 * One keyframe stop of a looping animation, reduced to the channels React Native's `Animated` can drive.
 *
 * <p>The reduction happens here rather than in the runtime because it is a LOWERING decision — which CSS
 * transform functions map onto animatable RN operations — and the rest of the transform lowering lives here
 * too. A stop's `filters` are dropped on the way: `filter` is a string style, so there is no number for a
 * driver to interpolate. That costs the `glow` looping preset its drop-shadow pulse; its transform and
 * opacity channels still run.</p>
 */
interface IAnimationStop {
  offset: number;
  translateX?: number;
  translateY?: number;
  scale?: number;
  scaleX?: number;
  scaleY?: number;
  rotateZ?: number;
  skewX?: number;
  skewY?: number;
  opacity?: number;
}

/** A looping animation as the runtime takes it. Mirrors `ILoopingAnimationItem` minus what RN cannot drive. */
interface IAnimationLoop {
  durationMs: number;
  delayMs?: number;
  timingFunction?: string;
  iterationCount?: number;
  direction?: string;
  stops: IAnimationStop[];
}

/** The designer's Effects animations for one component: the mount animation and/or the looping ones. */
interface IAnimationSpec {
  entry?: IEffectsEntryAnimation;
  loops?: IAnimationLoop[];
}

/** What {@link ReactNativeElementGenerator.resolveStyle} reports: the narrowed style, plus its bitmap fill. */
interface IResolvedStyle {
  style: Record<string, string | number>;
  fillImage: IFillImage | null;
  isBlockDisplay: boolean;
  explicitTextAlign: TChartLegendAlign;
}

type TChartLegendAlign = "start" | "end" | null;

export interface IPartitionedStyle {
  viewStyle: Record<string, string | number>;
  textStyle: Record<string, string | number>;
}

interface IComponentMapping {
  tag: TReactNativeTag;
  /**
   * RN style defaults restoring the web layout semantics the designer assumes. RN Views default to
   * flexDirection "column" and hug their content, but Kuika Rows lay children out horizontally and
   * wrap (so a full-span column takes its own line), a span-less Col shares the row width, and a
   * VerticalStack always spans its parent. Resolved designer styles always win over these defaults.
   */
  baseStyle?: Record<string, string | number>;
  /**
   * The DI property whose numeric value becomes the flex `gap` (inter-item spacing). Set for the
   * stacks (HorizontalStack/VerticalStack), whose only meaningful prop is the "Gap" — model name
   * `size` — between their children. RN `gap` applies along the flex axis for both row and column,
   * so one property covers both stacks, mirroring the web stacks' antd `<Space size>`. A designer-set
   * `gap` in the style object still wins.
   */
  gapProperty?: string;
}

/**
 * How a container node renders: a plain `View` or a scrolling `ScrollView` (and on which axis). The
 * stacks decide this per-node — HorizontalStack scrolls horizontally (unless `overflowX="hidden"`),
 * VerticalStack scrolls vertically only when height-bounded — so it can't be a static tag-map entry.
 */
interface IContainerRender {
  tag: "View" | "ScrollView";
  /** `horizontal` prop on the ScrollView (row-axis scroll). Always false for a View. */
  horizontal: boolean;
  /** Split box styles onto `style` and child-layout styles onto `contentContainerStyle` (ScrollView only). */
  splitContentContainer: boolean;
}

/** A container's resolved style partitioned into the scroll-viewport box and the child-layout content subsets. */
interface ISplitContainerStyle {
  box: Record<string, string | number>;
  content: Record<string, string | number>;
}

/**
 * What a child needs to know about the container emitting it. Replaces the former bare
 * `parentIsHorizontal` boolean: RN has no CSS cascade, so the inherited `textAlign` has to be carried
 * down explicitly (see {@link ReactNativeElementGenerator.resolveContentAlignment}).
 */
interface IParentLayout {
  /** The parent lays its children out on the row axis (`flexDirection: "row"`). */
  isHorizontal: boolean;
  /**
   * The horizontal text alignment in effect at this point in the tree — the parent's own `textAlign`
   * or whatever it inherited. CSS inherits `text-align` through the element tree and the web target
   * relies on that (a Kuika container's alignment reaches the `Label` text inside it); React Native
   * resolves each `Text` style in isolation, so the emitter re-implements the cascade. `undefined`
   * when no ancestor set one.
   */
  inheritedTextAlign?: string;
  /**
   * The rest of the inherited typography (colour, font family / size / weight, …), or `undefined` when nothing is
   * being inherited. Only a table cell seeds it today — web's `td` / `tr` styles reach the cell content by CSS
   * inheritance — so every screen outside a table emits exactly what it did before. Once seeded it cascades like
   * CSS: a container adds its own typography for the nodes below it, and a node's own style always wins.
   */
  inheritedTextStyle?: Readonly<Record<string, string | number>>;
}

/**
 * Action definition names (carried on `IAction.label` — the designer stamps
 * `action.label = actionDef.name`) that mutate a component's visibility at runtime. A component they
 * target needs a visibility guard even when its own `visibilityType` is the default `visible`, because
 * the action can flip it to hidden — the RN mirror of web's `DetectUiLogicVariables`, which registers
 * `isComp<id>Visible` for exactly these two action names.
 *
 * Matching on the label is what keeps the "Node emits layout, C# owns behavior" split intact: the label
 * is part of the screen design this leg already receives, so no action *semantics* (definitions,
 * parameter contracts, precedence) crosses the boundary — the C# leg still resolves the actual
 * statement. `server-side-element-generator` matches `label === "NAVIGATE"` the same way.
 */
/**
 * `EVisibilityType`'s ordinals, for the second spelling {@link ReactNativeElementGenerator.isControlledVisibilityType}
 * accepts. Declared here rather than imported because the TS model types `visibilityType` as the string union alone
 * — the ordinal is what the C# enum would send if its `JsonStringEnumConverter` were ever dropped, which is exactly
 * the case being defended against.
 */
const VISIBILITY_TYPE_HIDDEN_ORDINAL: number = 1;

/** @see VISIBILITY_TYPE_HIDDEN_ORDINAL */
const VISIBILITY_TYPE_CUSTOM_ORDINAL: number = 2;

const VISIBILITY_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["ChangeVisibilityOf", "ToggleVisibilityOf"]);

/** The action parameter naming a UI-logic action's target display item (its id as a string). */
const VISIBILITY_ACTION_TARGET_PARAM: string = "componentName";

/**
 * Per-screen memo of {@link ReactNativeElementGenerator.collectVisibilityActionTargets}. Keyed on the
 * generation context (rebuilt per screen by `client_code_generator`), so the O(actions) scan runs once
 * per screen instead of once per emitted node, without giving the generator mutable instance state that
 * could leak between screens — the same generator instance serves every screen in a request.
 */
const VISIBILITY_ACTION_TARGET_MEMO: WeakMap<ICodeGenerationContext, ReadonlySet<number>> = new WeakMap();

/**
 * The UI-logic action that WRITES a component's value, and the parameter naming its target. A component that is
 * only ever read keeps emitting its own literal or binding; one this action targets has to read a state variable
 * instead, or the write would have nowhere to land.
 */
/** Single-entry set so {@link ReactNativeElementGenerator.isUiLogicAction} takes one shape for every UI-logic action. */
const SET_VALUE_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["SetValueOf"]);

/** The action parameter naming a SetValueOf's target display item (its id as a string). */
const SET_VALUE_ACTION_TARGET_PARAM: string = "targetDiName";

/**
 * Platform action-definition ids for the UI-logic actions this leg has to recognise, keyed by the
 * definition name each one carries.
 *
 * <p><b>`action.label` is not a reliable key, and that is why these exist.</b> The label is a mutable,
 * creation-path-dependent cache of the definition's name: the interactive picker writes
 * `actionDef.name`, but the MCP/AI binder writes other things, and an older project can carry whatever
 * spelling it was authored with. `ProjectService.GetTargetWorkflowIdIfStartProcessAction` states the
 * rule for the same class of detection — *"Label/Description are mutable, creation-path-dependent
 * caches of def.name … actionDefinitionId is the source of truth"* — and hardcodes
 * `STARTPROCESS_ACTION_DEFINITION_ID` exactly like this. The C# leg keys on the definition's NAME
 * (resolved through the definitions it holds), so before this a label that had drifted made the two
 * legs disagree in the worst possible way: this leg left a Label reading its own binding, the C# leg
 * still recognised the SetValueOf, and the generated screen carried
 * `// kuika-rn: SetValueOf skipped — di<id> owns no state to write on this screen` for an action the
 * designer had wired correctly (KOA-18254).</p>
 *
 * <p>These ids are platform constants, not per-project rows: `SystemActionsCache` deserialises
 * `Backend/CoreApi/Metadatas/Actions/*.json` with each `Id` verbatim and serves the same value to every
 * domain, so an id is stable in a way a label is not. The definitions themselves never cross into this
 * leg (the codegen request carries Project/Screens/ComponentSet/Components only), which is why the id —
 * already present on every action in the screen design — is the key available here.</p>
 */
const UI_LOGIC_ACTION_DEFINITION_IDS: ReadonlyMap<string, string> = new Map<string, string>([
  ["SetValueOf", "c51d02cd-b19f-495c-afac-54a436eaa0ae"],
  ["ChangeVisibilityOf", "c17d64e9-b743-45c8-bafa-c2e8b9db3f31"],
  ["ToggleVisibilityOf", "3741772c-f125-4945-bbf6-0752f7bb31ef"],
  ["ChangeEnabledOf", "b564f20a-b52f-4231-aeb6-dba4f8adf422"],
  ["ToggleEditabilityOf", "f0d5935f-39e5-428f-a89f-c10bcf07998d"],
  ["OpenCollapsePanelOf", "28dfee30-1a98-422f-a541-22b9cfdfa4c9"],
  ["CloseCollapsePanelOf", "997f54d4-f8f3-49f0-98a2-b0e43dbe77ea"],
  ["ToggleCollapsePanelOf", "d529ffcd-dc26-409a-a757-b3767cd0939a"],
  ["SetFocusTo", "f4c18e35-fcff-4920-9c9c-efde3dd1163e"]
]);

/** Per-screen memo of {@link ReactNativeElementGenerator.collectSetValueTargets}, keyed like {@link VISIBILITY_ACTION_TARGET_MEMO}. */
const SET_VALUE_TARGET_MEMO: WeakMap<ICodeGenerationContext, ReadonlySet<number>> = new WeakMap();

/**
 * Action definition names that mutate a component's *editability* at runtime — the exact counterpart of
 * {@link VISIBILITY_ACTION_LABELS}, matched the same way and for the same reason. Web registers
 * `isComp<id>Enabled` for these two names (`Analyser.DetectUiLogicVariables`); this leg emits the
 * `isEnabled_di<id>` reference their target needs, and the C# leg declares it.
 *
 * They share `componentName` with the visibility actions, so {@link VISIBILITY_ACTION_TARGET_PARAM} is
 * reused rather than duplicated — the designer names the target parameter identically for every
 * component-addressing UI-logic action.
 */
const ENABLED_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["ChangeEnabledOf", "ToggleEditabilityOf"]);

/** Per-screen memo of {@link ReactNativeElementGenerator.collectEnabledActionTargets}, keyed like {@link VISIBILITY_ACTION_TARGET_MEMO}. */
const ENABLED_ACTION_TARGET_MEMO: WeakMap<ICodeGenerationContext, ReadonlySet<number>> = new WeakMap();

/**
 * Action definition names that open or close a Collapse's panels. A Collapse they target becomes
 * *controlled*: its open-key set is hoisted into screen state so the action has somewhere to write and the
 * open panels survive a re-render. Web makes the identical switch (KOA-10849, `React_UIGenerator`), and for
 * the identical reason. They address the Collapse through `componentName`, like every other UI-logic action.
 */
const COLLAPSE_ACTION_LABELS: ReadonlySet<string> = new Set<string>([
  "OpenCollapsePanelOf",
  "CloseCollapsePanelOf",
  "ToggleCollapsePanelOf"
]);

/** Per-screen memo of {@link ReactNativeElementGenerator.collectCollapseActionTargets}, keyed like {@link VISIBILITY_ACTION_TARGET_MEMO}. */
const COLLAPSE_ACTION_TARGET_MEMO: WeakMap<ICodeGenerationContext, ReadonlySet<number>> = new WeakMap();

/**
 * The action that moves keyboard focus, and the parameter naming its target. Unlike every other
 * component-addressing UI-logic action it uses `element` rather than `componentName` — the designer's own
 * picker writes the target display item's id into that parameter (`select-element-id-symbols-group.tsx`),
 * so the value is the same kind, only the key differs.
 */
const FOCUS_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["SetFocusTo"]);

/** @see FOCUS_ACTION_LABELS */
const FOCUS_ACTION_TARGET_PARAM: string = "element";

/** Per-screen memo of {@link ReactNativeElementGenerator.collectFocusActionTargets}, keyed like {@link VISIBILITY_ACTION_TARGET_MEMO}. */
const FOCUS_ACTION_TARGET_MEMO: WeakMap<ICodeGenerationContext, ReadonlySet<number>> = new WeakMap();

/** Whether an authorized component is hidden or merely disabled when the user lacks the required roles. */
type TAuthorizationMode = "hide" | "disable";

/** Resolved per-component authorization directive: the mode, the variable name used in JSX, and the role ids for the hoisted hook call. */
export interface IAuthDirective {
  mode: TAuthorizationMode;
  /** Variable name emitted in JSX (`isAuthorized_di${id}`), or the literal `"false"` when roleIds is empty. */
  authorizedExpr: string;
  /** Role ids for the `useIsAuthorizedForRoles` hook call hoisted to component top-level; empty only when `authorizedExpr === "false"`. */
  roleIds: string[];
}

/**
 * The per-node state `emitNode` computes once (style resolution, partitioning, authorization,
 * testID) plus the few helpers a strategy needs, handed to a {@link TComponentStrategy}. This is
 * the seam that lets each RN tag own its JSX without depending on the concrete generator: the
 * helper methods close over `emitNode`'s locals (`styleEntries`, the generation context, the DI),
 * so strategies stay decoupled from `ReactNativeElementGenerator`'s private statics.
 */
export interface IComponentEmitContext {
  /** The DI being emitted. */
  readonly di: IDisplayItem;
  /** Tree depth; `indent` is derived from it. A strategy with children indents at `depth + 1`. */
  readonly depth: number;
  /** Leading whitespace for this node's line, `INDENT_STEP.repeat(depth)`. */
  readonly indent: string;
  /** Kuika component registry name (e.g. "Label", "Email") — discriminates TextInput sub-variants. */
  readonly componentName: string;
  /** Stable per-node style key, `di<id>`. Pressable derives its label key as `<styleName>Label`. */
  readonly styleName: string;
  /** Full resolved style with component base-style defaults applied (Text takes this whole). */
  readonly style: Record<string, string | number>;
  /** `style` split into the box subset and the typography subset (Pressable/containers need this). */
  readonly partitioned: IPartitionedStyle;
  readonly explicitTextAlign: TChartLegendAlign;
  /**
   * The `<KBackgroundImage />` element for this node's Fill → Image, indented as its first child — or ""
   * when the designer set none. A strategy that HOSTS children splices it in front of them, so the picture
   * paints under everything the box holds; RN has no `background-image` style key to do it with.
   */
  readonly fillImageJsx: string;
  /**
   * The ` animation={{…}}` prop for this node's Effects animations (entry and/or looping), or "" when the
   * designer authored none. Only the components that render an `Animated.*` element read it — React Native
   * has no `@keyframes`, so an animation needs a driver, and a driver needs an animated host.
   */
  readonly animationProp: string;
  /** Role-based authorization directive, or null when the node is unguarded. */
  readonly authorization: IAuthDirective | null;
  /** ` testID="..."` (already formatted and quoted) or "" when the DI has no friendlyName. */
  readonly testIdProp: string;
  /** Registers a named style entry and returns ` style={styles.<name>}`, or "" when empty. */
  registerStyle(name: string, style: Record<string, string | number>): string;
  /**
   * The JSX props carrying this node's interaction-state style deltas — one per spec the component declares
   * support for, and "" for every state the designer left alone.
   *
   * <p>Each emitted prop names its own `StyleSheet` entry (`di5Pressed`, `di5PressedText`), so the delta
   * costs nothing per render and the runtime merges it over the base while the state holds — the RN
   * equivalent of the web target's `!important` state rule cascading over the inline base style.</p>
   */
  stateStyleProps(specs: readonly IStateStylePropSpec[]): string;
  /** The DI's fixed value for `propertyName` as a string, or "" when unset. */
  getFixedPropertyValue(propertyName: string): string;
  /**
   * The runtime lookup for a property the designer marked multi-language, or "" when this property is not
   * translated — in which case the caller emits the literal, exactly as before.
   *
   * <p>Text is the one property kind whose value is not fixed at generate time: the designer translates a
   * label per language, and Set Language has to change what it says without a rebuild. So a translated
   * property emits a CALL rather than a string, resolved per render against the screen's catalogue.</p>
   *
   * <p>Reached only through {@link textPropExpression}, never on its own: a caller that asked for this and
   * forgot the literal fallback would blank every untranslated property on the screen.</p>
   */
  getTranslatedTextExpression(propertyName: string): string;
  getBoundValueMarker(propertyName: string): string;
  /**
   * Whether a `SetValueOf` action on this screen writes THIS component. Only the components that own no state of
   * their own need to ask: a controlled input already reads `di<id>` unconditionally.
   */
  isSetValueTarget(): boolean;
  /**
   * The bind marker for a property whose symbol names an **action result through `actionDefinitionId`** even
   * though its category stays `fixed` — the shape the designer uses for a data repeater's row source: the
   * canvas sample rows live in `fixedValue`, the real source in `actionDefinitionId`. The web target keys the
   * same read off the property definition's `isTableOptionsProp` flag rather than off the symbol category
   * (`React_UIGenerator`), which is why a category check alone silently emits the sample rows.
   */
  getActionBoundMarker(propertyName: string): string;
  /**
   * The ` kuikaid='<id>'` join marker: the **slot** in this tag where event props belong. Node emits no action
   * logic and does not even look at the display item's events — it only says "props go here". The C#
   * `ReactNativeScreenGenerator` replaces the one marker with the `prop={handler}` pair of every event the
   * designer actually bound on this component (`onPress`, `onRowPress`, `onRowDoublePress`, `onFinish`, …, resolved
   * through `ReactNativeEventCatalog`) and strips it when there is none — mirroring how the web target injects
   * behavior at `React_UIGenerator`'s kuikaid markers. Emit it on any tag whose runtime component exposes an event
   * prop the catalog can reach.
   */
  actionMarker(): string;
  /** Setter name for this node's state variable (`setDi<id>`), matching the C#-emitted useState hook. */
  readonly setterName: string;
  /**
   * The `collapseKeys_di<id>` state variable this Collapse's open-panel set lives in, or null when it owns
   * the set itself. Non-null exactly when an Open/Close/ToggleCollapsePanelOf action targets it.
   */
  collapseKeysVar(): string | null;

  /**
   * The `focusRef_di<id>` handle this component's `focusRef` binds to, or null when no `SetFocusTo` targets
   * it. Only the input family can honour one (see {@link IKFocusable} in the RN runtime), so other
   * strategies do not read it.
   */
  focusRefVar(): string | null;

  /**
   * The disable prop for this node (` editable={...}` / ` disabled={...}`) when authorization disables
   * it, else "". Tag-aware and non-forgeable — bound to this node's own tag; a no-op on non-interactive
   * tags. The strategy must splice the result into its opening tag (a codegen guard enforces this).
   */
  disableProp(): string;
  /**
   * The disable, as a ` disabled={…}` prop for a **wrapper element that owns the node's press surface** — today
   * only `KTooltip`, which a Label's `tooltip` and an Icon's `hoverText` wrap their component in and which carries
   * the action marker, so the press lives there rather than on the component itself.
   *
   * <p>Without it a tooltip-wrapped node could not be disabled at all: the tag's own prop and the style boundary
   * both land on the INNER element, so the wrapper's press stayed live — and {@link
   * ReactNativeElementGenerator.assertDisableApplied}, which reads the outermost opening tag, turned that into a
   * codegen error. Returns "" when nothing disables the node.</p>
   */
  disableWrapperProp(): string;
  /**
   * How many data repeaters (tables and, later, lists/galleries) enclose this node. A repeater strategy passes
   * `repeaterDepth + 1` when it emits its cell content, so a nested repeater knows to name its row variable
   * apart from its parent's — see {@link reactNativeRowVariableName}.
   */
  readonly repeaterDepth: number;
  /** Recursively emits a child node (used by the container strategies and the data repeaters). */
  emitChild(child: IDisplayItem, depth: number, options?: IEmitChildOptions): IEmittedNode | null;
  /**
   * The value + change-handler expressions this node's controlled state binds to. Normally the screen-level
   * `di<id>` useState; inside an **editable table column's editor** it is the per-row cell buffer KTable hands to
   * `renderEditor`, because one screen-level state cannot hold a value per row. `kind` picks the coercion, since
   * the cell buffer is text and a Switch/Rating needs a boolean/number.
   */
  controlledBinding(kind: TControlledValueKind): IControlledBinding;
  /**
   * The props wiring an editable table cell's commit hooks (`IKTableCellEditor.onSubmit` / `onBlur`) into this node
   * — ` onBlur={cell.onBlur} onSubmitEditing={cell.onSubmit}` — or "" outside a cell editor, or when `component`
   * (the runtime component the strategy renders) cannot fire either. See {@link CELL_EDITOR_COMMIT_SUPPORT}.
   */
  cellEditorCommitProps(component: string): string;
  /**
   * A read-only view of a **structural** child display item — one a strategy renders itself instead of emitting
   * as a node (a Table's TableHeader / TableRow / column). It exposes exactly what such a child contributes:
   * its designer styles and its property values. Cell *content* still goes through {@link emitChild}.
   */
  structuralChild(child: IDisplayItem): IStructuralChildContext;
}

/**
 * The designer token standing in for "this component's own id", carried as the DEFAULT fixed value of the `key`
 * property on StepperItem / TabItem / CollapsePanel / MenuItem / SubMenu. Resolved on the way out — see
 * {@link ReactNativeElementGenerator.resolveValuePlaceholders}.
 */
const COMPONENT_INSTANCE_ID_TOKEN: string = "@componentInstanceId";

/** How a repeater strategy varies the emission of a subtree it renders itself. */
export interface IEmitChildOptions {
  /** Data-repeater nesting for the subtree (a table's cells are one level deeper than the table). */
  repeaterDepth?: number;
  /** The subtree is an editable column's editor, so controlled inputs bind to the row's cell buffer. */
  inCellEditor?: boolean;
  /**
   * Overrides the `textAlign` the subtree inherits — a table cell inherits its column's (or row's) alignment, where a
   * web `td` would, rather than the table's.
   */
  inheritedTextAlign?: string;
  /** Seeds the typography the subtree inherits — see {@link IParentLayout.inheritedTextStyle}. */
  inheritedTextStyle?: Readonly<Record<string, string | number>>;
}

/**
 * What a controlled input's value is typed as, so a cell-scoped binding can coerce the text buffer to it.
 *
 * `"location"` is the odd one out: it is an **object** (the place a LocationPicker / LocationSearch holds), which
 * a per-row text buffer cannot represent at all — see {@link ReactNativeElementGenerator.resolveControlledBinding}.
 */
export type TControlledValueKind = "string" | "boolean" | "number" | "location" | "upload";

/** The expressions a controlled input binds its value and change handler to. */
export interface IControlledBinding {
  valueExpr: string;
  setterExpr: string;
}

/**
 * The slice of a display item a data-repeater strategy needs from a structural child it renders itself: the
 * child's resolved box style (registered on demand) and its fixed / bound property values.
 */
export interface IStructuralChildContext {
  readonly di: IDisplayItem;
  /** Registers the child's resolved box style and returns the reference (`styles.di12`), or "" when empty. */
  registerStyle(): string;
  /**
   * Registers the child's resolved TYPOGRAPHY (`styles.di12Text`) and returns the reference, or "" when it has none —
   * the text half {@link registerStyle} leaves out, for a structural child whose text the runtime draws itself (a
   * table header's titles).
   */
  registerTextStyle(): string;
  /**
   * Registers the child's state-style delta and returns the reference (`styles.di12Pressed`), or "" when the
   * designer authored none. Like {@link registerStyle}, the bare reference rather than a `prop={...}` pair: a
   * structural child is a value inside the emitted row / header / column object, not a JSX element.
   */
  registerStateStyle(state: TStyleStateDelta, pick: TStateStylePick): string;
  /**
   * The boolean expression guarding whether this structural child is rendered at all
   * (`isVisible_di12 !== "hidden"`), or "" when nothing can hide it.
   *
   * <p>A node emitted through `emitNode` gets this guard for free — it wraps the JSX on the one path every
   * component passes through. A structural child never reaches that path: it is emitted as a **value** (a column
   * object inside `columns={[…]}`, a chart series inside `lines={[…]}`), and a value cannot be wrapped in a JSX
   * conditional. The strategy has to spend the guard itself, which is why it is exposed here rather than applied
   * for it. Web does the same thing for the same reason — `base-table.tsx` filters on
   * `col.visibility !== "hidden"` in about a dozen places instead of hiding a column with markup.</p>
   */
  visibilityGuard(): string;
  getFixedPropertyValue(propertyName: string): string;
  /** The child's translation lookup — see the ctx member of the same name. */
  getTranslatedTextExpression(propertyName: string): string;
  getBoundValueMarker(propertyName: string): string;
  /** The child's action-result bind marker (a repeater-row-source datasource) — see the ctx member of the same name. */
  getActionBoundMarker(propertyName: string): string;
  /**
   * The child's resolved typography (fontSize / fontWeight / fontFamily / color). A chart's centre label
   * travels to the runtime as DATA rather than as a style — it is painted on a canvas inside the WebView,
   * where a StyleSheet entry cannot reach — so its font has to be readable here.
   */
  resolveTextStyle(): Record<string, string | number>;
}

/**
 * Emits the JSX for one RN tag, given the shared per-node {@link IComponentEmitContext}. One
 * function per `TReactNativeTag`, registered in {@link RN_COMPONENT_STRATEGIES}; `emitNode` resolves
 * it by tag and delegates. Replaces the former hard-coded if/else dispatch so new tags are added by
 * registering a strategy (OCP). A plain function rather than a class: the strategies are stateless,
 * so an interface + class per tag would be pure ceremony — promote to a class only if a strategy
 * ever needs its own state or configuration.
 */
export type TComponentStrategy = (ctx: IComponentEmitContext) => IEmittedNode;

/**
 * Label → the `KLabel` runtime component (`@/components/kuika`). The generator emits a thin
 * reference; display behavior (value/formatter resolution, defaultText fallback, line clamping,
 * writing mode, link affordance) lives in KLabel so it is authored once instead of inlined into every
 * screen. Props are emitted only when the DI sets them — mirroring the web generator's
 * `shouldIncludeProperty` — so a bare label stays minimal. A navigate action on the label becomes
 * `onPress`; `customFormatter` feeds the `custom date` formatter; `writingMode` is emitted only for
 * the vertical case (horizontal is the default). TextStyle extends ViewStyle, so the full resolved
 * style passes through unpartitioned.
 *
 * A `tooltip` wraps the label in KTooltip (long-press tooltip — no hover on mobile), mirroring the
 * Icon's `hoverText`: KTooltip owns the press surface + a11y label, so the inner KLabel is visual
 * only (no testID/onPress) and the navigate onPress moves to KTooltip.
 */
const emitLabel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const stateStyleProps: string = ctx.stateStyleProps(PRESSED_WHOLE_SPEC);
  const actionMarker: string = ctx.actionMarker();
  const value: string = textPropExpression(ctx, "value");
  const defaultText: string = textPropExpression(ctx, "defaultText");
  const formatter: string = ctx.getFixedPropertyValue("formatter");
  const customFormatter: string = ctx.getFixedPropertyValue("customFormatter");
  const maxLine: string = ctx.getFixedPropertyValue("maxLine");
  const writingMode: string = ctx.getFixedPropertyValue("writingMode").toLowerCase();
  const tooltip: string = ctx.getFixedPropertyValue("tooltip");

  const valueProps: string[] = [];
  // The id a SetValueOf writes into at RUNTIME (KLabel reads it through useScreenValue), emitted
  // unconditionally — the RN mirror of web, where every component sits in a Form.Item and the action
  // writes the form store by name. Emitting it always is the point: the alternative was deciding here
  // which Labels an action targets, and when that detection missed, the write had nowhere to land and
  // the generated handler carried a "owns no state to write" comment for a correctly wired action.
  valueProps.push(`kuikaId={${ctx.di.id}}`);
  // The Label keeps rendering its own literal or data binding; a write only *overrides* it, and only once
  // one happens. That is web's behaviour too (the Form.Item shows the designed value until setFieldsValue
  // replaces it) and it is strictly better than the state-swap this replaced, which seeded a `useState`
  // from the fixed value alone — so a Label bound to a datasource came up blank until the button was
  // pressed.
  if (!pushBoundValueProp(valueProps, ctx, "value", "string") && value !== "") {
    valueProps.push(`value={${value}}`);
  }
  if (defaultText !== "") {
    valueProps.push(`defaultText={${defaultText}}`);
  }
  if (formatter !== "") {
    valueProps.push(`formatter={${JSON.stringify(formatter)}}`);
  }
  if (customFormatter !== "") {
    valueProps.push(`customFormatter={${JSON.stringify(customFormatter)}}`);
  }
  const maxLineValue: number = Number(maxLine);
  if (maxLine !== "" && Number.isInteger(maxLineValue)) {
    valueProps.push(`maxLine={${maxLineValue}}`);
  }
  // Designer exposes Horizontal/Vertical; horizontal is KLabel's default, so only vertical is emitted.
  if (writingMode === "vertical") {
    valueProps.push(`writingMode={"vertical"}`);
  }
  const valuePropsStr: string = valueProps.length > 0 ? ` ${valueProps.join(" ")}` : "";

  if (tooltip !== "") {
    const childIndent: string = INDENT_STEP.repeat(ctx.depth + 1);
    const jsx: string = [
      `${ctx.indent}<KTooltip${ctx.testIdProp} title={${JSON.stringify(tooltip)}}${actionMarker}${ctx.disableWrapperProp()}>`,
      `${childIndent}<KLabel${styleProp}${stateStyleProps}${valuePropsStr}${ctx.disableProp()} />`,
      `${ctx.indent}</KTooltip>`
    ].join("\n");
    return { jsx, usedTags: [], runtimeComponents: ["KLabel", "KTooltip"] };
  }

  const jsx: string = `${ctx.indent}<KLabel${ctx.testIdProp}${styleProp}${stateStyleProps}${ctx.animationProp}${actionMarker}${valuePropsStr}${ctx.disableProp()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KLabel"] };
};

/**
 * Icon → the `KIcon` runtime component (`@/components/kuika`). The generator emits a thin reference;
 * the library/variant → font selection lives in KIcon so it is authored once. The Icon is text-like
 * (a glyph), so its full resolved style (color, fontSize, margins) passes through as `style`
 * unpartitioned — those override KIcon's size/color defaults. `iconName`/`iconLibrary`/`iconType` are
 * emitted only when the DI sets them.
 *
 * `hoverText` (a web hover tooltip — no hover on mobile) becomes a **long-press** tooltip: when set, the
 * KIcon is wrapped in `KTooltip`, which owns the press surface (so the navigate `onPress` and the
 * long-press coexist) and sets the anchor's `accessibilityLabel`. A plain icon (no tooltip) keeps its
 * own `onPress` and stays a bare `<KIcon />`.
 */
const emitIcon: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const stateStyleProps: string = ctx.stateStyleProps(PRESSED_WHOLE_SPEC);
  const actionMarker: string = ctx.actionMarker();
  const iconName: string = ctx.getFixedPropertyValue("iconName");
  const iconLibrary: string = ctx.getFixedPropertyValue("iconLibrary");
  const iconType: string = ctx.getFixedPropertyValue("iconType");
  const hoverText: string = ctx.getFixedPropertyValue("hoverText");

  const valueProps: string[] = [];
  if (iconName !== "") {
    valueProps.push(`iconName={${JSON.stringify(iconName)}}`);
  }
  if (iconLibrary !== "") {
    valueProps.push(`iconLibrary={${JSON.stringify(iconLibrary)}}`);
  }
  if (iconType !== "") {
    valueProps.push(`iconType={${JSON.stringify(iconType)}}`);
  }
  const valuePropsStr: string = valueProps.length > 0 ? ` ${valueProps.join(" ")}` : "";

  // With a tooltip, KTooltip owns the press surface and the a11y label; the inner KIcon is visual only
  // (no testID/onPress). Without one, KIcon carries the testID and its own navigate onPress.
  if (hoverText !== "") {
    const childIndent: string = INDENT_STEP.repeat(ctx.depth + 1);
    const jsx: string = [
      `${ctx.indent}<KTooltip${ctx.testIdProp} title={${JSON.stringify(hoverText)}}${actionMarker}${ctx.disableWrapperProp()}>`,
      `${childIndent}<KIcon${styleProp}${stateStyleProps}${valuePropsStr} />`,
      `${ctx.indent}</KTooltip>`
    ].join("\n");
    return { jsx, usedTags: [], runtimeComponents: ["KIcon", "KTooltip"] };
  }

  const jsx: string = `${ctx.indent}<KIcon${ctx.testIdProp}${styleProp}${stateStyleProps}${ctx.animationProp}${valuePropsStr}${actionMarker} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KIcon"] };
};

/**
 * Kuika input component name → the runtime component the generator emits. Email/Password/NumberInput
 * each extend the KTextInput base and own their variant behavior (email keyboard + clear, secure
 * entry + show/hide, numeric keyboard), so the generator only picks the component and passes the
 * shared props; anything not listed here falls back to the `KTextInput` base.
 */
const INPUT_RUNTIME_COMPONENT: Record<string, string> = {
  Email: "KEmail",
  Password: "KPassword",
  NumberInput: "KNumberInput",
  Currency: "KCurrency",
  Percent: "KPercent",
  DynamicInput: "KDynamicInput",
  TextArea: "KTextArea",
  Url: "KUrl"
};

/** Which of an editable cell's commit hooks a runtime input component can be handed. */
interface ICellEditorCommitSupport {
  /** It declares `onSubmitEditing` (every single-line member of the input family, through IKInputCoreProps). */
  submit: boolean;
  /** It declares `onBlur` (the input family through IKInputCoreProps; KNumberInput after its range clamp). */
  blur: boolean;
}

/**
 * The runtime inputs an editable table cell's editor can commit through, and which hook each can fire. Keyed by the
 * RUNTIME component because that is what decides whether the prop type-checks. KTextArea is multiline — its return
 * key inserts a newline, so `onSubmitEditing` never fires (the same reason ReactNativeEventCatalog does not map
 * TextArea.onPressEnter) and it commits on blur only. KDynamicInput declares neither. A component absent here
 * commits through the table's row change instead.
 */
const CELL_EDITOR_COMMIT_SUPPORT: Readonly<Record<string, ICellEditorCommitSupport>> = {
  KTextInput: { submit: true, blur: true },
  KTextArea: { submit: false, blur: true },
  KEmail: { submit: true, blur: true },
  KPassword: { submit: true, blur: true },
  KUrl: { submit: true, blur: true },
  KNumberInput: { submit: true, blur: true },
  KCurrency: { submit: true, blur: true },
  KPercent: { submit: true, blur: true }
};

/**
 * The cell-editor commit props for `component` (see {@link CELL_EDITOR_COMMIT_SUPPORT}), blur first so the submit
 * prop sits last — directly in front of the action slot, where the C# assembler looks for it.
 */
function cellEditorCommitProps(component: string): string {
  const support: ICellEditorCommitSupport | undefined = CELL_EDITOR_COMMIT_SUPPORT[component];
  if (support === undefined) {
    return "";
  }
  const blur: string = support.blur ? " onBlur={cell.onBlur}" : "";
  const submit: string = support.submit ? " onSubmitEditing={cell.onSubmit}" : "";
  return `${blur}${submit}`;
}

/**
 * Emits a bool prop into `props`: the bare flag when the DI value is "true", `name={false}` when it is
 * "false" (nothing when unset). Mirrors JSX where a bare attribute is `true`, so the emitted value is
 * the DI's boolean verbatim (matching the web `shouldIncludeProperty` "emit when set" rule).
 */
function pushBoolProp(props: string[], name: string, value: string): void {
  if (value === "true") {
    props.push(name);
  } else if (value === "false") {
    props.push(`${name}={false}`);
  }
}

/**
 * A data repeater's "show the empty state" flag, deferred until the screen has actually looked.
 *
 * A repeater seeds its `ds<Key>` slot empty, so on the first frame it has no rows — and saying "No data found"
 * there states something the screen does not yet know: the page init has not run. The user sees the empty
 * illustration and message, then the rows arrive and replace them. So when the designer turned the empty state
 * ON, it is emitted gated on {@link PAGE_INIT_FINISHED_VARIABLE} instead of as a bare `true`.
 *
 * Off stays off — there is nothing to defer — and the runtime already draws nothing for `false` (KTable renders
 * an empty `View`; the list repeaters resolve to their `"blank"` state), so this needs no runtime change.
 *
 * Deliberately NOT the repeater's `loading` prop: that puts a spinner inside the table, and the page-init window
 * is exactly where the app-wide overlay is suppressed because the skeleton owns it. A third loading affordance
 * there would be noise. "We have not looked yet" is best said by showing nothing.
 */
function pushEmptyStateProp(props: string[], name: string, value: string, marker: string = ""): void {
  // A bound flag is deferred the same way: whatever it resolves to, nothing has been looked at before page init.
  if (marker !== "") {
    props.push(`${name}={${PAGE_INIT_FINISHED_VARIABLE} && ${boundBooleanExpression(marker)}}`);
    return;
  }
  if (value === "true") {
    props.push(`${name}={${PAGE_INIT_FINISHED_VARIABLE}}`);
    return;
  }
  pushBoolProp(props, name, value);
}

/** Emits `name={n}` when the DI value parses to a finite number (integers only when `integer`). */
function pushNumberProp(props: string[], name: string, value: string, integer: boolean): void {
  if (value === "") {
    return;
  }
  const numeric: number = Number(value);
  const valid: boolean = integer ? Number.isInteger(numeric) : Number.isFinite(numeric);
  if (valid) {
    props.push(`${name}={${numeric}}`);
  }
}

/**
 * Emits a value-carrying prop that may be data-bound, coercing a bound marker to the prop's TS type so the
 * generated screen type-checks: expo-router params read as `string | string[]`, so a raw read cannot flow into
 * a `string`/`number` prop. Bound → `<prop>={String(<marker> ?? "")}` (string) / `<prop>={Number(<marker> ?? 0)}`
 * (number); the C# assembler substitutes the inner marker with the resolved read expression. Returns true when a
 * bound marker was emitted, so the caller skips its fixed-value branch.
 */
function pushBoundValueProp(
  props: string[],
  ctx: IBindablePropertySource,
  propName: string,
  kind: "string" | "number"
): boolean {
  const marker: string = ctx.getBoundValueMarker(propName);
  if (marker === "") {
    return false;
  }
  props.push(`${propName}={${boundValueExpression(marker, kind)}}`);
  return true;
}

/**
 * What the bound-aware prop helpers need of a context: the literal and the bind marker. Both the full
 * {@link IComponentEmitContext} and a {@link IStructuralChildContext} (a table header / column) satisfy it.
 */
interface IBindablePropertySource {
  getFixedPropertyValue(propertyName: string): string;
  getBoundValueMarker(propertyName: string): string;
}

/** A bound marker coerced to a `string` / `number` prop's type — see {@link pushBoundValueProp}. */
function boundValueExpression(marker: string, kind: "string" | "number"): string {
  return kind === "number" ? `Number(${marker} ?? 0)` : `String(${marker} ?? "")`;
}

/**
 * A bound marker coerced to a `boolean` prop. Through `ReactSystemFunctions.toBoolean` rather than `Boolean(…)`,
 * because a bound value is as often a STRING as a boolean — a screen input is a route param, a backend flag arrives
 * as `"False"` — and `Boolean("false")` is `true`. The same coercion the boolean inputs' datasource seed uses; the C#
 * assembler imports ReactSystemFunctions whenever the JSX references it.
 */
function boundBooleanExpression(marker: string): string {
  return `ReactSystemFunctions.toBoolean(null, ${marker})`;
}

/**
 * A boolean prop that may be data-bound: `name={ReactSystemFunctions.toBoolean(null, <marker>)}` when bound, else the
 * fixed value exactly as {@link pushBoolProp} spells it (so a fixed-value screen is byte-identical to before).
 */
function pushBindableBoolProp(
  props: string[],
  source: IBindablePropertySource,
  propName: string,
  jsxName: string = propName
): void {
  const marker: string = source.getBoundValueMarker(propName);
  if (marker !== "") {
    props.push(`${jsxName}={${boundBooleanExpression(marker)}}`);
    return;
  }
  pushBoolProp(props, jsxName, source.getFixedPropertyValue(propName));
}

/** A number prop that may be data-bound: `Number(<marker> ?? 0)` when bound, else {@link pushNumberProp}. */
function pushBindableNumberProp(
  props: string[],
  source: IBindablePropertySource,
  propName: string,
  integer: boolean
): void {
  if (pushBoundValueProp(props, source, propName, "number")) {
    return;
  }
  pushNumberProp(props, propName, source.getFixedPropertyValue(propName), integer);
}

/** A string prop that may be data-bound: `String(<marker> ?? "")` when bound, else {@link pushStringProp}. */
function pushBindableStringProp(props: string[], source: IBindablePropertySource, propName: string): void {
  if (pushBoundValueProp(props, source, propName, "string")) {
    return;
  }
  pushStringProp(props, propName, source.getFixedPropertyValue(propName));
}

/** Prefix marking a MicroApp DI property that carries one embedded screen input (`screenInput_<inputName>`). */
export const MICRO_APP_SCREEN_INPUT_PREFIX: string = "screenInput_";

/**
 * Folds a MicroApp DI's `screenInput_<name>` properties into a `screenInputs={{ ... }}` record prop for
 * KMicroApp, ordinal-sorted by input name so the output is byte-stable regardless of designer property order.
 * Each entry is a bound marker coerced to string (`String(<marker> ?? "")` — embedded inputs arrive as
 * route-param strings) or a fixed string literal; the C# assembler later substitutes the inner markers exactly
 * like every other bound prop. Returns "" when the DI declares no screen inputs, so the caller omits the prop.
 */
function buildScreenInputsRecord(ctx: IComponentEmitContext): string {
  const inputNames: string[] = (ctx.di.item.properties ?? [])
    .map((property: IDisplayItemProperty): string => property.name)
    .filter((name: string): boolean => name.startsWith(MICRO_APP_SCREEN_INPUT_PREFIX))
    .sort(compareOrdinal);
  if (inputNames.length === 0) {
    return "";
  }
  const entries: string[] = inputNames.map((propertyName: string): string => {
    const inputName: string = propertyName.slice(MICRO_APP_SCREEN_INPUT_PREFIX.length);
    const key: string = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(inputName) ? inputName : JSON.stringify(inputName);
    const marker: string = ctx.getBoundValueMarker(propertyName);
    if (marker !== "") {
      return `${key}: String(${marker} ?? "")`;
    }
    return `${key}: ${JSON.stringify(ctx.getFixedPropertyValue(propertyName))}`;
  });
  return `screenInputs={{ ${entries.join(", ")} }}`;
}

/**
 * NumberInput's numeric-format props → the `KNumberInput` runtime component, mirroring the web
 * NumberInput (react-number-format): decimals (`decimalSeparator`/`decimalScale`/`fixedDecimalScale`),
 * grouping (`isThousandSeperatorOn` → `thousandSeparator`), sign/empty behaviour (`allowNegative`/
 * `allowEmptyFormatting`), affixes (`prefix`/`suffix`), pattern (`format`/`mask`), and `min`/`max`
 * clamping. Each prop is emitted only when the DI sets it (web `shouldIncludeProperty`).
 */
function pushNumberInputProps(ctx: IComponentEmitContext, props: string[]): void {
  const decimalSeparator: string = ctx.getFixedPropertyValue("decimalSeparator");
  if (decimalSeparator !== "") {
    props.push(`decimalSeparator=${JSON.stringify(decimalSeparator)}`);
  }
  pushNumberProp(props, "decimalScale", ctx.getFixedPropertyValue("decimalScale"), true);
  pushBoolProp(props, "fixedDecimalScale", ctx.getFixedPropertyValue("fixedDecimalScale"));
  // Web keeps the prop name `isThousandSeperatorOn`; the runtime prop is the react-number-format-style
  // `thousandSeparator` (the grouping mark is derived from the decimal separator inside KNumberInput).
  pushBoolProp(props, "thousandSeparator", ctx.getFixedPropertyValue("isThousandSeperatorOn"));
  pushBoolProp(props, "allowNegative", ctx.getFixedPropertyValue("allowNegative"));
  pushBoolProp(props, "allowEmptyFormatting", ctx.getFixedPropertyValue("allowEmptyFormatting"));
  pushTextAttr(props, ctx, "prefix");
  pushTextAttr(props, ctx, "suffix");
  const format: string = ctx.getFixedPropertyValue("format");
  if (format !== "") {
    props.push(`format=${JSON.stringify(format)}`);
  }
  const mask: string = ctx.getFixedPropertyValue("mask");
  if (mask !== "") {
    props.push(`mask=${JSON.stringify(mask)}`);
  }
  pushNumberProp(props, "min", ctx.getFixedPropertyValue("min"), false);
  pushNumberProp(props, "max", ctx.getFixedPropertyValue("max"), false);
}

/**
 * Currency's numeric-format props → the `KCurrency` runtime component (a `KNumberInput` with grouping
 * always on). It exposes the web Currency's strict subset of the NumberInput surface —
 * `decimalSeparator`, `decimalScale`, `fixedDecimalScale`, `allowNegative`, `prefix`, `suffix` — and no
 * more: Currency has no `min`/`max`, no "#"-pattern (`format`/`mask`), no `allowEmptyFormatting`, and no
 * `isThousandSeperatorOn` toggle (KCurrency pins `thousandSeparator`), so those are never emitted. Each
 * prop is emitted only when the DI sets it (web `shouldIncludeProperty`).
 */
function pushCurrencyProps(ctx: IComponentEmitContext, props: string[]): void {
  const decimalSeparator: string = ctx.getFixedPropertyValue("decimalSeparator");
  if (decimalSeparator !== "") {
    props.push(`decimalSeparator=${JSON.stringify(decimalSeparator)}`);
  }
  pushNumberProp(props, "decimalScale", ctx.getFixedPropertyValue("decimalScale"), true);
  pushBoolProp(props, "fixedDecimalScale", ctx.getFixedPropertyValue("fixedDecimalScale"));
  pushBoolProp(props, "allowNegative", ctx.getFixedPropertyValue("allowNegative"));
  pushTextAttr(props, ctx, "prefix");
  pushTextAttr(props, ctx, "suffix");
}

/**
 * DynamicInput's own props → the `KDynamicInput` runtime component, which is a SWITCH rather than an input: the
 * `dataType` picks which editor it becomes, and `dynamicformatter` carries the designer's per-type formatter
 * choices as an authored list.
 *
 * Three of the designer's properties are deliberately absent:
 * - `type` is the DOM input type the web component computes for ITSELF from `dataType` (`props.type = "text"` /
 *   `"checkbox"` / `"datetime"`) before handing it to antd. React Native has no such attribute, and the branch is
 *   already chosen by `dataType`, so forwarding the designer's own `type` would emit a prop nothing reads.
 * - `id` is a DOM id. There is no DOM.
 * - `disabled` reaches the tag through the shared input path's single `editable` slot, which an authorization
 *   disable owns first; a second source would emit a duplicate attribute.
 */
function pushDynamicInputProps(ctx: IComponentEmitContext, props: string[]): void {
  const dataType: string = ctx.getFixedPropertyValue("dataType");
  if (dataType !== "") {
    props.push(`dataType=${JSON.stringify(dataType)}`);
  }
  const formatters: string = ctx.getFixedPropertyValue("dynamicformatter").trim();
  if (formatters.startsWith("[")) {
    props.push(`dynamicFormatter={${formatters}}`);
  }
  const defaultValue: string = ctx.getFixedPropertyValue("defaultValue");
  if (defaultValue !== "") {
    props.push(`defaultValue=${JSON.stringify(defaultValue)}`);
  }
  const size: string = ctx.getFixedPropertyValue("size");
  if (size === "large" || size === "middle" || size === "small") {
    props.push(`size=${JSON.stringify(size)}`);
  }
  if (ctx.getFixedPropertyValue("bordered") === "false") {
    props.push("bordered={false}");
  }
  if (ctx.getFixedPropertyValue("allowClear") === "true") {
    props.push("allowClear");
  }
}

/**
 * Percent's own props → the `KPercent` runtime component (a `KNumberInput` bounded to 0–100 with a "%" affix).
 * Only three of the designer's properties reach the tag, and the rest are absent on purpose:
 *
 * - `symbolPosition` picks the side the "%" sits on; `decimalScale` is the shared numeric one; `step` is the
 *   ArrowUp / ArrowDown increment, which RN reports through `onKeyPress` exactly where web reports it — under a
 *   hardware keyboard.
 * - `readOnly` and `disabled` both mean "not editable", and the shared input path already owns the single
 *   `editable` slot (an authorization disable owns it first), so a second source would emit a duplicate attribute.
 * - `stringMode` is **inert on web**: the Percent spreads it into `react-number-format`, which has no such option
 *   (it is antd `InputNumber`'s). Emitting it here would invent behaviour the web target does not have.
 * - `value` / `placeholder` / `autoFocus` come from the shared input path, like every other input's.
 */
function pushPercentProps(ctx: IComponentEmitContext, props: string[]): void {
  const symbolPosition: string = ctx.getFixedPropertyValue("symbolPosition");
  if (symbolPosition === "left" || symbolPosition === "right") {
    props.push(`symbolPosition=${JSON.stringify(symbolPosition)}`);
  }
  pushNumberProp(props, "decimalScale", ctx.getFixedPropertyValue("decimalScale"), true);
  pushNumberProp(props, "step", ctx.getFixedPropertyValue("step"), false);
}

/**
 * TextArea's multi-line props → the `KTextArea` runtime component (an RN multiline `<TextInput>`):
 * `autoSize` (grow with content), `showCount` (character counter), `readOnly`, and the speech-to-text
 * mic (`speechToText`, web TextArea parity). Each emitted only when the DI turns it on (web
 * `shouldIncludeProperty`); the shared value/placeholder/formatter/maxLength/allowClear props come
 * from the common input path.
 */
function pushTextAreaProps(ctx: IComponentEmitContext, props: string[]): void {
  if (ctx.getFixedPropertyValue("autoSize") === "true") {
    props.push("autoSize");
  }
  if (ctx.getFixedPropertyValue("showCount") === "true") {
    props.push("showCount");
  }
  if (ctx.getFixedPropertyValue("readOnly") === "true") {
    props.push("readOnly");
  }
  if (ctx.getFixedPropertyValue("speechToText") === "true") {
    props.push("speechToText");
  }
}

/**
 * Email/Password/NumberInput/TextInput/TextArea → a Kuika runtime input component (`@/components/kuika`).
 * Only the props each component actually defines are emitted (matching the web contracts): all inputs get
 * value/onChangeText/placeholder/editable; Email/TextInput/TextArea also get allowClear/maxLength;
 * autoComplete is Email/TextInput; the case formatter is TextInput/TextArea and the character filters are
 * TextInput-only, as is the full presentation surface (size, bordered, prefix/suffix + iconColor,
 * addonBefore/addonAfter, mask, speechToText, and the static disabled→editable flag); NumberInput gets
 * the full numeric-format surface ({@link pushNumberInputProps}) and TextArea its multi-line surface
 * ({@link pushTextAreaProps}); Password stays minimal. value/onChangeText are wired to a screen-level
 * useState hook.
 */
const emitTextInput: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const stateStyleProps: string = ctx.stateStyleProps(INPUT_STATE_SPEC);
  // Formatter + character filtering are TextInput-only. The allowClear/maxLength block covers
  // Email + Url + TextInput + TextArea (Url has only allowClear — its absent maxLength no-ops);
  // autoComplete is Email + TextInput. Password exposes neither group; NumberInput has its own.
  const isPlainTextInput: boolean = ctx.componentName === "TextInput";
  const isEmail: boolean = ctx.componentName === "Email";
  const isUrl: boolean = ctx.componentName === "Url";
  const isTextArea: boolean = ctx.componentName === "TextArea";
  const isNumberInput: boolean = ctx.componentName === "NumberInput";
  const isCurrency: boolean = ctx.componentName === "Currency";
  const isPercent: boolean = ctx.componentName === "Percent";
  const isDynamicInput: boolean = ctx.componentName === "DynamicInput";
  const supportsFormatter: boolean = isPlainTextInput || isTextArea;
  const supportsClearAndMaxLength: boolean = isPlainTextInput || isEmail || isUrl || isTextArea;

  // Props are emitted only when the DI sets them (mirrors the web `shouldIncludeProperty`).
  const textBinding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${textBinding.valueExpr}}`, `onChangeText={${textBinding.setterExpr}}`];
  // Field-level validation errors (RNM-0108): when the DI carries designer validations, the input reads its error
  // list off the screen's `validationErrors` map (keyed by `di<id>`) — the C# pre-action gate writes it and
  // ReactNativeScreenGenerator declares the state, the same Node-references / C#-declares seam as `value={di<id>}`.
  const validations: IValidation[] | undefined = ctx.di.item.validations;
  if (validations !== undefined && validations.length > 0) {
    props.push(`errors={validationErrors[${JSON.stringify(ctx.styleName)}]}`);
  }
  pushTextAttr(props, ctx, "placeholder");
  if (supportsFormatter) {
    const formatter: string = ctx.getFixedPropertyValue("formatter");
    if (formatter !== "" && formatter !== "None") {
      props.push(`formatter=${JSON.stringify(formatter)}`);
    }
  }
  if (isPlainTextInput) {
    const inputMode: string = ctx.getFixedPropertyValue("inputMode");
    if (ctx.getFixedPropertyValue("isCharOnly") === "true" || inputMode === "char") {
      props.push("isCharOnly");
    }
    if (ctx.getFixedPropertyValue("isNumberOnly") === "true" || inputMode === "number") {
      props.push("isNumberOnly");
    }
    const allowedChars: string = ctx.getFixedPropertyValue("allowedChars");
    if (allowedChars !== "") {
      props.push(`allowedChars=${JSON.stringify(allowedChars)}`);
    }
  }
  if (supportsClearAndMaxLength) {
    const maxLength: string = ctx.getFixedPropertyValue("maxLength");
    const maxLengthValue: number = Number(maxLength);
    // A mask governs the field's length through its own pattern; forwarding maxLength would clip the MASKED
    // string (mask literals included) so the pattern could never complete. Web deletes maxLength under a mask
    // (KOA-18325), so we skip emitting it here too.
    const hasMask: boolean = ctx.getFixedPropertyValue("mask") !== "";
    if (maxLength !== "" && Number.isInteger(maxLengthValue) && !hasMask) {
      props.push(`maxLength={${maxLengthValue}}`);
    }
    if ((isPlainTextInput || isEmail) && ctx.getFixedPropertyValue("autoComplete") === "false") {
      props.push(`autoComplete="off"`);
    }
    if (ctx.getFixedPropertyValue("allowClear") === "true") {
      props.push("allowClear");
    }
  }
  if (isPlainTextInput) {
    // Presentation props unique to the plain TextInput (web/antd parity): size preset, borderless
    // toggle, prefix/suffix icons + their tint, before/after addon labels, input mask, and the
    // speech-to-text mic. Each is emitted only when the DI actually sets it (web shouldIncludeProperty).
    const size: string = ctx.getFixedPropertyValue("size");
    if (size === "large" || size === "middle" || size === "small") {
      props.push(`size=${JSON.stringify(size)}`);
    }
    if (ctx.getFixedPropertyValue("bordered") === "false") {
      props.push("bordered={false}");
    }
    // Read before they are pushed: iconColor below is emitted only when there is an icon to tint, and a
    // TRANSLATED prefix still is one — so presence is the expression's, not the raw literal's.
    const hasPrefix: boolean = textPropExpression(ctx, "prefix") !== "";
    const hasSuffix: boolean = textPropExpression(ctx, "suffix") !== "";
    pushTextAttr(props, ctx, "prefix");
    pushTextAttr(props, ctx, "suffix");
    const speechToText: boolean = ctx.getFixedPropertyValue("speechToText") === "true";
    // iconColor only tints the prefix/suffix/mic icons, so emit it only when one of them is present.
    const iconColor: string = ctx.getFixedPropertyValue("iconColor");
    if (iconColor !== "" && (hasPrefix || hasSuffix || speechToText)) {
      props.push(`iconColor=${JSON.stringify(iconColor)}`);
    }
    const addonBefore: string = ctx.getFixedPropertyValue("addonBefore");
    if (addonBefore !== "") {
      props.push(`addonBefore=${JSON.stringify(addonBefore)}`);
    }
    const addonAfter: string = ctx.getFixedPropertyValue("addonAfter");
    if (addonAfter !== "") {
      props.push(`addonAfter=${JSON.stringify(addonAfter)}`);
    }
    const mask: string = ctx.getFixedPropertyValue("mask");
    if (mask !== "") {
      props.push(`mask=${JSON.stringify(mask)}`);
    }
    // contentType is a native-only autofill hint (web ignores it); KTextInput maps it to iOS textContentType /
    // Android autoComplete, mirroring the native generators (KOA-18325). Default carries no hint.
    const contentType: string = ctx.getFixedPropertyValue("contentType");
    if (contentType !== "" && contentType !== "Default") {
      props.push(`contentType=${JSON.stringify(contentType)}`);
    }
    if (speechToText) {
      props.push("speechToText");
    }
  }
  if (isNumberInput) {
    pushNumberInputProps(ctx, props);
  }
  if (isCurrency) {
    pushCurrencyProps(ctx, props);
  }
  if (isPercent) {
    pushPercentProps(ctx, props);
  }
  if (isDynamicInput) {
    pushDynamicInputProps(ctx, props);
  }
  if (isTextArea) {
    pushTextAreaProps(ctx, props);
  }
  const disablePropStr: string = ctx.disableProp();
  // A design-time `disabled` becomes editable={false}. When authorization already drives `editable`
  // (disablePropStr is non-empty), that guard owns the single editable slot, so the static flag is
  // only emitted when there is no auth-disable — avoiding a duplicate `editable` attribute.
  //
  // This covers the WHOLE input family, not just plain TextInput: Password, Email, Url, NumberInput,
  // Currency, Percent, DynamicInput and TextArea all declare `disabled` in their metadata and all route to a
  // runtime component whose disable IS `editable={false}` (IKInputCoreProps). Gating it on `isPlainTextInput`
  // silently dropped the property everywhere else — a field the designer marked disabled shipped writable.
  //
  // Percent is the one member that ALSO declares `readOnly` (0032_Percent.json), and KPercent has no `readOnly`
  // prop of its own to take it — KTextArea does, and pushTextAreaProps emits it there — so it lowers to the same
  // `editable={false}`. Web reaches the identical end state: a read-only number input accepts no typing.
  const isStaticallyLocked: boolean =
    ctx.getFixedPropertyValue("disabled") === "true" || (isPercent && ctx.getFixedPropertyValue("readOnly") === "true");
  if (disablePropStr === "" && isStaticallyLocked) {
    props.push("editable={false}");
  }
  // A SetFocusTo target gets the focus handle the C# leg declares. Emitted only by this strategy because
  // only the input family implements IKFocusable — a Label the designer aimed a SetFocusTo at has nothing to
  // focus, so it emits no reference, gets no declaration, and the action lowers to a visible gap instead.
  const focusRefVar: string | null = ctx.focusRefVar();
  if (focusRefVar !== null) {
    props.push(`focusRef={${focusRefVar}}`);
  }
  const component: string = INPUT_RUNTIME_COMPONENT[ctx.componentName] ?? "KTextInput";
  // An editable table cell's editor commits on the return key and on blur, as web's editable cell does. Spliced
  // IMMEDIATELY before the action slot: C# folds `onSubmitEditing={cell.onSubmit}` into whatever return-key handler
  // it wires at that slot (the input's onPressEnter, the screen's Press Enter), since a tag cannot carry the prop twice.
  const cellCommitProps: string = ctx.cellEditorCommitProps(component);

  // The action slot: an input's free event props (`onSubmitEditing`, the designer's onPressEnter) land here.
  // Its `onChange` does NOT — that maps to the `onChangeText` above, which C# rewrites in place so the input
  // keeps its controlled state.
  const jsx: string = `${ctx.indent}<${component}${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${cellCommitProps}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: [component] };
};

/** antd's button sizes, the values KButton's `size` prop accepts. */
const BUTTON_SIZES: ReadonlySet<string> = new Set<string>(["small", "middle", "large"]);

/**
 * Button → the `KButton` runtime component (`@/components/kuika`). The generator emits a thin
 * reference; interaction behavior (icon placement, loading spinner, full-width block, disabled
 * gating) lives in KButton so it is authored once instead of inlined into every screen. Box styles
 * stay on the button (`style`), typography moves onto the label (`labelStyle`) — the same split the
 * web emitter uses. Props are emitted only when the DI sets them (and the icon config only when an
 * icon is present), mirroring the web generator's `shouldIncludeProperty`, so a bare button stays
 * minimal and byte-stable. onPress resolves the button's action (signin > signup > logout > forgot >
 * verify > reset > navigate); disable-mode authorization becomes a `disabled` prop.
 */
const emitButton: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const labelStyleName: string = `${ctx.styleName}Label`;
  const labelStyleRegistered: string = ctx.registerStyle(labelStyleName, ctx.partitioned.textStyle);
  const labelStyleProp: string = labelStyleRegistered === "" ? "" : ` labelStyle={styles.${labelStyleName}}`;
  const stateStyleProps: string = ctx.stateStyleProps(BUTTON_STATE_SPEC);

  const label: string = textPropExpression(ctx, "label");
  const icon: string = ctx.getFixedPropertyValue("icon");
  const iconLibrary: string = ctx.getFixedPropertyValue("iconLibrary");
  const iconType: string = ctx.getFixedPropertyValue("iconType");
  const iconPosition: string = ctx.getFixedPropertyValue("iconPosition");
  const loading: string = ctx.getFixedPropertyValue("loading");
  const block: string = ctx.getFixedPropertyValue("block");
  const size: string = ctx.getFixedPropertyValue("size");

  const valueProps: string[] = [];
  // The label can be bound — a button inside a table cell is labelled from the row (web resolves the same
  // binding into its per-row token). Fixed text is the fallback.
  if (!pushBoundValueProp(valueProps, ctx, "label", "string") && label !== "") {
    valueProps.push(`label={${label}}`);
  }
  if (icon !== "") {
    valueProps.push(`icon={${JSON.stringify(icon)}}`);
    if (iconLibrary !== "") {
      valueProps.push(`iconLibrary={${JSON.stringify(iconLibrary)}}`);
    }
    if (iconType !== "") {
      valueProps.push(`iconType={${JSON.stringify(iconType)}}`);
    }
    if (iconPosition !== "") {
      valueProps.push(`iconPosition={${JSON.stringify(iconPosition)}}`);
    }
  }
  if (loading === "true") {
    valueProps.push("loading");
  }
  if (block === "true") {
    valueProps.push("block");
  }
  // KButton floors its height at the size's antd height, as the web Button does; `middle` is its default.
  if (BUTTON_SIZES.has(size) && size !== "middle") {
    valueProps.push(`size={${JSON.stringify(size)}}`);
  }
  const valuePropsStr: string = valueProps.length > 0 ? ` ${valueProps.join(" ")}` : "";

  const actionMarker: string = ctx.actionMarker();
  const disablePropStr: string = ctx.disableProp();

  const jsx: string = `${ctx.indent}<KButton${ctx.testIdProp}${styleProp}${labelStyleProp}${stateStyleProps}${ctx.animationProp}${valuePropsStr}${actionMarker}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KButton"] };
};

/**
 * Image → the `KImage` runtime component (`@/components/kuika`). Backed by expo-image (better disk
 * caching via SDWebImage/Glide). Props emitted only when the DI sets them (matches web parity):
 * `value` (source URI), `imageFit` (fill/fit/stretch → contentFit mapping inside KImage),
 * `placeHolderImage` (placeholder URI), `position` (content alignment), `accessibilityLabel`.
 *
 * The `kuikaid` marker carries the designer's `onClick`: KImage turns its own box into the press surface when
 * one is bound (and flashes it, like every other clickable component).
 */
const emitImage: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => emitImageLike(ctx, "KImage");

/**
 * Thumbnail → the `KThumbnail` runtime component. The designer's Thumbnail and Image share renderer 94 and take
 * the same properties — web ships them as two components (`k-thumbnail.tsx` / `web-components/image`) that differ
 * only in what a **PDF-valued** thumbnail renders, and the RN pair mirrors that split: `KThumbnail` composes
 * `KImage` and adds the document card. So the emission is the Image's, byte for byte, with the tag swapped —
 * which is why both go through {@link emitImageLike} rather than through two prop lists that could drift.
 */
const emitThumbnail: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode =>
  emitImageLike(ctx, "KThumbnail");

/** The shared body of {@link emitImage} and {@link emitThumbnail} — see both for why they share one. */
function emitImageLike(ctx: IComponentEmitContext, tagName: string): IEmittedNode {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(PRESSED_BOX_SPEC);
  const props: string[] = [];
  // The id a SetValueOf writes into at RUNTIME (KImage reads it through useScreenValue, KThumbnail forwards it),
  // emitted unconditionally for the same reason the Label's is: the image keeps its own literal or binding until
  // an action writes — "take a photo, then show it in Image3" is that write — and nothing here has to recognise
  // the action in advance.
  props.push(`kuikaId={${ctx.di.id}}`);
  if (!pushBoundValueProp(props, ctx, "value", "string")) {
    const value: string = textPropExpression(ctx, "value");
    if (value !== "") {
      props.push(`value={${value}}`);
    }
  }
  const imageFit: string = ctx.getFixedPropertyValue("imageFit");
  if (imageFit !== "") {
    props.push(`imageFit={${JSON.stringify(imageFit)}}`);
  }
  const placeHolderImage: string = ctx.getFixedPropertyValue("placeHolderImage");
  if (placeHolderImage !== "") {
    props.push(`placeHolderImage={${JSON.stringify(placeHolderImage)}}`);
  }
  const position: string = ctx.getFixedPropertyValue("position");
  if (position !== "") {
    props.push(`position={${JSON.stringify(position)}}`);
  }
  const accessibilityLabel: string = ctx.getFixedPropertyValue("accessibilityLabel");
  if (accessibilityLabel !== "") {
    props.push(`accessibilityLabel={${JSON.stringify(accessibilityLabel)}}`);
  }
  // zoomOnClick (opt-in, default false): KImage opens a full-screen pinch/pan zoom viewer on tap, matching web
  // (react-medium-image-zoom) and the native ImagePincher (KOA-18276).
  if (ctx.getFixedPropertyValue("zoomOnClick") === "true") {
    props.push("zoomOnClick={true}");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  // The action marker is the slot for BOTH of the Image's events: the designer's onClick (KImage makes its own
  // box the press surface) and an editable image's onChange (KImage's picker → the designer action). A display-only
  // image with neither binds nothing, so the marker is stripped and the tag stays byte-identical.
  const jsx: string = `${ctx.indent}<${tagName}${ctx.testIdProp}${styleProp}${stateStyleProps}${ctx.animationProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: [tagName] };
}

/**
 * WebView → the `KWebView` runtime component (`@/components/kuika`): embeds web content with
 * react-native-webview — the RN counterpart of the web target's `<iframe>`, and native embedding (the platform
 * WebView), not a fallback. The `url` is a fixed literal, a data binding, or state a SetValueOf writes (the web
 * WebView exposes `setUrl`); KWebView normalises it (a bare host gains `https://`). Box style only, no children.
 */
const emitWebView: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (ctx.isSetValueTarget()) {
    props.push(`url={di${ctx.di.id}}`);
  } else if (!pushBoundValueProp(props, ctx, "url", "string")) {
    const url: string = ctx.getFixedPropertyValue("url");
    if (url !== "") {
      props.push(`url={${JSON.stringify(url)}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KWebView${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KWebView"] };
};

/**
 * VideoPlayer → the `KVideo` runtime component (`@/components/kuika`). The web VideoPlayer is `react-player`, which
 * plays any URL (YouTube/Vimeo/direct media) by embedding; the RN port mirrors that through `KVideo`, which embeds
 * via the existing KWebView (react-native-webview) — so **every URL the web plays works here too**, with no native
 * video dependency. Display-only, like WebView: the `url` is a fixed literal, a data binding, or a SetValueOf target
 * (the web player rebinds when its url changes); the `controls`/`muted`/`loop`/`playing` flags mirror react-player
 * and become embed query params in the runtime. Box style only, no children.
 */
const emitVideoPlayer: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (ctx.isSetValueTarget()) {
    props.push(`url={di${ctx.di.id}}`);
  } else if (!pushBoundValueProp(props, ctx, "url", "string")) {
    const url: string = ctx.getFixedPropertyValue("url");
    if (url !== "") {
      props.push(`url={${JSON.stringify(url)}}`);
    }
  }
  if (ctx.getFixedPropertyValue("controls") === "true") {
    props.push("controls");
  }
  if (ctx.getFixedPropertyValue("muted") === "true") {
    props.push("muted");
  }
  if (ctx.getFixedPropertyValue("loop") === "true") {
    props.push("loop");
  }
  if (ctx.getFixedPropertyValue("playing") === "true") {
    props.push("playing");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KVideo${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KVideo"] };
};

/**
 * MicroApp → the `KMicroApp` runtime component (`@/components/kuika`): embeds another Kuika project's
 * screen in-place — the RN counterpart of the web `MfeContainer`. The generator emits a thin, self-closing
 * reference (which project, which starting screen, the bound/fixed screen inputs); the actual embedding —
 * loader tier, auth handoff, embedded navigation — lives in KMicroApp so it is authored once instead of
 * inlined into every screen. `projectId`/`startingScreen` are fixed value props; `screenInput_*` properties
 * fold into the `screenInputs` record (see {@link buildScreenInputsRecord}). The embedded app owns its own
 * interactivity, so no action marker and no disable affordance are emitted.
 */
const emitMicroApp: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const projectId: string = ctx.getFixedPropertyValue("projectId");
  if (projectId !== "") {
    props.push(`projectId={${JSON.stringify(projectId)}}`);
  }
  const startingScreen: string = ctx.getFixedPropertyValue("startingScreen");
  if (startingScreen !== "") {
    props.push(`startingScreen={${JSON.stringify(startingScreen)}}`);
  }
  const screenInputsProp: string = buildScreenInputsRecord(ctx);
  if (screenInputsProp !== "") {
    props.push(screenInputsProp);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KMicroApp${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KMicroApp"] };
};

/**
 * Checkbox → the `KCheckbox` runtime component (`@/components/kuika`). Custom Pressable-based; mirrors
 * the Switch track: each Checkbox gets its own `React.useState<boolean>` (declared by the C#
 * `ReactNativeScreenGenerator`, seeded from the designer's `checked`). `value`/`onValueChange` wire that
 * controlled state.
 *
 * The **live** Checkbox is `Metadatas/Components/0113_Checkbox.json`, whose designer surface is
 * `label` / `checked` / `boxColor` / `boxColorOnChecked` / `checkIconColor` (+ `disabled`). This emitter
 * was written against the retired `0046_Checkbox_DEPRECATED.json` shape (`value` + a single `color`), so
 * a designer-configured checkbox lost its caption, its initial checked state and all three colors. The
 * deprecated `color` is still read as a fallback for `boxColorOnChecked` so older screens keep working.
 */
const emitCheckbox: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const booleanBinding: IControlledBinding = ctx.controlledBinding("boolean");
  const props: string[] = [`value={${booleanBinding.valueExpr}}`, `onValueChange={${booleanBinding.setterExpr}}`];
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    // `children` is the caption web hands antd's Checkbox as its content — the deprecated component's only
    // caption, and a fallback the live one also carries — so it is read when `label` is empty.
    const label: string = textPropExpression(ctx, "label") || textPropExpression(ctx, "children");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "") {
    const disabled: string = ctx.getFixedPropertyValue("disabled");
    if (disabled === "true") {
      props.push("disabled");
    }
  }
  const boxColor: string = ctx.getFixedPropertyValue("boxColor");
  if (boxColor !== "") {
    props.push(`boxColor={${JSON.stringify(boxColor)}}`);
  }
  // `color` is the deprecated component's single fill property — the checked color's legacy spelling.
  const boxColorOnChecked: string =
    ctx.getFixedPropertyValue("boxColorOnChecked") || ctx.getFixedPropertyValue("color");
  if (boxColorOnChecked !== "") {
    props.push(`boxColorOnChecked={${JSON.stringify(boxColorOnChecked)}}`);
  }
  const checkIconColor: string = ctx.getFixedPropertyValue("checkIconColor");
  if (checkIconColor !== "") {
    props.push(`checkIconColor={${JSON.stringify(checkIconColor)}}`);
  }
  const jsx: string = `${ctx.indent}<KCheckbox${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KCheckbox"] };
};

/** Kuika component name → the runtime component of the select family that serves it. */
const SELECT_RUNTIME_COMPONENT: Record<string, string> = {
  MultiSelect: "KMultiSelect",
  AutoComplete: "KAutoComplete"
};

/** The select variant whose runtime component pins `searchable` on, so the prop must not be emitted for it. */
const SELECT_SEARCHABLE_PINNED_COMPONENT: string = "AutoComplete";

/**
 * The select variant whose search box makes it searchable. On web a SelectBox with `showSearch` on IS an
 * AutoComplete: the C# leg swaps its renderer (`AppHelper.SetDiMissingObject` — `Renderer == SelectBox &&
 * GetShowSearchable()` → `AutoComplete`) and the web AutoComplete wrapper pins `searchable`. Only the SelectBox:
 * the swap is keyed on that renderer alone, and a MultiSelect with a search box stays a MultiSelect there.
 */
const SELECT_SEARCH_BOX_IMPLIES_SEARCHABLE_COMPONENT: string = "SelectBox";

/**
 * The `options` source of a select: a datasource binding, or the designer's data-repeater shape — a `fixed`
 * symbol whose `actionDefinitionId` names the action while its `fixedValue` keeps the canvas sample list. The
 * same two-step read {@link pushDataSourceProp} does for a repeater's `dataSource`, on the property the select
 * family binds instead (the web target treats them alike: one `isOptionsProp`/`isTableOptionsProp` branch
 * resolves both to the action's state slot).
 *
 * With no action bound, an option list the designer **actually authored** ships as a literal — its stored form
 * is a JS array literal, which is exactly what the prop needs.
 *
 * **`isFixed` is the whole discriminator**, and it is load-bearing: SelectBox and MultiSelect are the only two
 * components whose metadata gives `options` a non-empty DEFAULT (`[{'value':"Item 1"},{'value':"Item 2"}]`), so
 * emitting every `[`-shaped fixed value shipped those two sample items as the app's real options — visible in a
 * select whose datasource returned nothing. The property panel sets `isFixed` when the user writes a list (as do
 * the MCP/Binder paths), which is exactly the flag the web target gates the same emission on
 * (`ReactControllerGenerator.AppendFixedDataSourceVariableInitialization`: `IsFixed == true` before it declares
 * `dataSource_<id>`), so an untouched default renders an empty dropdown on both targets.
 */
function pushSelectOptionsProp(props: string[], ctx: IComponentEmitContext): void {
  const marker: string = ctx.getBoundValueMarker("options") || ctx.getActionBoundMarker("options");
  if (marker !== "") {
    props.push(`options={${marker}}`);
    return;
  }
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  if (optionsSymbol?.isFixed !== true) {
    return;
  }
  const fixedOptions: string = ctx.getFixedPropertyValue("options").trim();
  if (fixedOptions.startsWith("[")) {
    props.push(`options={${fixedOptions}}`);
  }
}

/**
 * `datavaluefield` / `datatextfield` — which row field is a select's VALUE and which is its CAPTION.
 *
 * Neither is stored on the property of that name: the designer's data editor writes both onto the **`options`
 * symbol**, as `fieldToUseAsKey` / `fieldToDisplay`. The web target reads them from exactly there
 * (`ServerSideElementGenerator.extractSpecialFieldValue`), camelCased through the shared
 * {@link CommonHelperFunctions.toCamelCase} so the emitted name matches the JSON the backend returns — and it
 * prefers the symbol over the property's own fixed value, which is the precedence mirrored here. Reading the
 * property alone would emit nothing and the app would render an empty dropdown over a full datasource.
 *
 * A comma-separated `fieldToDisplay` is the multi-field caption (a row of columns); each field is camelCased on
 * its own, so every segment resolves the same way the single-field case does.
 */
function pushSelectFieldProps(props: string[], ctx: IComponentEmitContext): void {
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  const valueField: string = camelCaseFieldNames(optionsSymbol?.fieldToUseAsKey ?? "");
  pushStringProp(props, "datavaluefield", valueField || ctx.getFixedPropertyValue("datavaluefield"));
  const captionField: string = camelCaseFieldNames(optionsSymbol?.fieldToDisplay ?? "");
  pushStringProp(props, "datatextfield", captionField || ctx.getFixedPropertyValue("datatextfield"));
}

/** CamelCases each comma-separated field of a select's key/display field spec (web camelCases the same names). */
function camelCaseFieldNames(fields: string): string {
  if (fields.trim().length === 0) {
    return "";
  }
  return fields
    .split(",")
    .map((field: string): string => CommonHelperFunctions.toCamelCase(field.trim()))
    .filter((field: string): boolean => field.length > 0)
    .join(",");
}

/**
 * SelectBox / MultiSelect / AutoComplete → the `KSelectBox` / `KMultiSelect` / `KAutoComplete` runtime
 * components (`@/components/kuika`). All three lower through one strategy because on web they are **one
 * component**: `KSelectBox`, wrapped twice — `MultiSelect` renders it with `mode="multiple"` and `AutoComplete`
 * with `searchable` — so they differ only in which properties the designer exposes, and this reads whichever of
 * them the display item carries.
 *
 * ```tsx
 * <KSelectBox value={di5} onValueChange={setDi5} options={dsGetCities} datavaluefield={"id"}
 *   datatextfield={"name"} placeholder={"Pick a city"} allowClear showSearch />
 * ```
 *
 * `value`/`onValueChange` wire the screen-level `useState<string>` the C# leg declares (a multi select keeps its
 * selection in that one string, comma-joined — see `k-select/k-select-types.ts`). Box styles stay on the field
 * and typography moves onto the caption (`textStyle`), the split the web component makes between its
 * `.ant-select-selector` and its `.ant-select-selection-item`. Every other prop is emitted only when the DI
 * sets it (web `shouldIncludeProperty`).
 *
 * Two designer properties are deliberately not emitted: `className` (CSS-only, no RN counterpart) and
 * MultiSelect's `mode` — the web MultiSelect pins `mode = "multiple"` over whatever the designer stored, so its
 * `tags` option (free-entry values) is inert there too, and `KMultiSelect` pins the same thing here.
 */
const emitSelect: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_AND_TEXT_SPEC);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;

  const textBinding: IControlledBinding = ctx.controlledBinding("string");
  // Two channels onto the same state. `onValueChange` is the USER's pick, and the C# leg composes the designer's
  // onChange action into it; `onValueSync` stays the bare setter, for the default the runtime picks when a select
  // with no placeholder row opens on its first option. Without the second one that default ran the designer's
  // action for a selection nobody made — on page load, and again on every render of a fixed option list.
  const props: string[] = [
    `value={${textBinding.valueExpr}}`,
    `onValueChange={${textBinding.setterExpr}}`,
    `onValueSync={${textBinding.setterExpr}}`
  ];

  pushSelectOptionsProp(props, ctx);
  pushSelectFieldProps(props, ctx);
  // placeholder and defaultText are BINDABLE on the select (the designer's own metadata carries a symbol for
  // each), so the bound marker is tried before the fixed value. defaultText is the sink of the
  // getTextFromValue round-trip — the app fetches the caption of a value it was handed and delivers it here, as
  // a datasource read — so reading it fixed-only left that whole event with nowhere to put its answer.
  if (!pushBoundValueProp(props, ctx, "placeholder", "string")) {
    pushTextProp(props, ctx, "placeholder");
  }
  pushBoolProp(props, "allowClear", ctx.getFixedPropertyValue("allowClear"));
  const showSearch: string = ctx.getFixedPropertyValue("showSearch");
  pushBoolProp(props, "showSearch", showSearch);
  // `searchable` is what MAKES an AutoComplete an AutoComplete, so `KAutoComplete` pins it on and its props type
  // `Omit`s it — emitting the designer's value here is a type error in every generated app that has one (the
  // runtime ignores the extra prop, so it only ever showed up as a red `tsc`, never as a broken screen). Same
  // shape as `mode`, which this strategy already leaves to `KMultiSelect`.
  if (ctx.componentName !== SELECT_SEARCHABLE_PINNED_COMPONENT) {
    // A SelectBox's search box turns `searchable` on, as web's renderer swap does (see
    // SELECT_SEARCH_BOX_IMPLIES_SEARCHABLE_COMPONENT). It is the flag the runtime gates its getTextFromValue ask
    // on, so without it a SelectBox that turned `showSearch` on and bound getTextFromValue was never asked for the
    // caption of a value its options do not describe (KOA-18255) — while the same screen on web was. Its local
    // filtering is untouched: the runtime searches remotely only when an `onSearch` handler is bound as well, and
    // a SelectBox declares no such event.
    const searchBoxImpliesSearchable: boolean =
      ctx.componentName === SELECT_SEARCH_BOX_IMPLIES_SEARCHABLE_COMPONENT && showSearch === "true";
    const searchable: string = searchBoxImpliesSearchable ? "true" : ctx.getFixedPropertyValue("searchable");
    pushBoolProp(props, "searchable", searchable);
  }
  pushBoolProp(props, "autoClearSearchValue", ctx.getFixedPropertyValue("autoClearSearchValue"));
  pushBoolProp(props, "containsNullItem", ctx.getFixedPropertyValue("containsNullItem"));
  pushBoolProp(props, "autoRefresh", ctx.getFixedPropertyValue("autoRefresh"));
  pushStringProp(props, "defaultValue", ctx.getFixedPropertyValue("defaultValue"));
  if (!pushBoundValueProp(props, ctx, "defaultText", "string")) {
    pushTextProp(props, ctx, "defaultText");
  }
  // The designer offers "none" alongside A-Z / Z-A; only a real sort direction is worth carrying.
  const sortBy: string = ctx.getFixedPropertyValue("sortBy");
  if (sortBy === "A-Z" || sortBy === "Z-A") {
    props.push(`sortBy={${JSON.stringify(sortBy)}}`);
  }
  pushNumberProp(props, "dropdownScrollItemLimit", ctx.getFixedPropertyValue("dropdownScrollItemLimit"), true);
  pushNumberProp(props, "maxTagCount", ctx.getFixedPropertyValue("maxTagCount"), true);
  const widthType: string = ctx.getFixedPropertyValue("widthType");
  if (widthType === "content" || widthType === "fill") {
    props.push(`widthType={${JSON.stringify(widthType)}}`);
  }

  const disablePropStr: string = ctx.disableProp();
  // A design-time `disabled` and an authorization disable share the one `disabled` prop, so the static flag is
  // emitted only when authorization is not already driving it (same rule as the text inputs' `editable`).
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }

  const component: string = SELECT_RUNTIME_COMPONENT[ctx.componentName] ?? "KSelectBox";
  // The action slot: the free events (`onSelect`, `onSearch`, `getTextFromValue`). The designer's `onChange`
  // does NOT land here — it maps to `onValueChange` above, which the C# leg rewrites in place so binding an
  // action to the change event cannot cost the select its controlled state.
  const jsx: string = `${ctx.indent}<${component}${ctx.testIdProp}${styleProp}${textStyleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: [component] };
};

/**
 * Date / DateTime / MonthPicker → the `KDatePicker` runtime component. All three designer components share the
 * one runtime, switched by `mode` — which is web's own arrangement: its registry gives the MonthPicker the
 * `KDatePicker` tagName and a fixed `displayType: "month"`, so the three are one component there too. The
 * controlled value is the same `di<id>` string state every input uses (the month mode's is `YYYY-MM`); `format`
 * is display-only. Its `onChange` maps to the controlled setter (rewritten in place by the C# leg), so a bound
 * change action keeps the input controlled.
 *
 * `displayType` is not emitted: it is the property web uses to make ONE component behave as three, and the mode
 * above already carries that decision. Emitting it as well would be a second source for one switch.
 */
const emitDatePicker: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_AND_TEXT_SPEC);
  const isDateTime: boolean = ctx.componentName === "DateTime";
  const isMonth: boolean = ctx.componentName === "MonthPicker";
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const mode: string = isMonth ? "month" : isDateTime ? "datetime" : "date";
  const props: string[] = [
    `value={${binding.valueExpr}}`,
    `onChange={${binding.setterExpr}}`,
    `mode=${JSON.stringify(mode)}`
  ];
  pushTextAttr(props, ctx, "placeholder");
  const format: string = ctx.getFixedPropertyValue("format");
  if (format !== "") {
    props.push(`format=${JSON.stringify(format)}`);
  }
  const disableBeforeDate: string = ctx.getFixedPropertyValue("disableBeforeDate");
  if (disableBeforeDate !== "") {
    props.push(`disableBeforeDate=${JSON.stringify(disableBeforeDate)}`);
  }
  const disableAfterDate: string = ctx.getFixedPropertyValue("disableAfterDate");
  if (disableAfterDate !== "") {
    props.push(`disableAfterDate=${JSON.stringify(disableAfterDate)}`);
  }
  // The time-of-day window and the step grid are DateTime-only on web too: `disabledTime` and
  // `validateTimeSteps` both return early unless the display is "Date & Time", and the metadata only offers
  // disableBefore/AfterTime on that component. The runtime enforces them on commit rather than on the wheel —
  // neither OS picker can grey out an hour or a minute (see `date-format.ts`).
  if (isDateTime) {
    const disableBeforeTime: string = ctx.getFixedPropertyValue("disableBeforeTime");
    if (disableBeforeTime !== "") {
      props.push(`disableBeforeTime=${JSON.stringify(disableBeforeTime)}`);
    }
    const disableAfterTime: string = ctx.getFixedPropertyValue("disableAfterTime");
    if (disableAfterTime !== "") {
      props.push(`disableAfterTime=${JSON.stringify(disableAfterTime)}`);
    }
    pushTimeStepAttr(props, ctx, "hourStep", 24);
    pushTimeStepAttr(props, ctx, "minuteStep", 60);
    pushTimeStepAttr(props, ctx, "secondStep", 60);
  }
  if (isDateTime && ctx.getFixedPropertyValue("use12Hours") === "true") {
    props.push("use12Hours");
  }
  if (ctx.getFixedPropertyValue("allowClear") === "true") {
    props.push("allowClear");
  }
  if (ctx.getFixedPropertyValue("returnDisplayValue") === "true") {
    props.push("returnDisplayValue");
  }
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }
  const jsx: string = `${ctx.indent}<KDatePicker${ctx.testIdProp}${styleProp}${textStyleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KDatePicker"] };
};

/**
 * Emits one of the DateTime step props when the designer set a whole number web would actually honour —
 * web deletes a step outside `(0, max]` before handing the props to antd, so an out-of-range one is not
 * emitted at all rather than emitted and ignored downstream.
 */
function pushTimeStepAttr(props: string[], ctx: IComponentEmitContext, name: string, max: number): void {
  const raw: string = ctx.getFixedPropertyValue(name);
  if (raw === "") {
    return;
  }
  const step: number = Number(raw);
  if (!Number.isInteger(step) || step <= 0 || step > max) {
    return;
  }
  props.push(`${name}={${step}}`);
}

/**
 * YearPicker → the `KYearPicker` runtime component. Its value is a plain number in a `useState<number>`
 * (the web component emits `parseInt(year)`), so it binds through the numeric controlled path. Selectable
 * years are `[minYear, maxYear)` — min inclusive, max exclusive — matching the web `disabledDate`.
 */
const emitYearPicker: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_AND_TEXT_SPEC);
  const binding: IControlledBinding = ctx.controlledBinding("number");
  const props: string[] = [`value={${binding.valueExpr}}`, `onChange={${binding.setterExpr}}`];
  pushTextAttr(props, ctx, "placeholder");
  const minYear: string = ctx.getFixedPropertyValue("minYear");
  if (minYear !== "" && Number.isInteger(Number(minYear))) {
    props.push(`minYear={${Number(minYear)}}`);
  }
  const maxYear: string = ctx.getFixedPropertyValue("maxYear");
  if (maxYear !== "" && Number.isInteger(Number(maxYear))) {
    props.push(`maxYear={${Number(maxYear)}}`);
  }
  if (ctx.getFixedPropertyValue("allowClear") === "true") {
    props.push("allowClear");
  }
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }
  const jsx: string = `${ctx.indent}<KYearPicker${ctx.testIdProp}${styleProp}${textStyleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KYearPicker"] };
};

/**
 * DateRange / DateTimeRange → the `KDateRange` runtime component. A range owns TWO endpoint states, so it
 * reads `startValue={di<id>Start}` / `endValue={di<id>End}` and writes through `onChange={setDi<id>}` — the
 * C#-emitted composite setter that fans one {startValue,endValue} payload out to both states, which also
 * lets a bound onChange action compose through the ordinary controlled-setter path. Each endpoint stores
 * `YYYY-MM-DD` (date) or `YYYY-MM-DDTHH:mm:ss` with no offset (datetime); `format` is display-only.
 */
const emitDateRange: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_AND_TEXT_SPEC);
  const isDateTime: boolean = ctx.componentName === "DateTimeRange";
  const stateVar: string = ctx.styleName;
  const props: string[] = [
    `startValue={${stateVar}Start}`,
    `endValue={${stateVar}End}`,
    `onChange={${ctx.setterName}}`,
    `mode=${JSON.stringify(isDateTime ? "datetime" : "date")}`
  ];
  const format: string = ctx.getFixedPropertyValue("format");
  if (format !== "") {
    props.push(`format=${JSON.stringify(format)}`);
  }
  pushTextAttr(props, ctx, "startDatePlaceholder", "startPlaceholder");
  pushTextAttr(props, ctx, "endDatePlaceholder", "endPlaceholder");
  if (ctx.getFixedPropertyValue("allowClear") === "true") {
    props.push("allowClear");
  }
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }
  const jsx: string = `${ctx.indent}<KDateRange${ctx.testIdProp}${styleProp}${textStyleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KDateRange"] };
};

/**
 * RadioGroup → the `KRadioGroup` runtime component (`@/components/kuika`).
 *
 * ```tsx
 * <KRadioGroup value={di5} onValueChange={setDi5} options={dsGetCities} datavaluefield={"id"}
 *   datatextfield={"name"} direction={"horizontal"} space={12} />
 * ```
 *
 * **Its option source is the select family's**, property for property: the designer writes
 * `datavaluefield` / `datatextfield` onto the `options` **symbol** for a RadioGroup exactly as it does for a
 * SelectBox, and the web target reads both through one generic branch
 * (`ServerSideElementGenerator.extractSpecialFieldValue`) — so {@link pushSelectOptionsProp} and
 * {@link pushSelectFieldProps} are reused rather than reimplemented. The `isFixed` gate they carry is
 * load-bearing here too: 0156_RadioGroup.json gives `options` a non-empty DEFAULT
 * (`[{'key':"Radio1",'text':"Radio 1"},…]`), so emitting every `[`-shaped fixed value would put three sample
 * radios in front of the user of a datasource-bound group.
 *
 * `value`/`onValueChange` wire the screen-level `useState<string>` the C# leg declares — a multi-selectable group
 * keeps its whole selection in that one string, comma-joined, which is the shape the web component's own
 * `getNormalizedMultiValue` accepts. Box styles stay on the group and typography moves onto the captions
 * (`textStyle`), the split the web component makes when it copies its own font properties onto each `<label>`.
 */
const emitRadioGroup: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_AND_TEXT_SPEC);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;

  const textBinding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${textBinding.valueExpr}}`, `onValueChange={${textBinding.setterExpr}}`];

  pushSelectOptionsProp(props, ctx);
  pushSelectFieldProps(props, ctx);
  // Truthy-only, because that is how the web component reads it (`if (multiselectable)`): a stored `false` is
  // indistinguishable from unset there, and 0156_RadioGroup.json's own default IS "false" — so emitting
  // `multiSelectable={false}` would put a no-op prop on every group the designer ever draws.
  pushTruthyBoolProp(props, "multiSelectable", ctx.getFixedPropertyValue("multiSelectable"));
  // The designer offers exactly two directions; vertical is the runtime's own default, so only a real
  // horizontal choice is worth carrying.
  if (ctx.getFixedPropertyValue("direction") === "horizontal") {
    props.push(`direction={"horizontal"}`);
  }
  pushNumberProp(props, "space", ctx.getFixedPropertyValue("space"), false);
  pushStringProp(props, "selectedRadioColor", ctx.getFixedPropertyValue("selectedRadioColor"));
  pushStringProp(props, "unSelectedRadioColor", ctx.getFixedPropertyValue("unSelectedRadioColor"));

  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KRadioGroup${ctx.testIdProp}${styleProp}${textStyleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRadioGroup"] };
};

/**
 * The four coordinate properties of the LocationPicker. Each is a plain number the designer can also **bind**
 * (a screen that opens a map on a stored place reads `latitude` / `longitude` off a datasource), so the bound
 * marker is tried before the fixed value — reading them fixed-only would leave such a screen centred on nothing.
 */
function pushLocationPickerCoordinateProps(props: string[], ctx: IComponentEmitContext): void {
  for (const propertyName of ["mapCenterLatitude", "mapCenterLongitude", "latitude", "longitude"]) {
    if (!pushBoundValueProp(props, ctx, propertyName, "number")) {
      pushNumberProp(props, propertyName, ctx.getFixedPropertyValue(propertyName), false);
    }
  }
}

/**
 * LocationPicker → the `KLocationPicker` runtime component (`@/components/kuika`).
 *
 * ```tsx
 * <KLocationPicker value={di5} onValueChange={setDi5} onValueSync={setDi5} mapCenterLatitude={41.0082} />
 * ```
 *
 * `value`/`onValueChange` wire the screen-level **location** state the C# leg declares — an object holding the
 * chosen place, not a string, because the designer reads two independent numbers back off this component
 * (`LocationPicker1.Latitude` / `.Longitude`) and one `useState<string>` cannot answer both.
 *
 * As on the select family the tag carries a second value channel, `onValueSync={setDi<id>}`: a point the runtime
 * picks for itself (the device's own position, when the picker opened with nothing set and no map centre
 * configured) moves the screen's value without running the designer's `onClick` action for a tap nobody made.
 */
const emitLocationPicker: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const binding: IControlledBinding = ctx.controlledBinding("location");
  const props: string[] = [
    `value={${binding.valueExpr}}`,
    `onValueChange={${binding.setterExpr}}`,
    `onValueSync={${binding.setterExpr}}`
  ];

  pushLocationPickerCoordinateProps(props, ctx);

  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KLocationPicker${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KLocationPicker"] };
};

/**
 * LocationSearch → the `KLocationSearch` runtime component (`@/components/kuika`). The designer's component name
 * is `LocationSearch`; its renderer — and the web component it maps to — is `PlacesAutocompleteInput`.
 *
 * ```tsx
 * <KLocationSearch value={di5} onValueChange={setDi5} placeholder={"Where to?"} prefix={"place"} allowClear />
 * ```
 *
 * Its state is the same **location** object the picker's is, for the same reason: the designer reads three fields
 * back off this component (`LocationSearch1.Address` / `.Latitude` / `.Longitude`).
 *
 * Every presentation property is the web TextInput's, because the web component *is* a TextInput — it renders
 * `<TextInput {...this.props} {...getInputProps(…)} />` — and `placeholder` is bindable, so it tries its bound
 * marker before its fixed value. `allowClear` defaults ON in the component metadata, which is why the fixed value
 * is emitted through {@link pushBoolProp} (present only when the designer actually set it) while the runtime keeps
 * "unset means on".
 */
const emitLocationSearch: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_WHOLE_SPEC);
  const binding: IControlledBinding = ctx.controlledBinding("location");
  const props: string[] = [`value={${binding.valueExpr}}`, `onValueChange={${binding.setterExpr}}`];

  if (!pushBoundValueProp(props, ctx, "placeholder", "string")) {
    pushTextProp(props, ctx, "placeholder");
  }
  pushTextProp(props, ctx, "prefix");
  pushTextProp(props, ctx, "suffix");
  pushStringProp(props, "iconColor", ctx.getFixedPropertyValue("iconColor"));
  pushStringProp(props, "addonBefore", ctx.getFixedPropertyValue("addonBefore"));
  pushStringProp(props, "addonAfter", ctx.getFixedPropertyValue("addonAfter"));
  pushBoolProp(props, "allowClear", ctx.getFixedPropertyValue("allowClear"));

  const disablePropStr: string = ctx.disableProp();
  // A design-time `disabled` and an authorization disable share the one `disabled` prop, so the static flag is
  // emitted only when authorization is not already driving it (the same rule the text inputs and the select apply).
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }

  const jsx: string = `${ctx.indent}<KLocationSearch${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KLocationSearch"] };
};

/**
 * Marquee → the `KMarquee` runtime component: a container whose children scroll horizontally, forever. A
 * container in the designer's own terms (its `childrenNames` is `["Column"]`), so it emits its children like the
 * Panel does; everything else is presentation the web passes straight to `react-fast-marquee`.
 *
 * `speed` keeps web's unit — points per SECOND, not a duration — so the apparent speed does not change with the
 * content's length, and `gradientWidth` keeps web's clamp (a negative or unset width means no fade at all,
 * `getProps`'s own rule). The gradient itself is always on, exactly as the web component hardcodes `gradient`.
 */
const emitMarquee: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushNumberProp(props, "speed", ctx.getFixedPropertyValue("speed"), false);
  pushBoolProp(props, "autoFill", ctx.getFixedPropertyValue("autoFill"));
  pushBoolProp(props, "pauseOnHover", ctx.getFixedPropertyValue("pauseOnHover"));
  pushBoolProp(props, "pauseOnClick", ctx.getFixedPropertyValue("pauseOnClick"));
  pushStringProp(props, "gradientColor", ctx.getFixedPropertyValue("gradientColor"));
  pushNumberProp(props, "gradientWidth", ctx.getFixedPropertyValue("gradientWidth"), false);
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";

  const runtimeComponents: string[] = ["KMarquee"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KMarquee${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KMarquee${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KMarquee>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Language Selector → the `KLanguageSelector` runtime component. The one select in the library that is NOT a
 * form field: its value is the app's language rather than screen state, which is why the designer's registry
 * gives it no `value` and no `onChange` and why nothing is bound here. `languages` is the project's own list —
 * a binding when the screen loads it, a literal when the designer pasted one — and the runtime derives the
 * offered entries from it through the shared `getAvailableLanguages`.
 *
 * `className` is not emitted: it is the web `Select`'s CSS hook, and there is no stylesheet to target.
 */
const emitLanguageSelector: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleProp: string = ctx.registerStyle(`${ctx.styleName}Text`, ctx.partitioned.textStyle);
  const props: string[] = [];
  const marker: string = ctx.getBoundValueMarker("languages") || ctx.getActionBoundMarker("languages");
  if (marker !== "") {
    props.push(`languages={${marker}}`);
  } else {
    const languages: string = ctx.getFixedPropertyValue("languages").trim();
    if (languages.startsWith("[")) {
      props.push(`languages={${languages}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const disablePropStr: string = ctx.disableProp();
  const textStyleAttr: string = textStyleProp === "" ? "" : textStyleProp.replace(" style=", " textStyle=");
  const jsx: string = `${ctx.indent}<KLanguageSelector${ctx.testIdProp}${styleProp}${textStyleAttr}${propsStr}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KLanguageSelector"] };
};

/**
 * RadioButtonGroup → the `KRadioButtonGroup` runtime component: a **segmented bar** (an antd `Radio.Group` with
 * `buttonStyle="solid"` around authored `Radio.Button` children), which is a different component from the
 * RadioGroup this target already lowers — that one is the circle-radio list over BOUND options (renderer 182).
 * The two are easy to confuse in the designer and impossible to confuse here.
 *
 * Its buttons are authored children rather than options, so the group emits them like any container does, and
 * the selection reaches them through the runtime's context. A controlled string value like every other input's.
 *
 * `className` is not emitted: it is the hook the web component hangs its generated CSS on, and there is no
 * stylesheet here — the three colours it drove are real props instead.
 */
const emitRadioButtonGroup: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onValueChange={${binding.setterExpr}}`];
  pushNumberProp(props, "space", ctx.getFixedPropertyValue("space"), false);
  pushStringProp(props, "selectedFontColor", ctx.getFixedPropertyValue("selectedFontColor"));
  pushStringProp(props, "selectedBgColor", ctx.getFixedPropertyValue("selectedBgColor"));
  pushStringProp(props, "unselectedBgColor", ctx.getFixedPropertyValue("unselectedBgColor"));
  const disablePropStr: string = ctx.disableProp();
  // A design-time `disabled` and an authorization disable share the one prop, so the static flag is emitted
  // only when authorization is not already driving it — the rule the whole input family follows.
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }
  const propsStr: string = ` ${props.join(" ")}`;

  const runtimeComponents: string[] = ["KRadioButtonGroup"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  const open: string = `${ctx.indent}<KRadioButtonGroup${ctx.testIdProp}${styleProp}${propsStr}${disablePropStr}${ctx.actionMarker()}`;
  if (childParts.length === 0) {
    return { jsx: `${open} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [`${open}>`, ...childParts, `${ctx.indent}</KRadioButtonGroup>`].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * RadioButton → the `KRadioButton` runtime component: one segment of the bar above. It carries only what makes
 * it that segment — the `value` it stands for and its `label` — because everything else (which one is chosen,
 * the three colours, whether the bar is disabled) belongs to the group and reaches it through context.
 *
 * Its own style splits like every captioned component's: the box on the button, the typography on the caption.
 * The `kuikaid` marker carries the designer's `onClick`, which web binds on the button rather than the group, so
 * an action hung on one segment runs for that segment only.
 */
const emitRadioButton: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleProp: string = ctx.registerStyle(`${ctx.styleName}Label`, ctx.partitioned.textStyle);
  const textStyleAttr: string = textStyleProp === "" ? "" : textStyleProp.replace(" style=", " textStyle=");
  const props: string[] = [];
  pushTextProp(props, ctx, "value");
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    pushTextProp(props, ctx, "label");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KRadioButton${ctx.testIdProp}${styleProp}${textStyleAttr}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRadioButton"] };
};

/** The registry names the NodeTree's node template can carry: the designer's name and the renderer tagName. */
const NODE_TREE_NODE_NAMES: ReadonlySet<string> = new Set<string>(["Node", "NodeTreeNode"]);

/**
 * The NodeTree's own field map — ten record fields, read off the `options` symbol like the Breadcrumb's and the
 * Timeline's, camelCased on the way out so each name matches the JSON the backend returns.
 *
 * `nodeTreeNodeBackgroundColorField` has no property entry in the component metadata: it exists only as a
 * sub-field of the options symbol, and the web generator emits it from there. Emitting it here too is what keeps
 * a per-row colour working on both targets rather than silently on one.
 */
function pushNodeTreeFieldMapProps(props: string[], fieldMap: NodeTreeNodeOptions | undefined): void {
  if (fieldMap === undefined) {
    return;
  }
  const emitField = (propName: string, raw: string | undefined): void => {
    const value: string = camelCaseFieldNames(raw ?? "");
    if (value !== "") {
      props.push(`${propName}={${JSON.stringify(value)}}`);
    }
  };
  emitField("nodeTreeNodeIdField", fieldMap.nodeTreeNodeId);
  emitField("nodeTreeNodeParentIdField", fieldMap.nodeTreeNodeParentId);
  emitField("nodeTreeConnectionHeightField", fieldMap.nodeTreeConnectionHeight);
  emitField("nodeTreeNodeWidthField", fieldMap.nodeTreeNodeWidth);
  emitField("nodeTreeNodeHeightField", fieldMap.nodeTreeNodeHeight);
  emitField("nodeTreeNodeBackgroundColorField", fieldMap.nodeTreeNodeBackgroundColor);
  emitField("nodeTreeCompactChildViewField", fieldMap.nodeTreeCompactChildView);
  emitField("nodeTreeCrossLinkIdField", fieldMap.nodeTreeCrossLinkId);
  emitField("nodeTreeCrossLinkWidthField", fieldMap.nodeTreeCrossLinkWidth);
  emitField("nodeTreeCrossLinkColorField", fieldMap.nodeTreeCrossLinkColor);
}

/**
 * NodeTree → the `KNodeTree` runtime component: an org chart over FLAT records, each row naming its parent.
 *
 * ```tsx
 * <KNodeTree options={staff} nodeTreeNodeIdField={"id"} nodeTreeNodeParentIdField={"managerId"}
 *            nodeStyle={styles.di97} renderNode={(row: any, rowIndex: number): React.ReactNode => (…)}
 *            value={di96} onNodeClick={setDi96} />
 * ```
 *
 * The designer's `Node` child is a TEMPLATE, not a component: web clones its subtree per record and mounts it
 * into the node d3 drew, so here it becomes a per-row closure exactly as a Gallery's item template does — one
 * repeater level deeper, so a row binding inside a node resolves against the row variable. Its own box travels
 * separately as `nodeStyle`, because web sizes every node from `children.props.style.width/height` and the
 * runtime needs the same default before a record overrides it.
 *
 * The selected node is a CONTROLLED value: web answers `NodeTree1.<field>` from `getSelectedData()` on the
 * component, so the screen holds the tapped node's KEY and the read indexes the bound records with it — the same
 * shape the select family's reads take. `onNodeClick`'s setter IS the callback, so an action bound to it composes
 * into the write instead of racing it.
 */
const emitNodeTree: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  pushNodeTreeFieldMapProps(props, optionsSymbol?.NodeTreeNodeOptions);

  const orientation: string = ctx.getFixedPropertyValue("orientation");
  if (orientation === "vertical" || orientation === "horizontal") {
    props.push(`orientation={${JSON.stringify(orientation)}}`);
  }
  pushNumberProp(props, "initialExpandLevel", ctx.getFixedPropertyValue("initialExpandLevel"), true);
  pushNumberProp(props, "levelDistance", ctx.getFixedPropertyValue("levelDistance"), false);
  pushNumberProp(props, "siblingsMargin", ctx.getFixedPropertyValue("siblingsMargin"), false);
  pushNumberProp(props, "rootMargin", ctx.getFixedPropertyValue("rootMargin"), false);
  pushNumberProp(props, "initialZoom", ctx.getFixedPropertyValue("initialZoom"), false);
  pushNumberProp(props, "duration", ctx.getFixedPropertyValue("duration"), true);
  pushStringProp(props, "linksColor", ctx.getFixedPropertyValue("linksColor"));
  pushNumberProp(props, "linksWidth", ctx.getFixedPropertyValue("linksWidth"), false);
  pushBoolProp(props, "compactChildView", ctx.getFixedPropertyValue("compactChildView"));
  pushBoolProp(props, "showToolbar", ctx.getFixedPropertyValue("showToolbar"));
  const toolbarPosition: string = ctx.getFixedPropertyValue("toolbarPosition");
  if (NODE_TREE_TOOLBAR_POSITIONS.has(toolbarPosition)) {
    props.push(`toolbarPosition={${JSON.stringify(toolbarPosition)}}`);
  }
  pushBoolProp(props, "showZoomControls", ctx.getFixedPropertyValue("showZoomControls"));
  pushBoolProp(props, "showExpandCollapseButtons", ctx.getFixedPropertyValue("showExpandCollapseButtons"));
  pushBoolProp(props, "showFullscreenButton", ctx.getFixedPropertyValue("showFullscreenButton"));
  pushBoolProp(props, "showExportPdfButton", ctx.getFixedPropertyValue("showExportPdfButton"));
  pushStringProp(props, "exportFileName", ctx.getFixedPropertyValue("exportFileName"));
  pushStringProp(props, "exportPdfTitle", ctx.getFixedPropertyValue("exportPdfTitle"));
  const drawType: string = ctx.getFixedPropertyValue("nodeTreeCrossLinkDrawType");
  if (drawType === "cornered" || drawType === "curved") {
    props.push(`nodeTreeCrossLinkDrawType={${JSON.stringify(drawType)}}`);
  }

  const binding: IControlledBinding = ctx.controlledBinding("string");
  props.push(`value={${binding.valueExpr}}`, `onNodeClick={${binding.setterExpr}}`);

  const runtimeComponents: string[] = ["KNodeTree"];
  const usedTags: TReactNativeTag[] = [];
  const wrapper: IDisplayItem | undefined = (ctx.di.children ?? []).find((child: IDisplayItem): boolean => {
    const name: string | undefined = child.item.name;
    return name !== undefined && NODE_TREE_NODE_NAMES.has(name);
  });
  if (wrapper !== undefined) {
    const nodeStyle: string = ctx.structuralChild(wrapper).registerStyle();
    if (nodeStyle !== "") {
      props.push(`nodeStyle={${nodeStyle}}`);
    }
  }
  const template: string = emitNamedTemplateProp(ctx, NODE_TREE_NODE_NAMES, "renderNode", usedTags, runtimeComponents);

  const propsStr: string = ` ${props.join(" ")}`;
  if (template === "") {
    const jsx: string = `${ctx.indent}<KNodeTree${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
    return { jsx, usedTags, runtimeComponents };
  }
  const jsx: string = [
    `${ctx.indent}<KNodeTree${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}`,
    `${INDENT_STEP.repeat(ctx.depth + 1)}${template}`,
    `${ctx.indent}/>`
  ].join("\n");
  return { jsx, usedTags, runtimeComponents };
};

/** The four corners the designer can pin the NodeTree's toolbar to. */
const NODE_TREE_TOOLBAR_POSITIONS: ReadonlySet<string> = new Set<string>([
  "top-right",
  "top-left",
  "bottom-right",
  "bottom-left"
]);

/**
 * BigFileUpload → the `KBigFileUpload` runtime component: a tap zone that picks files and uploads each in
 * `chunkNumber`-megabyte chunks, exactly as web does (`/resource/UploadChunks` per chunk, then
 * `/resource/UploadComplete`).
 *
 * ```tsx
 * <KBigFileUpload value={di5} onUploadFinish={setDi5} onFileUploadFinish={setDi5} chunkNumber={10} isMultiple />
 * ```
 *
 * Its result is a CONTROLLED OBJECT for the Calendar's reason: the designer reads four things off one upload
 * (`fileId`, `fileName`, `fileIds`, `fileNames`) and web answers each off the component instance, so the whole
 * result lives in a `useState<IKuikaUploadValue>` the C# leg declares and BOTH finish events write it — which is
 * what lets an action bound to either read the ids that upload just produced instead of racing them.
 *
 * The four designators are therefore never emitted as props: they are reads, not settings. `onUploadCancel` and
 * `onFileDelete` carry no value and stay ordinary events, reaching the tag through the action marker.
 *
 * As on the select family and the LocationPicker the tag carries a second value channel,
 * `onValueSync={setDi<id>}`: a cancel and a delete CLEAR the result (web clears its `uploadedFiles` in both
 * handlers) and must do so without running a finish action for an upload that never happened.
 */
const emitBigFileUpload: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("upload");
  const props: string[] = [
    `value={${binding.valueExpr}}`,
    `onUploadFinish={${binding.setterExpr}}`,
    `onFileUploadFinish={${binding.setterExpr}}`,
    `onValueSync={${binding.setterExpr}}`
  ];
  pushNumberProp(props, "chunkNumber", ctx.getFixedPropertyValue("chunkNumber"), true);
  const uploadTarget: string = ctx.getFixedPropertyValue("uploadTarget");
  if (uploadTarget === "Server" || uploadTarget === "Bucket") {
    props.push(`uploadTarget={${JSON.stringify(uploadTarget)}}`);
  }
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    pushTextProp(props, ctx, "label");
  }
  if (!pushBoundValueProp(props, ctx, "hint", "string")) {
    pushStringProp(props, "hint", ctx.getFixedPropertyValue("hint"));
  }
  pushStringProp(props, "accept", ctx.getFixedPropertyValue("accept"));
  pushBoolProp(props, "isMultiple", ctx.getFixedPropertyValue("isMultiple"));
  pushNumberProp(props, "maxFileCount", ctx.getFixedPropertyValue("maxFileCount"), true);
  pushBoolProp(props, "showUploadConfirmation", ctx.getFixedPropertyValue("showUploadConfirmation"));
  pushStringProp(props, "uploadConfirmationMessage", ctx.getFixedPropertyValue("uploadConfirmationMessage"));
  pushBoolProp(props, "showCancelIcon", ctx.getFixedPropertyValue("showCancelIcon"));
  pushBoolProp(props, "showDeleteIcon", ctx.getFixedPropertyValue("showDeleteIcon"));
  const propsStr: string = ` ${props.join(" ")}`;
  const jsx: string = `${ctx.indent}<KBigFileUpload${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KBigFileUpload"] };
};

/**
 * Calendar → the `KCalendar` runtime component: four panels (day / week / month / year) over events mapped from
 * bound records. `options` resolves like every data-bound widget's, and the twelve `data*field` names come off the
 * **options symbol** (`symbol.CalendarOptions`), where the designer's CalendarOptions editor writes them — the
 * same place the web target reads them from (`ServerSideElementGenerator.extractSpecialFieldValue`), with the
 * properties of the same name as the fallback. Reading the properties alone emitted no field map at all, and a
 * Calendar with no `dataStartField` has nothing to place: every panel drew its grid and not one event.
 * Each name is camelCased on the way out so it matches the JSON the backend returns, as the Timeline's and the
 * Tree's are.
 *
 * Its cursor is a CONTROLLED OBJECT, not a scalar: the designer reads six things off one Calendar
 * (`currentDay`, `currentMonth`, `currentYear`, `currentRangeStart`, `currentRangeEnd`, `currentEvent`) and web
 * answers each off the component instance, so the whole cursor lives in a `useState<IKuikaCalendarValue>` the C#
 * leg declares and all three of the designer's events write it. Their setters ARE the three callbacks, which is
 * what lets an action bound to any of them compose into the same write instead of racing it.
 *
 * The six `current*` properties are therefore never emitted as props — they are reads, not settings. `rangeStart`
 * / `rangeEnd` are not either: they are FullCalendar's `validRange`, which bounds navigation on web, and this
 * target's panels navigate from the cursor rather than from a validated range; emitting them would be props the
 * runtime does not read. `avatarMaxCount` follows `dataavatarfield`, and `className` is a CSS hook with no
 * stylesheet to target.
 */
const emitCalendar: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  const fieldMap: CalendarOptions | undefined = optionsSymbol?.CalendarOptions;
  const emitFieldProp = (propName: string, designerName: string, symbolField?: string): void => {
    const fromSymbol: string = camelCaseFieldNames(symbolField ?? "");
    const value: string = fromSymbol || camelCaseFieldNames(ctx.getFixedPropertyValue(designerName));
    if (value !== "") {
      props.push(`${propName}={${JSON.stringify(value)}}`);
    }
  };
  emitFieldProp("dataValueField", "datavaluefield", fieldMap?.datavaluefield);
  emitFieldProp("dataTextField", "datatextfield", fieldMap?.datatextfield);
  emitFieldProp("dataStartField", "datastartfield", fieldMap?.datastartfield);
  emitFieldProp("dataEndField", "dataendfield", fieldMap?.dataendfield);
  emitFieldProp("dataColorField", "datacolorfield", fieldMap?.datacolorfield);
  emitFieldProp("dataHeightField", "dataheightfield", fieldMap?.dataheightfield);
  emitFieldProp("dataFontColorField", "datafontcolorfield", fieldMap?.datafontcolorfield);
  emitFieldProp("dataFontFamilyField", "datafontfamilyfield", fieldMap?.datafontfamilyfield);
  emitFieldProp("dataFontSizeField", "datafontsizefield", fieldMap?.datafontsizefield);
  emitFieldProp("dataFontWeightField", "datafontweightfield", fieldMap?.datafontweightfield);
  emitFieldProp("dataIconField", "dataiconfield", fieldMap?.dataiconfield);
  emitFieldProp("dataAvatarField", "dataavatarfield", fieldMap?.dataavatarfield);
  const defaultPanel: string = ctx.getFixedPropertyValue("defaultPanel");
  if (defaultPanel === "day" || defaultPanel === "week" || defaultPanel === "month" || defaultPanel === "year") {
    props.push(`defaultPanel={${JSON.stringify(defaultPanel)}}`);
  }
  pushBoolProp(props, "displayEventTime", ctx.getFixedPropertyValue("displayEventTime"));
  pushStringProp(props, "minTime", ctx.getFixedPropertyValue("minTime"));
  pushStringProp(props, "maxTime", ctx.getFixedPropertyValue("maxTime"));
  const binding: IControlledBinding = ctx.controlledBinding("string");
  props.push(
    `value={${binding.valueExpr}}`,
    `onClickDay={${binding.setterExpr}}`,
    `onClickEvent={${binding.setterExpr}}`,
    `onPanelChange={${binding.setterExpr}}`
  );
  const propsStr: string = ` ${props.join(" ")}`;
  const jsx: string = `${ctx.indent}<KCalendar${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KCalendar"] };
};

/**
 * Stepper → the `KStepper` runtime component: a header of numbered steps over the ACTIVE step's content. Its
 * steps are authored `StepperItem` children rather than bound options, so it emits them like any container does
 * and the runtime reads their captions off the elements — which is exactly what the web component does with its
 * own children.
 *
 * The active step is a controlled string, because that is what the designer binds and reads back (`value`), and
 * its setter IS the `onValueChange` the designer's `onChange` composes into. The three colours paint the three
 * states a marker can be in.
 */
const emitStepper: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onValueChange={${binding.setterExpr}}`];
  const direction: string = ctx.getFixedPropertyValue("stepDirection");
  if (direction === "horizontal" || direction === "vertical") {
    props.push(`direction={${JSON.stringify(direction)}}`);
  }
  pushStringProp(props, "unfinishedStepColor", ctx.getFixedPropertyValue("unfinishedStepColor"));
  pushStringProp(props, "finishedStepColor", ctx.getFixedPropertyValue("finishedStepColor"));
  pushStringProp(props, "activeStepColor", ctx.getFixedPropertyValue("activeStepColor"));
  pushBoolProp(props, "disableStepClick", ctx.getFixedPropertyValue("disableStepClick"));
  const propsStr: string = ` ${props.join(" ")}`;

  const runtimeComponents: string[] = ["KStepper"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  const open: string = `${ctx.indent}<KStepper${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}`;
  if (childParts.length === 0) {
    return { jsx: `${open} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [`${open}>`, ...childParts, `${ctx.indent}</KStepper>`].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * StepperItem → the `KStepperItem` runtime component: one step's caption, key and content. Everything else about
 * a step (which one is showing, the colours, whether the header responds) belongs to the Stepper, so this is as
 * thin here as the web component is there — a wrapper around its children.
 *
 * The designer's `key` property is emitted as `stepKey`: `key` is React's own reserved prop, and passing it
 * would identify the element to React rather than the step to the Stepper.
 */
const emitStepperItem: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "title", "string")) {
    pushTextProp(props, ctx, "title");
  }
  pushStringProp(props, "stepKey", ctx.getFixedPropertyValue("key"));
  // Two sources, one prop slot. The step's own `disabled` property (0101_Step.json — web hands it to antd's Steps
  // item so the header will not move there) and an editability/authorization directive both mean "this step is not
  // selectable", so the static flag is emitted only when the directive did not already claim the slot — the same
  // rule the input family follows for `editable`. KStepper disables the step's HEADER MARKER off this prop; the
  // step's content is left alone, because a disabled step is not a disabled subtree.
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "" && ctx.getFixedPropertyValue("disabled") === "true") {
    props.push("disabled");
  }
  const propsStr: string = `${props.length > 0 ? ` ${props.join(" ")}` : ""}${disablePropStr}`;

  const runtimeComponents: string[] = ["KStepperItem"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  const open: string = `${ctx.indent}<KStepperItem${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}`;
  if (childParts.length === 0) {
    return { jsx: `${open} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [`${open}>`, ...childParts, `${ctx.indent}</KStepperItem>`].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Pivot Table → the `KPivotTable` runtime component. `dataSource` is the record set to pivot and resolves like
 * every data-bound widget's; `readOnly` freezes the configurator and `export` offers the export button.
 *
 * `export` is emitted as `exportEnabled`: `export` is a reserved word in the emitted TypeScript, so a prop of
 * that name could not be destructured in the runtime component.
 *
 * The pivot CONFIGURATION is not a property at all — on web it lives in the component's own state and is reached
 * through the ExportTableConfigs / ImportTableConfigs / ResetTableConfigs actions rather than through bindings,
 * so there is nothing here to bind. `onConfigChange` reaches the tag through the action slot like any free event.
 */
const emitPivotTable: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const marker: string = ctx.getBoundValueMarker("dataSource") || ctx.getActionBoundMarker("dataSource");
  if (marker !== "") {
    props.push(`dataSource={${marker}}`);
  }
  pushBoolProp(props, "readOnly", ctx.getFixedPropertyValue("readOnly"));
  if (ctx.getFixedPropertyValue("export") === "true") {
    props.push("exportEnabled");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KPivotTable${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KPivotTable"] };
};

const emitPanel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(PRESSED_BOX_SPEC);
  const props: string[] = [];

  const scrolling: string = ctx.getFixedPropertyValue("scrolling");
  if (scrolling !== "") {
    props.push(`scrolling={${JSON.stringify(scrolling)}}`);
  }
  const dynamicBackGroundColor: string = ctx.getFixedPropertyValue("dynamicBackGroundColor");
  if (dynamicBackGroundColor !== "") {
    props.push(`dynamicBackGroundColor={${JSON.stringify(dynamicBackGroundColor)}}`);
  }
  const dynamicBgImage: string = ctx.getFixedPropertyValue("dynamicBgImage");
  if (dynamicBgImage !== "") {
    props.push(`dynamicBgImage={${JSON.stringify(dynamicBgImage)}}`);
  }
  const dynamicFontColor: string = ctx.getFixedPropertyValue("dynamicFontColor");
  if (dynamicFontColor !== "") {
    props.push(`dynamicFontColor={${JSON.stringify(dynamicFontColor)}}`);
  }
  const hoverBackgroundColor: string = ctx.getFixedPropertyValue("hoverBackgroundColor");
  if (hoverBackgroundColor !== "") {
    props.push(`hoverBackgroundColor={${JSON.stringify(hoverBackgroundColor)}}`);
  }
  const hoverFontColor: string = ctx.getFixedPropertyValue("hoverFontColor");
  if (hoverFontColor !== "") {
    props.push(`hoverFontColor={${JSON.stringify(hoverFontColor)}}`);
  }
  const isVertical: string = ctx.getFixedPropertyValue("isVertical");
  if (isVertical === "true") {
    props.push("isVertical");
  }

  // The `kuikaid` marker carries the designer's `onClick`. A pressed panel flashes as a whole, children included
  // — Android's rule verbatim (the flash rides the view that owns the listener) — while a child with its own
  // onClick still wins the touch and flashes alone.
  const propsStr: string = `${stateStyleProps}${ctx.animationProp}${props.length > 0 ? ` ${props.join(" ")}` : ""}${ctx.actionMarker()}`;
  const runtimeComponents: string[] = ["KPanel"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  if (ctx.fillImageJsx) {
    childParts.push(ctx.fillImageJsx);
    runtimeComponents.push("KBackgroundImage");
  }

  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }

  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KPanel${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KPanel${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KPanel>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * The `navVisible_di<id>` variable a navigation component's `visible` prop binds to — "is this overlay open".
 *
 * Named apart from `isVisible_di<id>` (designer visibility, RNM-0097) on purpose: the two are independent
 * questions about the same node, and web keeps them apart too — `isComp<id>Visible` versus
 * `NavVisible<id>`. Declared by the C# leg, which reads the reference off the emitted fragment.
 */
function navVisibleVariable(di: IDisplayItem): string {
  return `navVisible_di${diToken(di.id)}`;
}

/**
 * Modal → the `KModal` runtime component (`@/components/kuika`): a dimmed-backdrop overlay hosting the child
 * DIs in a centered card — the RN counterpart of the web `KModal` (antd Modal). `title`/`closable`/`width`/
 * `height` are fixed value props.
 *
 * `visible` binds to `navVisible_di<id>`, unconditionally, for the same reason web does: `Modal` is an
 * `isItNavigationComponent` component, and the web leg rewrites its visible prop to
 * `this.state.NavVisible<id>` for every one of them (`server-side-element-generator`), never conditionally.
 * The variable is **declared by the C# `ReactNativeScreenGenerator`**, which reads the reference straight out
 * of this fragment — the `isVisible_di<id>` contract exactly (see `resolveVisibilityVariable`), so the two
 * legs cannot disagree about which modals own open/closed state.
 *
 * It is a **separate concept from `isVisible_di<id>`**, which answers "is this component in the tree at all"
 * (designer visibility, RNM-0097). This one answers "is the overlay open". A modal can be both: hidden by
 * designer visibility AND closed.
 *
 * The `kuikaid` marker carries the designer's `onOk`/`onCancel` — the two exits `KModal` now exposes.
 *
 * The body is the **navigated screen**, referenced through {@link buildOverlayContentMarker} rather than
 * emitted here: web replaces a navigation component's `children` with the screen named by
 * `di.navScreenInfo.targetScreenId`, so the child DIs a designer placed inside a modal are unrendered on web
 * too. The C# leg turns that marker into the import, the `OverlayHost` supplying the screen's inputs, and the
 * mounted screen. Drawer and FloatPanel join the same marker once their tags exist (RNM-0106/0107).
 */
const emitModal: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [`visible={${navVisibleVariable(ctx.di)}}`];
  const title: string = textPropExpression(ctx, "title");
  if (title !== "") {
    props.push(`title={${title}}`);
  }
  if (ctx.getFixedPropertyValue("closable") === "false") {
    props.push("closable={false}");
  }
  const width: string = ctx.getFixedPropertyValue("width");
  if (width !== "") {
    props.push(`width={${JSON.stringify(width)}}`);
  }
  const height: string = ctx.getFixedPropertyValue("height");
  if (height !== "") {
    props.push(`height={${JSON.stringify(height)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KModal"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];

  childParts.push(overlayContentLine(ctx));

  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KModal${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KModal${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}>`,
      ...childParts,
      `${ctx.indent}</KModal>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Tab → the `KTab` runtime component (`@/components/kuika`): a tabbed section whose {@link emitTabItem}
 * children become panes. `type`/`size`/`tabPosition`/`selectedTabColor` are fixed value props mapping the
 * web bar appearance; KTab owns tab selection locally. The web's `activeKey`/`onChange`/`value` are the
 * designer-side active-key binding (ignored on the generated app) and `justifiedTabTitles` is web-only —
 * all omitted here.
 */
const emitTab: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const barProps: string[] = ["type", "size", "tabPosition", "selectedTabColor"];
  for (const name of barProps) {
    const value: string = ctx.getFixedPropertyValue(name);
    if (value !== "") {
      props.push(`${name}={${JSON.stringify(value)}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KTab"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];

  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }

  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KTab${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [`${ctx.indent}<KTab${ctx.testIdProp}${styleProp}${propsStr}>`, ...childParts, `${ctx.indent}</KTab>`].join(
      "\n"
    ),
    usedTags,
    runtimeComponents
  };
};

/**
 * TabItem → the `KTabItem` runtime component (`@/components/kuika`): one pane of a {@link emitTab} tabbed
 * section. `tab` (the pane's bar label) is a fixed value prop; `forceRender` keeps the pane mounted while
 * inactive. Child DIs render as the pane body. The web-only `icon`/`iconColor`/`tabTitleWidth` props are not
 * in the mobile component set, and `onActive` (event) awaits the RN action track.
 */
const emitTabItem: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const tab: string = textPropExpression(ctx, "tab");
  if (tab !== "") {
    props.push(`tab={${tab}}`);
  }
  if (ctx.getFixedPropertyValue("forceRender") === "true") {
    props.push("forceRender={true}");
  }
  // The disable goes on the ITEM, and KTab reads it off its children to make that tab's header unselectable —
  // the item's own children stay live, because what the designer disabled is the tab, not its content.
  const propsStr: string = `${props.length > 0 ? ` ${props.join(" ")}` : ""}${ctx.disableProp()}`;
  const runtimeComponents: string[] = ["KTabItem"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];

  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }

  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KTabItem${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KTabItem${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KTabItem>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Spacer → the `KSpacer` runtime component (`@/components/kuika`). Container that adds gap between
 * children (positive → native `gap`; negative → margin wrapping, matching Swift VStack(spacing:) and
 * Android negative-margin behavior) or a standalone fixed-size spacing element when children are absent.
 * `direction` controls the flex axis; `size` is the gap/dimension in logical pixels (default 12).
 */
const emitSpacer: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const direction: string = ctx.getFixedPropertyValue("direction") || "vertical";
  const rawSize: string = ctx.getFixedPropertyValue("size");
  const sizeNum: number = rawSize !== "" ? parseFloat(rawSize) || 0 : DEFAULT_SPACER_SIZE;
  const props: string[] = [`direction={${JSON.stringify(direction)}}`, `size={${sizeNum}}`];

  const childParts: string[] = [];
  const usedTags: TReactNativeTag[] = [];
  const runtimeComponents: string[] = ["KSpacer"];

  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }

  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KSpacer${ctx.testIdProp}${styleProp} ${props.join(" ")} />`,
      usedTags,
      runtimeComponents
    };
  }

  return {
    jsx: [
      `${ctx.indent}<KSpacer${ctx.testIdProp}${styleProp} ${props.join(" ")}>`,
      ...childParts,
      `${ctx.indent}</KSpacer>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

// 0 matches the web generator default (getSpacerValue() returns "0" when unset).
const DEFAULT_SPACER_SIZE: number = 0;

/**
 * Badge → the `KBadge` runtime component (`@/components/kuika`). A standalone count/dot indicator, or
 * an icon pill when `icon` is set (reusing KIcon, overriding the count as on web). The style's fill
 * becomes the bubble/pill color. `count` is the value prop; `overflowCount` maps to the web `max`. A
 * `tooltip` wraps the badge in KTooltip (long-press — no hover on mobile), like Label/Icon. `icon`/
 * `tooltip` are web-appSupportType props brought to RN via the existing KIcon/KTooltip (same precedent
 * as speech-to-text on TextArea). Props are emitted only when set (web `shouldIncludeProperty`).
 *
 * The Badge's metadata declares no children, but the designer drops components INTO it all the same, and
 * the web target renders them as antd `<Badge>` children with the count on their top-right corner (native
 * Android attaches its BadgeDrawable to the same content). They used to be dropped here — a Label inside a
 * Badge vanished from the RN screen and left a bare bubble behind (KOA-18644) — so they are emitted as
 * KBadge's children, and KBadge self-closes only when there are none.
 */
const emitBadge: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  // The id a SetValueOf writes the count into at RUNTIME (KBadge reads it through useScreenValue), emitted
  // unconditionally for the same reason emitLabel emits it: web's Badge sits in a Form.Item and the action writes
  // that field by name, so no generation-time guess about which Badges an action targets is needed (KOA-18681).
  props.push(`kuikaId={${ctx.di.id}}`);
  if (!pushBoundValueProp(props, ctx, "count", "string")) {
    const count: string = ctx.getFixedPropertyValue("count");
    if (count !== "") {
      props.push(`count={${JSON.stringify(count)}}`);
    }
  }
  if (ctx.getFixedPropertyValue("showZero") === "true") {
    props.push("showZero");
  }
  pushNumberProp(props, "overflowCount", ctx.getFixedPropertyValue("overflowCount"), true);
  if (ctx.getFixedPropertyValue("dot") === "true") {
    props.push("dot");
  }
  const icon: string = ctx.getFixedPropertyValue("icon");
  if (icon !== "") {
    props.push(`icon={${JSON.stringify(icon)}}`);
    const iconLibrary: string = ctx.getFixedPropertyValue("iconLibrary");
    if (iconLibrary !== "") {
      props.push(`iconLibrary={${JSON.stringify(iconLibrary)}}`);
    }
    const iconType: string = ctx.getFixedPropertyValue("iconType");
    if (iconType !== "") {
      props.push(`iconType={${JSON.stringify(iconType)}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";

  // With a tooltip, KTooltip owns the press surface + a11y label; the inner KBadge is visual only.
  const tooltip: string = ctx.getFixedPropertyValue("tooltip");
  const badgeDepth: number = tooltip !== "" ? ctx.depth + 1 : ctx.depth;
  const badgeIndent: string = INDENT_STEP.repeat(badgeDepth);
  const badgeTestIdProp: string = tooltip !== "" ? "" : ctx.testIdProp;

  const childParts: string[] = [];
  const usedTags: TReactNativeTag[] = [];
  const runtimeComponents: string[] = tooltip !== "" ? ["KBadge", "KTooltip"] : ["KBadge"];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, badgeDepth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }

  const badgeLines: string[] =
    childParts.length === 0
      ? [`${badgeIndent}<KBadge${badgeTestIdProp}${styleProp}${propsStr} />`]
      : [`${badgeIndent}<KBadge${badgeTestIdProp}${styleProp}${propsStr}>`, ...childParts, `${badgeIndent}</KBadge>`];

  if (tooltip !== "") {
    const jsx: string = [
      `${ctx.indent}<KTooltip${ctx.testIdProp} title={${JSON.stringify(tooltip)}}>`,
      ...badgeLines,
      `${ctx.indent}</KTooltip>`
    ].join("\n");
    return { jsx, usedTags, runtimeComponents };
  }

  return { jsx: badgeLines.join("\n"), usedTags, runtimeComponents };
};

/**
 * MDViewer → the `KMDViewer` runtime component (`@/components/kuika`). A read-only markdown display
 * leaf driven by `value` (its sole meaningful prop, like Label); KMDViewer parses and renders the
 * markdown at run time so the generator emits only a thin self-closing reference. Box styles go on the
 * wrapper View. `theme` is web-only (appSupportType) and omitted. Props emitted only when set.
 */
/**
 * Emit the navigation-widget field-map (Breadcrumb/Menu) from the `options` symbol. The designer stores which
 * record field is the id / title / parent-id / link / icon / order on `optionsSymbol.BreadcrumbOptions` (or
 * `.MenuOptions`); each is camelCased through the shared {@link camelCaseFieldNames} to match the JSON the
 * backend returns — exactly as the select field props do — and emitted only when set.
 */
function pushWidgetFieldMapProps(props: string[], fieldMap: BreadcrumbOptions | MenuOptions | undefined): void {
  if (fieldMap === undefined) {
    return;
  }
  const emitField = (propName: string, raw: string | undefined): void => {
    const value: string = camelCaseFieldNames(raw ?? "");
    if (value !== "") {
      props.push(`${propName}={${JSON.stringify(value)}}`);
    }
  };
  emitField("dataIdField", fieldMap.dataidfield);
  emitField("dataTitleField", fieldMap.datatitlefield);
  emitField("dataParentIdField", fieldMap.dataparentidfield);
  emitField("dataLinkField", fieldMap.datalinkfield);
  emitField("dataIconField", fieldMap.dataiconfield);
  emitField("dataOrderField", fieldMap.dataorderfield);
}

/**
 * Breadcrumb → the `KBreadcrumb` runtime component: a data-bound trail. `options` resolves like a select's
 * (a bound datasource marker, or an authored literal gated on `isFixed`); the field-map is read off the
 * options symbol and emitted camelCased via {@link pushWidgetFieldMapProps}; `activeItemColor` tints the last
 * crumb. KBreadcrumb builds the ordered trail at runtime with the shared mapWidgetOptions. Breadcrumb is a
 * leaf (the trail comes from data, not authored children).
 */
const emitBreadcrumb: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  pushWidgetFieldMapProps(props, optionsSymbol?.BreadcrumbOptions);
  const activeItemColor: string = ctx.getFixedPropertyValue("activeItemColor");
  if (activeItemColor !== "") {
    props.push(`activeItemColor={${JSON.stringify(activeItemColor)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KBreadcrumb${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KBreadcrumb"] };
};

/**
 * Menu → the `KMenu` runtime component (`@/components/kuika`), the RN counterpart of the web SiderMenu. When
 * `options` are bound it builds a nested menu from the records (the field-map read off the options symbol, same
 * as Breadcrumb, emitted camelCased); otherwise it renders authored MenuItem/SubMenu children. `mode`/`theme`/
 * `primaryColor`/`selectedKey` map through. The web-only collapse/hamburger chrome is dropped on mobile.
 */
const emitMenu: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  pushWidgetFieldMapProps(props, optionsSymbol?.MenuOptions);
  const mode: string = ctx.getFixedPropertyValue("mode");
  if (mode !== "") {
    props.push(`mode={${JSON.stringify(mode)}}`);
  }
  const theme: string = ctx.getFixedPropertyValue("theme");
  if (theme !== "") {
    props.push(`theme={${JSON.stringify(theme)}}`);
  }
  const primaryColor: string = ctx.getFixedPropertyValue("primaryColor");
  if (primaryColor !== "") {
    props.push(`primaryColor={${JSON.stringify(primaryColor)}}`);
  }
  const selectedKey: string = ctx.getFixedPropertyValue("selectedKey");
  if (selectedKey !== "") {
    props.push(`selectedKey={${JSON.stringify(selectedKey)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KMenu"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return { jsx: `${ctx.indent}<KMenu${ctx.testIdProp}${styleProp}${propsStr} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [`${ctx.indent}<KMenu${ctx.testIdProp}${styleProp}${propsStr}>`, ...childParts, `${ctx.indent}</KMenu>`].join(
      "\n"
    ),
    usedTags,
    runtimeComponents
  };
};

/**
 * MenuItem → the `KMenuItem` leaf row (icon + label). `itemKey` is the item DI's id (selection identity); the
 * label may be bound; `link` is the navigation target the runtime reports on select. Item-press navigation is
 * wired through the item's own onClick action via the actionMarker (like Button/Label): the C# assembler splices
 * an `onPress` handler onto the marker, which runs the item's NAVIGATE — the RN mirror of web, where each MenuItem
 * carries its own NAVIGATE. An item with no onClick action has its marker stripped and stays a plain row.
 */
const emitMenuItem: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [`itemKey={${JSON.stringify(String(ctx.di.id))}}`];
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    const label: string = textPropExpression(ctx, "label");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  const icon: string = ctx.getFixedPropertyValue("icon");
  if (icon !== "") {
    props.push(`iconName={${JSON.stringify(icon)}}`);
  }
  const link: string = ctx.getFixedPropertyValue("link");
  if (link !== "") {
    props.push(`link={${JSON.stringify(link)}}`);
  }
  const propsStr: string = ` ${props.join(" ")}`;
  const jsx: string = `${ctx.indent}<KMenuItem${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KMenuItem"] };
};

/**
 * SubMenu → the `KSubMenu` expandable group (icon + label header over nested items). Label may be bound; its
 * MenuItem/SubMenu children render through the ordinary child path.
 */
const emitSubMenu: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    const label: string = textPropExpression(ctx, "label");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  const icon: string = ctx.getFixedPropertyValue("icon");
  if (icon !== "") {
    props.push(`iconName={${JSON.stringify(icon)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KSubMenu"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return { jsx: `${ctx.indent}<KSubMenu${ctx.testIdProp}${styleProp}${propsStr} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [
      `${ctx.indent}<KSubMenu${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KSubMenu>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Timeline → the `KTimeline` runtime component: a data-bound list of events on a rail. Shaped exactly like the
 * Breadcrumb and the Menu, because the designer treats it the same way — `options` resolves as theirs does (a
 * bound datasource marker, or an authored literal gated on `isFixed`), and the field map lives on the options
 * SYMBOL rather than on properties of its own (`TimelineOptions.datatitlefield` / `datadescriptionfield` /
 * `datacolorfield`, the trio the property panel's Title/Description/Color Field editors write). Each is
 * camelCased on the way out, like every other field-map prop, so the emitted name matches the JSON the backend
 * returns.
 *
 * `mode` (left / right / alternate) is a fixed choice from the designer's own option list. KTimeline lays the
 * events out from it, porting antd's `getPositionCls` rule — the web target renders an antd Timeline in label
 * mode, and the two targets have to alternate the same way round.
 */
const emitTimeline: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  pushTimelineFieldMapProps(props, optionsSymbol?.TimelineOptions);
  const mode: string = ctx.getFixedPropertyValue("mode");
  if (mode !== "") {
    props.push(`mode={${JSON.stringify(mode)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KTimeline${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KTimeline"] };
};

/**
 * The Timeline's own field map, emitted like {@link pushWidgetFieldMapProps} emits the navigation widgets': read
 * off the `options` symbol, camelCased, and omitted when unset. Kept separate because the trio is a different
 * one — a timeline maps a title, a description and a colour, not an id/parent/link tree.
 */
function pushTimelineFieldMapProps(props: string[], fieldMap: TimelineOptions | undefined): void {
  if (fieldMap === undefined) {
    return;
  }
  const emitField = (propName: string, raw: string | undefined): void => {
    const value: string = camelCaseFieldNames(raw ?? "");
    if (value !== "") {
      props.push(`${propName}={${JSON.stringify(value)}}`);
    }
  };
  emitField("dataTitleField", fieldMap.datatitlefield);
  emitField("dataDescriptionField", fieldMap.datadescriptionfield);
  emitField("dataColorField", fieldMap.datacolorfield);
}

/**
 * Suffix of the Tree's checked-keys state (`di<id>CheckedKeys`), holding the comma-joined keys the designer reads
 * back as `Tree1.checkedKeys`.
 *
 * **Must stay in step with the C# leg**: `ReactNativeScreenGenerator.TreeCheckedKeysSuffix` declares the very
 * state this reference reads, and `ReactNativeSymbolResolver` resolves the designer's read to the same name. A
 * suffix changed on one side alone is a screen that references a state nothing declares.
 */
const TREE_CHECKED_KEYS_SUFFIX: string = "CheckedKeys";

/**
 * Tree → the `KTree` runtime component: a hierarchy built from FLAT records. `options` resolves like every other
 * data-bound widget's, and the four field names that build the hierarchy live on the **options symbol** — the
 * designer's TreeOptions editor writes them there as `fieldToUseAsKey` / `fieldToDisplay` / `fieldToUseAsParent` /
 * `fieldToUseAsChecked`, not onto the `datavaluefield`-style properties of the same meaning. The web target reads
 * them from exactly there (`ServerSideElementGenerator.extractSpecialFieldValue`) and falls back to the property's
 * own fixed value, which is the precedence mirrored here; reading the properties alone emitted nothing, and
 * `buildTreeNodes` answers an unmapped tree with no nodes — a full datasource rendered as an empty box.
 * `disablecheckboxfield` has no symbol counterpart on either target, so it stays a property read. Every name is
 * camelCased on the way out so it matches the JSON the backend returns.
 *
 * Two values are controlled, because the designer reads both back and web answers both off the component through a
 * ref (`getSelectedKey()` / `getCheckedKeys()`): the selected key is the unsuffixed `di<id>` state, so a bare
 * `Tree1` read and a SetValueOf both reach it, and the comma-joined checked set is `di<id>CheckedKeys`. Their two
 * setters ARE the `onSelect` / `onCheck` props, which is what lets the C# leg compose a designer action bound to
 * either event into the same callback (`EReactNativeEventSlot.ControlledSetter`) instead of appending a second one
 * that would race the state write.
 *
 * `defaultCheckedKeys` is not emitted: it seeds `di<id>CheckedKeys` on the C# leg, exactly as an input's `value`
 * seeds its own state, so emitting it here as well would give the component two sources for one value.
 */
const emitTree: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  pushSelectOptionsProp(props, ctx);
  const optionsSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "options"
  )?.symbol;
  const emitFieldProp = (propName: string, designerName: string, symbolField?: string): void => {
    const fromSymbol: string = camelCaseFieldNames(symbolField ?? "");
    const value: string = fromSymbol || camelCaseFieldNames(ctx.getFixedPropertyValue(designerName));
    if (value !== "") {
      props.push(`${propName}={${JSON.stringify(value)}}`);
    }
  };
  emitFieldProp("dataValueField", "datavaluefield", optionsSymbol?.fieldToUseAsKey);
  emitFieldProp("dataTextField", "datatextfield", optionsSymbol?.fieldToDisplay);
  emitFieldProp("dataParentField", "dataparentfield", optionsSymbol?.fieldToUseAsParent);
  emitFieldProp("dataCheckedField", "datacheckedfield", optionsSymbol?.fieldToUseAsChecked);
  emitFieldProp("disableCheckboxField", "disablecheckboxfield");
  if (ctx.getFixedPropertyValue("checkable") === "true") {
    props.push("checkable={true}");
  }
  const binding: IControlledBinding = ctx.controlledBinding("string");
  props.push(`value={${binding.valueExpr}}`, `onSelect={${binding.setterExpr}}`);
  props.push(
    `checkedKeys={di${ctx.di.id}${TREE_CHECKED_KEYS_SUFFIX}}`,
    `onCheck={setDi${ctx.di.id}${TREE_CHECKED_KEYS_SUFFIX}}`
  );
  const propsStr: string = ` ${props.join(" ")}`;
  // KTree takes `disabled` (see disableProp's Tree arm) — a Tree is interactive twice over, and without this the
  // directive was resolved and then never spliced, so a disabled or unauthorized Tree shipped fully live.
  const jsx: string = `${ctx.indent}<KTree${ctx.testIdProp}${styleProp}${propsStr}${ctx.disableProp()}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KTree"] };
};

/**
 * Drawer → the `KDrawer` slide-in panel (`@/components/kuika`) that hosts the screen a navigate opens inside it.
 * `placement`/`width`/`height` place and size it; `push`/`closable` default true, so only an explicit false is
 * emitted. Like KModal it is an overlay host (RNM-0113): `visible={NavVisible<id>}` is bound and its children are
 * the overlay-content marker (the hosted screen), so a NAVIGATE whose openAs targets this drawer opens it and the
 * generic close wiring (setNavVisible<id>(false)) hides it — see emitModal / ResolveOverlayOpenStatement.
 */
const emitDrawer: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [`visible={${navVisibleVariable(ctx.di)}}`];
  const placement: string = ctx.getFixedPropertyValue("placement");
  if (placement !== "") {
    props.push(`placement={${JSON.stringify(placement)}}`);
  }
  const width: string = ctx.getFixedPropertyValue("width");
  if (width !== "") {
    props.push(`width={${JSON.stringify(width)}}`);
  }
  const height: string = ctx.getFixedPropertyValue("height");
  if (height !== "") {
    props.push(`height={${JSON.stringify(height)}}`);
  }
  if (ctx.getFixedPropertyValue("push") === "false") {
    props.push("push={false}");
  }
  if (ctx.getFixedPropertyValue("closable") === "false") {
    props.push("closable={false}");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KDrawer"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [overlayContentLine(ctx)];
  return {
    jsx: [
      `${ctx.indent}<KDrawer${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}>`,
      ...childParts,
      `${ctx.indent}</KDrawer>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * The two state-variable suffixes a Pagination's controlled page + page size are declared under
 * (`di<id>Current` / `di<id>PageSize`). A pager reports BOTH values in one `onChange(page, pageSize)` — antd's
 * signature, which the web Pagination keeps — so, like DateRange and Range Slider, it owns two suffixed states plus
 * one composite `setDi<id>` setter rather than a single `di<id>`.
 *
 * **Must stay in step with the C# leg**: `ReactNativeScreenGenerator.PaginationCurrentSuffix` /
 * `PaginationPageSizeSuffix` declare the very states these references read, and
 * `ReactNativeSymbolResolver` resolves a designer read of `Pagination1.current` / `.pageSize` to the same
 * names. A suffix changed on one side alone is a screen that references a state nothing declares.
 */
const PAGINATION_CURRENT_SUFFIX: string = "Current";
const PAGINATION_PAGE_SIZE_SUFFIX: string = "PageSize";

/**
 * Pagination → the `KPagination` runtime pager, as a **controlled** component: `current` / `pageSize` read the two
 * `di<id>Current` / `di<id>PageSize` states (declared C#-side) and `onChange` writes both through the composite
 * `setDi<id>` setter, so the page a datasource's `currentpage` / `pagesize` parameters read is the page the user
 * actually picked. Web keeps that pair in the component's own `this.state` and reaches it through a ref
 * (`ReactSystemFunctions.value(this, "…_current", "current")` → `getCurrentData()` → `{ current, pageSize }`);
 * React Native has no instance to interrogate, so the pair lives in the screen's state instead — the same move
 * every other RN component read makes.
 *
 * `total` is the row count the pager spans and is normally **bound** to a count datasource, so it goes through the
 * bind-marker path first and falls back to its fixed value. The show* flags map straight through; `showNumbers`
 * defaults true so only an explicit false is emitted; `selectedBorderColor` tints the active page. Pagination is a
 * leaf.
 */
const emitPagination: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateVar: string = ctx.styleName;
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "total", "number")) {
    pushNumberProp(props, "total", ctx.getFixedPropertyValue("total"), true);
  }
  props.push(`pageSize={${stateVar}${PAGINATION_PAGE_SIZE_SUFFIX}}`);
  props.push(`current={${stateVar}${PAGINATION_CURRENT_SUFFIX}}`);
  props.push(`onChange={${ctx.setterName}}`);
  if (ctx.getFixedPropertyValue("showLessItems") === "true") {
    props.push("showLessItems");
  }
  if (ctx.getFixedPropertyValue("showQuickJumper") === "true") {
    props.push("showQuickJumper");
  }
  if (ctx.getFixedPropertyValue("showSizeChanger") === "true") {
    props.push("showSizeChanger");
  }
  if (ctx.getFixedPropertyValue("showFirstPageButton") === "true") {
    props.push("showFirstPageButton");
  }
  if (ctx.getFixedPropertyValue("showLastPageButton") === "true") {
    props.push("showLastPageButton");
  }
  if (ctx.getFixedPropertyValue("showNumbers") === "false") {
    props.push("showNumbers={false}");
  }
  if (ctx.getFixedPropertyValue("showText") === "true") {
    props.push("showText");
  }
  // The designer's own `disabled` property (0095_Pagination.json, appSupportType 0 — mobile-visible), which web
  // applies through getStyleProps() on the pager's root. Distinct from an editability directive, which reaches
  // the same component as the style boundary emitNode composes.
  pushBoolProp(props, "disabled", ctx.getFixedPropertyValue("disabled"));
  const selectedBorderColor: string = ctx.getFixedPropertyValue("selectedBorderColor");
  if (selectedBorderColor !== "") {
    props.push(`selectedBorderColor={${JSON.stringify(selectedBorderColor)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KPagination${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KPagination"] };
};

/**
 * FloatPanel → the `KFloatPanel` runtime component: an absolutely-positioned floating card (title + close
 * "X" + body) anchored to a screen corner. `position`/`offsetX`/`offsetY` place it; `width`/`height` size
 * it (KFloatPanel parses the `"380px"` form); `hideCloseButton` drops the "X". Like KModal it is an overlay
 * host (RNM-0113): `visible={NavVisible<id>}` is bound and its children are the overlay-content marker (the
 * hosted screen), so a NAVIGATE whose openAs targets this panel opens it and the generic close wiring hides it.
 */
const emitFloatPanel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [`visible={${navVisibleVariable(ctx.di)}}`];
  const title: string = textPropExpression(ctx, "title");
  if (title !== "") {
    props.push(`title={${title}}`);
  }
  const position: string = ctx.getFixedPropertyValue("position");
  if (position !== "") {
    props.push(`position={${JSON.stringify(position)}}`);
  }
  pushNumberProp(props, "offsetX", ctx.getFixedPropertyValue("offsetX"), true);
  pushNumberProp(props, "offsetY", ctx.getFixedPropertyValue("offsetY"), true);
  const width: string = ctx.getFixedPropertyValue("width");
  if (width !== "") {
    props.push(`width={${JSON.stringify(width)}}`);
  }
  const height: string = ctx.getFixedPropertyValue("height");
  if (height !== "") {
    props.push(`height={${JSON.stringify(height)}}`);
  }
  if (ctx.getFixedPropertyValue("hideCloseButton") === "true") {
    props.push("hideCloseButton");
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KFloatPanel"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [overlayContentLine(ctx)];
  return {
    jsx: [
      `${ctx.indent}<KFloatPanel${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()}>`,
      ...childParts,
      `${ctx.indent}</KFloatPanel>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Collapse → the `KCollapse` accordion container (`@/components/kuika`), hosting its CollapsePanel children.
 * `accordion` defaults true on both sides, so only an explicit `false` is emitted; `destroyInactivePanel`
 * unmounts a closed panel's body. The panels render through the ordinary child path.
 */
const emitCollapse: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (ctx.getFixedPropertyValue("accordion") === "false") {
    props.push("accordion={false}");
  }
  if (ctx.getFixedPropertyValue("destroyInactivePanel") === "true") {
    props.push("destroyInactivePanel");
  }
  // A Collapse an Open/Close/ToggleCollapsePanelOf targets becomes controlled: the open-key set moves into
  // screen state (declared C#-side off this very reference) so the action has somewhere to write. The
  // change handler comes with it, or the panels would render but stop responding to a tap.
  const collapseKeysVar: string | null = ctx.collapseKeysVar();
  if (collapseKeysVar !== null) {
    props.push(`activeKeys={${collapseKeysVar}}`);
    props.push(`onActiveKeysChange={set${collapseKeysVar.charAt(0).toUpperCase()}${collapseKeysVar.slice(1)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KCollapse"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return { jsx: `${ctx.indent}<KCollapse${ctx.testIdProp}${styleProp}${propsStr} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [
      `${ctx.indent}<KCollapse${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KCollapse>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * CollapsePanel → the `KCollapsePanel` runtime component: a pressable header (text + chevron) over a body
 * of children. `panelKey` is the panel DI's own id, the stable identity KCollapse uses to enforce accordion.
 * `header` may be bound; `defaultOpen`/`showArrow`/`titleBgColor`/`titleFontColor` are fixed props. Body DIs
 * render through the ordinary child path.
 */
const emitCollapsePanel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [`panelKey={${JSON.stringify(String(ctx.di.id))}}`];
  if (!pushBoundValueProp(props, ctx, "header", "string")) {
    const header: string = textPropExpression(ctx, "header");
    if (header !== "") {
      props.push(`header={${header}}`);
    }
  }
  if (ctx.getFixedPropertyValue("defaultOpen") === "true") {
    props.push("defaultOpen");
  }
  if (ctx.getFixedPropertyValue("showArrow") === "false") {
    props.push("showArrow={false}");
  }
  const titleBgColor: string = ctx.getFixedPropertyValue("titleBgColor");
  if (titleBgColor !== "") {
    props.push(`titleBgColor={${JSON.stringify(titleBgColor)}}`);
  }
  const titleFontColor: string = ctx.getFixedPropertyValue("titleFontColor");
  if (titleFontColor !== "") {
    props.push(`titleFontColor={${JSON.stringify(titleFontColor)}}`);
  }
  const propsStr: string = ` ${props.join(" ")}`;
  const runtimeComponents: string[] = ["KCollapsePanel"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KCollapsePanel${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KCollapsePanel${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KCollapsePanel>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * FloatButton → the `KFloatButton` runtime component: an absolutely-positioned floating action button
 * (icon + optional label) anchored to a screen corner. `icon` → `iconName`; `label` may be bound (a FAB
 * in a row is labelled from data); `position`/`offsetX`/`offsetY` place it (KFloatButton defaults them
 * when unset). Interactive: the `onClick` press rides the `kuikaid` marker and a disable directive
 * becomes `disabled`.
 */
const emitFloatButton: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const props: string[] = [];
  const icon: string = ctx.getFixedPropertyValue("icon");
  if (icon !== "") {
    props.push(`iconName={${JSON.stringify(icon)}}`);
  }
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    const label: string = textPropExpression(ctx, "label");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  const position: string = ctx.getFixedPropertyValue("position");
  if (position !== "") {
    props.push(`position={${JSON.stringify(position)}}`);
  }
  pushNumberProp(props, "offsetX", ctx.getFixedPropertyValue("offsetX"), true);
  pushNumberProp(props, "offsetY", ctx.getFixedPropertyValue("offsetY"), true);
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const actionMarker: string = ctx.actionMarker();
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KFloatButton${ctx.testIdProp}${styleProp}${stateStyleProps}${propsStr}${actionMarker}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KFloatButton"] };
};

/**
 * Standalone Tooltip → `KTooltip` wrapping the content the designer put inside it. Tooltip is a CONTAINER
 * (`componentType: 3`, like Panel and the stacks) and on web the authored child IS the anchor — antd clones
 * it and hangs the hover on it — so the children render through the ordinary child path, and KTooltip turns
 * them into a long-press surface, touch having no hover (KOA-18562: the strategy used to discard them and
 * substitute a generated info glyph, so a tooltipped Button/TextInput/Image vanished on the device).
 *
 * <p>`title` is emitted always (KTooltip requires it). KTooltip exposes no `style` prop — the designer marks
 * Tooltip's own style ignored — so none is spliced, and an empty Tooltip emits a childless tag, as an empty
 * antd Tooltip renders nothing on web.</p>
 */
const emitTooltip: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "title", "string")) {
    // Emitted even when empty — KTooltip requires the prop — so this one cannot go through the shared
    // helper, which (rightly, everywhere else) leaves an unset property off the tag entirely.
    const title: string = textPropExpression(ctx, "title");
    props.push(`title={${title === "" ? '""' : title}}`);
  }
  const placement: string = ctx.getFixedPropertyValue("placement");
  if (placement !== "") {
    props.push(`placement={${JSON.stringify(placement)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KTooltip"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return { jsx: `${ctx.indent}<KTooltip${ctx.testIdProp}${propsStr} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [`${ctx.indent}<KTooltip${ctx.testIdProp}${propsStr}>`, ...childParts, `${ctx.indent}</KTooltip>`].join("\n"),
    usedTags,
    runtimeComponents
  };
};

const emitMarkdown: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "value", "string")) {
    const value: string = textPropExpression(ctx, "value");
    if (value !== "") {
      props.push(`value={${value}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KMDViewer${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KMDViewer"] };
};

/**
 * Divider → the `KDivider` runtime component (`@/components/kuika`). A separator line, the RN
 * counterpart of the web Divider (antd `Divider`). The generator emits a thin reference; the line
 * geometry (border color/width extracted from the box style, the label with its orientation, dashed
 * rendering, horizontal vs vertical) lives in KDivider so it is authored once. Box styles (the
 * designer border/height/margins that KDivider reads to draw the line) stay on `style`; typography
 * (color/textAlign/fontSize for the centered label) moves onto `labelStyle` — the same split the web
 * emitter's getProps/getOrientation performs and the split KButton already uses. Only `dashed` and the
 * vertical `type` are emitted (horizontal is KDivider's default), and `label` only when set — mirroring
 * the web generator's `shouldIncludeProperty`, so a bare divider stays minimal. The Divider is a leaf,
 * so KDivider is always self-closing.
 */
const emitDivider: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const labelStyleName: string = `${ctx.styleName}Label`;
  const labelStyleRegistered: string = ctx.registerStyle(labelStyleName, ctx.partitioned.textStyle);
  const labelStyleProp: string = labelStyleRegistered === "" ? "" : ` labelStyle={styles.${labelStyleName}}`;

  const props: string[] = [];
  // A bound label (e.g. a tab titled from data) travels as a marker; fixed text is the fallback.
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    const label: string = textPropExpression(ctx, "label");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  if (ctx.getFixedPropertyValue("dashed") === "true") {
    props.push("dashed");
  }
  // Designer exposes horizontal/vertical; horizontal is KDivider's default, so only vertical is emitted.
  if (ctx.getFixedPropertyValue("type") === "vertical") {
    props.push(`type={"vertical"}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";

  const jsx: string = `${ctx.indent}<KDivider${ctx.testIdProp}${styleProp}${labelStyleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KDivider"] };
};

/**
 * Switch → the `KSwitch` runtime component (`@/components/kuika`). Wraps RN's native `<Switch>` and
 * exposes `value`/`onValueChange`/`disabled` — the three props the generator binds. Each Switch gets
 * its own `React.useState<boolean>` (the C# `ReactNativeScreenGenerator` declares it from the model)
 * so the generated screen is a fully-controlled form, matching the web's Ant Design controlled Switch pattern.
 */
const emitSwitch: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const booleanBinding: IControlledBinding = ctx.controlledBinding("boolean");
  const props: string[] = [`value={${booleanBinding.valueExpr}}`, `onValueChange={${booleanBinding.setterExpr}}`];
  const disablePropStr: string = ctx.disableProp();
  if (disablePropStr === "") {
    const disabled: string = ctx.getFixedPropertyValue("disabled");
    if (disabled === "true") {
      props.push("disabled");
    }
  }
  const jsx: string = `${ctx.indent}<KSwitch${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KSwitch"] };
};

/**
 * Rating → the `KRating` runtime component (`@/components/kuika`). The RN port of the web Rating (antd
 * `Rate`): each Rating gets its own `React.useState<number>` (declared by the C# `ReactNativeScreenGenerator`),
 * and `value`/`onChange` wire that controlled numeric state — the same controlled-input pattern as Switch/
 * Checkbox, but numeric rather than boolean. `count` (star count) and `allowHalf` (half-star selection) are
 * emitted only when the DI sets them (mirroring the web `shouldIncludeProperty`); `allowClear` stays antd's
 * default (true) inside KRating so it is not emitted. Role-based disable becomes the `disabled` prop.
 */
const emitRating: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const numberBinding: IControlledBinding = ctx.controlledBinding("number");
  const props: string[] = [`value={${numberBinding.valueExpr}}`, `onChange={${numberBinding.setterExpr}}`];
  pushNumberProp(props, "count", ctx.getFixedPropertyValue("count"), true);
  if (ctx.getFixedPropertyValue("allowHalf") === "true") {
    props.push("allowHalf");
  }
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KRating${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRating"] };
};

/**
 * Slider → the `KSlider` runtime component (`@/components/kuika`). The RN port of the web Slider (antd
 * `Slider`): a single-value numeric input, so — like Rating — each Slider gets its own `React.useState<number>`
 * (declared C#-side) and `value`/`onChange` wire that controlled state. `min`/`max`/`step` bound the track (all
 * numbers); `vertical` and `tooltipVisible` map straight through only when the DI sets them; the three colour
 * props tint track/handle/rail. Role-based disable becomes the `disabled` prop.
 */
const emitSlider: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const numberBinding: IControlledBinding = ctx.controlledBinding("number");
  const props: string[] = [`value={${numberBinding.valueExpr}}`, `onChange={${numberBinding.setterExpr}}`];
  pushNumberProp(props, "min", ctx.getFixedPropertyValue("min"), true);
  pushNumberProp(props, "max", ctx.getFixedPropertyValue("max"), true);
  pushNumberProp(props, "step", ctx.getFixedPropertyValue("step"), true);
  if (ctx.getFixedPropertyValue("vertical") === "true") {
    props.push("vertical");
  }
  const sliderColor: string = ctx.getFixedPropertyValue("sliderColor");
  if (sliderColor !== "") {
    props.push(`sliderColor={${JSON.stringify(sliderColor)}}`);
  }
  const handleColor: string = ctx.getFixedPropertyValue("handleColor");
  if (handleColor !== "") {
    props.push(`handleColor={${JSON.stringify(handleColor)}}`);
  }
  const railColor: string = ctx.getFixedPropertyValue("railColor");
  if (railColor !== "") {
    props.push(`railColor={${JSON.stringify(railColor)}}`);
  }
  if (ctx.getFixedPropertyValue("tooltipVisible") === "true") {
    props.push("tooltipVisible");
  }
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KSlider${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KSlider"] };
};

/**
 * Range Slider → the `KRangeSlider` runtime component. Like DateRange it owns TWO endpoint states, so it reads
 * `startValue={di<id>Start}` / `endValue={di<id>End}` and writes through `onChange={setDi<id>}` — the C#-emitted
 * composite setter that fans one {startValue,endValue} payload out to both states (also letting a bound onChange
 * action compose through the controlled-setter path). It is the generator's first NUMERIC multi-value component:
 * both endpoints are `useState<number>` and the payload is `IKRangeSliderValue`, the numeric analog of
 * IKDateRangeValue. `min`/`max`/`step` bound the track; the three colour props tint it.
 */
const emitRangeSlider: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const stateVar: string = ctx.styleName;
  const props: string[] = [
    `startValue={${stateVar}Start}`,
    `endValue={${stateVar}End}`,
    `onChange={${ctx.setterName}}`
  ];
  pushNumberProp(props, "min", ctx.getFixedPropertyValue("min"), true);
  pushNumberProp(props, "max", ctx.getFixedPropertyValue("max"), true);
  pushNumberProp(props, "step", ctx.getFixedPropertyValue("step"), true);
  if (ctx.getFixedPropertyValue("vertical") === "true") {
    props.push("vertical");
  }
  const sliderColor: string = ctx.getFixedPropertyValue("sliderColor");
  if (sliderColor !== "") {
    props.push(`sliderColor={${JSON.stringify(sliderColor)}}`);
  }
  const handleColor: string = ctx.getFixedPropertyValue("handleColor");
  if (handleColor !== "") {
    props.push(`handleColor={${JSON.stringify(handleColor)}}`);
  }
  const railColor: string = ctx.getFixedPropertyValue("railColor");
  if (railColor !== "") {
    props.push(`railColor={${JSON.stringify(railColor)}}`);
  }
  if (ctx.getFixedPropertyValue("tooltipVisible") === "true") {
    props.push("tooltipVisible");
  }
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KRangeSlider${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRangeSlider"] };
};

/**
 * ProgressBar → the `KProgressBar` runtime component (`@/components/kuika`). The RN port of the web
 * ProgressBar (antd `Progress`): a read-only completion bar. The generator emits a thin self-closing
 * reference; the fill/track geometry, gradient rendering, and the percentage label live in KProgressBar so
 * they are authored once instead of inlined into every screen. Box styles (the designer fill via
 * `backgroundColor`, plus width/height/margins) stay on `style`; typography (the `%` label color/size)
 * moves onto `textStyle` — the same split KButton/KDivider use. `percent` is the numeric value prop: a
 * fixed literal, a data binding, or — when a SetValueOf targets the bar (web's Form.Item `percent` write) —
 * the `di<id>` number state the C# leg declares off that reference; the gradient colors, `size`, `hidePercentage`,
 * the left `percentageDirection`, and the vertical `direction` are emitted only when the DI sets them
 * (mirroring the web `shouldIncludeProperty`), so a bare bar stays minimal. Presentational leaf → always
 * self-closing. The circular variant (web ProgressCircle) is a separate component/renderer.
 */
const emitProgressBar: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const labelStyleName: string = `${ctx.styleName}Label`;
  const labelStyleRegistered: string = ctx.registerStyle(labelStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = labelStyleRegistered === "" ? "" : ` textStyle={styles.${labelStyleName}}`;

  const props: string[] = [];
  if (ctx.isSetValueTarget()) {
    props.push(`percent={di${ctx.di.id}}`);
  } else if (!pushBoundValueProp(props, ctx, "percent", "number")) {
    pushNumberProp(props, "percent", ctx.getFixedPropertyValue("percent"), false);
  }
  const firstGradientColor: string = ctx.getFixedPropertyValue("firstGradientColor");
  if (firstGradientColor !== "") {
    props.push(`firstGradientColor={${JSON.stringify(firstGradientColor)}}`);
  }
  const secondGradientColor: string = ctx.getFixedPropertyValue("secondGradientColor");
  if (secondGradientColor !== "") {
    props.push(`secondGradientColor={${JSON.stringify(secondGradientColor)}}`);
  }
  const thirdGradientColor: string = ctx.getFixedPropertyValue("thirdGradientColor");
  if (thirdGradientColor !== "") {
    props.push(`thirdGradientColor={${JSON.stringify(thirdGradientColor)}}`);
  }
  const size: string = ctx.getFixedPropertyValue("size");
  if (size === "small" || size === "medium" || size === "large") {
    props.push(`size={${JSON.stringify(size)}}`);
  }
  if (ctx.getFixedPropertyValue("hidePercentage") === "true") {
    props.push("hidePercentage");
  }
  // Designer exposes left/right; right is KProgressBar's default, so only left is emitted.
  if (ctx.getFixedPropertyValue("percentageDirection") === "left") {
    props.push(`percentageDirection={"left"}`);
  }
  // Designer exposes horizontal/vertical; horizontal is KProgressBar's default, so only vertical is emitted.
  if (ctx.getFixedPropertyValue("direction") === "vertical") {
    props.push(`direction={"vertical"}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KProgressBar${ctx.testIdProp}${styleProp}${textStyleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KProgressBar"] };
};

/**
 * ProgressCircle → the `KProgressCircle` runtime component (`@/components/kuika`). The RN port of the web
 * ProgressCircle (antd `Progress type="circle"`): a read-only circular completion ring. The generator emits
 * a thin self-closing reference; the ring geometry (react-native-svg arc) and the centered percentage label
 * live in KProgressCircle so they are authored once instead of inlined into every screen. Box styles (the
 * designer fill via `backgroundColor` and the width/height diameter) stay on `style`; the centered `%`
 * label typography moves onto `textStyle` — the same split KProgressBar/KButton use. `percent` is the
 * numeric value prop (fixed, bound, or the `di<id>` state a SetValueOf writes, as on the bar); the left `percentageDirection` is
 * emitted only when set (right is the default), mirroring the web `shouldIncludeProperty`. Unlike the bar,
 * the circle is a fixed-diameter widget, so it is in CONTENT_WIDTH_COMPONENTS (hugs its size).
 * Presentational leaf → always self-closing.
 */
const emitProgressCircle: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const labelStyleName: string = `${ctx.styleName}Label`;
  const labelStyleRegistered: string = ctx.registerStyle(labelStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = labelStyleRegistered === "" ? "" : ` textStyle={styles.${labelStyleName}}`;

  const props: string[] = [];
  if (ctx.isSetValueTarget()) {
    props.push(`percent={di${ctx.di.id}}`);
  } else if (!pushBoundValueProp(props, ctx, "percent", "number")) {
    pushNumberProp(props, "percent", ctx.getFixedPropertyValue("percent"), false);
  }
  // Designer exposes left/right; right is KProgressCircle's default, so only left is emitted.
  if (ctx.getFixedPropertyValue("percentageDirection") === "left") {
    props.push(`percentageDirection={"left"}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KProgressCircle${ctx.testIdProp}${styleProp}${textStyleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KProgressCircle"] };
};

/**
 * QRRenderer → the read-only `KQRRenderer` (ERendererName.QRRenderer = 137). Display-only, like ProgressCircle:
 * the `value` string is the QR payload (bound or fixed) with no controlled state and no events; the runtime draws
 * the code with react-native-svg. Always self-closing.
 */
const emitQRRenderer: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "value", "string")) {
    const value: string = textPropExpression(ctx, "value");
    if (value !== "") {
      props.push(`value={${value}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KQRRenderer${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KQRRenderer"] };
};

/**
 * BarcodeRenderer → the read-only `KBarcodeRenderer` (ERendererName.BarcodeRenderer = 138). The QR sibling's shape:
 * display-only, no state, no events — a `value` in, a barcode out.
 *
 * Two properties reach the tag. `value` is a fixed literal, a data binding, or — like the Label and the WebView — a
 * `SetValueOf` target, because a barcode is exactly the kind of thing an action recomputes (scan a code, render the
 * next one); the C# leg declares the state off the `value={di<id>}` reference. `format` is the symbology name and is
 * fixed by definition: the designer picks it from a list, and a barcode whose symbology changed at runtime would be
 * a different barcode.
 *
 * The FULL style goes to the tag, not the box subset: the web component derives the bar width, bar height, line
 * colour, caption font and quiet-zone margins from its CSS rather than from properties (`barcode-renderer.tsx`), so
 * the typography keys are load-bearing here in a way they are not for a plain View. `KBarcodeRenderer` performs that
 * same style → JsBarcode option mapping, which is why it is authored once in the runtime instead of inlined here.
 */
const emitBarcodeRenderer: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const props: string[] = [];
  if (ctx.isSetValueTarget()) {
    props.push(`value={di${ctx.di.id}}`);
  } else if (!pushBoundValueProp(props, ctx, "value", "string")) {
    const value: string = textPropExpression(ctx, "value");
    if (value !== "") {
      props.push(`value={${value}}`);
    }
  }
  const format: string = ctx.getFixedPropertyValue("format");
  if (format !== "") {
    props.push(`format={${JSON.stringify(format)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KBarcodeRenderer${ctx.testIdProp}${styleProp}${propsStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KBarcodeRenderer"] };
};

/**
 * ColorPicker → the `KColorPicker` runtime component. A controlled STRING value like the Signature's, because
 * that is what the designer binds and reads back: the web component reports its pick as `rgba(r, g, b, a)` and
 * writes that string into whatever the value is bound to. So the tag takes the screen's `di<id>` state and its
 * setter, and the designer's `onChange` composes into the same `onChange` prop rather than appending a second
 * handler that would race the state write.
 *
 * Box style only: the swatch is a box, and its border/size come from the designer's own style exactly as web's
 * `.kuika-color-picker` does. Carries a disable prop — a colour swatch that still opened its picker under an
 * authorization disable would be the same hole as an editable input.
 */
const emitColorPicker: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onChange={${binding.setterExpr}}`];
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KColorPicker${ctx.testIdProp}${styleProp} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KColorPicker"] };
};

/**
 * MDEditor → the `KMDEditor` runtime component: a markdown editor with a toolbar and a live preview. A controlled
 * STRING like the Signature and the ColorPicker — the value the designer binds and reads back is the markdown
 * itself — so the tag takes the screen's `di<id>` state and its setter, and the designer's `onChange` composes
 * into that same `onChange` prop.
 *
 * `height` comes from the STYLE, not from the property of that name: web passes `height={this.props.style?.height}`
 * to the uiw editor, so the designer's box height is what sizes it, and the `height` property alone would size
 * nothing. It is read off the resolved style here for the same reason.
 *
 * `className` is not emitted: it is the web wrapper's CSS hook (the generated `kuika-style-<id>` class that state
 * CSS targets), and React Native has no stylesheet to target.
 */
const emitMDEditor: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onChange={${binding.setterExpr}}`];
  const preview: string = ctx.getFixedPropertyValue("preview");
  if (preview === "edit" || preview === "live" || preview === "preview") {
    props.push(`preview={${JSON.stringify(preview)}}`);
  }
  const theme: string = ctx.getFixedPropertyValue("theme");
  if (theme === "light" || theme === "dark") {
    props.push(`theme={${JSON.stringify(theme)}}`);
  }
  const height: string | number | undefined = ctx.style.height;
  if (typeof height === "number") {
    props.push(`height={${height}}`);
  }
  pushBoolProp(props, "hideToolbar", ctx.getFixedPropertyValue("hideToolbar"));
  pushBoolProp(props, "fullscreen", ctx.getFixedPropertyValue("fullscreen"));
  pushBoolProp(props, "autoFocus", ctx.getFixedPropertyValue("autoFocus"));
  pushBoolProp(props, "enableScroll", ctx.getFixedPropertyValue("enableScroll"));
  pushBoolProp(props, "simplified", ctx.getFixedPropertyValue("simplified"));
  pushBoolProp(props, "singleLineBreakAsDouble", ctx.getFixedPropertyValue("singleLineBreakAsDouble"));
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KMDEditor${ctx.testIdProp}${styleProp} ${props.join(" ")}${disablePropStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KMDEditor"] };
};

/**
 * Signature → the `KSignature` draw pad (ERendererName.Signature = 95). A controlled STRING value — a base64 PNG
 * data URL, web parity — so like the input family it binds `value`/`onChange` to screen state; the runtime draws
 * natively (react-native-svg) and reports the PNG. Pen/canvas come from style. Carries a disabled prop.
 */
const emitSignature: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onChange={${binding.setterExpr}}`];
  const disablePropStr: string = ctx.disableProp();
  const jsx: string = `${ctx.indent}<KSignature${ctx.testIdProp}${styleProp}${stateStyleProps} ${props.join(" ")}${disablePropStr} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KSignature"] };
};

/**
 * Recaptcha → the `KRecaptcha` runtime component (ERendererName.Recaptcha = 187). Like Signature, a controlled
 * STRING value — but the string is the Google reCAPTCHA v2 token the widget mints, so it binds `value`/`onChange`
 * to screen state purely to CAPTURE the token (the widget ignores an incoming value). The captured token is meant
 * to be verified by a server-side VerifyRecaptchaToken; the client never self-approves. No disabled prop — a
 * challenge has no meaningful disabled state.
 */
const emitRecaptcha: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const binding: IControlledBinding = ctx.controlledBinding("string");
  const props: string[] = [`value={${binding.valueExpr}}`, `onChange={${binding.setterExpr}}`];
  const jsx: string = `${ctx.indent}<KRecaptcha${ctx.testIdProp}${styleProp} ${props.join(" ")} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRecaptcha"] };
};

/**
 * Process Automation Viewer → the `KProcessAutomation` runtime component (ERendererName.ProcessAutomation).
 * A read-only BPMN diagram of ONE running instance, so the only binding is which instance: everything else —
 * the diagram, where the process stands, and what a tap on the current step opens — is resolved at run time
 * from the backend. `style` is technical and already partitioned into the stylesheet.
 *
 * No action marker: the component has no designer-bindable event, and the one interaction it does have
 * (tapping the active step) is its own NavigateToTask, not something a screen wires.
 */
const emitProcessAutomation: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const instanceMarker: string = ctx.getBoundValueMarker("processInstance");
  if (instanceMarker !== "") {
    props.push(`value={String(${instanceMarker} ?? "")}`);
  } else {
    pushStringProp(props, "value", ctx.getFixedPropertyValue("processInstance"));
  }
  const jsx: string = `${ctx.indent}<KProcessAutomation${ctx.testIdProp}${styleProp}${
    props.length > 0 ? " " + props.join(" ") : ""
  } />`;
  return { jsx, usedTags: [], runtimeComponents: ["KProcessAutomation"] };
};

/**
 * PA Interaction Buttons → the `KPaInteractionButtons` runtime component (ERendererName.PAInteractionButtons
 * = 197). The buttons are not authored on the canvas: they are the transitions the selected task's own BPMN
 * declares, which the runtime fetches and renders — so the only thing to bind here is WHICH task(s) are
 * selected, plus the slot where `onCompleted` lands.
 *
 * Two of the designer's four properties are deliberately not emitted. `style` is technical
 * (`isIgnoredOnGeneratedApp`) and already partitioned into the registered stylesheet like every other
 * component's. `parentScreen` is pre-seeded with the fixed value `"this"`, which is a web class-component
 * reference — there is no `this` in a function component, and the runtime needs none: it reports through
 * `onCompleted` rather than reaching into its host.
 */
const emitPaInteractionButtons: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  // The value prop (`SelectedTasks`) — a bound read in every real screen, since the id comes from the task the
  // user picked; a fixed value is still honoured so a single-task screen can name its step directly.
  const selectedMarker: string = ctx.getBoundValueMarker("SelectedTasks");
  if (selectedMarker !== "") {
    props.push(`value={String(${selectedMarker} ?? "")}`);
  } else {
    pushStringProp(props, "value", ctx.getFixedPropertyValue("SelectedTasks"));
  }
  const jsx: string = `${ctx.indent}<KPaInteractionButtons${ctx.testIdProp}${styleProp}${
    props.length > 0 ? " " + props.join(" ") : ""
  }${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KPaInteractionButtons"] };
};

/**
 * Countdown → the `KCountdown` runtime component (`@/components/kuika`). The RN port of the web
 * Countdown (antd `Statistic.Countdown`): a self-ticking timer counting down from `value` seconds,
 * rendered per `format`. The ticking, antd-parity time formatting, and finish/cleanup live in
 * KCountdown so they are authored once instead of inlined into every screen. A text display like
 * KLabel, so the full resolved style passes through unpartitioned onto the `Text` (the web applies it
 * as `valueStyle`). `value` (seconds) and `format` are emitted only when the DI sets them — both carry
 * a metadata default, so a bare countdown still emits both, mirroring the web `shouldIncludeProperty`.
 * Always self-closing; the `onFinish`/`onChange` actions, if the designer bound any, are wired C#-side at the
 * tag's `kuikaid` slot.
 */
const emitCountdown: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const props: string[] = [timerRefProp(ctx)];
  // integer=false: seconds may be authored fractional; pass it through (KCountdown floors) rather than
  // silently dropping a non-integer value.
  if (!pushBoundValueProp(props, ctx, "value", "number")) {
    pushNumberProp(props, "value", ctx.getFixedPropertyValue("value"), false);
  }
  const format: string = ctx.getFixedPropertyValue("format");
  if (format !== "") {
    props.push(`format={${JSON.stringify(format)}}`);
  }
  const propsStr: string = ` ${props.join(" ")}`;
  const jsx: string = `${ctx.indent}<KCountdown${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KCountdown"] };
};

/**
 * CountUp → the `KCountUp` runtime component (`@/components/kuika`). The RN port of the web CountUp
 * (ERendererName.CountUp = 156). Despite the name it is a **stopwatch**: it renders the time elapsed
 * since mount as `HH:mm:ss` and the `value` prop does not drive that display — so a bare CountUp needs
 * no props to render. `value` (seeds the internal counter the web reports through `onChange`) is
 * emitted only when the DI sets it — a metadata default of `0` means a bare CountUp still emits it,
 * mirroring the web `shouldIncludeProperty`. A text display like KLabel, so the full resolved style passes
 * through unpartitioned onto the `Text`. Always self-closing; the `onChange` action, if the designer bound one,
 * is wired C#-side at the tag's `kuikaid` slot.
 */
const emitCountUp: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.style);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "value", "number")) {
    pushNumberProp(props, "value", ctx.getFixedPropertyValue("value"), false);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KCountUp${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KCountUp"] };
};

/**
 * Stopwatch → the `KStopwatch` count-up timer with play/pause/stop controls (ERendererName.Stopwatch = 152). A
 * DIFFERENT component from CountUp: user-driven, reporting the four lifecycle events (onStart/onStop/onPause/
 * onResume) rather than a per-tick onChange — so, like emitCountUp, `value` seeds the display but is not a
 * controlled round-trip, and the wired events land on the tag's kuikaid slot (the C# assembler splices all four).
 * Self-closing; the controls live inside the runtime component.
 */
const emitStopwatch: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const textStyleName: string = `${ctx.styleName}Text`;
  const textStyleRegistered: string = ctx.registerStyle(textStyleName, ctx.partitioned.textStyle);
  const textStyleProp: string = textStyleRegistered === "" ? "" : ` textStyle={styles.${textStyleName}}`;
  const props: string[] = [timerRefProp(ctx)];
  if (!pushBoundValueProp(props, ctx, "value", "number")) {
    pushNumberProp(props, "value", ctx.getFixedPropertyValue("value"), false);
  }
  const propsStr: string = ` ${props.join(" ")}`;
  const jsx: string = `${ctx.indent}<KStopwatch${ctx.testIdProp}${styleProp}${textStyleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KStopwatch"] };
};

/**
 * The `valueRef={timerRef_di<id>}` a Stopwatch or Countdown binds its value handle to; the C# leg declares the ref
 * and lowers a `<Timer>.value` read to `timerRef_di<id>.current?.getValue()`.
 */
function timerRefProp(ctx: IComponentEmitContext): string {
  return `valueRef={timerRef_di${diToken(ctx.di.id)}}`;
}

/**
 * ContentMenu → the `KContentMenu` dropdown container (ERendererName.ContentMenu = 132). Recurses its children —
 * the trigger `ContentMenuPanel` first, then the `ContentMenuItem`s — like emitMenu, but the runtime shows them as
 * a Modal-anchored popover, not an inline list. Static children only (no data-binding, unlike Menu).
 */
const emitContentMenu: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const menuOpeningType: string = ctx.getFixedPropertyValue("menuOpeningType");
  if (menuOpeningType !== "") {
    props.push(`menuOpeningType={${JSON.stringify(menuOpeningType)}}`);
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const runtimeComponents: string[] = ["KContentMenu"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return {
      jsx: `${ctx.indent}<KContentMenu${ctx.testIdProp}${styleProp}${propsStr} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KContentMenu${ctx.testIdProp}${styleProp}${propsStr}>`,
      ...childParts,
      `${ctx.indent}</KContentMenu>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * ContentMenuPanel → the `KContentMenuPanel` trigger surface. A plain container that renders the designer's trigger
 * content; the enclosing KContentMenu wraps it in the pressable that opens the dropdown.
 */
const emitContentMenuPanel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const runtimeComponents: string[] = ["KContentMenuPanel"];
  const usedTags: TReactNativeTag[] = [];
  const childParts: string[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 1);
    if (emitted) {
      childParts.push(emitted.jsx);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (childParts.length === 0) {
    return { jsx: `${ctx.indent}<KContentMenuPanel${ctx.testIdProp}${styleProp} />`, usedTags, runtimeComponents };
  }
  return {
    jsx: [
      `${ctx.indent}<KContentMenuPanel${ctx.testIdProp}${styleProp}>`,
      ...childParts,
      `${ctx.indent}</KContentMenuPanel>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * ContentMenuItem → the `KContentMenuItem` row (label). Its onClick navigates and lands on the tag's kuikaid slot
 * (like MenuItem), spliced C#-side to onPress; the runtime also closes the dropdown after the action runs.
 */
const emitContentMenuItem: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  if (!pushBoundValueProp(props, ctx, "label", "string")) {
    const label: string = textPropExpression(ctx, "label");
    if (label !== "") {
      props.push(`label={${label}}`);
    }
  }
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const jsx: string = `${ctx.indent}<KContentMenuItem${ctx.testIdProp}${styleProp}${propsStr}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KContentMenuItem"] };
};

/** Kuika component name of a Table's header child (design-time container the Table strategy consumes itself). */
const TABLE_HEADER_COMPONENT: string = "TableHeader";

/** Kuika component name of a Table's row child — the template whose column children define the cells. */
const TABLE_ROW_COMPONENT: string = "TableRow";

/** Kuika component names of a TableRow's column children (both variants map to the same emitted column object). */
const TABLE_COLUMN_COMPONENTS: ReadonlySet<string> = new Set<string>(["TableDefaultColumn", "TableTemplateColumn"]);

/**
 * The three table SECTIONS whose designer box is the table's own grid — header row, row template, and column
 * (cell). {@link tableSectionBoxStyle} rewrites their authored `height`; a component *inside* a cell is not one of
 * them and keeps its height exactly as authored.
 */
const TABLE_SECTION_COMPONENTS: ReadonlySet<string> = new Set<string>([
  TABLE_HEADER_COMPONENT,
  TABLE_ROW_COMPONENT,
  ...TABLE_COLUMN_COMPONENTS
]);

/**
 * A table section's authored `height` becomes a **`minHeight`**, because that is what a height means on a table
 * row or cell in the layout the designer canvas and the web target render: an HTML `tr`/`td` grows to fit its
 * cells, so `height: 30px` there is a floor, never a ceiling.
 *
 * React Native flexbox takes `height` literally, and Android's default `overflow` is `visible` — so a 30px row
 * whose cells need 46px both **clips** its text at the row boundary and **paints** the overflow over whatever the
 * screen laid out after the table. On the device that showed up as half-cut cell values plus a pager drawn inside
 * the last row, from one style key: `rowStyle`'s `height: 30`.
 *
 * An explicitly authored `minHeight` wins (the designer asked for that floor by name); `height` is dropped either
 * way, since keeping it is the fixed size that caused the clipping.
 */
function tableSectionBoxStyle(style: Record<string, string | number>): Record<string, string | number> {
  if (!("height" in style)) {
    return style;
  }
  const relaxed: Record<string, string | number> = { ...style };
  const authoredHeight: string | number = relaxed.height;
  delete relaxed.height;
  if (!("minHeight" in relaxed)) {
    relaxed.minHeight = authoredHeight;
  }
  return relaxed;
}

/**
 * The row variable a table's cell closures bind through: `row` for a top-level table, `row2`/`row3`… for a table
 * nested inside another table's cell. The C# `ReactNativeSymbolResolver` derives the same name from the bound
 * display item's data-repeater ancestry, so a row-field (`Current`) binding resolves to this exact identifier —
 * the one contract the two legs share about row scope.
 */
export function reactNativeRowVariableName(repeaterDepth: number): string {
  return repeaterDepth <= 0 ? "row" : `row${repeaterDepth + 1}`;
}

/** Emits `name={"value"}` when the DI sets the property, mirroring the web "emit only when set" rule. */
function pushStringProp(props: string[], name: string, value: string): void {
  if (value !== "") {
    props.push(`${name}={${JSON.stringify(value)}}`);
  }
}

/**
 * What the text helpers below need of a context: the literal, and the lookup that may replace it. Both the
 * full {@link IComponentEmitContext} and the narrower {@link IStructuralChildContext} (a table column, a
 * dropdown item — a value inside an emitted object rather than a JSX element) satisfy it, and both carry
 * translatable text, so the helpers ask for exactly this much.
 */
interface ITextPropertySource {
  getFixedPropertyValue(propertyName: string): string;
  getTranslatedTextExpression(propertyName: string): string;
}

/**
 * The JSX expression for one of a component's TEXT properties — already quoted or already a call, ready to go
 * inside `{...}` — or "" when the designer left the property empty.
 *
 * <p>This is the one place that decides literal-vs-lookup, and every translatable property goes through it:
 * `value`, `label`, `placeholder`, `title`, `tab`, `header`, and the rest of the set the component metadata
 * marks `isSupportMultiLanguage`. Keeping the decision here rather than at ~35 call sites is what stops half
 * the library from translating while the other half does not — a split that would be invisible in a diff and
 * visible only on a screen in a second language.</p>
 *
 * <p>It is deliberately NOT folded into {@link ReactNativeElementGenerator.getFixedPropertyValue}, whose 480
 * call sites include `pushNumberProp(props, "value", …)` — `value` is a NUMBER on ProgressBar, Rating and
 * CountUp. Returning a call expression from the shared accessor would put `ReactSystemFunctions.translate(…)`
 * where a number belongs on any component whose metadata ever flagged the property, and nothing would catch
 * it. The narrow helper cannot reach those sites at all.</p>
 */
function textPropExpression(ctx: ITextPropertySource, propName: string): string {
  const translated: string = ctx.getTranslatedTextExpression(propName);
  if (translated !== "") {
    return translated;
  }
  const literal: string = ctx.getFixedPropertyValue(propName);
  return literal === "" ? "" : JSON.stringify(literal);
}

/** Adds a text property to a prop list — translated when the designer marked it so, else the literal. */
function pushTextProp(props: string[], ctx: ITextPropertySource, propName: string, jsxName?: string): void {
  const expr: string = textPropExpression(ctx, propName);
  if (expr !== "") {
    props.push(`${jsxName ?? propName}={${expr}}`);
  }
}

/**
 * The same, in the ATTRIBUTE spelling this emitter has always used for the input family
 * (`placeholder="Enter your email"` rather than `placeholder={"…"}`).
 *
 * <p>Only a literal can take that spelling; a translated property is necessarily an expression, so it
 * switches to braces. Keeping the literal form is not cosmetic fussiness — it means a project with one
 * language, or a component nobody translated, emits a byte-identical screen to before, so the fixtures and
 * goldens that pin this output keep pinning what they were written for.</p>
 */
function pushTextAttr(props: string[], ctx: ITextPropertySource, propName: string, attrName?: string): void {
  const name: string = attrName ?? propName;
  const translated: string = ctx.getTranslatedTextExpression(propName);
  if (translated !== "") {
    props.push(`${name}={${translated}}`);
    return;
  }
  const literal: string = ctx.getFixedPropertyValue(propName);
  if (literal !== "") {
    props.push(`${name}=${JSON.stringify(literal)}`);
  }
}

/**
 * The symbol categories that read a **field of the current row** inside a data repeater. The designer emits
 * `actionField` for a table/grid column cell (its `fieldName` is the datasource column — see the designer's
 * own `checkDataFieldBindings`, and the canvas placeholder `actionField:<field>`), and `current` for a
 * current-record binding. The web target funnels both into the very same per-row `[datafield:X]` token
 * (`React_UIGenerator`), so both are row reads here too.
 */
function isRowFieldCategory(category: ESymbolCategory | undefined): boolean {
  return category === ESymbolCategory.actionField || category === ESymbolCategory.current;
}

/**
 * The row field a column reads — the camelCase path of the first row-scoped (`Current`) binding among the
 * column's descendants, preferring a `value` property. It is the column's sort / search / filter / footer /
 * export key, the RN counterpart of the field the web table infers at runtime from its `[datafield:X]` token
 * (`base-table.tsx` `setRow`), resolved here at generation time instead.
 */
function resolveColumnDataField(column: IDisplayItem): string {
  const visit: (di: IDisplayItem) => string = (di: IDisplayItem): string => {
    const properties: IDisplayItemProperty[] = di.item?.properties ?? [];
    const rowBound: IDisplayItemProperty[] = properties.filter(
      (property: IDisplayItemProperty): boolean =>
        isRowFieldCategory(property.symbol?.category) &&
        property.symbol?.fieldName !== undefined &&
        property.symbol.fieldName.length > 0
    );
    const preferred: IDisplayItemProperty | undefined =
      rowBound.find((property: IDisplayItemProperty): boolean => property.name === "value") ?? rowBound[0];
    if (preferred?.symbol?.fieldName !== undefined) {
      return toRowFieldPath(preferred.symbol.fieldName);
    }
    for (const child of di.children ?? []) {
      const found: string = visit(child);
      if (found !== "") {
        return found;
      }
    }
    return "";
  };
  return visit(column);
}

/**
 * Lower-camelCases each segment of a designer field path, exactly like the web `[datafield:]` token
 * (`React_UIGenerator` splits on "." and camelCases each part). Locale-independent by construction.
 */
function toRowFieldPath(fieldName: string): string {
  return fieldName
    .split(".")
    .map((segment: string): string =>
      segment.replace(/^([A-Z])|[\s-](\w)/g, (_match: string, leading: string, joined: string): string =>
        joined ? joined.toUpperCase() : leading.toLowerCase()
      )
    )
    .join(".");
}

/**
 * The page sizes a table's size changer offers, as a TSX array literal — parsed from the designer's `options`
 * property (an option list whose entries carry a `text`/`value`, which the web `getPageSizeOptions` also reads as
 * numbers). Returns "" when the designer set none, so the runtime falls back to the web default sizes.
 */
function resolvePageSizeOptionsLiteral(options: string): string {
  const trimmed: string = options.trim();
  if (trimmed.length === 0) {
    return "";
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // A malformed option list is a designer-side problem, not a codegen failure: emit nothing and let the
    // runtime use the default page sizes.
    return "";
  }
  if (!Array.isArray(parsed)) {
    return "";
  }
  const sizes: number[] = [];
  for (const entry of parsed) {
    const raw: unknown =
      typeof entry === "object" && entry !== null
        ? ((entry as IOptionEntry).text ?? (entry as IOptionEntry).value)
        : entry;
    const size: number = Number.parseInt(String(raw ?? "").trim(), 10);
    if (Number.isInteger(size) && size >= 1 && !sizes.includes(size)) {
      sizes.push(size);
    }
  }
  if (sizes.length === 0) {
    return "";
  }
  sizes.sort((a: number, b: number): number => a - b);
  return `[${sizes.join(", ")}]`;
}

/** One entry of a designer option list, as it appears in the property's fixed JSON value. */
interface IOptionEntry {
  text?: string;
  value?: string;
}

/**
 * Table → the `KTable` runtime component (`@/components/kuika`): the data-bound table. Unlike every other
 * strategy this one is a **data repeater** — its TableHeader / TableRow / column display items are design-time
 * structure, not nodes, so it consumes them itself and lowers them into KTable's `columns` model:
 *
 * ```tsx
 * <KTable dataSource={dsGetOrders} columns={[
 *   { key: "di13", title: "Customer", dataField: "customer",
 *     render: (row: any, rowIndex: number): React.ReactNode => (<KLabel value={String(row?.customer ?? "")} />) }
 * ]} />
 * ```
 *
 * Each column's authored cell subtree becomes its `render` closure, emitted through the ordinary child path so
 * any component may sit in a cell; row-field bindings inside it stay as `kuika-rn` bind markers that the C#
 * assembler resolves against the closure's row variable ({@link reactNativeRowVariableName}). Header labels come
 * from the column's `title`, overridden by the TableHeader's `;`-separated `columnTitles` (web `setRow` parity);
 * the header's and row's own designer styles ride along as `headerStyle` / `rowStyle`.
 */
/**
 * One entry of an emitted array-literal prop (`columns={[…]}`, `lines={[…]}`), guarded so a designer-hidden
 * structural child contributes nothing to the array.
 *
 * <p>A structural child is emitted as a **value**, not as JSX, so the single `emitNode` visibility wrap that covers
 * every other component cannot reach it — a hidden Table column kept its header, its data, its search box and its
 * footer total. A conditional spread is the faithful lowering: it drops the entry entirely, which is what web's
 * `col.visibility !== "hidden"` filter does, and it works for a runtime-controlled guard as well as a declared one
 * (the array is rebuilt on every render either way). An unguarded entry is emitted exactly as before.</p>
 */
function guardedArrayEntry(entry: string, guard: string, indent: string): string {
  if (guard === "") {
    return entry;
  }
  // A single-line entry (a chart series literal) keeps its shape; a block entry (a table column object) is
  // bracketed on its own lines so the emitted screen stays readable.
  if (!entry.includes("\n")) {
    return `...(${guard} ? [${entry}] : [])`;
  }
  return [`${indent}...(${guard} ? [`, entry, `${indent}] : [])`].join("\n");
}

/**
 * The Table's `showHeader`, which is its own property AND the TableHeader child's designer visibility.
 *
 * <p>The header row is a structural child: it never passes through `emitNode`, so hiding it in the designer used to
 * change nothing. `showHeader` is the prop that removes it, so the two combine here — a Table that turned the
 * property off stays off whatever the child says, and otherwise the child's guard decides. Web reaches the same
 * result from the other end, by rendering no header cell for a hidden TableHeader.</p>
 */
function pushTableShowHeaderProp(
  props: string[],
  ctx: IComponentEmitContext,
  headerDi: IDisplayItem | undefined
): void {
  const guard: string = headerDi === undefined ? "" : ctx.structuralChild(headerDi).visibilityGuard();
  const marker: string = ctx.getBoundValueMarker("showHeader");
  if (marker !== "") {
    const bound: string = boundBooleanExpression(marker);
    props.push(`showHeader={${guard === "" ? bound : `${bound} && ${guard}`}}`);
    return;
  }
  const fixed: string = ctx.getFixedPropertyValue("showHeader");
  if (fixed === "false") {
    props.push("showHeader={false}");
    return;
  }
  if (guard !== "") {
    props.push(`showHeader={${guard}}`);
    return;
  }
  if (fixed === "true") {
    props.push("showHeader");
  }
}

/**
 * A table section's typography split the way the cell cascade consumes it: the horizontal alignment (the one
 * {@link IParentLayout.inheritedTextAlign} already carries) apart from the rest (colour, font, …).
 */
interface ITableCellTextCascade {
  textAlign?: TKTableCellTextAlign;
  textStyle: Record<string, string | number>;
}

/** The alignments `IKTableColumn.textAlign` accepts — the RN runtime's `TKTableTextAlign`. */
type TKTableCellTextAlign = "left" | "center" | "right";

const TABLE_CELL_TEXT_ALIGNS: ReadonlySet<string> = new Set<string>(["left", "center", "right"]);

/**
 * Splits a table section's resolved typography into its alignment and the rest. An alignment KTable cannot take
 * (`justify`, `auto`) is left out rather than coerced, so the cells keep whatever they inherited before.
 */
function splitCellTextCascade(textStyle: Record<string, string | number>): ITableCellTextCascade {
  const rest: Record<string, string | number> = {};
  let textAlign: TKTableCellTextAlign | undefined;
  for (const [key, value] of Object.entries(textStyle)) {
    if (key !== "textAlign") {
      rest[key] = value;
    } else if (typeof value === "string" && TABLE_CELL_TEXT_ALIGNS.has(value)) {
      textAlign = value as TKTableCellTextAlign;
    }
  }
  return textAlign === undefined ? { textStyle: rest } : { textAlign, textStyle: rest };
}

/**
 * A column's text-valued property as the expression that goes into the emitted column object: a bound marker coerced
 * to a string, else the translated lookup (when `translatable`) or the literal — "" when unset.
 */
function columnTextExpression(column: IStructuralChildContext, propName: string, translatable: boolean = true): string {
  const marker: string = column.getBoundValueMarker(propName);
  if (marker !== "") {
    return boundValueExpression(marker, "string");
  }
  if (translatable) {
    return textPropExpression(column, propName);
  }
  const literal: string = column.getFixedPropertyValue(propName);
  return literal === "" ? "" : JSON.stringify(literal);
}

/** A column's boolean flag as a column-object entry (`hideSorting: true`), bound or fixed; nothing when unset. */
function pushColumnBoolEntry(entryProps: string[], column: IStructuralChildContext, flag: string): void {
  const marker: string = column.getBoundValueMarker(flag);
  if (marker !== "") {
    entryProps.push(`${flag}: ${boundBooleanExpression(marker)}`);
    return;
  }
  const value: string = column.getFixedPropertyValue(flag);
  if (value === "true" || value === "false") {
    entryProps.push(`${flag}: ${value}`);
  }
}

/**
 * A column's designer `width` as the literal `IKTableColumn.width` takes: a percentage passes through as a string
 * (`"20%"`, which RN lays out against the table's width exactly as a `td` does), anything else is dp — so `"120"`
 * and `"120px"` both become `120`. "" when the width is unset or unparseable.
 *
 * <p>A percentage used to be read by `parseFloat`, which turned `"20%"` into a 20 dp sliver.</p>
 */
function resolveColumnWidthLiteral(raw: string): string {
  const trimmed: string = raw.trim();
  const numeric: number = Number.parseFloat(trimmed);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  if (trimmed.endsWith("%")) {
    return JSON.stringify(`${numeric}%`);
  }
  return String(numeric);
}

/**
 * Suffix of a Table's selection state (`di<id>SelectedValues`), holding the comma-joined row keys.
 *
 * **Must stay in step with the C# leg**: `ReactNativeScreenGenerator.TableSelectedValuesSuffix` declares the state
 * this reference reads, and `ReactNativeSymbolResolver` resolves `Table1.selectedValues` / `.selectedCount` to it.
 */
const TABLE_SELECTED_VALUES_SUFFIX: string = "SelectedValues";

const emitTable: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  const children: IDisplayItem[] = ctx.di.children ?? [];
  const headerDi: IDisplayItem | undefined = children.find(
    (child: IDisplayItem): boolean => child.item?.name === TABLE_HEADER_COMPONENT
  );
  const rowDi: IDisplayItem | undefined = children.find(
    (child: IDisplayItem): boolean => child.item?.name === TABLE_ROW_COMPONENT
  );

  pushDataSourceProp(props, ctx);

  pushBindableStringProp(props, ctx, "rowKey");
  // `size`, `tableWidthMode`, `insertRowPosition` and `multiSelectScope` are string UNIONS on KTable: a bound value is
  // a plain `string` there and could not type-check, and the designer offers them as a fixed pick anyway.
  pushStringProp(props, "size", ctx.getFixedPropertyValue("size"));
  pushStringProp(props, "tableWidthMode", ctx.getFixedPropertyValue("tableWidthMode"));
  // `bordered` is deliberately not emitted: web hard-codes antd's `bordered={false}` whatever the designer set, so
  // the property is a no-op there and KTable draws web's row lines unconditionally.
  pushBindableBoolProp(props, ctx, "loading");
  pushTableShowHeaderProp(props, ctx, headerDi);
  pushBindableBoolProp(props, ctx, "fixedHeader");
  pushBindableBoolProp(props, ctx, "striped");
  pushBindableStringProp(props, ctx, "stripedColor");
  pushBindableBoolProp(props, ctx, "transformedOnMobileResolution");
  pushBindableBoolProp(props, ctx, "hideHeaderOnMobileResolution");
  pushBindableBoolProp(props, ctx, "hideTableOnEmpty");
  pushEmptyStateProp(
    props,
    "showNoDataFound",
    ctx.getFixedPropertyValue("showNoDataFound"),
    ctx.getBoundValueMarker("showNoDataFound")
  );
  if (!pushBoundValueProp(props, ctx, "nodatafoundmessage", "string")) {
    pushTextProp(props, ctx, "nodatafoundmessage");
  }
  pushBindableStringProp(props, ctx, "noDataFoundImage");
  pushBindableBoolProp(props, ctx, "showRowNumbers");
  pushBindableBoolProp(props, ctx, "sorter");
  pushBindableBoolProp(props, ctx, "filtering");
  pushBindableBoolProp(props, ctx, "searchable");
  pushBindableBoolProp(props, ctx, "globalSearch");
  pushBindableBoolProp(props, ctx, "shouldSearchOnChange");
  pushBindableNumberProp(props, ctx, "searchDelay", true);
  pushBindableBoolProp(props, ctx, "columnChooser");
  pushBindableBoolProp(props, ctx, "pagination");
  pushBindableNumberProp(props, ctx, "pageSize", true);
  pushBindableNumberProp(props, ctx, "current", true);
  pushBindableBoolProp(props, ctx, "showSizeChanger");
  const pageSizeOptions: string = resolvePageSizeOptionsLiteral(ctx.getFixedPropertyValue("options"));
  if (pageSizeOptions !== "") {
    props.push(`pageSizeOptions={${pageSizeOptions}}`);
  }
  pushBindableBoolProp(props, ctx, "export");
  pushBindableStringProp(props, ctx, "exportFileName");
  pushBindableStringProp(props, ctx, "exportButtonBgColor");
  pushBindableStringProp(props, ctx, "paginationSelectedTextColor");
  pushBindableStringProp(props, ctx, "paginationUnselectedTextColor");
  pushBindableBoolProp(props, ctx, "editable");
  pushBindableBoolProp(props, ctx, "editableAlwaysActive");
  pushBindableBoolProp(props, ctx, "insertRowActive");
  pushStringProp(props, "insertRowPosition", ctx.getFixedPropertyValue("insertRowPosition"));
  pushBindableBoolProp(props, ctx, "multiSelect");
  pushStringProp(props, "multiSelectScope", ctx.getFixedPropertyValue("multiSelectScope"));
  pushBindableStringProp(props, ctx, "selectedRowColor");
  // The selection is screen state (`di<id>SelectedValues`), not a prop value: the designer reads it back
  // (`Table1.selectedValues` / `.selectedCount`) and SetValueOf writes it, which web does through the table ref. The
  // C# leg declares the state (seeded from the fixed or bound `selectedValues`), and composes a designer
  // onSelectedValuesChange action into this setter — the Tree's `checkedKeys` / `onCheck` pair, exactly.
  props.push(
    `selectedValues={di${ctx.di.id}${TABLE_SELECTED_VALUES_SUFFIX}}`,
    `onSelectedValuesChange={setDi${ctx.di.id}${TABLE_SELECTED_VALUES_SUFFIX}}`
  );

  // `columnTitles` is not a KTable prop: each entry is baked into its column's `title` below, where web applies it.
  let columnTitles: readonly string[] = [];
  if (headerDi !== undefined) {
    const header: IStructuralChildContext = ctx.structuralChild(headerDi);
    columnTitles = header.getFixedPropertyValue("columnTitles").split(";");
    const headerStyle: string = header.registerStyle();
    if (headerStyle !== "") {
      props.push(`headerStyle={${headerStyle}}`);
    }
    // The header's typography — web puts the TableHeader's whole style on every `th`, text half included.
    const headerTextStyle: string = header.registerTextStyle();
    if (headerTextStyle !== "") {
      props.push(`headerTextStyle={${headerTextStyle}}`);
    }
    // Web's stacked (mobile) layout drops each cell's column-title label when the TableHeader asks for it; the
    // Table's own `hideHeaderOnMobileResolution` is the other flag, which hides the header ROW only.
    pushBindableBoolProp(props, header, "hideOnMobileResolution", "hideCellTitlesOnMobile");
    const writingMode: string = header.getFixedPropertyValue("writingMode");
    if (writingMode === "horizontal" || writingMode === "vertical") {
      props.push(`headerWritingMode={${JSON.stringify(writingMode)}}`);
    }
  }
  if (rowDi !== undefined) {
    const row: IStructuralChildContext = ctx.structuralChild(rowDi);
    const rowStyle: string = row.registerStyle();
    if (rowStyle !== "") {
      props.push(`rowStyle={${rowStyle}}`);
    }
    // A row's press IS the table's designer onClick (ReactNativeEventCatalog maps Table.onClick →
    // onRowPress), so the row is the pressed element and carries the `pressed` delta the designer authored
    // on the TableRow.
    const rowPressedStyle: string = row.registerStateStyle("pressed", "view");
    if (rowPressedStyle !== "") {
      props.push(`rowPressedStyle={${rowPressedStyle}}`);
    }
  }
  // The row's typography reaches every cell's content the way a web `tr`'s does — by CSS inheritance, under the
  // column's (a `td` style) and under each component's own.
  const rowText: ITableCellTextCascade = splitCellTextCascade(
    rowDi === undefined ? {} : ctx.structuralChild(rowDi).resolveTextStyle()
  );

  const runtimeComponents: string[] = ["KTable"];
  const usedTags: TReactNativeTag[] = [];
  const columnEntries: string[] = [];
  const rowVariable: string = reactNativeRowVariableName(ctx.repeaterDepth);
  const columnIndent: string = INDENT_STEP.repeat(ctx.depth + 2);
  const cellIndent: string = INDENT_STEP.repeat(ctx.depth + 4);

  // Indexed over ALL of the row's children, before any designer-visibility filtering: web's `setRow` applies
  // `columnTitles[grandChildIndex]` inside `React.Children.forEach` over the TableRow's children, so hiding one
  // column never shifts the titles of the columns after it.
  const columnDis: IDisplayItem[] = rowDi?.children ?? [];
  for (let columnIndex = 0; columnIndex < columnDis.length; columnIndex++) {
    const columnDi: IDisplayItem = columnDis[columnIndex];
    if (!TABLE_COLUMN_COMPONENTS.has(columnDi.item?.name ?? "")) {
      continue;
    }
    const column: IStructuralChildContext = ctx.structuralChild(columnDi);
    const entryProps: string[] = [`key: ${JSON.stringify(`di${columnDi.id}`)}`];
    const titleOverride: string = columnIndex < columnTitles.length ? columnTitles[columnIndex] : "";
    const title: string =
      titleOverride.length > 0 ? JSON.stringify(titleOverride) : columnTextExpression(column, "title");
    if (title !== "") {
      entryProps.push(`title: ${title}`);
    }
    const secondTitle: string = columnTextExpression(column, "secondTitle", false);
    if (secondTitle !== "") {
      entryProps.push(`secondTitle: ${secondTitle}`);
    }
    const width: string = resolveColumnWidthLiteral(column.getFixedPropertyValue("width"));
    if (width !== "") {
      entryProps.push(`width: ${width}`);
    }
    const dataField: string = resolveColumnDataField(columnDi);
    if (dataField !== "") {
      entryProps.push(`dataField: ${JSON.stringify(dataField)}`);
    }
    const dateSearchFormat: string = column.getFixedPropertyValue("dateSearchFormat");
    if (dateSearchFormat !== "") {
      entryProps.push(`dateSearchFormat: ${JSON.stringify(dateSearchFormat)}`);
    }
    const placeholder: string = columnTextExpression(column, "placeholder");
    if (placeholder !== "") {
      entryProps.push(`placeholder: ${placeholder}`);
    }
    const titleBgColor: string = columnTextExpression(column, "titleBgColor", false);
    if (titleBgColor !== "") {
      entryProps.push(`titleBgColor: ${titleBgColor}`);
    }
    for (const flag of [
      "hideSorting",
      "hideSearch",
      "hideFiltering",
      "excludeFromExport",
      "alwaysVisibleOnMobileResolution"
    ]) {
      pushColumnBoolEntry(entryProps, column, flag);
    }
    const footerOptions: string = column.getFixedPropertyValue("footerOptions");
    if (footerOptions !== "" && footerOptions !== "none") {
      entryProps.push(`footerOptions: ${JSON.stringify(footerOptions)}`);
      pushColumnFooterProps(entryProps, column);
    }
    const columnStyle: string = column.registerStyle();
    if (columnStyle !== "") {
      entryProps.push(`style: ${columnStyle}`);
    }
    // The column's style is a web `td` style: its alignment drives the column (header title included, as web's
    // TableHeaderCell reads `columnStyle.textAlign`), and its typography is inherited by the cell's content.
    const columnText: ITableCellTextCascade = splitCellTextCascade(column.resolveTextStyle());
    if (columnText.textAlign !== undefined) {
      entryProps.push(`textAlign: ${JSON.stringify(columnText.textAlign)}`);
    }
    const cellTextAlign: string | undefined = columnText.textAlign ?? rowText.textAlign;
    const cellTextStyle: Record<string, string | number> = { ...rowText.textStyle, ...columnText.textStyle };
    const cellInheritance: IEmitChildOptions = {
      ...(cellTextAlign === undefined ? {} : { inheritedTextAlign: cellTextAlign }),
      ...(Object.keys(cellTextStyle).length === 0 ? {} : { inheritedTextStyle: cellTextStyle })
    };

    // An editable column carries TWO authored subtrees — display first, editor second (the web editable column's
    // two children, which `setRow` picks between per row). Both go through the ordinary child path (so any
    // component may sit in a cell) one repeater level deeper, which is what names a nested table's row variable
    // apart from this one's; the editor subtree is emitted in cell-editor scope, so its inputs bind to KTable's
    // per-row cell buffer instead of one screen-level state shared by every row.
    const isEditableColumn: boolean = column.getFixedPropertyValue("isEditableColumn") === "true";
    const cellChildren: IDisplayItem[] = columnDi.children ?? [];
    // EXACTLY two, as web's `setRow` checks (`children.length === 2`): with three or more children web picks no
    // editor at all and renders every child as display, so the third child is not a second editor.
    const hasEditorSubtree: boolean = isEditableColumn && cellChildren.length === 2;
    const displayChildren: IDisplayItem[] = hasEditorSubtree ? cellChildren.slice(0, 1) : cellChildren;
    const editorChildren: IDisplayItem[] = hasEditorSubtree ? cellChildren.slice(1) : [];
    if (isEditableColumn) {
      entryProps.push("isEditableColumn: true");
    }

    /** Emits one authored subtree of a cell, folding its tags / runtime components into the table's. */
    const emitCellSubtree: (children: readonly IDisplayItem[], inCellEditor: boolean) => IEmittedNode[] = (
      children: readonly IDisplayItem[],
      inCellEditor: boolean
    ): IEmittedNode[] => {
      const nodes: IEmittedNode[] = [];
      for (const cellChild of children) {
        const emitted: IEmittedNode | null = ctx.emitChild(cellChild, ctx.depth + 4, {
          ...cellInheritance,
          repeaterDepth: ctx.repeaterDepth + 1,
          inCellEditor
        });
        if (emitted) {
          nodes.push(emitted);
          usedTags.push(...emitted.usedTags);
          runtimeComponents.push(...(emitted.runtimeComponents ?? []));
        }
      }
      return nodes;
    };

    /** Wraps a cell's emitted nodes in its render closure — see {@link expressionPositionBody} for the fragment rule. */
    const renderClosure: (name: string, signature: string, nodes: IEmittedNode[]) => string = (
      name: string,
      signature: string,
      nodes: IEmittedNode[]
    ): string => {
      const body: string = expressionPositionBody(nodes, cellIndent);
      return [`${name}: (${signature}): React.ReactNode => (`, body, `${INDENT_STEP.repeat(ctx.depth + 3)})`].join(
        "\n"
      );
    };

    const displayNodes: IEmittedNode[] = emitCellSubtree(displayChildren, false);
    if (displayNodes.length === 0) {
      entryProps.push(`render: (): React.ReactNode => null`);
    } else {
      entryProps.push(renderClosure("render", `${rowVariable}: any, rowIndex: number`, displayNodes));
    }
    const editorNodes: IEmittedNode[] = emitCellSubtree(editorChildren, true);
    if (editorNodes.length > 0) {
      entryProps.push(
        renderClosure("renderEditor", `${rowVariable}: any, rowIndex: number, cell: IKTableCellEditor`, editorNodes)
      );
    }

    // A hidden column contributes no entry at all — header, data, search, filter and footer total go with it,
    // which is exactly what web's `col.visibility !== "hidden"` filter achieves across base-table.tsx.
    columnEntries.push(
      guardedArrayEntry(
        [
          `${columnIndent}{`,
          ...entryProps.map((entry: string): string => `${columnIndent}${INDENT_STEP}${entry},`),
          `${columnIndent}}`
        ].join("\n"),
        column.visibilityGuard(),
        columnIndent
      )
    );
  }

  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  // The action slot: KTable's row / selection / edit callbacks land here (C# resolves the marker into every event
  // prop the designer bound — see ReactNativeEventCatalog).
  const actionMarker: string = ctx.actionMarker();
  if (columnEntries.length === 0) {
    return {
      jsx: `${ctx.indent}<KTable${ctx.testIdProp}${styleProp}${propsStr}${actionMarker} />`,
      usedTags,
      runtimeComponents
    };
  }
  return {
    jsx: [
      `${ctx.indent}<KTable${ctx.testIdProp}${styleProp}${propsStr}${actionMarker}`,
      `${INDENT_STEP.repeat(ctx.depth + 1)}columns={[`,
      columnEntries.join(",\n"),
      `${INDENT_STEP.repeat(ctx.depth + 1)}]}`,
      `${ctx.indent}/>`
    ].join("\n"),
    usedTags,
    runtimeComponents
  };
};

/**
 * Emits a list-shaped repeater's authored child subtree as its `renderItem` closure — the RN counterpart of the
 * single synthetic column the web `base-table.tsx` builds for its `gallery` / `flex-grid` modes
 * (`setGalleryItem` / `setFlexGridItem` wrap the repeater's whole `children` in one per-row `TableData`).
 *
 * Unlike a Table there is no structural child to consume: the repeater's own children *are* the item template.
 * They go through the ordinary child path one repeater level deeper — so any component may sit in an item, and a
 * repeater nested inside one names its row variable apart from this one's — and their row bindings stay bind
 * markers that the C# assembler resolves against {@link reactNativeRowVariableName}.
 *
 * Returns "" when nothing was authored, so the caller emits a self-closing tag.
 */
/**
 * Emitted nodes assembled for a JSX **expression** position — everything a generated screen puts inside `(…)`
 * rather than between two tags: a data repeater's item template (a table cell's `render` / `renderEditor`, a
 * Gallery/FlexGrid/Carousel `renderItem`, a Maps `renderMarker` / `renderMarkerPopup`) and a master shell's
 * `drawerContent={(props) => { … return ( … ); }}` panel.
 *
 * A node that came back as an expression container (`{isVisible_di<id> !== "hidden" && (…)}` — a designer-hidden
 * or role-hidden component) is a **syntax error** in that position: the leading brace reads as an object literal
 * and Metro cannot parse the file, so the whole bundle fails and the app crashes on launch rather than
 * misrendering one cell. Wrapping it in a fragment puts it back in child position, where the guard is exactly
 * what web emits. Every expression position must therefore route through here rather than reading `node.jsx`
 * directly — the drawer panel crashed the entire app precisely because it did the latter.
 *
 * The fragment is added only when it is needed — more than one node (which already required one), or a single
 * guarded node — so an ordinary cell's emitted output is unchanged. Fragments cost no native view either way.
 */
function expressionPositionBody(nodes: readonly IEmittedNode[], indent: string): string {
  const needsFragment: boolean = nodes.length > 1 || nodes.some((node) => node.isExpressionContainer === true);
  if (!needsFragment) {
    return nodes[0].jsx;
  }
  return [`${indent}<>`, ...nodes.map((node: IEmittedNode): string => indentBlock(node.jsx)), `${indent}</>`].join(
    "\n"
  );
}

/** Indents every line of an emitted node by one step — a node's JSX is multi-line whenever it has children. */
function indentBlock(jsx: string): string {
  return jsx
    .split("\n")
    .map((line: string): string => `${INDENT_STEP}${line}`)
    .join("\n");
}

function emitItemTemplateProp(
  ctx: IComponentEmitContext,
  usedTags: TReactNativeTag[],
  runtimeComponents: string[]
): string {
  const itemIndent: string = INDENT_STEP.repeat(ctx.depth + 2);
  const nodes: IEmittedNode[] = [];
  for (const child of ctx.di.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 2, {
      repeaterDepth: ctx.repeaterDepth + 1
    });
    if (emitted) {
      nodes.push(emitted);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (nodes.length === 0) {
    return "";
  }
  const rowVariable: string = reactNativeRowVariableName(ctx.repeaterDepth);
  const body: string = expressionPositionBody(nodes, itemIndent);
  return [
    `renderItem={(${rowVariable}: any, rowIndex: number): React.ReactNode => (`,
    body,
    `${INDENT_STEP.repeat(ctx.depth + 1)})}`
  ].join("\n");
}

/**
 * The row source of a data repeater: a datasource binding (ActionResult → the `ds<Key>` state slot), or the
 * designer's data-repeater shape — a `fixed` symbol whose `actionDefinitionId` names the action (its `fixedValue`
 * holds the canvas sample rows). Only when the property names no action at all do the sample rows ship, which is
 * what the web target does with the same value.
 */
function pushDataSourceProp(props: string[], ctx: IComponentEmitContext): void {
  const marker: string = ctx.getBoundValueMarker("dataSource") || ctx.getActionBoundMarker("dataSource");
  if (marker !== "") {
    props.push(`dataSource={${marker}}`);
    return;
  }
  const fixedRows: string = ctx.getFixedPropertyValue("dataSource").trim();
  if (fixedRows.startsWith("[")) {
    props.push(`dataSource={${fixedRows}}`);
  }
}

/** The empty-state properties every list-shaped repeater exposes (`GalleryView` and `FlexGrid` share these). */
function pushListEmptyStateProps(props: string[], ctx: IComponentEmitContext): void {
  pushEmptyStateProp(props, "showNoDataFoundImage", ctx.getFixedPropertyValue("showNoDataFoundImage"));
  pushTextProp(props, ctx, "nodatafoundmessage");
}

/**
 * Assembles a list-shaped repeater's JSX: `<KGallery … renderItem={…} />`, self-closing either way (the item
 * template is a prop, not children, so there is never a closing tag to balance).
 */
function listRepeaterJsx(
  ctx: IComponentEmitContext,
  component: string,
  styleProp: string,
  props: string[],
  itemTemplate: string
): string {
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const opening: string = `${ctx.indent}<${component}${ctx.testIdProp}${styleProp}${propsStr}`;
  if (itemTemplate === "") {
    return `${opening} />`;
  }
  return [opening, `${INDENT_STEP.repeat(ctx.depth + 1)}${itemTemplate}`, `${ctx.indent}/>`].join("\n");
}

/**
 * GalleryView → the `KGallery` runtime component (`@/components/kuika`): a data-bound, responsive N-column card
 * grid. A **data repeater** like the Table, but its authored child subtree is the item template rather than a
 * `TableRow` of columns, so it lowers to one `renderItem` closure:
 *
 * ```tsx
 * <KGallery dataSource={dsGetProducts} phoneColumnsCount={1} desktopColumnsCount={4} horizontalGutter={8}
 *   renderItem={(row: any, rowIndex: number): React.ReactNode => (
 *     <KPanel><KLabel value={String(row?.productName ?? "")} /></KPanel>
 *   )}
 * />
 * ```
 *
 * The designer exposes no pagination, sorting, filtering, search, selection or export on a Gallery — and the web
 * component honours none of them in `gallery` mode either — so the emitted surface is exactly the fifteen
 * properties of `0135_GalleryView.json` minus the three that are inert on both targets (`className` has no RN
 * counterpart; `rowBgColor` / `rowFontColor` are function-typed and generated output never sets them;
 * `hoverBgColor` / `hoverFontColor` are consumed only by the carousel body wrapper, and touch has no hover).
 */
const emitGallery: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  pushDataSourceProp(props, ctx);
  pushNumberProp(props, "phoneColumnsCount", ctx.getFixedPropertyValue("phoneColumnsCount"), true);
  pushNumberProp(props, "tabletColumnsCount", ctx.getFixedPropertyValue("tabletColumnsCount"), true);
  pushNumberProp(props, "desktopColumnsCount", ctx.getFixedPropertyValue("desktopColumnsCount"), true);
  pushNumberProp(props, "horizontalGutter", ctx.getFixedPropertyValue("horizontalGutter"), false);
  pushNumberProp(props, "verticalGutter", ctx.getFixedPropertyValue("verticalGutter"), false);
  pushBoolProp(props, "loading", ctx.getFixedPropertyValue("loading"));
  pushListEmptyStateProps(props, ctx);

  const runtimeComponents: string[] = ["KGallery"];
  const usedTags: TReactNativeTag[] = [];
  const itemTemplate: string = emitItemTemplateProp(ctx, usedTags, runtimeComponents);

  return { jsx: listRepeaterJsx(ctx, "KGallery", styleProp, props, itemTemplate), usedTags, runtimeComponents };
};

/**
 * FlexGrid → the `KFlexGrid` runtime component (`@/components/kuika`): a data-bound wrap flow of content-sized
 * items (web: `base-table.tsx` in `flex-grid` mode, whose body wrapper is an antd `<Space wrap>`). Same item
 * template contract as {@link emitGallery}; the designer exposes only four properties on it — no responsive
 * counts, no gutters — because the wrap flow sizes its items from their content.
 */
const emitFlexGrid: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  pushDataSourceProp(props, ctx);
  pushListEmptyStateProps(props, ctx);

  const runtimeComponents: string[] = ["KFlexGrid"];
  const usedTags: TReactNativeTag[] = [];
  const itemTemplate: string = emitItemTemplateProp(ctx, usedTags, runtimeComponents);

  return { jsx: listRepeaterJsx(ctx, "KFlexGrid", styleProp, props, itemTemplate), usedTags, runtimeComponents };
};

/** The registry names a Maps marker template can carry: the designer's name and the renderer/web tagName. */
const MAPS_MARKER_NAMES: ReadonlySet<string> = new Set<string>(["MapsMarker", "GoogleMapsMarker"]);

/** The registry names a Maps marker-popup template can carry. */
const MAPS_MARKER_POPUP_NAMES: ReadonlySet<string> = new Set<string>(["MapsMarkerPopup", "GoogleMapsMarkerPopup"]);

/**
 * Emits a NAMED template — a subtree the designer wraps in a child of a particular registry name — as a per-row
 * closure. Maps' `MapsMarker` / `MapsMarkerPopup` and the NodeTree's `Node` are the callers.
 *
 * Unlike a Gallery, whose children *are* the item template, these components have a template that is not simply
 * "the children", so the designer wraps it in its own structural child. Such a wrapper carries no properties of
 * its own that belong on the closure (the Maps wrappers declare none at all; the NodeTree's Node declares only a
 * style, which travels separately as the node's box), so the wrapper is stepped over and its children become the
 * closure body — one repeater level deeper, exactly as {@link emitItemTemplateProp} does, so a row binding inside
 * the template resolves against {@link reactNativeRowVariableName} and a repeater nested in it names its own row
 * apart.
 *
 * Returns "" when the designer authored no such template, so the prop is omitted rather than emitted empty — which
 * is what lets a runtime distinguish "nothing designed" (no popup at all, as on web; a default node card) from "an
 * empty one".
 */
function emitNamedTemplateProp(
  ctx: IComponentEmitContext,
  names: ReadonlySet<string>,
  propName: string,
  usedTags: TReactNativeTag[],
  runtimeComponents: string[]
): string {
  const wrapper: IDisplayItem | undefined = (ctx.di.children ?? []).find((child: IDisplayItem): boolean => {
    const name: string | undefined = child.item.name;
    return name !== undefined && names.has(name);
  });
  if (wrapper === undefined) {
    return "";
  }

  const itemIndent: string = INDENT_STEP.repeat(ctx.depth + 2);
  const nodes: IEmittedNode[] = [];
  for (const child of wrapper.children ?? []) {
    const emitted: IEmittedNode | null = ctx.emitChild(child, ctx.depth + 2, {
      repeaterDepth: ctx.repeaterDepth + 1
    });
    if (emitted) {
      nodes.push(emitted);
      usedTags.push(...emitted.usedTags);
      runtimeComponents.push(...(emitted.runtimeComponents ?? []));
    }
  }
  if (nodes.length === 0) {
    return "";
  }

  const rowVariable: string = reactNativeRowVariableName(ctx.repeaterDepth);
  const body: string = expressionPositionBody(nodes, itemIndent);
  // The wrapper (a NodeTree `Node`, a Maps marker/popup) is stepped over — only its children are emitted — so its
  // own designer visibility would be lost with it. Hiding the wrapper hides the whole template, which is what web
  // does by not rendering the wrapper element at all.
  const wrapperGuard: string = ctx.structuralChild(wrapper).visibilityGuard();
  const guardedBody: string =
    wrapperGuard === ""
      ? body
      : [`${itemIndent}${wrapperGuard} ? (`, indentBlock(body), `${itemIndent}) : null`].join("\n");
  return [
    `${propName}={(${rowVariable}: any, rowIndex: number): React.ReactNode => (`,
    guardedBody,
    `${INDENT_STEP.repeat(ctx.depth + 1)})}`
  ].join("\n");
}

/**
 * The tri-state control properties, lowered as plain strings. Web declares them as
 * `"show" | "hide" | "user select"` and resolves the vocabulary itself, so the string is passed through verbatim
 * rather than pre-resolved to a boolean here — the runtime has to keep the raw value anyway to know which controls
 * belong in its display-settings panel (`"user select"`), which a boolean would have already thrown away.
 */
function pushMapsControlProps(props: string[], ctx: IComponentEmitContext): void {
  for (const name of [
    "fullScreenControlV1",
    "streetViewControlV1",
    "scaleControlV1",
    "zoomControlV1",
    "cameraControl",
    "hideTrafficLayer",
    "hideTransitLayer",
    "mapType"
  ]) {
    pushStringProp(props, name, ctx.getFixedPropertyValue(name));
  }
}

/**
 * The coordinate and radius properties, each preferring its designer binding over its fixed value — the same
 * bound-then-fixed order {@link pushLocationPickerCoordinateProps} uses. `latitude` / `longitude` are declared as
 * strings in web's own prop types (it `parseFloat`s them), so they lower as strings and the runtime parses.
 */
function pushMapsCoordinateProps(props: string[], ctx: IComponentEmitContext): void {
  for (const name of ["latitude", "longitude"]) {
    const bound: boolean = pushBoundValueProp(props, ctx, name, "string");
    if (!bound) {
      pushStringProp(props, name, ctx.getFixedPropertyValue(name));
    }
  }
  for (const name of ["zoom", "circleRadius", "markerDetailWidth", "markerDetailHeight"]) {
    const bound: boolean = pushBoundValueProp(props, ctx, name, "number");
    if (!bound) {
      pushNumberProp(props, name, ctx.getFixedPropertyValue(name), false);
    }
  }
  for (const name of ["markerDetailView", "markerDetailPosition"]) {
    pushNumberProp(props, name, ctx.getFixedPropertyValue(name), true);
  }
}

/**
 * Maps → the `KMaps` runtime component (`@/components/kuika`): a data-bound map whose rows are markers.
 *
 * A **data repeater** like the Table — web literally renders it in place of a `<tbody>`, one row per marker — but with
 * two named templates instead of one item template, so it lowers to a pair of closures:
 *
 * ```tsx
 * <KMaps dataSource={dsGetStores} dataLatField={"lat"} dataLngField={"lng"} value={di7} onValueChange={setDi7}
 *   renderMarker={(row: any, rowIndex: number): React.ReactNode => (
 *     <KIcon name={"map-marker"} />
 *   )}
 *   renderMarkerPopup={(row: any, rowIndex: number): React.ReactNode => (
 *     <KLabel value={String(row?.storeName ?? "")} />
 *   )}
 * />
 * ```
 *
 * Four of the 47 metadata properties are deliberately not emitted, because they are inert on web and honouring them
 * would *break* parity rather than extend it — `centerLat` / `centerLng` (their Seattle defaults ship on every
 * untouched screen, so a map that opens on `latitude`/`longitude` in a browser would open in Seattle on a device),
 * `keepCenterOnZoom`, and `showMarkerDetailViewOnClick`. See the note on `IKMapsProps.centerLat` in the runtime
 * component. `onClickDesign` / `designClicked` are designer-only affordances (`isIgnoredOnGeneratedApp`), and the
 * deprecated pre-`V1` control booleans are superseded by the tri-states above.
 */
function pushMapsFieldProps(
  props: string[],
  ctx: IComponentEmitContext,
  sourcePropertyName: string,
  latPropName: string,
  lngPropName: string
): void {
  const sourceSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === sourcePropertyName
  )?.symbol;
  pushStringProp(props, latPropName, sourceSymbol?.fieldToLatitude || ctx.getFixedPropertyValue(latPropName));
  pushStringProp(props, lngPropName, sourceSymbol?.fieldToLongitude || ctx.getFixedPropertyValue(lngPropName));
}

const emitMaps: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const stateStyleProps: string = ctx.stateStyleProps(DISABLED_BOX_SPEC);
  // Maps carries the picked point, so it binds a location value exactly as the LocationPicker does — same state kind,
  // same `ReactSystemFunctions.locationField` reads on the C# side.
  const binding: IControlledBinding = ctx.controlledBinding("location");
  const props: string[] = [`value={${binding.valueExpr}}`, `onValueChange={${binding.setterExpr}}`];

  pushDataSourceProp(props, ctx);
  pushMapsFieldProps(props, ctx, "dataSource", "dataLatField", "dataLngField");
  pushMapsCoordinateProps(props, ctx);
  pushMapsControlProps(props, ctx);
  pushBoolProp(props, "isPicker", ctx.getFixedPropertyValue("isPicker"));
  pushBoolProp(props, "hasSearchBox", ctx.getFixedPropertyValue("hasSearchBox"));
  pushBoolProp(props, "disableDoubleClickZoom", ctx.getFixedPropertyValue("disableDoubleClickZoom"));
  pushBoolProp(props, "autoCenter", ctx.getFixedPropertyValue("autoCenter"));
  pushBoolProp(props, "isMarkerPicker", ctx.getFixedPropertyValue("isMarkerPicker"));
  pushBoolProp(props, "enableLocationTracking", ctx.getFixedPropertyValue("enableLocationTracking"));
  pushStringProp(props, "groupPickerIcon", ctx.getFixedPropertyValue("groupPickerIcon"));
  pushStringProp(props, "groupPickerColor", ctx.getFixedPropertyValue("groupPickerColor"));
  pushStringProp(props, "selectedLocationIcon", ctx.getFixedPropertyValue("selectedLocationIcon"));
  pushStringProp(props, "selectedLocationColor", ctx.getFixedPropertyValue("selectedLocationColor"));
  pushStringProp(props, "currentLocationIcon", ctx.getFixedPropertyValue("currentLocationIcon"));
  pushStringProp(props, "currentLocationColor", ctx.getFixedPropertyValue("currentLocationColor"));
  pushMapsFieldProps(props, ctx, "waypoints", "waypointLatField", "waypointLngField");

  // The route source is a datasource binding like `dataSource`, not a fixed array.
  const waypointsMarker: string = ctx.getBoundValueMarker("waypoints") || ctx.getActionBoundMarker("waypoints");
  if (waypointsMarker !== "") {
    props.push(`waypoints={${waypointsMarker}}`);
  }

  const runtimeComponents: string[] = ["KMaps"];
  const usedTags: TReactNativeTag[] = [];
  const markerTemplate: string = emitNamedTemplateProp(
    ctx,
    MAPS_MARKER_NAMES,
    "renderMarker",
    usedTags,
    runtimeComponents
  );
  const popupTemplate: string = emitNamedTemplateProp(
    ctx,
    MAPS_MARKER_POPUP_NAMES,
    "renderMarkerPopup",
    usedTags,
    runtimeComponents
  );

  const disablePropStr: string = ctx.disableProp();
  const propsStr: string = props.length > 0 ? ` ${props.join(" ")}` : "";
  const opening: string = `${ctx.indent}<KMaps${ctx.testIdProp}${styleProp}${stateStyleProps}${propsStr}${disablePropStr}${ctx.actionMarker()}`;

  const templates: string[] = [markerTemplate, popupTemplate].filter((template: string): boolean => template !== "");
  if (templates.length === 0) {
    return { jsx: `${opening} />`, usedTags, runtimeComponents };
  }

  const childIndent: string = INDENT_STEP.repeat(ctx.depth + 1);
  return {
    jsx: [opening, ...templates.map((template: string): string => `${childIndent}${template}`), `${ctx.indent}/>`].join(
      "\n"
    ),
    usedTags,
    runtimeComponents
  };
};

/**
 * Emits a pager flag **only when the designer set it true**, because that is precisely what the web target
 * does with this family: `getCarouselBodyWrapperProps` guards each of them with `if (p.<flag>)`, so a `false`
 * never reaches react-slick and slick's own default stands. Emitting `flag={false}` instead would hand the RN
 * pager an instruction the web pager provably ignores — most visibly for `swipe`, whose slick default is
 * `true` while the designer's stored default is `false`.
 */
function pushTruthyBoolProp(props: string[], name: string, value: string): void {
  if (value === "true") {
    props.push(name);
  }
}

/**
 * Carousel / HorizontalGrid → the `KCarousel` runtime component (`@/components/kuika`): the **pager** data
 * repeater. Both Kuika components lower to the same component because on web they are the same one —
 * `base-table.tsx` in its `carousel` mode, under the shared tagName `KCarousel` — differing only in which
 * properties the designer exposes, so one strategy reads whichever of them the display item carries:
 *
 * ```tsx
 * <KCarousel dataSource={dsGetBanners} slidesToShow={4} autoplay autoplaySpeed={500} speed={300}
 *   renderItem={(row: any, rowIndex: number): React.ReactNode => (<KImage value={String(row?.image ?? "")} />)}
 * />
 * ```
 *
 * Item template, dataSource resolution and empty state are the same seams the other repeaters use
 * ({@link emitItemTemplateProp} / {@link pushDataSourceProp} / {@link pushListEmptyStateProps}). Two
 * properties are read but deliberately not emitted, both inert on web too: `pauseOnDotsHover` (touch has no
 * hover, and neither does the RN pager) and `className` (CSS-only).
 */
const emitCarousel: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  pushDataSourceProp(props, ctx);
  pushNumberProp(props, "slidesToShow", ctx.getFixedPropertyValue("slidesToShow"), true);
  pushNumberProp(props, "slidesToScroll", ctx.getFixedPropertyValue("slidesToScroll"), true);
  pushTruthyBoolProp(props, "vertical", ctx.getFixedPropertyValue("vertical"));
  pushTruthyBoolProp(props, "swipe", ctx.getFixedPropertyValue("swipe"));
  pushTruthyBoolProp(props, "fade", ctx.getFixedPropertyValue("fade"));
  pushTruthyBoolProp(props, "autoplay", ctx.getFixedPropertyValue("autoplay"));
  pushNumberProp(props, "autoplaySpeed", ctx.getFixedPropertyValue("autoplaySpeed"), true);
  pushNumberProp(props, "speed", ctx.getFixedPropertyValue("speed"), true);
  pushStringProp(props, "cssEase", ctx.getFixedPropertyValue("cssEase"));
  // `dots` and `showPageIndicator` are the two the web wrapper forwards even when false, so they keep the
  // ordinary emit: turning the dot strip off has to survive the trip.
  pushBoolProp(props, "dots", ctx.getFixedPropertyValue("dots"));
  pushBoolProp(props, "showPageIndicator", ctx.getFixedPropertyValue("showPageIndicator"));
  pushStringProp(props, "arrowColor", ctx.getFixedPropertyValue("arrowColor"));
  pushNumberProp(props, "startingIndex", ctx.getFixedPropertyValue("startingIndex"), true);
  pushListEmptyStateProps(props, ctx);

  // The action slot for the pager's own page-change callback (`onSlideChange`); pushed as a prop so it joins the
  // rest of the opening tag. C# strips it when the designer bound no event.
  props.push(ctx.actionMarker().trim());

  const runtimeComponents: string[] = ["KCarousel"];
  const usedTags: TReactNativeTag[] = [];
  const itemTemplate: string = emitItemTemplateProp(ctx, usedTags, runtimeComponents);

  return { jsx: listRepeaterJsx(ctx, "KCarousel", styleProp, props, itemTemplate), usedTags, runtimeComponents };
};

/** The HTML character references a JSX attribute string decodes that the designer's text fields produce. */
const JSX_NAMED_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: "\u00A0",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'"
};

/**
 * `value` as web's generated JSX attribute string (`footerOptionLabel="Total&nbsp;"`) reaches its component: JSX
 * decodes HTML character references in attribute strings, so the designer's `&nbsp;` for a leading/trailing space
 * renders as a no-break space there. The RN emitter writes the value into a JS string literal, which decodes nothing,
 * so it decodes the same references here — named ones from {@link JSX_NAMED_ENTITIES}, and numeric `&#…;` /
 * `&#x…;`. An unknown name is left as typed, as JSX leaves it.
 */
export function decodeJsxAttributeEntities(value: string): string {
  return value.replace(/&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (reference: string, body: string): string => {
    if (body.charAt(0) === "#") {
      const isHex: boolean = body.charAt(1) === "x" || body.charAt(1) === "X";
      const codePoint: number = isHex ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      const isValid: boolean = Number.isFinite(codePoint) && codePoint > 0 && codePoint <= 0x10ffff;
      return isValid ? String.fromCodePoint(codePoint) : reference;
    }
    return JSX_NAMED_ENTITIES[body] ?? reference;
  });
}

/** Footer presentation of a column with an aggregate: its label, formatter, and typography (web `renderSummary`). */
function pushColumnFooterProps(entryProps: string[], column: IStructuralChildContext): void {
  const label: string = decodeJsxAttributeEntities(column.getFixedPropertyValue("footerOptionLabel"));
  if (label !== "") {
    entryProps.push(`footerOptionLabel: ${JSON.stringify(label)}`);
  }
  const formatter: string = column.getFixedPropertyValue("footerFormatter");
  if (formatter !== "") {
    entryProps.push(`footerFormatter: ${JSON.stringify(formatter)}`);
  }
  const fontSize: number = Number.parseFloat(column.getFixedPropertyValue("footerFontSize"));
  if (Number.isFinite(fontSize)) {
    entryProps.push(`footerFontSize: ${fontSize}`);
  }
  const fontColor: string = column.getFixedPropertyValue("footerFontColor");
  if (fontColor !== "") {
    entryProps.push(`footerFontColor: ${JSON.stringify(fontColor)}`);
  }
  const fontWeight: string = column.getFixedPropertyValue("footerFontWeight");
  if (fontWeight !== "") {
    entryProps.push(`footerFontWeight: ${JSON.stringify(fontWeight)}`);
  }
  const textAlign: string = column.getFixedPropertyValue("footerTextAlign");
  if (textAlign !== "") {
    entryProps.push(`footerTextAlign: ${JSON.stringify(textAlign)}`);
  }
}

/**
 * Kuika chart component name → the KChart `chartType` it lowers to. Gauge / Radial / Mixed are deliberately
 * absent — each needs its own KChart runtime support (a gauge plugin, a level model, per-dataset types) — so a
 * screen using one emits no chart rather than a broken one until KChart carries it.
 */
const CHART_TYPE_BY_COMPONENT: Record<string, string> = {
  BarChart: "bar",
  LineChart: "line",
  AreaChart: "area",
  PieChart: "pie",
  DonutChart: "doughnut",
  RadarChart: "radar"
};

/** One of a chart's series slots, read off the dataSource symbol (the three fixed ones or an additional entry). */
interface IChartSeriesSlot {
  field: string | undefined;
  /** A literal colour (bg-colour source "fixed"), or undefined. */
  color: string | undefined;
  /** A datasource colour column (bg-colour source "column"), or undefined. */
  colorField: string | undefined;
  legend: string | undefined;
  /** solid / dashed / dotted for a line/area series, or undefined. */
  lineStyle: string | undefined;
}

/** The colour half of a series slot — a fixed colour or a colour column, never both. */
interface IChartSeriesColour {
  color: string | undefined;
  colorField: string | undefined;
}

/** Web's rule (`React_UIGenerator.BuildChartOptionProps`): only a "fixed" source type, case-insensitively, is a literal colour. */
function isFixedColourSource(sourceType: string | undefined): boolean {
  return sourceType !== undefined && sourceType.trim().toLowerCase() === "fixed";
}

/**
 * Resolves a series colour from the designer's bg-colour value and its source type the way the web target does:
 * a "fixed" source means a literal colour (→ color); anything else — "column", or no source type at all, which is
 * what the designer stores on a mobile screen where only the column picker is offered — means the value names a
 * datasource colour column (→ colorField, camelCased like every field name). Empty stays unset so KChart uses
 * its palette.
 */
function chartSeriesColour(sourceType: string | undefined, bgColor: string | undefined): IChartSeriesColour {
  if (bgColor === undefined || bgColor.trim().length === 0) {
    return { color: undefined, colorField: undefined };
  }
  if (isFixedColourSource(sourceType)) {
    return { color: bgColor, colorField: undefined };
  }
  return { color: undefined, colorField: camelCaseFieldNames(bgColor) };
}

/** The three fixed series slots plus any additional (series 4+, KOA-18057) the designer authored, in order. */
function chartSeriesSlots(symbol: ISymbol | undefined): IChartSeriesSlot[] {
  const fixed: IChartSeriesSlot[] = [
    {
      field: symbol?.chartFirstDataField,
      ...chartSeriesColour(symbol?.chartFirstDataBgColorSourceType, symbol?.chartFirstDataBgColor),
      legend: symbol?.chartFirstDataLegendLabel,
      lineStyle: symbol?.chartFirstDataLineStyle
    },
    {
      field: symbol?.chartSecondDataField,
      ...chartSeriesColour(symbol?.chartSecondDataBgColorSourceType, symbol?.chartSecondDataBgColor),
      legend: symbol?.chartSecondDataLegendLabel,
      lineStyle: symbol?.chartSecondDataLineStyle
    },
    {
      field: symbol?.chartThirdDataField,
      ...chartSeriesColour(symbol?.chartThirdDataBgColorSourceType, symbol?.chartThirdDataBgColor),
      legend: symbol?.chartThirdDataLegendLabel,
      lineStyle: symbol?.chartThirdDataLineStyle
    }
  ];
  const additional: IChartSeriesSlot[] = (symbol?.chartAdditionalSeries ?? []).map(
    (entry: ChartAdditionalSeries): IChartSeriesSlot => ({
      field: entry.dataField,
      ...chartSeriesColour(entry.bgColorSourceType, entry.bgColor),
      legend: entry.legendLabel,
      lineStyle: entry.lineStyle
    })
  );
  return [...fixed, ...additional];
}

/**
 * The `series` prop literal — one `{ valueField, fieldLabel?, color?/colorField?, legendLabel?, lineStyle? }` per
 * bound data field, in slot order then additional-series order. The field names live on the dataSource symbol
 * (`chartFirstDataField` …), camelCased so they match the JSON the backend returns, exactly as
 * {@link pushSelectFieldProps} does for a select's key/caption fields. Web's legend (and radar label boxes) show
 * the field AS TYPED (`Sales`), so that spelling rides along as `fieldLabel` whenever camelCasing changed it. An
 * unbound slot is skipped, so a one-series bar and a seven-series line both emit only what they carry.
 */
function buildChartSeriesLiteral(symbol: ISymbol | undefined): string {
  const entries: string[] = chartSeriesSlots(symbol)
    .filter((slot: IChartSeriesSlot): boolean => slot.field !== undefined && slot.field.trim().length > 0)
    .map((slot: IChartSeriesSlot): string => {
      const typedField: string = (slot.field ?? "").trim();
      const valueField: string = camelCaseFieldNames(typedField);
      const parts: string[] = [`valueField: ${JSON.stringify(valueField)}`];
      if (typedField !== valueField) {
        parts.push(`fieldLabel: ${JSON.stringify(typedField)}`);
      }
      if (slot.colorField !== undefined && slot.colorField.length > 0) {
        parts.push(`colorField: ${JSON.stringify(slot.colorField)}`);
      } else if (slot.color !== undefined && slot.color.length > 0) {
        parts.push(`color: ${JSON.stringify(slot.color)}`);
      }
      if (slot.legend !== undefined && slot.legend.length > 0) {
        parts.push(`legendLabel: ${JSON.stringify(slot.legend)}`);
      }
      if (slot.lineStyle === "dashed" || slot.lineStyle === "dotted") {
        parts.push(`lineStyle: ${JSON.stringify(slot.lineStyle)}`);
      }
      return `{ ${parts.join(", ")} }`;
    });
  return `[${entries.join(", ")}]`;
}

/**
 * The chart's presentation + styling props, each emitted only when the designer set it (web
 * `shouldIncludeProperty`), with the designer name mapping to KChart's where they diverge. The web quirks are
 * matched deliberately: the legend shows only for one of the four side positions, and the X axis is gated on
 * `rightAxis` (not `bottomAxis`). The target-line column and the radar r-scale colours round out full parity.
 */
function pushChartPresentationProps(props: string[], ctx: IComponentEmitContext, symbol: ISymbol | undefined): void {
  const legend: string = ctx.getFixedPropertyValue("legend");
  if (legend === "top" || legend === "bottom" || legend === "left" || legend === "right") {
    props.push(`legendPosition={${JSON.stringify(legend)}}`);
  } else {
    props.push("showLegend={false}");
  }

  pushBoolProp(props, "showLeftAxis", ctx.getFixedPropertyValue("leftAxis"));
  pushBoolProp(props, "showBottomAxis", ctx.getFixedPropertyValue("rightAxis"));
  pushBoolProp(props, "beginAtZero", ctx.getFixedPropertyValue("beginAtZero"));

  pushBoolProp(props, "showDataLabels", ctx.getFixedPropertyValue("showDataLabels"));
  pushStringProp(props, "dataLabelPosition", ctx.getFixedPropertyValue("dataLabelPosition"));
  pushBoolProp(props, "verticalDataLabels", ctx.getFixedPropertyValue("verticalDataLabels"));
  pushStringProp(props, "dataLabelFormatter", ctx.getFixedPropertyValue("dataLabelFormatter"));

  pushNumberProp(props, "maxYAxisValue", ctx.getFixedPropertyValue("maxYAxisValue"), false);
  pushNumberProp(props, "maxYAxisValueRatio", ctx.getFixedPropertyValue("maxYAxisValueRatio"), false);
  pushBoolProp(props, "stacked", ctx.getFixedPropertyValue("stacked"));
  pushBoolProp(props, "halfDonut", ctx.getFixedPropertyValue("halfDonut"));
  pushNumberProp(props, "barThickness", ctx.getFixedPropertyValue("barThickness"), false);

  pushStringProp(props, "lineColor", ctx.getFixedPropertyValue("lineColor"));
  pushNumberProp(props, "lineThickness", ctx.getFixedPropertyValue("lineThickness"), false);
  pushNumberProp(props, "lineDotWidth", ctx.getFixedPropertyValue("lineDotWidth"), false);

  // Tooltip content: web shows the title column on the hovered row, an optional footer column, and an
  // optional multi-field body — all three from the datasource symbol, not from a designer property.
  const tooltipTitle: string = symbol?.chartDataLabel ?? "";
  if (tooltipTitle.trim().length > 0) {
    props.push(`tooltipTitleField={${JSON.stringify(camelCaseFieldNames(tooltipTitle))}}`);
  }
  const tooltipFooter: string = symbol?.chartTooltipFooterDataLabel ?? "";
  if (tooltipFooter.trim().length > 0) {
    props.push(`tooltipFooterField={${JSON.stringify(camelCaseFieldNames(tooltipFooter))}}`);
  }
  // Emitted verbatim: the spec's "column:Label" halves are split in the runtime, which camel-cases only
  // the column — the label is free text the designer typed.
  const multiTooltip: string = symbol?.chartMultiTooltipContentField ?? "";
  if (multiTooltip.trim().length > 0) {
    props.push(`multiTooltipContentField={${JSON.stringify(multiTooltip)}}`);
  }

  const targetField: string = symbol?.chartTargetLineField ?? "";
  if (targetField.trim().length > 0) {
    props.push(`targetLineField={${JSON.stringify(camelCaseFieldNames(targetField))}}`);
  }
  pushBoolProp(props, "showLineLabel", ctx.getFixedPropertyValue("showLineLabel"));
  pushStringProp(props, "lineLabelColor", ctx.getFixedPropertyValue("lineLabelColor"));
  pushStringProp(props, "lineLabelPosition", ctx.getFixedPropertyValue("lineLabelPosition"));

  pushStringProp(props, "levelColor", ctx.getFixedPropertyValue("levelColor"));
  pushNumberProp(props, "levelFrequency", ctx.getFixedPropertyValue("levelFrequency"), false);
  pushBoolProp(props, "showLevelValueBoxes", ctx.getFixedPropertyValue("showLevelValueBoxes"));
  pushBoolProp(props, "showLabelBoxes", ctx.getFixedPropertyValue("showLabelBoxes"));
  pushStringProp(props, "angleLinesColor", ctx.getFixedPropertyValue("angleLinesColor"));
  pushStringProp(props, "pointLabelColor", ctx.getFixedPropertyValue("pointLabelColor"));
}

/**
 * BarChart / LineChart / AreaChart / PieChart / DonutChart → the one `KChart` runtime component, which draws
 * chart.js in an offline WebView (the platform's mobile chart strategy). The chart kind comes from the
 * component name; the datasource and its field bindings (category label + up to three series) come off the
 * dataSource symbol the same way the data repeaters and the select family read theirs. Presentation and
 * styling props (legend, axes, data labels + formatter, bar width, line styling, target line, radar r-scale)
 * are emitted by {@link pushChartPresentationProps} when the designer sets them.
 *
 * ```tsx
 * <KChart chartType={"bar"} dataSource={dsGetSales} labelField={"month"}
 *   series={[{ valueField: "sales", legendLabel: "Sales" }, { valueField: "profit" }]} />
 * ```
 */
/**
 * Chat → the `KChat` runtime component (`@/components/kuika`): a message list with a composer.
 *
 * Data-bound like a chart rather than a repeater — the bubble template is the component's, not the designer's — so
 * this emits `dataSource` plus the six field mappings that say which key of a record holds the text, the sender, the
 * avatar and the date. `currentUsername` is the record the bubbles are measured against: a record whose
 * `chatUserNameField` equals it is drawn as sent.
 *
 * The composer is a CONTROLLED input on the screen's own `di<id>` string state, seeded C#-side from the component's
 * `message` property (not `value` — Chat names its draft `message`, the same way Checkbox names its state
 * `checked`). `onMessageSend` lands at the `kuikaid` marker through `ReactNativeEventCatalog`, and its argument is
 * the component's value, so a designer action reading `Chat1.message` inside that handler sees the message being
 * sent rather than the cleared draft — which is a race on web, where the callback takes the text but the generated
 * handler is emitted with no parameters.
 *
 * **Five of the component's properties are deliberately absent**: `allowEditMessage`, `allowDeleteMessage`,
 * `onEditMessage`, `onDeleteMessage` and `messageIdToBeDeleted` are all `appSupportType: Web` in the registry, so the
 * designer cannot set them on a mobile screen and emitting them would be props no palette can reach.
 *
 * ```tsx
 * <KChat dataSource={dsGetMessages} chatMessageField={"text"} chatUserNameField={"sender"}
 *        currentUsername={String(ReactSystemFunctions.getMyUsername() ?? "")} message={di7}
 *        onMessageChange={setDi7} kuikaid='7' />
 * ```
 */
const emitChat: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  pushDataSourceProp(props, ctx);
  for (const fieldProperty of CHAT_FIELD_PROPERTIES) {
    pushStringProp(props, fieldProperty, ctx.getFixedPropertyValue(fieldProperty));
  }

  // Who "I" am is normally bound (to the signed-in user), so the bound read wins over any fixed value.
  const currentUsernameMarker: string = ctx.getBoundValueMarker("currentUsername");
  if (currentUsernameMarker !== "") {
    props.push(`currentUsername={String(${currentUsernameMarker} ?? "")}`);
  } else {
    pushStringProp(props, "currentUsername", ctx.getFixedPropertyValue("currentUsername"));
  }

  const draftBinding: IControlledBinding = ctx.controlledBinding("string");
  props.push(`message={${draftBinding.valueExpr}}`);
  props.push(`onMessageChange={${draftBinding.setterExpr}}`);

  // Editing / deleting one's own messages. The affordance is the designer's to allow, and when neither is allowed
  // the picked-message state below is pointless — so it is emitted only when one of them is, and the C# leg declares
  // the state by scanning for that very reference (the collapseKeys_di<id> pattern), which is what keeps the two
  // legs from drifting.
  const allowEdit: boolean = ctx.getFixedPropertyValue("allowEditMessage") === "true";
  const allowDelete: boolean = ctx.getFixedPropertyValue("allowDeleteMessage") === "true";
  pushBoolProp(props, "allowEditMessage", ctx.getFixedPropertyValue("allowEditMessage"));
  pushBoolProp(props, "allowDeleteMessage", ctx.getFixedPropertyValue("allowDeleteMessage"));
  if (allowEdit || allowDelete) {
    const selectedName: string = `selectedMessageId_di${diToken(ctx.di.id)}`;
    props.push(`selectedMessageId={${selectedName}}`);
    props.push(`onSelectedMessageIdChange={set${selectedName.charAt(0).toUpperCase()}${selectedName.slice(1)}}`);
  }

  pushBoolProp(props, "darkMode", ctx.getFixedPropertyValue("darkMode"));
  pushBoolProp(props, "showProfilePhoto", ctx.getFixedPropertyValue("showProfilePhoto"));
  pushBoolProp(props, "showUserName", ctx.getFixedPropertyValue("showUserName"));
  pushBoolProp(props, "speechToText", ctx.getFixedPropertyValue("speechToText"));

  const jsx: string = `${ctx.indent}<KChat${ctx.testIdProp}${styleProp} ${props.join(" ")}${ctx.actionMarker()} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KChat"] };
};

/** The Chat's field-mapping properties, in the order the runtime declares them. */
const CHAT_FIELD_PROPERTIES: readonly string[] = [
  "chatIdField",
  "chatMessageField",
  "chatUserDisplayNameField",
  "chatUserNameField",
  "chatUserAvatarField",
  "chatDateField"
];

/**
 * The chart typography the web base chart reads off the component's own style rather than off a property:
 * the legend font size (`style.fontSize ?? 12`), the data-label colour (`style.color ?? "black"`) and font
 * (`style.fontSize ?? 14`, `style.fontWeight ?? "bold"`, `style.fontFamily ?? "Arial"`), and the legend
 * alignment, which web derives from `style.textAlign`. The RN runtime takes them as props because a canvas
 * inside a WebView cannot inherit a StyleSheet entry.
 */
function pushChartTypographyProps(props: string[], ctx: IComponentEmitContext): void {
  const textStyle: Record<string, string | number> = ctx.partitioned.textStyle;
  const fontSize: string | number | undefined = textStyle.fontSize;
  if (typeof fontSize === "number") {
    props.push(`legendFontSize={${fontSize}}`);
    props.push(`dataLabelFontSize={${fontSize}}`);
  }
  const color: string | number | undefined = textStyle.color;
  if (typeof color === "string" && color.length > 0) {
    props.push(`dataLabelColor={${JSON.stringify(color)}}`);
  }
  const fontWeight: string | number | undefined = textStyle.fontWeight;
  if (typeof fontWeight === "string" && fontWeight.length > 0) {
    props.push(`dataLabelFontWeight={${JSON.stringify(fontWeight)}}`);
  }
  const fontFamily: string | number | undefined = textStyle.fontFamily;
  if (typeof fontFamily === "string" && fontFamily.length > 0) {
    props.push(`dataLabelFontFamily={${JSON.stringify(fontFamily)}}`);
  }
  if (ctx.explicitTextAlign !== null) {
    props.push(`legendAlign={${JSON.stringify(ctx.explicitTextAlign)}}`);
  }
}

function resolveExplicitTextAlignValue(value: unknown): TChartLegendAlign {
  if (value === "-webkit-left" || value === "-moz-left") {
    return "start";
  }
  if (value === "-webkit-right" || value === "-moz-right") {
    return "end";
  }
  return null;
}

/**
 * The `ChartLabel` children of a pie/doughnut/radial, lowered to the `centerLabels` literal the runtime paints
 * in the middle of the hole. Mirrors the web charts' reading of the same children: the text comes from the
 * label's value prop (bound or fixed) and the typography from its style, with web's fallbacks — Arial, weight
 * "medium", colour black, and a default size that is 14 on a doughnut but 30 on a radial.
 */
function buildCenterLabelLiterals(ctx: IComponentEmitContext, defaultFontSize: number): string[] {
  const literals: string[] = [];
  for (const childDi of ctx.di.children ?? []) {
    if (childDi.item?.name !== "ChartLabel") {
      continue;
    }
    const child: IStructuralChildContext = ctx.structuralChild(childDi);
    const bound: string = child.getBoundValueMarker("text");
    const text: string = bound.length > 0 ? bound : JSON.stringify(child.getFixedPropertyValue("text"));
    const style: Record<string, string | number> = child.resolveTextStyle();
    const fontSize: number = typeof style.fontSize === "number" ? style.fontSize : defaultFontSize;
    const fontWeight: string = typeof style.fontWeight === "string" ? style.fontWeight : "medium";
    const fontFamily: string = typeof style.fontFamily === "string" ? style.fontFamily : "Arial";
    const color: string = typeof style.color === "string" ? style.color : "black";
    literals.push(
      `{ text: ${text}, fontSize: ${fontSize}, fontWeight: ${JSON.stringify(fontWeight)}, fontFamily: ${JSON.stringify(
        fontFamily
      )}, color: ${JSON.stringify(color)} }`
    );
  }
  return literals;
}

const emitChart: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  const chartType: string = CHART_TYPE_BY_COMPONENT[ctx.componentName] ?? "bar";
  props.push(`chartType={${JSON.stringify(chartType)}}`);

  pushDataSourceProp(props, ctx);

  const dataSourceSymbol: ISymbol | undefined = ctx.di.item.properties?.find(
    (property: IDisplayItemProperty): boolean => property.name === "dataSource"
  )?.symbol;

  // The category axis reads `chartDataDisplayLabel` — web's canonical label role (`dataDisplayLabelKeyName`),
  // the same field Android/iOS use for the axis; `chartDataLabel` is the tooltip title, not the axis.
  props.push(`labelField={${JSON.stringify(camelCaseFieldNames(dataSourceSymbol?.chartDataDisplayLabel ?? ""))}}`);
  props.push(`series={${buildChartSeriesLiteral(dataSourceSymbol)}}`);

  pushChartPresentationProps(props, ctx, dataSourceSymbol);
  pushChartTypographyProps(props, ctx);

  const centerLabels: string[] = buildCenterLabelLiterals(ctx, 14);
  if (centerLabels.length > 0) {
    props.push(`centerLabels={[${centerLabels.join(", ")}]}`);
  }

  const jsx: string = `${ctx.indent}<KChart${ctx.testIdProp}${styleProp} ${props.join(" ")} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KChart"] };
};

/**
 * Parses the designer's gauge `colors` value into a colour list, mirroring the backend `GetColors` cleanup so
 * the two targets read the same stored string identically: strip the array brackets/quotes, split elements on
 * the `","` boundary (and any newline), drop spaces, and keep `rgba(…)` commas intact. Culture-invariant.
 */
function parseChartColors(raw: string): string[] {
  if (raw.trim().length === 0) {
    return [];
  }
  const cleaned: string = raw
    .replace(/\[/g, "")
    .replace(/\]/g, "")
    .replace(/'/g, "")
    .replace(/","/g, "-")
    .replace(/\n/g, "-")
    .replace(/"/g, "")
    .replace(/ /g, "")
    .replace(/\),/g, ")");
  return cleaned.split("-").filter((color: string): boolean => color.length > 0);
}

/**
 * The gauge's centre "N%" typography, which web paints from the component's own style (`gauge-chart.tsx`
 * `getGaugePercentagePlugin`: colour, font style/weight/family, and a font size that otherwise scales with the
 * chart). Each is emitted only when the designer set it, so the runtime keeps web's defaults for the rest.
 */
function pushGaugeTypographyProps(props: string[], ctx: IComponentEmitContext): void {
  const textStyle: Record<string, string | number> = ctx.partitioned.textStyle;
  const color: string | number | undefined = textStyle.color;
  if (typeof color === "string" && color.length > 0) {
    props.push(`textColor={${JSON.stringify(color)}}`);
  }
  const fontFamily: string | number | undefined = textStyle.fontFamily;
  if (typeof fontFamily === "string" && fontFamily.length > 0) {
    props.push(`fontFamily={${JSON.stringify(fontFamily)}}`);
  }
  const fontWeight: string | number | undefined = textStyle.fontWeight;
  if (typeof fontWeight === "string" && fontWeight.length > 0) {
    props.push(`fontWeight={${JSON.stringify(fontWeight)}}`);
  }
  const fontStyle: string | number | undefined = textStyle.fontStyle;
  if (typeof fontStyle === "string" && fontStyle.length > 0) {
    props.push(`fontStyle={${JSON.stringify(fontStyle)}}`);
  }
  const fontSize: string | number | undefined = textStyle.fontSize;
  if (typeof fontSize === "number") {
    props.push(`fontSize={${fontSize}}`);
  }
}

/**
 * GaugeChart → the `KGaugeChart` runtime component, a semicircle doughnut (web `GaugeChart`) drawn in the same
 * offline WebView as KChart. Unlike the datasource charts, a gauge binds a scalar `value` against a `max`
 * (default 100 in the runtime) with a two-colour `colors` array (filled arc, rest); `value` and `max` may each
 * be data-bound, as web emits either binding.
 *
 * ```tsx
 * <KGaugeChart value={Number(dsGetKpi ?? 0)} max={100} colors={["#0d6efd", "#e9ecef"]} />
 * ```
 */
const emitGauge: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];

  const valueBound: boolean = pushBoundValueProp(props, ctx, "value", "number");
  if (!valueBound) {
    pushNumberProp(props, "value", ctx.getFixedPropertyValue("value"), false);
  }
  const maxBound: boolean = pushBoundValueProp(props, ctx, "max", "number");
  if (!maxBound) {
    pushNumberProp(props, "max", ctx.getFixedPropertyValue("max"), false);
  }

  const colors: string[] = parseChartColors(ctx.getFixedPropertyValue("colors"));
  if (colors.length > 0) {
    props.push(`colors={${JSON.stringify(colors)}}`);
  }
  pushBoolProp(props, "hidePercentage", ctx.getFixedPropertyValue("hidePercentage"));
  pushGaugeTypographyProps(props, ctx);

  const jsx: string = `${ctx.indent}<KGaugeChart${ctx.testIdProp}${styleProp} ${props.join(" ")} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KGaugeChart"] };
};

/** Resolves a named datasource property to its runtime expression (bound marker, action marker, or a fixed array). */
function resolveDataSourceExpr(ctx: IComponentEmitContext, propertyName: string): string {
  const marker: string = ctx.getBoundValueMarker(propertyName) || ctx.getActionBoundMarker(propertyName);
  if (marker !== "") {
    return marker;
  }
  const fixed: string = ctx.getFixedPropertyValue(propertyName).trim();
  return fixed.startsWith("[") ? fixed : "";
}

/** Emits a radial field-name attribute (a camelCased datasource column) only when the designer bound it. */
function pushRadialField(props: string[], attribute: string, value: string | undefined): void {
  if (value !== undefined && value.trim().length > 0) {
    props.push(`${attribute}={${JSON.stringify(camelCaseFieldNames(value))}}`);
  }
}

/**
 * RadialChart → the `KRadialChart` runtime component, a two-canvas gradient doughnut (web `RadialChart` +
 * `GradientDoughnut`) drawn in the offline WebView. Radial binds TWO datasources — the slices (the property
 * whose symbol carries `RadialChartOptions`) and the level rings (`RadialChartLevelOptions`) — each with its own
 * field map, plus the line/level styling. The datasource props are found by their symbol so the designer's
 * property names need not be hard-coded.
 */
const emitRadial: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const properties: IDisplayItemProperty[] = ctx.di.item.properties ?? [];

  const slicesProp: IDisplayItemProperty | undefined = properties.find(
    (property: IDisplayItemProperty): boolean => property.symbol?.RadialChartOptions !== undefined
  );
  if (slicesProp !== undefined) {
    const optionsExpr: string = resolveDataSourceExpr(ctx, slicesProp.name);
    if (optionsExpr !== "") {
      props.push(`options={${optionsExpr}}`);
    }
    const sliceFields: RadialChartOptions | undefined = slicesProp.symbol?.RadialChartOptions;
    pushRadialField(props, "dataslicelabelfield", sliceFields?.dataslicelabelfield);
    pushRadialField(props, "dataslicetextfield", sliceFields?.dataslicetextfield);
    pushRadialField(props, "dataslicepercentagefield", sliceFields?.dataslicepercentagefield);
    pushRadialField(props, "dataslicepointfield", sliceFields?.dataslicepointfield);
  }

  const levelsProp: IDisplayItemProperty | undefined = properties.find(
    (property: IDisplayItemProperty): boolean => property.symbol?.RadialChartLevelOptions !== undefined
  );
  if (levelsProp !== undefined) {
    const levelsExpr: string = resolveDataSourceExpr(ctx, levelsProp.name);
    if (levelsExpr !== "") {
      props.push(`levels={${levelsExpr}}`);
    }
    const levelFields: RadialChartLevelOptions | undefined = levelsProp.symbol?.RadialChartLevelOptions;
    pushRadialField(props, "datalevelpercentagefield", levelFields?.datalevelpercentagefield);
    pushRadialField(props, "datalevelcolorfield", levelFields?.datalevelcolorfield);
    pushRadialField(props, "dataleveltextfield", levelFields?.dataleveltextfield);
  }

  pushStringProp(props, "lineColor", ctx.getFixedPropertyValue("lineColor"));
  pushStringProp(props, "radialLineColor", ctx.getFixedPropertyValue("radialLineColor"));
  pushNumberProp(props, "lineWidth", ctx.getFixedPropertyValue("lineWidth"), false);
  pushStringProp(props, "backgroundColor", ctx.getFixedPropertyValue("backgroundColor"));
  pushStringProp(props, "legendTextColor", ctx.getFixedPropertyValue("legendTextColor"));
  const centerLabels: string[] = buildCenterLabelLiterals(ctx, 30);
  if (centerLabels.length > 0) {
    props.push(`centerLabels={[${centerLabels.join(", ")}]}`);
  }

  const jsx: string = `${ctx.indent}<KRadialChart${ctx.testIdProp}${styleProp} ${props.join(" ")} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KRadialChart"] };
};

/** Resolves a child's named datasource to its runtime expression (bound/action marker, or a fixed array). */
function resolveChildDataSourceExpr(child: IStructuralChildContext, propertyName: string): string {
  const marker: string = child.getBoundValueMarker(propertyName) || child.getActionBoundMarker(propertyName);
  if (marker !== "") {
    return marker;
  }
  const fixed: string = child.getFixedPropertyValue(propertyName).trim();
  return fixed.startsWith("[") ? fixed : "";
}

/** Adds `key: "<camelCased field>"` to an object literal's parts when the field is set. */
function pushLiteralField(parts: string[], key: string, value: string | undefined): void {
  if (value !== undefined && value.trim().length > 0) {
    parts.push(`${key}: ${JSON.stringify(camelCaseFieldNames(value))}`);
  }
}

/** Adds `key: "<value>"` to an object literal's parts when the value is set (a literal, not camelCased). */
function pushLiteralString(parts: string[], key: string, value: string): void {
  if (value !== "") {
    parts.push(`${key}: ${JSON.stringify(value)}`);
  }
}

/**
 * The object-literal counterpart of {@link pushTextProp} — `label: "Save"` or `label: translate(…)`.
 *
 * <p>Used where a component's children are lowered to a data array rather than to JSX (a DropDown's items, a
 * ContentMenu's entries): the text still has to follow the active language, and an entry built with
 * `JSON.stringify` would freeze the designer's literal into the array.</p>
 */
function pushTextLiteralEntry(parts: string[], ctx: ITextPropertySource, key: string, propName: string): void {
  const expr: string = textPropExpression(ctx, propName);
  if (expr !== "") {
    parts.push(`${key}: ${expr}`);
  }
}

/** Adds `key: <n>` to an object literal's parts when the value parses to a finite number. */
function pushLiteralNumber(parts: string[], key: string, value: string): void {
  if (value === "") {
    return;
  }
  const numeric: number = Number(value);
  if (Number.isFinite(numeric)) {
    parts.push(`${key}: ${numeric}`);
  }
}

/** One `{ options, dataxaxisfield, … }` line-series literal read off a MixedChartLineItem child. */
function buildMixedLineLiteral(child: IStructuralChildContext, optionsProp: IDisplayItemProperty): string {
  const parts: string[] = [];
  const optionsExpr: string = resolveChildDataSourceExpr(child, optionsProp.name);
  if (optionsExpr !== "") {
    parts.push(`options: ${optionsExpr}`);
  }
  const fields: MixedChartLineOptions | undefined = optionsProp.symbol?.MixedChartLineOptions;
  pushLiteralField(parts, "dataxaxisfield", fields?.dataxaxisfield);
  pushLiteralField(parts, "dataxaxisvaluefield", fields?.dataxaxisvaluefield);
  pushLiteralField(parts, "dataxaxistooltipfield", fields?.dataxaxistooltipfield);
  pushLiteralField(parts, "datavaluetooltipfield", fields?.datavaluetooltipfield);
  pushTextLiteralEntry(parts, child, "label", "label");
  pushLiteralString(parts, "color", child.getFixedPropertyValue("color"));
  pushLiteralNumber(parts, "dashLength", child.getFixedPropertyValue("dashLength"));
  pushLiteralNumber(parts, "dashSpacing", child.getFixedPropertyValue("dashSpacing"));
  pushLiteralNumber(parts, "lineWidth", child.getFixedPropertyValue("lineWidth"));
  return `{ ${parts.join(", ")} }`;
}

/** One `{ options, dataxaxisfield, datavaluefield, color }` point-series literal read off a MixedChartPointItem child. */
function buildMixedPointLiteral(child: IStructuralChildContext, optionsProp: IDisplayItemProperty): string {
  const parts: string[] = [];
  const optionsExpr: string = resolveChildDataSourceExpr(child, optionsProp.name);
  if (optionsExpr !== "") {
    parts.push(`options: ${optionsExpr}`);
  }
  const fields: MixedChartPointOptions | undefined = optionsProp.symbol?.MixedChartPointOptions;
  pushLiteralField(parts, "dataxaxisfield", fields?.dataxaxisfield);
  pushLiteralField(parts, "datavaluefield", fields?.datavaluefield);
  pushLiteralField(parts, "dataxaxistooltipfield", fields?.dataxaxistooltipfield);
  pushLiteralField(parts, "datavaluetooltipfield", fields?.datavaluetooltipfield);
  pushLiteralString(parts, "color", child.getFixedPropertyValue("color"));
  return `{ ${parts.join(", ")} }`;
}

/**
 * MixedChart → the `KMixedChart` runtime component, line + scatter series on a shared axis. The web renders this
 * with BizCharts and its series are child components (MixedChartLineItem / MixedChartPointItem); RN folds each
 * child into one `lines`/`points` entry — the child's own datasource (found by its `MixedChart*Options` symbol)
 * plus its field map and styling — so a single KMixedChart carries every series. The parent x-axis is the
 * property whose symbol carries `MixedChartOptions`.
 */
/**
 * A MixedChart `Bar` child → one entry of the runtime's `bars` array. Same shape as the line/point literals:
 * the child's own datasource, its x/value columns, and the label and colour the designer set.
 */
function buildMixedBarLiteral(child: IStructuralChildContext, optionsProp: IDisplayItemProperty): string {
  const parts: string[] = [];
  const optionsExpr: string = resolveChildDataSourceExpr(child, optionsProp.name);
  if (optionsExpr !== "") {
    parts.push(`options: ${optionsExpr}`);
  }
  const fields: MixedChartBarOptions | undefined = optionsProp.symbol?.MixedChartBarOptions;
  pushLiteralField(parts, "dataxaxisfield", fields?.dataxaxisfield);
  pushLiteralField(parts, "datavaluefield", fields?.datavaluefield);
  pushLiteralField(parts, "dataxaxistooltipfield", fields?.dataxaxistooltipfield);
  pushLiteralField(parts, "datavaluetooltipfield", fields?.datavaluetooltipfield);
  pushTextLiteralEntry(parts, child, "label", "label");
  pushLiteralString(parts, "color", child.getFixedPropertyValue("color"));
  return `{ ${parts.join(", ")} }`;
}

const emitMixed: TComponentStrategy = (ctx: IComponentEmitContext): IEmittedNode => {
  const styleProp: string = ctx.registerStyle(ctx.styleName, ctx.partitioned.viewStyle);
  const props: string[] = [];
  const properties: IDisplayItemProperty[] = ctx.di.item.properties ?? [];

  const parentProp: IDisplayItemProperty | undefined = properties.find(
    (property: IDisplayItemProperty): boolean => property.symbol?.MixedChartOptions !== undefined
  );
  if (parentProp !== undefined) {
    const optionsExpr: string = resolveDataSourceExpr(ctx, parentProp.name);
    if (optionsExpr !== "") {
      props.push(`options={${optionsExpr}}`);
    }
    const parentFields: MixedChartOptions | undefined = parentProp.symbol?.MixedChartOptions;
    pushRadialField(props, "dataxaxisesfield", parentFields?.dataxaxisesfield);
  }

  const lineLiterals: string[] = [];
  const pointLiterals: string[] = [];
  const barLiterals: string[] = [];
  for (const childDi of ctx.di.children ?? []) {
    const childProperties: IDisplayItemProperty[] = childDi.item.properties ?? [];
    const lineProp: IDisplayItemProperty | undefined = childProperties.find(
      (property: IDisplayItemProperty): boolean => property.symbol?.MixedChartLineOptions !== undefined
    );
    const pointProp: IDisplayItemProperty | undefined = childProperties.find(
      (property: IDisplayItemProperty): boolean => property.symbol?.MixedChartPointOptions !== undefined
    );
    const barProp: IDisplayItemProperty | undefined = childProperties.find(
      (property: IDisplayItemProperty): boolean => property.symbol?.MixedChartBarOptions !== undefined
    );
    // A series is a literal inside `lines={[…]}`, not a node, so it never meets the emitNode visibility wrap —
    // a hidden line/point/bar series is dropped from the array instead.
    const child: IStructuralChildContext = ctx.structuralChild(childDi);
    const guard: string = child.visibilityGuard();
    if (lineProp !== undefined) {
      lineLiterals.push(guardedArrayEntry(buildMixedLineLiteral(child, lineProp), guard, ""));
    } else if (pointProp !== undefined) {
      pointLiterals.push(guardedArrayEntry(buildMixedPointLiteral(child, pointProp), guard, ""));
    } else if (barProp !== undefined) {
      barLiterals.push(guardedArrayEntry(buildMixedBarLiteral(child, barProp), guard, ""));
    }
  }
  if (lineLiterals.length > 0) {
    props.push(`lines={[${lineLiterals.join(", ")}]}`);
  }
  if (pointLiterals.length > 0) {
    props.push(`points={[${pointLiterals.join(", ")}]}`);
  }
  if (barLiterals.length > 0) {
    props.push(`bars={[${barLiterals.join(", ")}]}`);
  }

  const jsx: string = `${ctx.indent}<KMixedChart${ctx.testIdProp}${styleProp} ${props.join(" ")} />`;
  return { jsx, usedTags: [], runtimeComponents: ["KMixedChart"] };
};

/**
 * RN tag → the strategy that emits it. Keyed by `TReactNativeTag` (the resolved RN tag): the input
 * sub-variants (Email/Password/NumberInput/TextInput) all resolve to the "TextInput" tag and share
 * one strategy; Button resolves to the "Pressable" tag. Tags absent here (View, ScrollView) are
 * emitted directly in `emitNode`: containers map to built-in RN primitives with no runtime component
 * file, and ScrollView is the overflow:scroll variant — neither needs a separate strategy.
 */
const RN_COMPONENT_STRATEGIES: Map<TReactNativeTag, TComponentStrategy> = new Map<TReactNativeTag, TComponentStrategy>([
  ["Badge", emitBadge],
  ["Breadcrumb", emitBreadcrumb],
  ["Carousel", emitCarousel],
  ["Chart", emitChart],
  ["GaugeChart", emitGauge],
  ["RadialChart", emitRadial],
  ["MixedChart", emitMixed],
  ["Checkbox", emitCheckbox],
  ["Collapse", emitCollapse],
  ["CollapsePanel", emitCollapsePanel],
  ["Countdown", emitCountdown],
  ["CountUp", emitCountUp],
  ["Stopwatch", emitStopwatch],
  ["ContentMenu", emitContentMenu],
  ["ContentMenuItem", emitContentMenuItem],
  ["ContentMenuPanel", emitContentMenuPanel],
  ["DatePicker", emitDatePicker],
  ["DateRange", emitDateRange],
  ["Chat", emitChat],
  ["Divider", emitDivider],
  ["Drawer", emitDrawer],
  ["FlexGrid", emitFlexGrid],
  ["FloatButton", emitFloatButton],
  ["FloatPanel", emitFloatPanel],
  ["Gallery", emitGallery],
  ["Icon", emitIcon],
  ["Image", emitImage],
  ["Thumbnail", emitThumbnail],
  ["Timeline", emitTimeline],
  ["Tree", emitTree],
  ["WebView", emitWebView],
  ["VideoPlayer", emitVideoPlayer],
  ["Markdown", emitMarkdown],
  ["Menu", emitMenu],
  ["MenuItem", emitMenuItem],
  ["SubMenu", emitSubMenu],
  ["MicroApp", emitMicroApp],
  ["Modal", emitModal],
  ["Pagination", emitPagination],
  ["LanguageSelector", emitLanguageSelector],
  ["Calendar", emitCalendar],
  ["BigFileUpload", emitBigFileUpload],
  ["NodeTree", emitNodeTree],
  ["PivotTable", emitPivotTable],
  ["Stepper", emitStepper],
  ["StepperItem", emitStepperItem],
  ["RadioButtonGroup", emitRadioButtonGroup],
  ["RadioButton", emitRadioButton],
  ["Marquee", emitMarquee],
  ["Panel", emitPanel],
  ["Pressable", emitButton],
  ["ProgressBar", emitProgressBar],
  ["ProgressCircle", emitProgressCircle],
  ["QRRenderer", emitQRRenderer],
  ["BarcodeRenderer", emitBarcodeRenderer],
  ["Signature", emitSignature],
  ["ColorPicker", emitColorPicker],
  ["MDEditor", emitMDEditor],
  ["Recaptcha", emitRecaptcha],
  ["PaInteractionButtons", emitPaInteractionButtons],
  ["ProcessAutomation", emitProcessAutomation],
  ["LocationPicker", emitLocationPicker],
  ["LocationSearch", emitLocationSearch],
  ["Maps", emitMaps],
  ["RadioGroup", emitRadioGroup],
  ["Rating", emitRating],
  ["Slider", emitSlider],
  ["RangeSlider", emitRangeSlider],
  ["Select", emitSelect],
  ["Spacer", emitSpacer],
  ["Switch", emitSwitch],
  ["Tab", emitTab],
  ["TabItem", emitTabItem],
  ["Table", emitTable],
  ["Text", emitLabel],
  ["TextInput", emitTextInput],
  ["Tooltip", emitTooltip],
  ["YearPicker", emitYearPicker]
]);

/**
 * Component names the web emitter excludes from its absolute-design `width: "100%"` rule
 * (ServerSideElementGenerator.buildProps) — tables and their parts, dividers, and page breaks size
 * themselves. Kept in sync so the RN target applies the same width parity.
 */
const ABSOLUTE_DESIGN_WIDTH_EXCLUDED: ReadonlySet<string> = new Set<string>([
  "TableColumn",
  "TableRow",
  "TableHeader",
  "ReportTableColumn",
  "ReportTableRow",
  "ReportTableHeader",
  "StaticTable",
  "ReportTable",
  "StaticTableColumn",
  "StaticTableRow",
  "StaticTableHeader",
  "Page Break",
  "Divider"
]);

/**
 * Maps Kuika component identifiers to their RN counterpart. Screen designs carry the
 * component registry *name* on `di.item.name` ("Row"/"Col"); the registry tagName
 * ("KRow"/"KCol") is also accepted so both contracts resolve without a registry lookup.
 */
const KUIKA_TAG_TO_REACT_NATIVE: Record<string, IComponentMapping> = {
  // Row and Col are the two containers the designer lets you put an onClick on (0108_Row.json / 0114_Col.json);
  // the stacks and the header/footer boxes below carry no event surface, so they stay plain Views.
  Row: { tag: "Container", baseStyle: { flexDirection: "row", flexWrap: "wrap" } },
  Col: { tag: "Container", baseStyle: { flex: 1 } },
  KRow: { tag: "Container", baseStyle: { flexDirection: "row", flexWrap: "wrap" } },
  KCol: { tag: "Container", baseStyle: { flex: 1 } },
  HorizontalStack: { tag: "View", baseStyle: { flexDirection: "row" }, gapProperty: "size" },
  VerticalStack: { tag: "View", baseStyle: { width: "100%" }, gapProperty: "size" },
  Panel: { tag: "Panel" },
  "Vertical Panel": { tag: "Panel" },
  KPanel: { tag: "Panel" },
  // The table-driven Form (0148_Form.json) registers the KPanel tagName and web renders it as a plain KPanel around
  // its generated field rows — unmapped it was skipped WITH every field inside it (KOA-18676).
  Form: { tag: "Panel" },
  Modal: { tag: "Modal" },
  KModal: { tag: "Modal" },
  Tab: { tag: "Tab" },
  KTab: { tag: "Tab" },
  TabItem: { tag: "TabItem" },
  KTabItem: { tag: "TabItem" },
  Table: { tag: "Table" },
  KTable: { tag: "Table" },
  GalleryView: { tag: "Gallery" },
  KGallery: { tag: "Gallery" },
  FlexGrid: { tag: "FlexGrid" },
  KFlexGrid: { tag: "FlexGrid" },
  Carousel: { tag: "Carousel" },
  HorizontalGrid: { tag: "Carousel" },
  KCarousel: { tag: "Carousel" },
  Chat: { tag: "Chat" },
  KChat: { tag: "Chat" },
  BarChart: { tag: "Chart" },
  KBarChart: { tag: "Chart" },
  LineChart: { tag: "Chart" },
  KLineChart: { tag: "Chart" },
  AreaChart: { tag: "Chart" },
  KAreaChart: { tag: "Chart" },
  PieChart: { tag: "Chart" },
  KPieChart: { tag: "Chart" },
  DonutChart: { tag: "Chart" },
  KDonutChart: { tag: "Chart" },
  RadarChart: { tag: "Chart" },
  KRadarChart: { tag: "Chart" },
  GaugeChart: { tag: "GaugeChart" },
  KGaugeChart: { tag: "GaugeChart" },
  RadialChart: { tag: "RadialChart" },
  KRadialChart: { tag: "RadialChart" },
  MixedChart: { tag: "MixedChart" },
  KMixedChart: { tag: "MixedChart" },
  Label: { tag: "Text" },
  Button: { tag: "Pressable" },
  Icon: { tag: "Icon" },
  Email: { tag: "TextInput" },
  Password: { tag: "TextInput" },
  NumberInput: { tag: "TextInput" },
  Currency: { tag: "TextInput" },
  // Percent is a numeric text field on the web too (react-number-format bounded to 0–100 with a "%" affix), so it
  // joins the input family rather than getting a tag of its own — it gets the family's controlled state,
  // validations, focus handle and disable slot for free, exactly as Currency does.
  Percent: { tag: "TextInput" },
  // DynamicInput is a switch over the input family (its web render returns a TextInput, NumberInput, Checkbox,
  // DatePicker, Email, Url, Password, BarcodeInput or ColorPicker), so it belongs to that family here too — one
  // controlled string state, one editable slot, one focus handle, whichever editor is showing.
  DynamicInput: { tag: "TextInput" },
  TextInput: { tag: "TextInput" },
  TextArea: { tag: "TextInput" },
  // BarcodeInput is a text field on the web (antd Input with hardware-scanner ergonomics — not a camera), so on
  // React Native it maps to the same KTextInput a plain TextInput does (RNM-0141). Like SiderMenu → Menu, the name
  // routes to an existing tag; emitTextInput sees componentName="BarcodeInput" and emits only the controlled-string
  // core (value/onChangeText + validation + placeholder), skipping the TextInput-only formatter/char-filter props.
  BarcodeInput: { tag: "TextInput" },
  Url: { tag: "TextInput" },
  Switch: { tag: "Switch" },
  SelectBox: { tag: "Select" },
  KSelectBox: { tag: "Select" },
  MultiSelect: { tag: "Select" },
  AutoComplete: { tag: "Select" },
  Date: { tag: "DatePicker" },
  DateTime: { tag: "DatePicker" },
  // The MonthPicker is the date picker in month mode, on both targets: web's registry gives it the KDatePicker
  // tagName and pins `displayType: "month"`.
  MonthPicker: { tag: "DatePicker" },
  YearPicker: { tag: "YearPicker" },
  DateRange: { tag: "DateRange" },
  DateTimeRange: { tag: "DateRange" },
  Checkbox: { tag: "Checkbox" },
  KCheckbox: { tag: "Checkbox" },
  // The retired 0046 checkbox is still on older screens, and web still renders it (same antd Checkbox). Keyed by
  // NAME like the rest of this map, so without this entry it was skipped as unsupported and vanished (KOA-18644).
  Checkbox_DEPRECATED: { tag: "Checkbox" },
  RadioGroup: { tag: "RadioGroup" },
  // The segmented bar and its buttons — a different component from RadioGroup above, which is the circle-radio
  // list over bound options. `Radio` is the older spelling of the button in the registry (0011_Radio.json).
  RadioButtonGroup: { tag: "RadioButtonGroup" },
  KRadioGroup: { tag: "RadioButtonGroup" },
  RadioButton: { tag: "RadioButton" },
  KRadioButton: { tag: "RadioButton" },
  Radio: { tag: "RadioButton" },
  LocationPicker: { tag: "LocationPicker" },
  // The designer's component is named `LocationSearch`; its renderer (and the web component) is
  // `PlacesAutocompleteInput`, which is the name a screen authored against the renderer can carry.
  LocationSearch: { tag: "LocationSearch" },
  PlacesAutocompleteInput: { tag: "LocationSearch" },
  // The designer's component is named `Maps`; its renderer and web component are `GoogleMaps`, which is the name a
  // screen authored against the renderer can carry. `MapsMarker` / `MapsMarkerPopup` are deliberately absent: they are
  // structural children `emitMaps` consumes itself, never nodes emitted in their own right.
  Maps: { tag: "Maps" },
  GoogleMaps: { tag: "Maps" },
  Rate: { tag: "Rating" },
  Rating: { tag: "Rating" },
  Slider: { tag: "Slider" },
  RangeSlider: { tag: "RangeSlider" },
  // The designer's own name for it carries a space (0111_Range_Slider.json: `name: "Range Slider"`, tagName
  // `RangeSlider`), and this map is keyed by NAME — so a Range Slider on a real screen matched nothing and was
  // emitted as an "unsupported component" comment. Same shape as the "Pivot Table" / "Mixed Chart" aliases above.
  "Range Slider": { tag: "RangeSlider" },
  Spacer: { tag: "Spacer" },
  KSpacer: { tag: "Spacer" },
  Badge: { tag: "Badge" },
  KBadge: { tag: "Badge" },
  MDViewer: { tag: "Markdown" },
  KMDViewer: { tag: "Markdown" },
  Divider: { tag: "Divider" },
  Tooltip: { tag: "Tooltip" },
  FloatButton: { tag: "FloatButton" },
  Collapse: { tag: "Collapse" },
  CollapsePanel: { tag: "CollapsePanel" },
  FloatPanel: { tag: "FloatPanel" },
  Breadcrumb: { tag: "Breadcrumb" },
  Pagination: { tag: "Pagination" },
  Drawer: { tag: "Drawer" },
  KDrawer: { tag: "Drawer" },
  Menu: { tag: "Menu" },
  SiderMenu: { tag: "Menu" },
  MenuItem: { tag: "MenuItem" },
  SubMenu: { tag: "SubMenu" },
  ProgressBar: { tag: "ProgressBar" },
  ProgressCircle: { tag: "ProgressCircle" },
  QRRenderer: { tag: "QRRenderer" },
  BarcodeRenderer: { tag: "BarcodeRenderer" },
  Signature: { tag: "Signature" },
  ColorPicker: { tag: "ColorPicker" },
  MDEditor: { tag: "MDEditor" },
  Marquee: { tag: "Marquee" },
  // The designer's component is named "Language Selector" (with the space); its tagName is the one a screen
  // authored against the renderer carries, so both spellings resolve.
  "Language Selector": { tag: "LanguageSelector" },
  LanguageSelector: { tag: "LanguageSelector" },
  Recaptcha: { tag: "Recaptcha" },
  PaInteractionButtons: { tag: "PaInteractionButtons" },
  ProcessAutomation: { tag: "ProcessAutomation" },
  // This map is keyed by the designer's component NAME, and both PA components carry spaced names in the
  // registry (0173: "PA Interaction Buttons", tagName PaInteractionButtons; 0155: "Process Automation Viewer",
  // tagName ProcessAutomation). Without these two aliases a real screen emitted them as "unsupported component"
  // comments — the RNM-0123 platform-flag sweep is what catches it. `PAInteractionButtons` is the C# renderer's
  // spelling (ERendererName), which a screen authored against the renderer can carry, as with LocationSearch.
  "PA Interaction Buttons": { tag: "PaInteractionButtons" },
  PAInteractionButtons: { tag: "PaInteractionButtons" },
  "Process Automation Viewer": { tag: "ProcessAutomation" },
  Countdown: { tag: "Countdown" },
  CountDown: { tag: "Countdown" },
  Countup: { tag: "CountUp" },
  CountUp: { tag: "CountUp" },
  Stopwatch: { tag: "Stopwatch" },
  ContentMenu: { tag: "ContentMenu" },
  ContentMenuItem: { tag: "ContentMenuItem" },
  ContentMenuPanel: { tag: "ContentMenuPanel" },
  Image: { tag: "Image" },
  KImage: { tag: "Image" },
  Thumbnail: { tag: "Thumbnail" },
  KThumbnail: { tag: "Thumbnail" },
  Timeline: { tag: "Timeline" },
  Calendar: { tag: "Calendar" },
  BigFileUpload: { tag: "BigFileUpload" },
  NodeTree: { tag: "NodeTree" },
  Stepper: { tag: "Stepper" },
  // The designer's component is named "Pivot Table" (with the space); its tagName is the one a screen authored
  // against the renderer carries, so both spellings resolve.
  "Pivot Table": { tag: "PivotTable" },
  PivotTable: { tag: "PivotTable" },
  StepperItem: { tag: "StepperItem" },
  Tree: { tag: "Tree" },
  WebView: { tag: "WebView" },
  VideoPlayer: { tag: "VideoPlayer" },
  Video: { tag: "VideoPlayer" },
  YoutubeVideoPlayer: { tag: "VideoPlayer" },
  KVideo: { tag: "VideoPlayer" },
  MicroApp: { tag: "MicroApp" },
  MfeContainer: { tag: "MicroApp" },
  Header: { tag: "View" },
  Footer: { tag: "View" },
  PageHeader: { tag: "View" },
  PageFooter: { tag: "View" },
  // The MASTER shell's four chrome slots. They are containers exactly like the screen-level Header/Footer above
  // (componentCategory "Container", blockStyle 2 in 0083_LayoutHeader.json …), and they were simply absent — so
  // `generateMasterLayoutFragment` emitted an "unsupported component" comment for each and **every master screen
  // rendered no chrome at all**: no header, no footer, no side menu. The compile fixture hid it because its
  // fragment is hand-written C# rather than produced by this emitter. Found on a device (RNM-0188).
  LayoutHeader: { tag: "View" },
  LayoutFooter: { tag: "View" },
  LayoutLeft: { tag: "View" },
  LayoutRight: { tag: "View" }
};

/**
 * Every component identifier this target lowers — the keys of {@link KUIKA_TAG_TO_REACT_NATIVE}, exported
 * because the map itself is an implementation detail but the *list* is a contract other code must check
 * against.
 *
 * It exists for the platform-flag invariant (RNM-0123): a component whose registry entry carries
 * `appSupportType: Web` is filtered out of the designer's component palette on every mobile screen
 * (`component-toolkit.tsx`, `drag-drop-helper.ts`), so lowering one here produces a strategy that can never
 * run. Collapse, CollapsePanel, RadarChart and RadialChart shipped in exactly that state: fully emitted by
 * this generator, impossible to drag onto a mobile screen.
 * `src/__tests__/react-native-component-platform-flags.test.ts` reads the shipped metadata and fails on any
 * recurrence — the mapping and the registry live in different languages, so nothing else connects them.
 */
export const REACT_NATIVE_LOWERED_COMPONENTS: readonly string[] = Object.keys(KUIKA_TAG_TO_REACT_NATIVE);

/**
 * Inline-flow widgets that hug their content instead of filling their parent's width in a vertical
 * container (see the block-flow fill in `emitNode`). Mirrors the web's inline elements — a Button/Icon
 * sizes to its content (Android `wrap_content`); for Button, full width is opt-in via `block`.
 *
 * The web authority is `KMainFunctions.setChildWidthAndAlignment`: only `blockStyle === 2` (block)
 * children get `width: "100%"`; everything else is left content-sized. Every name here is a
 * `blockStyle: 1` component in `Backend/CoreApi/Metadatas/Components/`. **Label belongs in this list**
 * (`0000_Label.json` is `blockStyle: 1`) and its absence was a real bug: a force-filled Label spans the
 * whole row, so its parent Col's `alignItems` (the designer's left/center/right) had nothing left to
 * align and the text stayed pinned left by its own default `textAlign`. Content-sized, the Col's
 * alignment positions the text box — and RN's default `alignItems: "stretch"` still gives a Label in an
 * unaligned container the full width, so wrapping behaviour is unchanged.
 */
const CONTENT_WIDTH_COMPONENTS: ReadonlySet<string> = new Set<string>([
  "AutoComplete",
  "Badge",
  "Button",
  "Checkbox",
  "Countup",
  "CountUp",
  "Icon",
  // 0036_Image.json / 0069_Image.json are `blockStyle: 1`. A force-filled `width: "100%"` made a height-only
  // Image span the row and letterbox (KImage then saw both axes "set" and skipped its single-axis aspectRatio),
  // breaking alignment (KOA-18276). Content-sized, KImage derives the missing axis from the intrinsic ratio, and
  // the parent Col's alignment can position it — exactly like web (`setChildWidthAndAlignment`, blockStyle !== 2).
  "Image",
  // 0118_Thumbnail.json is `blockStyle: 1` too — it IS the Image over renderer 94 — so it hugs its content for
  // exactly the reasons above. Both registry spellings, since a screen may carry either.
  "Thumbnail",
  "KThumbnail",
  "Label",
  "MultiSelect",
  "ProgressCircle",
  // 0156_RadioGroup.json is `blockStyle: 1`, so the group hugs its options rather than spanning the row — which
  // is also what lets its parent Col's left/center/right alignment position it.
  "RadioButton",
  "RadioButtonGroup",
  "RadioGroup",
  "Rate",
  "Rating",
  "Spacer",
  "Switch"
]);

/**
 * Kuika's `isItNavigationComponent` overlays. They are portals, not flow children: each renders *over* the
 * screen and is sized by its own `width`/`height` property, so the block-flow fill must not hand them their
 * parent's width.
 *
 * Web is `blockStyle: 2` for all three and fills them too, but structurally can never collide: antd renders
 * Drawer/Modal into a portal whose panel is sized by antd's own `width`, and FloatPanel is `position: fixed`
 * with `{...style, width, height}` — the designer's size is applied last in both. RN has no such indirection;
 * the fill and the panel size meet on one View, and the fill wins (KOA-18512: a 25% drawer covered the screen).
 */
const OVERLAY_COMPONENTS: ReadonlySet<string> = new Set<string>(["Drawer", "FloatPanel", "Modal"]);

/**
 * The components a page-init **skeleton** covers: while the screen's page init runs, each of these is
 * wrapped in `<KShimmer>` and shows a shimmering grey placeholder instead of its (still empty) content.
 *
 * The set is Android's, verbatim — the `{ApplySkeleton}` key appears in exactly these component templates in
 * `AndroidTemplates.cs`, and iOS applies `.applySkeleton(...)` to the same family. It is the *data-bearing*
 * components: what a page init fills. Deliberately absent, because Android leaves them out too: containers
 * (Row/Col/the stacks/Panel), the data repeaters (Table/Gallery/FlexGrid — their rows do not exist yet, and
 * each row's own leaves carry skeletons), navigation chrome (Tab/Menu/Breadcrumb/Pagination/Drawer), the
 * overlays (Modal/FloatPanel), and Button — a button's label is authored, not loaded.
 *
 * Both the registry name and the registry tagName are listed wherever the tag map lists both, so either
 * contract resolves. `Countup` is the one addition: Android has no Countup component at all, so there is no
 * template to mirror, and it is the CountDown's data-bearing sibling.
 *
 * When a component starts being lowered here, check whether its Android template carries `{ApplySkeleton}` and
 * add it — nothing else connects the two, so the gap would be silent. `BarcodeInput`, `QRRenderer`, `Signature`
 * and the `VideoPlayer` aliases arrived that way (RNM-0141/0146): all four skeleton on Android
 * (`BARCODE_INPUT_TEMPLATE`, `QR_RENDERER_TEMPLATE`, `SIGNATURE_TEMPLATE`, `YOUTUBE_PLAYER_TEMPLATE`).
 */
export const SKELETON_COMPONENTS: ReadonlySet<string> = new Set<string>([
  "AreaChart",
  "AutoComplete",
  "BarChart",
  "BarcodeInput",
  "BarcodeRenderer",
  "Checkbox",
  "ColorPicker",
  "Calendar",
  "CountDown",
  "CountUp",
  "Countdown",
  "Countup",
  "Currency",
  "Date",
  "DateRange",
  "DateTime",
  "DateTimeRange",
  "Divider",
  "DonutChart",
  "DynamicInput",
  "Email",
  "GaugeChart",
  "GoogleMaps",
  "Icon",
  "Image",
  "KAreaChart",
  "KBarChart",
  "KCheckbox",
  "KDonutChart",
  "KGaugeChart",
  "KImage",
  "KLineChart",
  "KMDViewer",
  "KMixedChart",
  "KPieChart",
  "KRadarChart",
  "KRadialChart",
  "KSelectBox",
  "KThumbnail",
  "KVideo",
  "Label",
  "LineChart",
  "LocationPicker",
  "LocationSearch",
  "MDEditor",
  "MDViewer",
  "Maps",
  "MixedChart",
  "MultiSelect",
  "NumberInput",
  "Password",
  "Percent",
  "PieChart",
  "PivotTable",
  "PlacesAutocompleteInput",
  "ProgressBar",
  "ProgressCircle",
  "QRRenderer",
  "RadarChart",
  "RadialChart",
  "RadioButtonGroup",
  "RadioGroup",
  "RangeSlider",
  "Rate",
  "Rating",
  "SelectBox",
  "Signature",
  "Slider",
  "Stopwatch",
  "Switch",
  "TextArea",
  "TextInput",
  "Thumbnail",
  "Timeline",
  "Tree",
  "Url",
  "Video",
  "VideoPlayer",
  "WebView",
  "YearPicker",
  "YoutubeVideoPlayer"
]);

/**
 * The screen-scope boolean a skeleton reads: false until the screen's page init settles. Declared by the C#
 * `ReactNativeScreenGenerator` (a `useState` when the screen has a page init, a `true` const when it has
 * none, so a screen with nothing to load never flashes a skeleton) — the same Node-references /
 * C#-declares split as `isVisible_di<id>` and the `di<id>` input states.
 */
const PAGE_INIT_FINISHED_VARIABLE: string = "kuikaPageInitFinished";

/**
 * Box-size keys the skeleton wrapper **copies** off the component it wraps. Copied, not moved: the component
 * still paints its own background and border, so taking its width away would shrink the box the user sees
 * once the skeleton retracts. A *percentage* value is the exception (see
 * {@link ReactNativeElementGenerator.extractShimmerBoxStyle}) — resolved against the wrapper instead of the
 * original parent it would compound.
 */
const SHIMMER_BOX_SIZE_KEYS: readonly string[] = ["width", "minWidth", "maxWidth", "height", "minHeight", "maxHeight"];

/**
 * Keys the skeleton wrapper **moves** off the component: everything that positions the box inside its
 * parent rather than sizing the box itself. Leaving a margin or a flex share on the child would apply it
 * twice (once around the wrapper, once inside it) and push the component out of the box the wrapper reserved.
 */
const SHIMMER_BOX_MOVE_KEYS: readonly string[] = [
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "marginHorizontal",
  "marginVertical",
  "marginStart",
  "marginEnd",
  "flex",
  "flexGrow",
  "flexShrink",
  "flexBasis",
  "alignSelf",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "start",
  "end",
  "zIndex"
];

/** The moved keys that make the wrapper's own main-axis size definite, so the child has to fill it. */
const SHIMMER_FLEX_KEYS: ReadonlySet<string> = new Set<string>(["flex", "flexGrow", "flexShrink", "flexBasis"]);

/**
 * Components that participate in the Kuika/Ant-Design 24-unit column grid: their responsive span
 * (`xs`/`sm`/…) sizes them as a fraction of the enclosing Row rather than as a flex share. Both the
 * registry name and the registry tagName are listed so either contract resolves (as in the tag map).
 */
const GRID_COLUMN_COMPONENTS: ReadonlySet<string> = new Set<string>(["Col", "KCol"]);

/** The 24 units of one full row in the Kuika/Ant-Design column grid. */
const GRID_COLUMN_UNITS: number = 24;

/**
 * The designer's six column-span properties, narrowest breakpoint first — Ant Design's own grid attributes,
 * which the designer fills from its three device buckets (`deviceTypeForColumnAttribute` in `0114_Col.json`:
 * phone → `xs`, tablet → `sm`/`md`/`lg`, desktop → `xl`/`xxl`).
 *
 * <p>Order is data, not presentation: it is the cascade order both this emitter and the runtime read them in.</p>
 */
const GRID_SPAN_PROPERTIES: readonly string[] = ["xs", "sm", "md", "lg", "xl", "xxl"];

/**
 * The screen-level const holding the live breakpoint, and the runtime helper that turns a column's span set into
 * a width at that breakpoint (`src/shared/kuika-breakpoint.ts`).
 *
 * <p>Same Node-references / C#-declares split as `isVisible_di<id>` and `kuikaPageInitFinished`: this leg emits
 * the reference, and `ReactNativeScreenGenerator` declares the const (and imports the helper) because it found
 * the name in the emitted JSX.</p>
 *
 * <p>One const per screen rather than a prop per column, for two reasons. The width then does not depend on
 * which tag a column lowered to — today always a `KContainer`, but the container path can also emit a raw
 * `ScrollView`, which takes no Kuika props. And every column on a screen reads one subscription instead of one
 * each.</p>
 */
const RN_BREAKPOINT_VARIABLE: string = "kuikaBreakpoint";
const RN_COLUMN_WIDTH_HELPER: string = "kuikaColumnWidth";

/** One breakpoint the designer set a span for, in {@link GRID_SPAN_PROPERTIES} order. */
interface IColumnSpanEntry {
  breakpoint: string;
  span: number;
}

/**
 * Kuika's align section writes the *horizontal* content alignment as `textAlign` (the web reads it
 * back in `KMainFunctions.handleAlignments` and turns it into `justifyContent`); this maps those CSS
 * values onto the flex values React Native understands. Kept separate from `normalizeTextAlign`,
 * which keeps `textAlign` as text alignment for `Text` nodes — "justify" has no flex counterpart and
 * is deliberately absent here.
 */
/** The font size a React Native `Text` renders at when its style names none. */
const REACT_NATIVE_DEFAULT_FONT_SIZE: number = 14;

/**
 * Content-sized components whose RUNTIME pins its own `alignSelf: "flex-start"` so it hugs its content instead
 * of stretching across a column (RN's default `alignItems: "stretch"`). An `alignSelf` outranks the parent's
 * `alignItems`, so a centred container could not move them — the emitter has to hand them the web position
 * itself: an inline element sits where the inherited `text-align` puts it (see `emitNode`, KOA-18617).
 * KButton is the one listed: its designer style is applied after that pin, so the emitted value wins. KBadge
 * and KRating pin the same way and would need it too; KCheckbox never applies the designer style to its box.
 */
const SELF_ALIGNED_CONTENT_COMPONENTS: ReadonlySet<string> = new Set<string>(["Button"]);

const FLEX_ALIGNMENT_FROM_TEXT_ALIGN: Record<string, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end"
};

/**
 * Tags whose `style` is the box that actually lays the designer's children out, and which therefore
 * translate the align pair onto their own flex axes (see
 * {@link ReactNativeElementGenerator.resolveContentAlignment}). Two exclusions, both deliberate:
 * - Leaf tags (`Text`, `Image`, `TextInput`, …) have no children to align, and `textAlign` already
 *   reaches their own style through the typography partition.
 * - `Modal`, `MicroApp` and `Tab` are containers, but their `style` lands on a *chrome* box, not on
 *   the children's box — KModal styles the dialog card (children live in a nested body ScrollView),
 *   KTab styles the shell holding the tab bar plus the panel area, and a MicroApp's children come
 *   from another project. Aligning those would move the chrome, not the content.
 */
const CONTENT_ALIGNING_TAGS: ReadonlySet<TReactNativeTag> = new Set<TReactNativeTag>([
  "View",
  "Container",
  "ScrollView",
  "Panel",
  "TabItem"
]);

/**
 * The layout every root-level node is emitted under: the screen body is a vertical `ScrollView` (so
 * children fill its width, as on the web) and nothing above it sets a text alignment.
 */
const SCREEN_BODY_LAYOUT: IParentLayout = { isHorizontal: false };

/**
 * CSS keys (as produced by StyleHelper.convertToHtmlStyleFromStyleObj) that are valid
 * React Native style props for the L1 component set. Everything else is dropped —
 * RN's StyleSheet rejects unknown keys at runtime.
 *
 * <p><b>What is deliberately NOT here, and why (RNM-0153).</b> The web style pipe emits a handful of
 * declarations that React Native 0.85 has no counterpart for at all, so they stay dropped rather than
 * being half-translated into something that renders differently from the browser:</p>
 * <ul>
 *   <li>`transition` — CSS transitions do not exist in RN; the equivalent is an `Animated` driver, which
 *       is a runtime concern the StyleSheet cannot express.</li>
 *   <li>`perspective` / `perspectiveOrigin` (the designer's <i>children</i> perspective) and
 *       `transformStyle` — RN has no 3D context for children. Self-perspective survives, because the
 *       web pipe folds it into the `transform` function list, which RN does support.</li>
 *   <li>`backdropFilter` / `WebkitBackdropFilter` — no RN style key.</li>
 *   <li>`backgroundClip` / `WebkitBackgroundClip` / `WebkitTextFillColor` — the web trick for gradient
 *       TEXT. Painting glyphs with a gradient needs a mask layer in RN, not a style key; see
 *       {@link ReactNativeElementGenerator.toReactNativeStyle}, which drops the accompanying
 *       `backgroundImage` too rather than flooding the whole box with the text's gradient.</li>
 *   <li>`backgroundRepeat` / `backgroundSize` / `backgroundPosition` — RN's `experimental_background*`
 *       keys only describe a bitmap background, and a bitmap background (`url(...)`) is not translated
 *       (that needs an `ImageBackground`, a component change rather than a style one). With only
 *       gradients reaching `experimental_backgroundImage`, these three have nothing to act on.</li>
 *   <li>`objectFit` — an `ImageStyle` key, not a `ViewStyle` one. KImage carries the designer's fit
 *       through its own prop.</li>
 *   <li>`cursor` — a pointer-device concept. RN types accept it, but it is inert on the platforms this
 *       target generates for, and every emitted key costs bytes in every generated screen.</li>
 *   <li>`elevation` — Android-only, and unnecessary: on the New Architecture `boxShadow` renders a real
 *       shadow on both platforms, so the designer's one shadow model maps to one RN key.</li>
 * </ul>
 */
const REACT_NATIVE_SAFE_STYLE_KEYS: readonly string[] = [
  "alignItems",
  "alignSelf",
  "backfaceVisibility",
  "backgroundColor",
  "backgroundImage",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
  "borderBottomWidth",
  "borderColor",
  "borderLeftWidth",
  "borderRadius",
  "borderRightWidth",
  "borderStyle",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderTopWidth",
  "borderWidth",
  "bottom",
  "boxShadow",
  "color",
  "display",
  "filter",
  "flex",
  "flexDirection",
  "flexWrap",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "gap",
  "height",
  "justifyContent",
  "left",
  "letterSpacing",
  "lineHeight",
  "margin",
  "marginBottom",
  "marginLeft",
  "marginRight",
  "marginTop",
  "maxHeight",
  "maxWidth",
  "minHeight",
  "minWidth",
  "mixBlendMode",
  "opacity",
  "outlineColor",
  "outlineOffset",
  "outlineStyle",
  "outlineWidth",
  "overflow",
  "padding",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "paddingTop",
  "pointerEvents",
  "position",
  "right",
  "textAlign",
  "textDecoration",
  "textTransform",
  "top",
  "transform",
  "transformOrigin",
  "width",
  "zIndex"
];

/**
 * The RN tags that take an inherited colour / font (see {@link IParentLayout.inheritedTextStyle}): the ones whose web
 * element inherits them — a Label's `span`, an Icon's glyph. The rest either carry their own colours on web (antd
 * buttons, inputs, selects) or render no text of their own.
 */
const TEXT_STYLE_INHERITING_TAGS: ReadonlySet<TReactNativeTag> = new Set<TReactNativeTag>(["Text", "Icon"]);

/**
 * Style keys that are only legal on Text in React Native. `TextStyle` extends `ViewStyle`,
 * but `ViewStyle` rejects typography keys — `<View style>` carrying them fails tsc. The
 * designer's style model attaches typography to containers too, so the emitter partitions:
 * View keeps the box subset, Text keeps everything, Pressable moves these onto its inner Text.
 */
const TEXT_ONLY_STYLE_KEYS: readonly string[] = [
  "color",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "letterSpacing",
  "lineHeight",
  "textAlign",
  "textDecorationLine",
  "textTransform"
];

/**
 * Style keys that govern how a scroll container arranges its *children* and therefore belong on a
 * `ScrollView`'s `contentContainerStyle` rather than its `style` (which is the scroll viewport box).
 * Putting `gap`/padding/flex-alignment on `style` is a no-op on RN's ScrollView — the content lives in
 * the inner content view. Everything not listed here (width/height/background/border/margin/position)
 * stays on `style`. Only used for the stack scroll variants; plain Views keep a single merged style.
 */
const CONTENT_CONTAINER_STYLE_KEYS: readonly string[] = [
  "alignItems",
  "flexDirection",
  "flexWrap",
  "gap",
  "justifyContent",
  "padding",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "paddingTop"
];

/** Valid React Native textAlign values; the web style pipe emits "-webkit-left"/"start" etc. */
const TEXT_ALIGN_VALUES: readonly string[] = ["auto", "left", "right", "center", "justify"];

/** RN overflow accepts "visible" | "hidden" | "scroll". "auto" is normalised to "scroll". */
const RN_OVERFLOW_PASSTHROUGH: ReadonlySet<string> = new Set<string>(["visible", "hidden", "scroll"]);

/** RN display accepts only "flex" (default) and "none". All other web values are dropped. */
const RN_DISPLAY_VALUES: ReadonlySet<string> = new Set<string>(["flex", "none"]);

/** RN position accepts only "absolute" and "relative". "fixed"/"sticky" are web-only. */
const RN_POSITION_VALUES: ReadonlySet<string> = new Set<string>(["absolute", "relative"]);

/**
 * An interaction state the designer authors a style DELTA for — {@link StyleState} without the resting
 * `"none"`, which is the base style every component already carries.
 */
export type TStyleStateDelta = Exclude<StyleState, "none">;

/**
 * The four state deltas every styling section carries, as the shape they have in common. The six section
 * models (`IStylingBorder`, `IFillStyle`, …) each declare these four fields with their own delta type, so
 * one generic reader serves all six instead of six near-identical accessors.
 */
interface IStatefulStyleSection<TDelta> {
  hoverStyles?: TDelta;
  pressedStyles?: TDelta;
  focusedStyles?: TDelta;
  disabledStyles?: TDelta;
}

/**
 * Which part of a state delta a prop carries: the box (`ViewStyle`), the typography (`TextStyle`), or the
 * whole thing unpartitioned — which is what a single-element `Text` component takes, since `TextStyle`
 * extends `ViewStyle` and its base style is not partitioned either.
 */
type TStateStylePick = "view" | "text" | "all";

/**
 * One state-style prop a component supports — the state, the prop name on the runtime component, and which
 * half of the partitioned delta it carries.
 *
 * <p>Support is declared per component rather than emitted uniformly, because unlike CSS — where a
 * `:active` rule costs nothing on an element that can never be pressed — an RN prop only exists if the
 * runtime component reads it. A `pressedStyle` on a component that renders no press surface would be a
 * prop nothing applies, and tsc would reject it.</p>
 */
interface IStateStylePropSpec {
  state: TStyleStateDelta;
  prop: string;
  pick: TStateStylePick;
}

/**
 * The `pressed` state on a component whose press surface IS the designer's box (`KContainer`, `KPanel`) —
 * one prop, the box half of the delta.
 *
 * <p>`hover` is deliberately not projected onto any component. It is a pointer-device state; on a touch
 * screen there is no hover, and the web target's `:hover` rule has no moment at which it could apply. A
 * `hoverStyle` prop would be a prop nothing ever sets.</p>
 */
const PRESSED_BOX_SPEC: readonly IStateStylePropSpec[] = [{ state: "pressed", prop: "pressedStyle", pick: "view" }];

/**
 * The `pressed` state on a component that lays out its own label `Text` (`KButton`): the box half rides
 * `pressedStyle`, the typography half `pressedLabelStyle`, mirroring how the base style already splits
 * across `style` / `labelStyle`.
 */
const PRESSED_BOX_AND_LABEL_SPEC: readonly IStateStylePropSpec[] = [
  { state: "pressed", prop: "pressedStyle", pick: "view" },
  { state: "pressed", prop: "pressedLabelStyle", pick: "text" }
];

/**
 * The `pressed` state on a single-element component that carries box AND typography on one style
 * (`KLabel`, `KIcon` — a `Text` underneath, and `TextStyle` extends `ViewStyle`). The whole delta travels
 * unpartitioned, exactly as the base style does for these two.
 */
const PRESSED_WHOLE_SPEC: readonly IStateStylePropSpec[] = [{ state: "pressed", prop: "pressedStyle", pick: "all" }];

/**
 * The `disabled` state on a component whose disable affordance is a box (`KSwitch`, `KCheckbox`, `KSlider`,
 * `KRangeSlider`, `KRating`, `KSignature`, `KFloatButton`, `KLocationPicker`, `KMaps`).
 *
 * <p>Which components get this is not a judgement call: it is exactly the tags
 * {@link ReactNativeElementGenerator.disableProp} can disable. A tag with no disable affordance has no state to
 * be in — the web target says the same by giving a Label no `[data-disabled]` moment.</p>
 */
const DISABLED_BOX_SPEC: readonly IStateStylePropSpec[] = [{ state: "disabled", prop: "disabledStyle", pick: "view" }];

/** The `disabled` state on a component that lays out its own label `Text` (`KButton`). */
const DISABLED_BOX_AND_LABEL_SPEC: readonly IStateStylePropSpec[] = [
  { state: "disabled", prop: "disabledStyle", pick: "view" },
  { state: "disabled", prop: "disabledLabelStyle", pick: "text" }
];

/** The `disabled` state on a component whose typography prop is `textStyle` (the select family, `KRadioGroup`). */
const DISABLED_BOX_AND_TEXT_SPEC: readonly IStateStylePropSpec[] = [
  { state: "disabled", prop: "disabledStyle", pick: "view" },
  { state: "disabled", prop: "disabledTextStyle", pick: "text" }
];

/**
 * The `disabled` state on a single-element component carrying box AND typography on one style — the input
 * family (a `TextInput`, whose `style` is a `TextStyle`) and the date family, whose base style is likewise
 * emitted unpartitioned.
 */
const DISABLED_WHOLE_SPEC: readonly IStateStylePropSpec[] = [{ state: "disabled", prop: "disabledStyle", pick: "all" }];

/**
 * The `focused` state — emitted on the input family and nowhere else.
 *
 * <p>That is web's own scope, not a narrowing of it. The web target emits `.kuika-style-{id}:focus-visible`
 * (`React_UIGenerator.BuildStateSelector`), and `:focus-visible` matches when the element ITSELF holds focus
 * and the browser decides the focus should be shown — which it does for a text field on any interaction, and
 * deliberately does NOT for an element merely tapped or clicked. So a Select or a DatePicker the user opens is
 * not in web's focused state either. Mapping "its dropdown is open" onto this state would invent a rendering
 * the browser never shows, and React Native has no other notion of focus to reach for: the input family is
 * the only component set with a real `onFocus` / `onBlur`.</p>
 *
 * <p>Whole-style, like the input family's base: a `TextInput`'s `style` IS a `TextStyle`, so its box and its
 * typography travel together.</p>
 */
const FOCUSED_WHOLE_SPEC: readonly IStateStylePropSpec[] = [{ state: "focused", prop: "focusedStyle", pick: "all" }];

/** `focused` + `disabled` for the input family, the one component set that can be in either. */
const INPUT_STATE_SPEC: readonly IStateStylePropSpec[] = [...FOCUSED_WHOLE_SPEC, ...DISABLED_WHOLE_SPEC];

/** `pressed` + `disabled` for `KButton`, the one component that can be in either. */
const BUTTON_STATE_SPEC: readonly IStateStylePropSpec[] = [
  ...PRESSED_BOX_AND_LABEL_SPEC,
  ...DISABLED_BOX_AND_LABEL_SPEC
];

/**
 * Web CSS key → the React Native style key carrying the same intent, applied as the narrowed value is
 * written out. Two of the keys the web pipe emits are spelled differently in RN rather than absent:
 *
 * <ul>
 *   <li>`backgroundImage` → `experimental_backgroundImage`. RN 0.85 still ships gradients under the
 *       `experimental_` prefix. The prefix is a React Native version detail, so it lives here as one
 *       constant rather than being spelled at the call site: when the generated app's pinned RN version
 *       promotes the key, this line is the whole change.</li>
 *   <li>`textDecoration` → `textDecorationLine`. CSS `text-decoration` is a shorthand; RN only has the
 *       line part, and the designer's panel only ever writes a line value ("underline"/"line-through"),
 *       so the two carry exactly the same information.</li>
 * </ul>
 */
const RN_STYLE_KEY_RENAMES: ReadonlyMap<string, string> = new Map<string, string>([
  ["backgroundImage", "experimental_backgroundImage"],
  ["textDecoration", "textDecorationLine"]
]);

/** RN `mixBlendMode` — the CSS `<blend-mode>` set, minus `plus-lighter`/`plus-darker`. */
const RN_BLEND_MODES: ReadonlySet<string> = new Set<string>([
  "normal",
  "multiply",
  "screen",
  "overlay",
  "darken",
  "lighten",
  "color-dodge",
  "color-burn",
  "hard-light",
  "soft-light",
  "difference",
  "exclusion",
  "hue",
  "saturation",
  "color",
  "luminosity"
]);

/** RN `outlineStyle`. CSS's `none` is absent — an outline is switched off by having no width, not by a style. */
const RN_OUTLINE_STYLES: ReadonlySet<string> = new Set<string>(["solid", "dotted", "dashed"]);

/** RN `pointerEvents` (a ViewStyle key here, not the legacy prop). `box-none`/`box-only` are RN-only additions. */
const RN_POINTER_EVENTS: ReadonlySet<string> = new Set<string>(["auto", "none", "box-none", "box-only"]);

/** RN `backfaceVisibility`. */
const RN_BACKFACE_VISIBILITY: ReadonlySet<string> = new Set<string>(["visible", "hidden"]);

/** RN `textDecorationLine`. CSS `overline` and any shorthand carrying style/colour have no counterpart. */
const RN_TEXT_DECORATION_LINES: ReadonlySet<string> = new Set<string>([
  "none",
  "underline",
  "line-through",
  "underline line-through"
]);

/** RN `textTransform`. */
const RN_TEXT_TRANSFORMS: ReadonlySet<string> = new Set<string>(["none", "capitalize", "uppercase", "lowercase"]);

/**
 * The gradient functions RN can paint. `conic-gradient` is in Kuika's {@link GRADIENT_PREFIXES} — the
 * designer's colour picker offers it — but `processBackgroundImage` only parses linear and radial, so a
 * conic fill is dropped rather than emitted as a value RN silently ignores.
 */
const RN_GRADIENT_PREFIXES: readonly string[] = ["linear-gradient", "radial-gradient"];

/**
 * Transform functions React Native accepts, mapped to how many arguments each takes.
 *
 * <p>The list is `_validateTransform`'s in `processTransform.js`, NOT the wider one
 * `conversions.h` folds into a matrix on the C++ side. The JS validator ends in
 * `default: invariant(false, "Invalid transform …")`, so a function only the renderer knows —
 * `scaleZ` is the one that matters here, since C++ handles it and JS does not — red-screens the app in
 * development instead of rendering. The narrower list is the real contract.</p>
 *
 * <p>`matrix` is deliberately absent: it is legal in RN (9 or 16 numbers) but the designer's Effects
 * panel has no way to author one, and its two-valued arity does not fit this table.</p>
 *
 * <p>The web pipe (`StyleHelper.buildTransformFunction`) emits three functions that are NOT in this set:
 * `scale(x,y)`, `scale3d(x,y,z)` and `skew(x,y)`. They are not merely ignored — RN's JS parser turns
 * `scale(1.2,1.5)` into `{scale: "1.2,1.5"}`, and the C++ side then fails its type check and
 * <b>discards the entire transform list</b>, so one two-argument scale silently cancels every other
 * transform on the component. {@link ReactNativeElementGenerator.normalizeTransform} rewrites them into
 * the per-axis functions instead of dropping them.</p>
 */
const RN_TRANSFORM_ARITY: ReadonlyMap<string, number> = new Map<string, number>([
  ["perspective", 1],
  ["rotate", 1],
  ["rotateX", 1],
  ["rotateY", 1],
  ["rotateZ", 1],
  ["scale", 1],
  ["scaleX", 1],
  ["scaleY", 1],
  ["skewX", 1],
  ["skewY", 1],
  ["translate", 2],
  ["translateX", 1],
  ["translateY", 1]
]);

/** The transform functions RN validates as an angle: the value must carry `deg` or `rad`. */
const RN_ANGLE_TRANSFORMS: ReadonlySet<string> = new Set<string>([
  "rotate",
  "rotateX",
  "rotateY",
  "rotateZ",
  "skewX",
  "skewY"
]);

/**
 * CSS filter functions RN can apply, and the unit each one's amount may carry (`null` = unitless only).
 * Mirrors `_getFilterAmount` in RN's `processFilter`, which returns `undefined` for anything outside
 * these rules — and one `undefined` makes RN drop the WHOLE `filter` declaration, so the emitter
 * validates every function before letting the declaration through.
 */
const RN_FILTER_UNITS: ReadonlyMap<string, string | null> = new Map<string, string | null>([
  ["blur", "px"],
  ["brightness", "%"],
  ["contrast", "%"],
  ["grayscale", "%"],
  ["hue-rotate", "deg"],
  ["invert", "%"],
  ["opacity", "%"],
  ["saturate", "%"],
  ["sepia", "%"]
]);

/**
 * CSS numeric font weight → the weight word the bundled TTFs are named by.
 *
 * <p>Mirrors `FontFamilyManager.GetFontStyle` on the C# side, which names the files
 * (`poppins_semibold.ttf`) and — through `ReactNativeProjectGenerator.ApplyFonts` — the keys the
 * generated app registers with expo-font. The two tables must agree: this one decides what a
 * StyleSheet asks for, that one decides what the app registers. Both are pinned by tests
 * (`react-native-element-generator.test.ts` here, `ReactNativeFontEmissionTests` there) using the
 * same expected strings, and the alias entries ApplyFonts writes mean a weight with no dedicated
 * face still resolves rather than falling through to the OS default.</p>
 *
 * <p>Keyed by the string form because `StyleHelper.getTextStyle` passes the designer's
 * `fontWeight` through unchanged and it may arrive as either a number or a string.</p>
 *
 * <p>The nine CSS steps and nothing else — deliberately NOT the `normal`/`bold` keywords. The
 * designer's model types the weight as a number (`IStylingTypography.fontWeight`, `ushort?` in the C#
 * mirror `TypographyFontInfoModel`), so a keyword cannot reach the C# side at all: it would arrive as
 * null there and register the `_regular` face. Accepting `"bold"` here would therefore emit
 * `lato_bold` against a registry that only holds `lato_regular` — a family nothing resolves, i.e.
 * back to the OS default. Anything unmapped falls to {@link DEFAULT_FONT_WEIGHT_NAME}, which is
 * exactly what `FontFamilyManager.GetFontStyle` does for a null or out-of-range weight, so both sides
 * stay the same function of the same input.</p>
 */
const FONT_WEIGHT_NAMES: ReadonlyMap<string, string> = new Map<string, string>([
  ["100", "thin"],
  ["200", "extralight"],
  ["300", "light"],
  ["400", "regular"],
  ["500", "medium"],
  ["600", "semibold"],
  ["700", "bold"],
  ["800", "extrabold"],
  ["900", "black"]
]);

/** The weight face used when the designer set a family but no usable weight — mirrors `GetFontStyle`'s null/out-of-range answer. */
const DEFAULT_FONT_WEIGHT_NAME: string = "regular";

const INDENT_STEP: string = "  ";

/**
 * Structural separator between the two sections of a layout fragment (body JSX ⟂ StyleSheet block).
 * The C# `ReactNativeScreenGenerator` splits on this to place the body inside the `<ScrollView>`
 * wrapper and the StyleSheet after the function, without having to parse JSX. It is a delimiter, not
 * metadata — it carries no data, only marks the boundary. Kept deliberately unlikely to occur in
 * emitted content; the C# side matches this exact literal (`ReactNativeScreenGenerator.StylesDelimiter`).
 */
export const FRAGMENT_STYLES_DELIMITER: string = "\n{/* __KUIKA_RN_STYLES__ */}\n";

/**
 * Marker attribute identifying the screen body `<ScrollView>` — the single node a screen-level scroll
 * behavior can attach to. Emitted unconditionally, exactly like a component's `kuikaid`: it says "the
 * screen body's scroll props go here", nothing more. The C# `ReactNativeScreenGenerator` replaces it with
 * the `refreshControl={…}` prop when the screen's designer **Pull-to-Refresh Enabled** switch
 * (`ScreenModel.ShouldRefresh`) is on, and strips it otherwise — the same layout-in-Node /
 * behavior-in-C# split every other marker follows, so Node never has to know the flag exists. Naming the
 * site rather than letting C# guess is what keeps a nested container that happens to be a `ScrollView`
 * (an `overflow: scroll` box) from being mistaken for the screen body. The C# side matches this exact
 * literal (`ReactNativeScreenGenerator.ScreenBodyMarker`).
 */
export const SCREEN_BODY_MARKER: string = " kuikascreenbody='1'";

/**
 * The runtime body scroll view a screen gets instead of the `ScrollView` primitive when something in its body follows
 * the page scroll (a Table's fixed header). It carries {@link SCREEN_BODY_MARKER} like the primitive, and the C#
 * leg treats both tags as the screen body (pull-to-refresh, the overlay's View conversion).
 */
export const SCREEN_SCROLL_VIEW_TAG: string = "KScreenScrollView";

/**
 * Builds the overlay-content marker a navigation component emits **instead of** its child DIs:
 * `__KOVERLAY_<diId>_<targetScreenId>__`, on its own line inside the overlay's tag.
 *
 * A navigation component's content is not its designer children — web replaces the `children` property of
 * every `isItNavigationComponent` component with the screen named by `di.navScreenInfo.targetScreenId`
 * (`server-side-element-generator`), so those children are never rendered on web either. This marker names
 * that screen and nothing else; the C# `ReactNativeScreenGenerator` resolves it into the import, the
 * `OverlayHost` that supplies the screen's inputs, and the mounted screen itself.
 *
 * Same division of labour as ` kuikaid='<id>'` and {@link SCREEN_BODY_MARKER}: this leg names a slot, the
 * C# leg fills it, and because the reference is read back off the emitted fragment neither leg can decide
 * the other emitted something it did not. The overlay's id rides along so C# knows which
 * `setNavVisible_di<id>` closes it.
 */
/**
 * The single line a navigation component emits as its content: the marker naming the screen it hosts, or a
 * comment when the designer chose none.
 *
 * All three of Kuika's `isItNavigationComponent` components — Modal, Drawer, FloatPanel — answer this the
 * same way, because web answers it in one place too: `server-side-element-generator` replaces the `children`
 * property of *any* such component with its `navScreenInfo` screen. Keeping it here rather than in each
 * strategy is what made Drawer and FloatPanel free once RNM-0106/0107 emitted their tags.
 */
function overlayContentLine(ctx: IComponentEmitContext): string {
  const targetScreenId: string = String(ctx.di.navScreenInfo?.targetScreenId ?? "");
  if (targetScreenId !== "") {
    return `${ctx.indent}${INDENT_STEP}${buildOverlayContentMarker(ctx.di.id, targetScreenId)}`;
  }
  // No target screen chosen in the designer: nothing to mount. Say so where a reader will look, rather than
  // emitting an empty overlay that reads as a runtime bug.
  return `${ctx.indent}${INDENT_STEP}{/* kuika-rn: no target screen set on this overlay (di ${ctx.di.id}) — nothing to mount */}`;
}

export function buildOverlayContentMarker(diId: number, targetScreenId: string): string {
  return `__KOVERLAY_${diId.toString()}_${targetScreenId}__`;
}

/** Locale-independent ordinal comparison — generated output must be byte-stable on any machine. */
/**
 * A display item's id as the NUMBER the action-target sets are keyed by.
 *
 * <p>`IDisplayItem.id` is typed `number`, but the persisted screen design carries it as a **string** —
 * and `Set<number>.has("100816")` is false. That mismatch is invisible everywhere else, because every
 * other use interpolates the id into a name (`styles.di100816`, `kuikaid='100816'`), which reads the
 * same either way. It only surfaced in the action-target lookups, where it silently disabled every
 * UI-logic action in real projects: a correctly wired `SetValueOf` / `ChangeVisibilityOf` /
 * `ChangeEnabledOf` never recognised its target, so the component was emitted without the state
 * reference its action needed and the C# leg reported an honest gap for an action the designer had
 * wired (KOA-18254). Synthetic fixtures pass ids as real numbers, which is why the tests never caught
 * it.</p>
 */
function toDiId(id: number): number {
  return Number(id);
}

/**
 * A display item's id as the identifier fragment generated code names it by — `100816` for an ordinary
 * item, `Minus2` for a special layout slot.
 *
 * <p>The special slots carry NEGATIVE ids ({@link SpecialDIIds}: headerDI −2, footerDI −3, pageHeaderDI −4,
 * pageFooterDI −5, layoutHeaderDI −10 … layoutRightDI −13), and they are reachable from the product: the
 * designer's Visibility and Editability panels exclude only `rootDI`, so a hidden header emits a guard on
 * a slot with id −2. Interpolating that raw would produce `isVisible_di-2`, which the parser reads as a
 * subtraction — the screen would not compile at all.</p>
 *
 * <p>The spelling is the platform's own: `DisplayItemModel.StrId` is `Id.ToString().Replace("-", "Minus")`,
 * which is what the web target already names its `isComp&lt;id&gt;Visible` state by. The C# leg mirrors it in
 * `ReactNativeScreenGenerator.DiToken` / `ParseDiToken`, so its reference regexes still read back every name
 * this leg emits.</p>
 *
 * <p>This is for **identifiers only**. A name that is a string key — a StyleSheet entry, a `kuikaid` marker,
 * a `__KBIND_…__` placeholder — keeps the raw id and is read through bracket notation where needed (see
 * {@link ReactNativeElementGenerator.registerStyleEntry}), because a key has no identifier grammar to satisfy
 * and the C# leg matches those markers on the raw id.</p>
 */
function diToken(id: number): string {
  return String(id).replace("-", "Minus");
}

function compareOrdinal(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

/** One `name(args)` segment of a CSS function-list value (`transform`, `filter`). */
interface ICssFunctionSegment {
  name: string;
  args: string;
}

/**
 * Splits a CSS value on top-level commas — separators inside parentheses (a `rgba(0,0,0,.5)` colour,
 * a `cubic-bezier(...)`) are not separators. Empty segments are dropped.
 */
function splitTopLevelCommas(value: string): string[] {
  const segments: string[] = [];
  let depth: number = 0;
  let current: string = "";
  for (const char of value) {
    if (char === "(") {
      depth++;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    }
    if (char === "," && depth === 0) {
      segments.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  segments.push(current.trim());
  return segments.filter((segment: string): boolean => segment !== "");
}

/**
 * Splits a CSS value on top-level whitespace, so a `rgba(0, 0, 0, .5)` colour stays one token.
 */
function splitTopLevelWhitespace(value: string): string[] {
  const tokens: string[] = [];
  let depth: number = 0;
  let current: string = "";
  for (const char of value) {
    if (char === "(") {
      depth++;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
    }
    if (depth === 0 && /\s/.test(char)) {
      if (current !== "") {
        tokens.push(current);
        current = "";
      }
      continue;
    }
    current += char;
  }
  if (current !== "") {
    tokens.push(current);
  }
  return tokens;
}

/**
 * Reads a CSS function-list value (`translate(2px,0) scale(1.2)`) into its segments, preserving order —
 * CSS applies transform and filter functions in written order, so the sequence is meaningful data.
 * Anything between the segments (stray text a hand-written value might carry) is ignored.
 *
 * <p>Depth-counted rather than matched with one regex, because a segment's arguments can themselves hold
 * a function: `drop-shadow(0px 2px 4px rgba(0,0,0,.35))` is ONE filter function, and a `([^()]*)`
 * pattern would read the inner colour as a second one.</p>
 */
function parseCssFunctionSegments(value: string): ICssFunctionSegment[] {
  const segments: ICssFunctionSegment[] = [];
  const namePattern: RegExp = /[a-zA-Z][a-zA-Z0-9-]*$/;
  let index: number = 0;
  while (index < value.length) {
    const open: number = value.indexOf("(", index);
    if (open === -1) {
      break;
    }
    const nameMatch: RegExpMatchArray | null = value.substring(index, open).match(namePattern);
    let depth: number = 1;
    let cursor: number = open + 1;
    while (cursor < value.length && depth > 0) {
      if (value[cursor] === "(") {
        depth++;
      } else if (value[cursor] === ")") {
        depth--;
      }
      cursor++;
    }
    if (depth > 0) {
      break;
    }
    if (nameMatch !== null) {
      segments.push({ name: nameMatch[0], args: value.substring(open + 1, cursor - 1).trim() });
    }
    index = cursor;
  }
  return segments;
}

/**
 * A CSS `<length>` token React Native can evaluate: a number, optionally with `px` or `%`. A non-zero
 * value with no unit is NOT one — RN's `processTransform` raises a DEV-only `invariant` for it, which
 * red-screens the app rather than mis-rendering, so such a value must never be emitted.
 */
function isReactNativeLength(token: string): boolean {
  const match: RegExpMatchArray | null = token.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(px|%)?$/);
  if (match === null) {
    return false;
  }
  return match[2] !== undefined || Number(match[1]) === 0;
}

/** A unitless CSS number (a `scale` factor, a `brightness` amount). */
function isCssNumber(token: string): boolean {
  return /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(token);
}

/**
 * One styling section's delta for an interaction state.
 *
 * <p>A switch rather than an index into a state→field-name map: the four fields are typed, and looking them
 * up by a string would need an index signature the models deliberately do not have. Exhaustive over
 * {@link TStyleStateDelta}, so adding a state to the model surfaces here as a compile error rather than as a
 * state that silently never projects.</p>
 */
function pickStateDelta<TDelta>(
  section: IStatefulStyleSection<TDelta> | undefined,
  state: TStyleStateDelta
): TDelta | undefined {
  if (section === undefined) {
    return undefined;
  }
  switch (state) {
    case "hover":
      return section.hoverStyles;
    case "pressed":
      return section.pressedStyles;
    case "focused":
      return section.focusedStyles;
    case "disabled":
      return section.disabledStyles;
  }
}

/**
 * A CSS `<angle>` token React Native reads: a number carrying `deg` or `rad`. `_validateTransform`
 * asserts exactly this for every rotate and skew function, so an angle-less `rotate(45)` is a DEV
 * `invariant` — a red screen — rather than a rotation the renderer guesses at.
 */
function isCssAngle(token: string): boolean {
  return /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(deg|rad)$/.test(token);
}

export class ReactNativeElementGenerator {
  /** The strategy registered for an RN tag, or undefined when the tag is still emitted inline. */
  static getComponentStrategy(tag: TReactNativeTag): TComponentStrategy | undefined {
    return RN_COMPONENT_STRATEGIES.get(tag);
  }

  /**
   * Emits the screen's **layout fragment** — the full return-JSX tree (the `<SafeAreaView>` shell with
   * its header/footer/pageHeader/pageFooter slots and the body `<ScrollView>`, every actionable
   * component carrying a `kuikaid='<id>'` marker and the body ScrollView carrying
   * {@link SCREEN_BODY_MARKER}) followed by {@link FRAGMENT_STYLES_DELIMITER} and the
   * `StyleSheet` block. Node owns layout only: no imports, no `export default function`, no hooks
   * (`useRouter`/`useState`/authorization hooks), no action logic. The C# `ReactNativeScreenGenerator`
   * consumes this fragment, derives imports/hooks/handlers from the `ScreenModel`, replaces each
   * `kuikaid='<id>'` with the resolved `onPress={on_di<id>}` (stripping it where the component has no
   * action), places the JSX inside `return ( … )`, and appends the StyleSheet. This mirrors the web
   * split between `client_code_generator` (fragment) and `React_UIGenerator` (assembly).
   *
   * Header/footer sit outside the ScrollView (fixed); pageHeader/pageFooter sit inside it (scroll with
   * content). These layout slots are the screen's own special DIs (ids -2…-5, siblings of rootDI), so
   * the C# side already sees their components in `ScreenModel.DisplayItems` when it derives state/actions.
   *
   * Returns "" when the context carries no usable screen design.
   */
  generateScreenLayoutFragment(context: ICodeGenerationContext): string {
    if (!context.sd || !context.sd.displayItems) {
      return "";
    }

    // A master screen is not a scrollable body — it is the shell that wraps its content screens. It composes
    // its four layout slots around a <Slot/> content outlet instead of a ScrollView of its own display items.
    if (context.sdPublic?.screenType === EScreenType.Master) {
      return this.generateMasterLayoutFragment(context);
    }

    const styleEntries: IStyleSheetEntry[] = [];

    const rootDI: IDisplayItem | undefined = context.sd.displayItems.find((di) => di.id === SpecialDIIds.rootDI);
    const topLevelItems: IDisplayItem[] = rootDI ? rootDI.children : context.sd.displayItems;

    // Emits a special layout slot DI (header/footer/pageHeader/pageFooter) into the screen JSX, or ""
    // when absent. Import/state/action wiring for its components is derived C#-side (they live in the
    // screen's own displayItems), so Node only produces the layout here.
    const emitSlot: (di: IDisplayItem | undefined, depth: number) => string = (di, depth) => {
      if (!di) {
        return "";
      }
      const emitted: IEmittedNode | null = this.emitNode(context, di, depth, styleEntries, SCREEN_BODY_LAYOUT);
      return emitted ? emitted.jsx : "";
    };
    // Header/footer sit outside the ScrollView (depth 3); pageHeader/pageFooter sit inside it (depth 4).
    const headerJsx: string = emitSlot(context.sdHeader, 3);
    const footerJsx: string = emitSlot(context.sdFooter, 3);
    const pageHeaderJsx: string = emitSlot(context.sdPageHeader, 4);
    const pageFooterJsx: string = emitSlot(context.sdPageFooter, 4);

    const childJsxParts: string[] = [];
    for (const di of topLevelItems) {
      // Top-level items sit in the screen's vertical ScrollView, so they fill width like web.
      const emitted: IEmittedNode | null = this.emitNode(context, di, 4, styleEntries, SCREEN_BODY_LAYOUT);
      if (emitted) {
        childJsxParts.push(emitted.jsx);
      }
    }

    const resolvedRoot: IResolvedStyle = rootDI
      ? this.resolveStyle(context, rootDI)
      : { style: {}, fillImage: null, isBlockDisplay: false, explicitTextAlign: null };
    const rootStyle: Record<string, string | number> = ReactNativeElementGenerator.partitionStyle(
      resolvedRoot.style
    ).viewStyle;
    if (rootStyle.flex === undefined) {
      rootStyle.flex = 1;
    }
    styleEntries.unshift({ name: "screen", style: rootStyle });
    // When a fixed header or footer is present the ScrollView must flex:1 to fill the remaining
    // space between them; without it the scroll area collapses to its intrinsic content height.
    const hasExternalSlot: boolean = !!context.sdHeader || !!context.sdFooter;
    if (hasExternalSlot) {
      styleEntries.splice(1, 0, { name: "scrollView", style: { flex: 1 } });
    }

    const innerParts: string[] = [
      ...(pageHeaderJsx ? [pageHeaderJsx] : []),
      ...childJsxParts,
      ...(pageFooterJsx ? [pageFooterJsx] : [])
    ];
    // The body sits in a vertical ScrollView (children at depth 4, closing tag at depth 3).
    const body: string = innerParts.length > 0 ? `\n${innerParts.join("\n")}\n${INDENT_STEP.repeat(3)}` : "";

    // The full return-JSX tree, assembled here so ALL layout lives in Node; C# places it inside
    // `return ( … )` and appends the StyleSheet. SafeAreaView at depth 2; header/ScrollView/footer at depth 3.
    const scrollViewStyle: string = hasExternalSlot ? ` style={styles.scrollView}` : "";
    const returnLines: string[] = [`${INDENT_STEP.repeat(2)}<SafeAreaView style={styles.screen}>`];
    // The screen's own Fill → Image, painted behind everything the screen holds — header and footer
    // included, which is what the web target does with a background on the screen's root box. First child,
    // so document order puts it under every sibling.
    const screenFillJsx: string = ReactNativeElementGenerator.buildFillImageJsx(
      resolvedRoot.fillImage,
      INDENT_STEP.repeat(3)
    );
    if (screenFillJsx) {
      returnLines.push(screenFillJsx);
    }
    if (headerJsx) {
      returnLines.push(headerJsx);
    }
    // A Table whose header follows the page scroll needs the body scroll view that shares its offset
    // (KScreenScrollView); every other screen keeps the primitive.
    const bodyScrollTag: string = ReactNativeElementGenerator.hasScrollFollowingTable(topLevelItems)
      ? SCREEN_SCROLL_VIEW_TAG
      : "ScrollView";
    returnLines.push(
      `${INDENT_STEP.repeat(3)}<${bodyScrollTag}${scrollViewStyle}${SCREEN_BODY_MARKER}>${body}</${bodyScrollTag}>`
    );
    if (footerJsx) {
      returnLines.push(footerJsx);
    }
    returnLines.push(`${INDENT_STEP.repeat(2)}</SafeAreaView>`);
    const returnJsx: string = returnLines.join("\n");

    const styleSheetBlock: string = [
      `const styles = StyleSheet.create({`,
      styleEntries.map((entry) => ReactNativeElementGenerator.formatStyleEntry(entry)).join(",\n"),
      `});`
    ].join("\n");

    return `${returnJsx}${FRAGMENT_STYLES_DELIMITER}${styleSheetBlock}`;
  }

  /**
   * The master-screen layout fragment — the RN mirror of the web BaseElementGenerator.getMasterScreenLayout.
   * The four chrome slots (header/left/right/footer, each Primary = outer band or Secondary = inner band)
   * compose around a central expo-router <Slot/> content outlet: the master group's child routes render into
   * the Slot while this chrome stays mounted. An absent or Invisible slot collapses out. The C# assembler wraps
   * this exactly like a screen fragment (imports/hooks/handlers for the chrome components) and adds the Slot import.
   *
   * When a single side holds a SiderMenu (the opposite left/right slot empty), the fragment instead emits an
   * expo-router <Drawer>: the menu becomes the slide-out panel, the Drawer replaces the <Slot/> outlet (its
   * screens ARE the group's child routes), and the header/footer sit as siblings around it — the mobile-idiomatic
   * master. Overlay-hosted master chrome (root Modal/FloatPanel) is out of base scope — ADR-005/008.
   */
  generateMasterLayoutFragment(context: ICodeGenerationContext): string {
    const styleEntries: IStyleSheetEntry[] = [];
    const headerPos: ELayoutPosition = this.resolveLayoutPosition(context.sdLayoutHeader);
    const footerPos: ELayoutPosition = this.resolveLayoutPosition(context.sdLayoutFooter);
    const leftPos: ELayoutPosition = this.resolveLayoutPosition(context.sdLayoutLeft);
    const rightPos: ELayoutPosition = this.resolveLayoutPosition(context.sdLayoutRight);

    // Slots are emitted as whole nodes rather than as raw JSX: a slot the designer hid — or that a role hides —
    // comes back as an expression container, which every CHILD position below takes verbatim but the drawer
    // panel's `return ( … )` cannot (see {@link expressionPositionBody}).
    const emitSlotNode: (di: IDisplayItem | undefined, depth: number) => IEmittedNode | null = (di, depth) => {
      if (!di) {
        return null;
      }
      return this.emitNode(context, di, depth, styleEntries, SCREEN_BODY_LAYOUT);
    };
    const emitSlot: (di: IDisplayItem | undefined, depth: number) => string = (di, depth) => {
      const emitted: IEmittedNode | null = emitSlotNode(di, depth);
      return emitted ? emitted.jsx : "";
    };
    const pushIf: (parts: string[], jsx: string) => void = (parts, jsx) => {
      if (jsx) {
        parts.push(jsx);
      }
    };
    const wrapRow: (styleName: string, depth: number, parts: string[]) => string = (styleName, depth, parts) =>
      `${INDENT_STEP.repeat(depth)}<View style={styles.${styleName}}>\n${parts.join("\n")}\n${INDENT_STEP.repeat(depth)}</View>`;

    // A single menu-bearing side (with the opposite left/right slot empty) becomes a mobile slide-out Drawer: the
    // menu is the drawer panel (drawerContent), the group's child routes are the Drawer's own screens — it replaces
    // the <Slot/> outlet — and the header/footer chrome sits as siblings above and below. A master with menus on
    // BOTH sides, or a non-empty opposite slot, keeps the inline composition below: one Drawer navigator drives a
    // single side, so the rest fall back to the inline sidebar (which still renders and navigates). ADR-008.
    const leftHasMenu: boolean = this.slotHasMenu(context.sdLayoutLeft);
    const rightHasMenu: boolean = this.slotHasMenu(context.sdLayoutRight);
    let drawerSide: string | null = null;
    if (leftHasMenu && !rightHasMenu && rightPos === ELayoutPosition.invisible) {
      drawerSide = "left";
    } else if (rightHasMenu && !leftHasMenu && leftPos === ELayoutPosition.invisible) {
      drawerSide = "right";
    }

    if (drawerSide !== null) {
      const menuSlot: IDisplayItem | undefined = drawerSide === "left" ? context.sdLayoutLeft : context.sdLayoutRight;
      // The panel body sits in `drawerContent={(props) => { … return ( … ); }}` — an expression position, so a
      // guarded menu is fragment-wrapped here rather than spliced in raw.
      const menuNode: IEmittedNode | null = emitSlotNode(menuSlot, 6);
      const menuJsx: string = menuNode ? expressionPositionBody([menuNode], INDENT_STEP.repeat(6)) : "";
      const drawerRootChildren: string[] = [];
      if (headerPos !== ELayoutPosition.invisible) {
        pushIf(drawerRootChildren, emitSlot(context.sdLayoutHeader, 3));
      }
      // No custom header → the drawer shows its own header, whose built-in toggle button is the hamburger; a
      // master with a custom header keeps it off (the header renders as the sibling above) and stays swipe-driven.
      const drawerHeaderShown: string = headerPos === ELayoutPosition.invisible ? "true" : "false";
      drawerRootChildren.push(
        [
          `${INDENT_STEP.repeat(3)}<View style={styles.masterMain}>`,
          `${INDENT_STEP.repeat(4)}<Drawer`,
          `${INDENT_STEP.repeat(5)}screenOptions={{ headerShown: ${drawerHeaderShown}, drawerPosition: ${JSON.stringify(drawerSide)} }}`,
          `${INDENT_STEP.repeat(5)}drawerContent={(props: DrawerContentComponentProps): React.JSX.Element => {`,
          `${INDENT_STEP.repeat(6)}setMasterDrawerNav(props.navigation);`,
          `${INDENT_STEP.repeat(6)}return (`,
          menuJsx,
          `${INDENT_STEP.repeat(6)});`,
          `${INDENT_STEP.repeat(5)}}}`,
          `${INDENT_STEP.repeat(4)}/>`,
          `${INDENT_STEP.repeat(3)}</View>`
        ].join("\n")
      );
      if (footerPos !== ELayoutPosition.invisible) {
        pushIf(drawerRootChildren, emitSlot(context.sdLayoutFooter, 3));
      }

      styleEntries.unshift({ name: "screen", style: { flex: 1 } });
      styleEntries.push({ name: "masterMain", style: { flex: 1 } });

      const drawerReturnJsx: string = [
        `${INDENT_STEP.repeat(2)}<SafeAreaView style={styles.screen}>`,
        ...drawerRootChildren,
        `${INDENT_STEP.repeat(2)}</SafeAreaView>`
      ].join("\n");
      const drawerStyleSheet: string = [
        `const styles = StyleSheet.create({`,
        styleEntries
          .map((entry: IStyleSheetEntry): string => ReactNativeElementGenerator.formatStyleEntry(entry))
          .join(",\n"),
        `});`
      ].join("\n");
      return `${drawerReturnJsx}${FRAGMENT_STYLES_DELIMITER}${drawerStyleSheet}`;
    }

    // Innermost row: [left-secondary] <Slot outlet> [right-secondary]
    const innerRow: string[] = [];
    if (leftPos === ELayoutPosition.secondary) pushIf(innerRow, emitSlot(context.sdLayoutLeft, 6));
    innerRow.push(`${INDENT_STEP.repeat(6)}<View style={styles.masterOutlet}><Slot /></View>`);
    if (rightPos === ELayoutPosition.secondary) pushIf(innerRow, emitSlot(context.sdLayoutRight, 6));

    // Center column: [header-secondary] <inner row> [footer-secondary]
    const centerColumn: string[] = [];
    if (headerPos === ELayoutPosition.secondary) pushIf(centerColumn, emitSlot(context.sdLayoutHeader, 5));
    centerColumn.push(wrapRow("masterInner", 5, innerRow));
    if (footerPos === ELayoutPosition.secondary) pushIf(centerColumn, emitSlot(context.sdLayoutFooter, 5));

    // Main row: [left-primary] <center column> [right-primary]
    const mainRow: string[] = [];
    if (leftPos === ELayoutPosition.primary) pushIf(mainRow, emitSlot(context.sdLayoutLeft, 4));
    mainRow.push(wrapRow("masterCenter", 4, centerColumn));
    if (rightPos === ELayoutPosition.primary) pushIf(mainRow, emitSlot(context.sdLayoutRight, 4));

    // Root: [header-primary] <main row> [footer-primary]
    const rootChildren: string[] = [];
    if (headerPos === ELayoutPosition.primary) pushIf(rootChildren, emitSlot(context.sdLayoutHeader, 3));
    rootChildren.push(wrapRow("masterMain", 3, mainRow));
    if (footerPos === ELayoutPosition.primary) pushIf(rootChildren, emitSlot(context.sdLayoutFooter, 3));

    styleEntries.unshift({ name: "screen", style: { flex: 1 } });
    styleEntries.push({ name: "masterMain", style: { flex: 1, flexDirection: "row" } });
    styleEntries.push({ name: "masterCenter", style: { flex: 1 } });
    styleEntries.push({ name: "masterInner", style: { flex: 1, flexDirection: "row" } });
    styleEntries.push({ name: "masterOutlet", style: { flex: 1 } });

    const returnJsx: string = [
      `${INDENT_STEP.repeat(2)}<SafeAreaView style={styles.screen}>`,
      ...rootChildren,
      `${INDENT_STEP.repeat(2)}</SafeAreaView>`
    ].join("\n");

    const styleSheetBlock: string = [
      `const styles = StyleSheet.create({`,
      styleEntries.map((entry) => ReactNativeElementGenerator.formatStyleEntry(entry)).join(",\n"),
      `});`
    ].join("\n");

    return `${returnJsx}${FRAGMENT_STYLES_DELIMITER}${styleSheetBlock}`;
  }

  /** True when a layout slot contains a SiderMenu anywhere in its subtree — the slot that becomes the Drawer panel. */
  private slotHasMenu(di: IDisplayItem | undefined): boolean {
    if (!di) {
      return false;
    }
    const name: string = di.item?.name ?? "";
    if (name === "Menu" || name === "SiderMenu") {
      return true;
    }
    for (const child of di.children ?? []) {
      if (this.slotHasMenu(child)) {
        return true;
      }
    }
    return false;
  }

  /** A layout slot's Primary/Secondary/Invisible position, read from its <c>layoutPosition</c> property (RN mirror of the base getLayoutPosition). */
  private resolveLayoutPosition(di: IDisplayItem | undefined): ELayoutPosition {
    const layoutPosition: IDisplayItemProperty | undefined = di?.item?.properties?.find(
      (property: IDisplayItemProperty): boolean => property.name === "layoutPosition"
    );
    if (layoutPosition?.symbol?.fixedValue === undefined) {
      return ELayoutPosition.invisible;
    }
    return Number(layoutPosition.symbol.fixedValue) as ELayoutPosition;
  }

  private emitNode(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    depth: number,
    styleEntries: IStyleSheetEntry[],
    parent: IParentLayout,
    repeaterDepth: number = 0,
    inCellEditor: boolean = false
  ): IEmittedNode | null {
    if (!di || !di.item) {
      return null;
    }

    const indent: string = INDENT_STEP.repeat(depth);
    const componentName: string = di.item.name ?? "";
    const mapping: IComponentMapping | undefined = KUIKA_TAG_TO_REACT_NATIVE[componentName];

    if (!mapping) {
      return {
        // A `{/* … */}` comment is an expression container just as a visibility guard is: dropped bare into a
        // repeater's `render: (row) => ( … )` its leading brace reads as an object literal and Metro cannot parse
        // the screen. A Table column or Gallery item whose only child is unsupported would take the bundle down.
        jsx: `${indent}{/* kuika-rn: unsupported component "${componentName}" (id ${di.id}) — skipped */}`,
        usedTags: [],
        isExpressionContainer: true
      };
    }
    const rnTag: TReactNativeTag = mapping.tag;
    // Authorization is resolved here only for the JSX it produces (the hide-wrap and the
    // `editable`/`disabled` prop), which reference the `isAuthorized_di<id>` variable the C#
    // `ReactNativeScreenGenerator` declares via `useIsAuthorizedForRoles` — the same convention that
    // lets the input JSX reference the C#-declared `di<id>` useState. Node no longer emits the hook.
    const authorization: IAuthDirective | null = ReactNativeElementGenerator.resolveAuthorization(context, di);
    // The disable directive resolved once per node into the tag's prop (`editable`/`disabled`), or "".
    // Every emission path (strategy or inline) uses this single value, and assertDisableApplied below
    // verifies it actually landed — so no interactive tag can silently ship without its disable guard.
    const disableRendering: IDisableRendering = ReactNativeElementGenerator.resolveDisableRendering(
      rnTag,
      authorization,
      ReactNativeElementGenerator.resolveEnabledVariable(context, di, repeaterDepth)
    );
    const disablePropStr: string = disableRendering.prop;
    // The style-merged half of the same directive, for a tag with no disable prop of its own. Composed into the
    // node's OWN style prop below (strategies through ctx.registerStyle, containers through composeStyleProp),
    // so it blocks that view's whole subtree exactly as web's `pointer-events: none` does.
    const disabledStyleTerm: string = ReactNativeElementGenerator.disabledStyleTerm(
      styleEntries,
      disableRendering.boundaryExpr
    );

    const styleName: string = `di${di.id}`;
    const resolvedStyle: IResolvedStyle = this.resolveStyle(context, di);
    const resolved: Record<string, string | number> = resolvedStyle.style;
    // A grid column's spans, and the runtime width expression they need when they differ by breakpoint. Filled
    // by the grid block below; read by the container emission at the bottom, which is the only path a Col takes.
    let columnSpans: readonly IColumnSpanEntry[] = [];
    let columnWidthExpr: string = "";
    // CSS cascade emulation: `text-align` inherits on the web, so a container's alignment reaches the
    // text inside it. RN resolves every style in isolation, so the ancestor value is threaded down and
    // applied here — the node's own textAlign always wins.
    if (resolved.textAlign === undefined && parent.inheritedTextAlign !== undefined) {
      resolved.textAlign = parent.inheritedTextAlign;
    }
    // The same cascade for the rest of the typography, where one is being inherited (a table cell's column / row
    // style). Only the components whose web element lets colour and font inherit take it: a Label's text and an
    // Icon's glyph do, whereas a button or an input carries its own antd colours that an inherited one never beats.
    const ownTextStyle: Record<string, string | number> =
      ReactNativeElementGenerator.partitionStyle(resolved).textStyle;
    if (parent.inheritedTextStyle !== undefined && TEXT_STYLE_INHERITING_TAGS.has(rnTag)) {
      for (const [key, value] of Object.entries(parent.inheritedTextStyle)) {
        if (resolved[key] === undefined) {
          resolved[key] = value;
        }
      }
    }
    // Web parity (block-flow fill): with no explicit width, a component fills its parent's width in a
    // vertical container; in a horizontal one it stays content-sized. (Absolute-design screens have
    // already forced width in resolveStyle, so this only applies to ordinary flow layout.) Inline-flow
    // widgets (Button, Icon) are the exception — like the web's inline `<button>`/`<i>` (and Android
    // `wrap_content`) they hug their content unless the designer sets an explicit width; Button's
    // `block` prop is the opt-in to full width (KButton renders that as `alignSelf: "stretch"`).
    if (
      resolved.width === undefined &&
      !parent.isHorizontal &&
      (!CONTENT_WIDTH_COMPONENTS.has(componentName) || resolvedStyle.isBlockDisplay) &&
      !OVERLAY_COMPONENTS.has(componentName)
    ) {
      resolved.width = "100%";
    }
    // Web parity (inline placement): an inline element in a vertical flow sits where the INHERITED text-align
    // puts it — the ancestor's, never its own (a Button's own textAlign aligns its label). A runtime that pins
    // its alignSelf would otherwise stay flush left under a centred parent, so the ancestor's alignment is
    // emitted as the component's alignSelf. Left is the pin itself, and a block component stretches.
    if (
      SELF_ALIGNED_CONTENT_COMPONENTS.has(componentName) &&
      !parent.isHorizontal &&
      !resolvedStyle.isBlockDisplay &&
      resolved.alignSelf === undefined &&
      resolved.position !== "absolute" &&
      ReactNativeElementGenerator.getFixedPropertyValue(di, "block") !== "true"
    ) {
      const selfAlignment: string | undefined =
        parent.inheritedTextAlign === undefined ? undefined : FLEX_ALIGNMENT_FROM_TEXT_ALIGN[parent.inheritedTextAlign];
      if (selfAlignment !== undefined && selfAlignment !== "flex-start") {
        resolved.alignSelf = selfAlignment;
      }
    }
    // Col span → fractional width (Kuika/Ant-Design 24-unit column grid), the RN mirror of antd's
    // `.ant-col-<n> { flex: 0 0 <n/24>%; max-width: <n/24>% }`. A *width* — not a flex share — is what
    // makes the grid behave: three `xs=24` columns are each 100% wide, so the wrapping Row pushes them
    // onto their own lines exactly as the web does, while `xs=8` + `xs=16` still sit side by side.
    // (A flex share would have spread those three columns across one row instead.) `flex: 1` remains the
    // fallback for a span-less column, and applyBaseStyle drops it once a width is set.
    if (
      parent.isHorizontal &&
      GRID_COLUMN_COMPONENTS.has(componentName) &&
      resolved.flex === undefined &&
      resolved.width === undefined
    ) {
      columnSpans = ReactNativeElementGenerator.resolveColumnSpans(di);
      columnWidthExpr = ReactNativeElementGenerator.columnWidthExpression(columnSpans);
      // A column whose span is the same at every breakpoint keeps a plain StyleSheet width — byte-identical to
      // what it emitted before. A responsive one leaves `width` off the entry and resolves it per render
      // instead, because React Native has no media queries to do it in the stylesheet.
      if (columnWidthExpr === "" && columnSpans.length > 0) {
        resolved.width = ReactNativeElementGenerator.spanToWidth(columnSpans[0].span);
      }
    }
    // Stack spacing: the "Gap" property (model name `size`) becomes the flex `gap` between children —
    // the RN mirror of the web stacks' antd `<Space size>`. RN `gap` runs along the flex axis, so the
    // single property spaces HorizontalStack's row and VerticalStack's column alike. A designer-set gap
    // in the style object wins (the `undefined` guard), matching "resolved designer styles always win".
    if (mapping.gapProperty !== undefined && resolved.gap === undefined) {
      const gap: number | null = ReactNativeElementGenerator.resolveGap(di, mapping.gapProperty);
      if (gap !== null) {
        resolved.gap = gap;
      }
    }
    const merged: Record<string, string | number> = ReactNativeElementGenerator.applyBaseStyle(
      mapping.baseStyle,
      resolved,
      columnWidthExpr !== ""
    );
    // This node's own orientation governs how its children size their width (a row lays children out
    // horizontally, so they stay content-sized; any other container stacks vertically) and which flex
    // key carries which alignment intent. Read before the alignment pass, which never touches it.
    const selfIsHorizontal: boolean = merged.flexDirection === "row";
    const style: Record<string, string | number> = CONTENT_ALIGNING_TAGS.has(rnTag)
      ? ReactNativeElementGenerator.resolveContentAlignment(merged, selfIsHorizontal)
      : merged;
    // Page-init skeleton (RN mirror of Android's KShimmerLayout): a data-bearing component is wrapped in
    // <KShimmer>, which needs the component's OUTER box so inserting it changes no layout. Extracted before
    // the style is partitioned, because it also rewrites what stays on the component itself.
    const isSkeletonTarget: boolean = SKELETON_COMPONENTS.has(componentName);
    const shimmerBoxStyle: Record<string, string | number> = isSkeletonTarget
      ? ReactNativeElementGenerator.extractShimmerBoxStyle(style)
      : {};
    const partitioned: IPartitionedStyle = ReactNativeElementGenerator.partitionStyle(style);
    // Handed to every child of this node: its layout axis plus the textAlign the children inherit
    // (this node's own, or whatever it inherited when it set none).
    const childLayout: IParentLayout = {
      isHorizontal: selfIsHorizontal,
      inheritedTextAlign: typeof resolved.textAlign === "string" ? resolved.textAlign : undefined,
      inheritedTextStyle: ReactNativeElementGenerator.cascadeTextStyle(parent.inheritedTextStyle, ownTextStyle)
    };
    const testIdProp: string = di.friendlyName ? ` testID=${JSON.stringify(di.friendlyName)}` : "";
    const usedTags: TReactNativeTag[] = [rnTag];
    const runtimeComponents: string[] = [];
    let jsx: string;

    const strategy: TComponentStrategy | undefined = RN_COMPONENT_STRATEGIES.get(rnTag);
    if (strategy) {
      // Shared per-node state + helpers handed to the strategy. The helper methods close over
      // emitNode's locals, so strategies never reach into ReactNativeElementGenerator's privates.
      const emitContext: IComponentEmitContext = {
        di,
        depth,
        indent,
        componentName,
        styleName,
        style,
        partitioned,
        explicitTextAlign: resolvedStyle.explicitTextAlign,
        fillImageJsx: ReactNativeElementGenerator.buildFillImageJsx(
          resolvedStyle.fillImage,
          INDENT_STEP.repeat(depth + 1)
        ),
        animationProp: ReactNativeElementGenerator.buildAnimationProp(
          ReactNativeElementGenerator.resolveAnimation(context, di)
        ),
        authorization,
        testIdProp,
        registerStyle: (name: string, styleObj: Record<string, string | number>): string =>
          // The node's OWN style is where the disable boundary rides; a sub-style (a label, a content container)
          // is a different element and must not be dimmed twice.
          name === styleName
            ? ReactNativeElementGenerator.composeStyleProp(styleEntries, name, styleObj, disabledStyleTerm)
            : ReactNativeElementGenerator.registerStyle(styleEntries, name, styleObj),
        stateStyleProps: (specs: readonly IStateStylePropSpec[]): string =>
          this.buildStateStyleProps(context, di, styleName, styleEntries, specs),
        getFixedPropertyValue: (propertyName: string): string =>
          ReactNativeElementGenerator.getFixedPropertyValue(di, propertyName),
        getTranslatedTextExpression: (propertyName: string): string =>
          ReactNativeElementGenerator.getTranslatedTextExpression(context, di, propertyName),
        getBoundValueMarker: (propertyName: string): string =>
          ReactNativeElementGenerator.getBoundValueMarker(di, propertyName),
        isSetValueTarget: (): boolean => ReactNativeElementGenerator.collectSetValueTargets(context).has(toDiId(di.id)),
        getActionBoundMarker: (propertyName: string): string =>
          ReactNativeElementGenerator.getActionBoundMarker(di, propertyName),
        actionMarker: (): string => ` kuikaid='${di.id}'`,
        setterName: ReactNativeElementGenerator.toSetterName(styleName),
        collapseKeysVar: (): string | null => ReactNativeElementGenerator.resolveCollapseKeysVariable(context, di),
        focusRefVar: (): string | null => ReactNativeElementGenerator.resolveFocusRefVariable(context, di),
        disableProp: (): string => disablePropStr,
        disableWrapperProp: (): string =>
          disableRendering.disabledExpr === "" ? "" : ` disabled={${disableRendering.disabledExpr}}`,
        repeaterDepth,
        emitChild: (child: IDisplayItem, childDepth: number, options?: IEmitChildOptions): IEmittedNode | null =>
          this.emitNode(
            context,
            child,
            childDepth,
            styleEntries,
            ReactNativeElementGenerator.overrideInheritedText(childLayout, options),
            options?.repeaterDepth ?? repeaterDepth,
            options?.inCellEditor ?? inCellEditor
          ),
        controlledBinding: (kind: TControlledValueKind): IControlledBinding =>
          ReactNativeElementGenerator.resolveControlledBinding(kind, inCellEditor, styleName),
        cellEditorCommitProps: (component: string): string => (inCellEditor ? cellEditorCommitProps(component) : ""),
        structuralChild: (child: IDisplayItem): IStructuralChildContext =>
          this.buildStructuralChild(context, child, styleEntries)
      };
      // Strategy owns its tags; reconcile the shared accumulator with what it emitted.
      const emitted: IEmittedNode = strategy(emitContext);
      jsx = emitted.jsx;
      usedTags.length = 0;
      emitted.usedTags.forEach((tag) => usedTags.push(tag));
      emitted.runtimeComponents?.forEach((component) => runtimeComponents.push(component));
    } else {
      // Containers: ViewStyle rejects typography keys, so only the box subset is emitted. The render
      // kind (View vs ScrollView, and the axis) is resolved per node — the stacks are logically scroll
      // containers (HorizontalStack scrolls horizontally, VerticalStack vertically when height-bounded);
      // a generic overflow:scroll container is a plain vertical ScrollView (unchanged, single style).
      let containerViewStyle: Record<string, string | number> = { ...partitioned.viewStyle };
      const render: IContainerRender = ReactNativeElementGenerator.resolveContainerRender(
        componentName,
        di,
        containerViewStyle
      );
      const containerTag: "View" | "ScrollView" = render.tag;
      if (containerTag === "ScrollView") {
        // Scroll is the tag itself, not a style prop — RN's ScrollView has no `overflow` counterpart.
        delete containerViewStyle.overflow;
        usedTags.push("ScrollView");
        // RN's ScrollView ships `flexGrow: 1` in its own base style; the web stack it stands for is a div that
        // never grows. Inside a Col that shares a WRAPPING Row with other Cols, that grow made Yoga hand the
        // whole row's height to the first Col: a HorizontalStack of two labels stood 116dp tall, and the Cols
        // wrapped after it were pushed out of the row onto the next one (KOA-18644 — the TS10 badges on top of
        // the "serkan" button). A flex the node already carries — a scrolling Col's base `flex: 1` — is left alone.
        if (containerViewStyle.flex === undefined && containerViewStyle.flexGrow === undefined) {
          containerViewStyle = ReactNativeElementGenerator.sortStyleKeys({ ...containerViewStyle, flexGrow: 0 });
        }
      }
      // A Row/Col renders as KContainer, the box that can take the designer's onClick. A SCROLLING Row/Col stays
      // a raw ScrollView and carries no marker: RN's ScrollView has no press prop, so a press there is its own
      // feature (documented in ReactNativeEventCatalog.UnmappedEvents) rather than a silently dropped action.
      const isPressableContainer: boolean = rnTag === "Container" && containerTag === "View";
      const emittedTag: string = isPressableContainer ? "KContainer" : containerTag;
      const containerActionMarker: string = isPressableContainer ? ` kuikaid='${di.id}'` : "";
      if (isPressableContainer) {
        runtimeComponents.push("KContainer");
      }

      let openTagProps: string;
      if (render.splitContentContainer) {
        // ScrollView: box styles stay on `style` (the viewport), child-layout styles (gap/flexDirection/
        // alignment/padding) move to `contentContainerStyle` where RN actually applies them to the content.
        const split: ISplitContainerStyle = ReactNativeElementGenerator.splitContentContainerStyle(containerViewStyle);
        const styleProp: string = ReactNativeElementGenerator.composeStyleProp(
          styleEntries,
          styleName,
          split.box,
          columnWidthExpr,
          disabledStyleTerm
        );
        const contentName: string = `${styleName}Content`;
        const contentRegistered: string = ReactNativeElementGenerator.registerStyle(
          styleEntries,
          contentName,
          split.content
        );
        const contentProp: string = contentRegistered === "" ? "" : ` contentContainerStyle={styles.${contentName}}`;
        const horizontalProp: string = render.horizontal ? " horizontal" : "";
        openTagProps = `${testIdProp}${horizontalProp}${styleProp}${contentProp}`;
      } else {
        const styleProp: string = ReactNativeElementGenerator.composeStyleProp(
          styleEntries,
          styleName,
          containerViewStyle,
          columnWidthExpr,
          disabledStyleTerm
        );
        // A pressed state needs a press surface, and only KContainer is one — a plain View has no `pressed`
        // moment, so the delta would be a prop nothing could apply.
        const stateStyleProps: string = isPressableContainer
          ? this.buildStateStyleProps(context, di, styleName, styleEntries, PRESSED_BOX_SPEC)
          : "";
        // Only KContainer can host an animation: a raw View is not an Animated component, and the ScrollView
        // branch above is not one either.
        const animationProp: string = isPressableContainer
          ? ReactNativeElementGenerator.buildAnimationProp(ReactNativeElementGenerator.resolveAnimation(context, di))
          : "";
        openTagProps = `${testIdProp}${styleProp}${stateStyleProps}${animationProp}`;
      }

      const childParts: string[] = [];
      // A container's own Fill → Image goes in front of its children, so it paints under them.
      const containerFillJsx: string = ReactNativeElementGenerator.buildFillImageJsx(
        resolvedStyle.fillImage,
        INDENT_STEP.repeat(depth + 1)
      );
      if (containerFillJsx) {
        childParts.push(containerFillJsx);
        runtimeComponents.push("KBackgroundImage");
      }
      for (const child of di.children ?? []) {
        const emitted: IEmittedNode | null = this.emitNode(
          context,
          child,
          depth + 1,
          styleEntries,
          childLayout,
          repeaterDepth,
          inCellEditor
        );
        if (emitted) {
          childParts.push(emitted.jsx);
          emitted.usedTags.forEach((tag) => usedTags.push(tag));
          emitted.runtimeComponents?.forEach((component) => runtimeComponents.push(component));
        }
      }

      if (childParts.length === 0) {
        jsx = `${indent}<${emittedTag}${openTagProps}${containerActionMarker} />`;
      } else {
        jsx = [
          `${indent}<${emittedTag}${openTagProps}${containerActionMarker}>`,
          ...childParts,
          `${indent}</${emittedTag}>`
        ].join("\n");
      }
    }

    // Role-based authorization (mirrors web/native ReactSystemFunctions.hideIsNotAuthorized /
    // disableIsNotAuthorized): disable → the tag's editable/disabled prop, spliced by the emitting
    // path from the shared `disablePropStr`; hide → conditional render, applied here uniformly.
    // Fail loud if an interactive node dropped its disable prop — turns a silent authorization gap
    // into a codegen error. Must run BEFORE the hide-wrap: the guard inspects the bare opening tag,
    // and hide/disable are mutually exclusive (resolveAuthorization returns a single mode).
    ReactNativeElementGenerator.assertDisableApplied(rnTag, di, disableRendering, disabledStyleTerm, jsx);
    // Page-init skeleton, innermost of the three wrappers: a component the user is not allowed to see, or
    // that the designer hid, must render nothing at all — not a placeholder for something that never appears.
    if (isSkeletonTarget) {
      const shimmerStyleProp: string = ReactNativeElementGenerator.registerStyle(
        styleEntries,
        `${styleName}Shimmer`,
        shimmerBoxStyle
      );
      jsx = ReactNativeElementGenerator.wrapShimmer(jsx, indent, shimmerStyleProp);
      runtimeComponents.push("KShimmer");
    }
    // Designer visibility (di.item.visibility) — applied here, on the single path every component's JSX
    // passes through, so it covers the whole supported component set rather than per-tag opt-in. Web's
    // `withCommonEvents` HOC returns `<></>` when `visibility === "hidden"`, so conditional render — not
    // a style — is the faithful RN lowering.
    const visibilityVar: string | null = ReactNativeElementGenerator.resolveVisibilityVariable(
      context,
      di,
      repeaterDepth
    );
    // Either guard turns the node from an element into a `{… && (…)}` expression container, which a repeater's
    // item template has to wrap in a fragment to stay parseable (see IEmittedNode.isExpressionContainer).
    const guards: string[] = [];
    if (authorization && authorization.mode === "hide") {
      guards.push(authorization.authorizedExpr);
    }
    if (visibilityVar !== null) {
      guards.push(`${visibilityVar} !== "hidden"`);
    }
    let isExpressionContainer: boolean = false;
    if (guards.length > 0) {
      jsx = ReactNativeElementGenerator.wrapHidden(jsx, indent, guards.join(" && "));
      isExpressionContainer = true;
    }
    return { jsx, usedTags, runtimeComponents, isExpressionContainer };
  }

  /**
   * The `isVisible_di<id>` variable a node's JSX guards on, or null when the node is always rendered.
   *
   * A node is guarded when its designer visibility is `hidden`/`custom`, **or** when a
   * `ChangeVisibilityOf`/`ToggleVisibilityOf` action anywhere on the screen targets it (its declared type
   * is then irrelevant — the action can flip it). This mirrors web, where `InjectHiddenComponentVisibility`
   * + `InjectComponentVisibility2PageInit` + `DetectUiLogicVariables` register `isComp<id>Visible` for the
   * same three cases.
   *
   * The variable itself is **declared by the C# `ReactNativeScreenGenerator`** — as `useState` for a
   * genuinely controlled node, or as a `const … = "visible"` when C#'s authoritative action analysis finds
   * no visibility control. That is the same contract as `isAuthorized_di<id>`: this leg references the
   * name, the C# leg owns what it means. C# must therefore recognise a superset of the nodes guarded here,
   * which it does by re-deriving the identical predicate from the typed `ScreenModel`.
   */
  private static resolveVisibilityVariable(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    repeaterDepth: number = 0
  ): string | null {
    const isDeclaredControlled: boolean = ReactNativeElementGenerator.isControlledVisibilityType(
      di.item.visibility?.visibilityType
    );
    const isActionTarget: boolean = ReactNativeElementGenerator.collectVisibilityActionTargets(context).has(
      toDiId(di.id)
    );
    if (!isDeclaredControlled && !isActionTarget) {
      return null;
    }
    const slot: string = `isVisible_di${diToken(di.id)}`;
    const isRowScoped: boolean =
      repeaterDepth > 0 && !isActionTarget && di.item.visibility?.visibilityType === "custom";
    return isRowScoped ? ReactNativeElementGenerator.rowScopedSlotReference(slot, repeaterDepth) : slot;
  }

  /**
   * Whether a declared `visibility.visibilityType` is one this leg must guard — `hidden` or `custom`.
   *
   * <p>Accepts BOTH wire spellings on purpose. `EVisibilityType` currently crosses the wire as the string its
   * `EnumMember` names (`"hidden"`, `"custom"`) because `JsonStringEnumConverter` is registered for it, while its
   * sibling `EEditabilityType`'s converter is commented out and that one arrives as the ordinal — so the two
   * comparisons in this file look inconsistent only because the wire formats are. The asymmetry is not the risk;
   * the risk is that it is invisible: if that converter were ever dropped, a string-only comparison would match
   * nothing, NO guard would be emitted anywhere on any screen, and every string-based fixture in the test suite
   * would still pass. Accepting the ordinal too costs one comparison and removes a silent, total failure mode.</p>
   */
  /**
   * The reference a `custom` visibility/editability inside a **data repeater** reads: a CALL that takes the row
   * (`isEnabled_di83461(row)`), rather than the screen-scoped name every other component reads.
   *
   * <p>A repeater renders its subtree once per row, so "this Label is editable when `Current.FirstName` equals the
   * current user's" has a different answer per row — a single screen-level state cannot hold it. Until this, the
   * C# leg generated such a condition with no row variable, the `Current` operand resolved to nothing, and the
   * condition was dropped with a `// kuika-rn:` note: the component stayed permanently enabled (RNM-0188, reported
   * from a device on a Table column's Label).</p>
   *
   * <p>Only a **declared** `custom` condition takes this form. An action target must stay a screen-scoped state,
   * because `ChangeEnabledOf` writes through a setter and there is nothing per-row to write; and a plain
   * `hidden`/`disabled` declaration is already row-independent. The C# leg reads the row variable back out of the
   * call site, so the two legs cannot disagree about which row is meant.</p>
   */
  private static rowScopedSlotReference(slot: string, repeaterDepth: number): string {
    return `${slot}(${reactNativeRowVariableName(repeaterDepth - 1)})`;
  }

  private static isControlledVisibilityType(visibilityType: string | number | undefined): boolean {
    return (
      visibilityType === "hidden" ||
      visibilityType === "custom" ||
      visibilityType === VISIBILITY_TYPE_HIDDEN_ORDINAL ||
      visibilityType === VISIBILITY_TYPE_CUSTOM_ORDINAL
    );
  }

  /**
   * Display-item ids targeted by a visibility-mutating action on this screen, read off each action's
   * `componentName` parameter (whose `fixedValue` is the target id as a string). Ids that do not parse to
   * an integer are dropped: the designer stores special DIs in a `Minus`-prefixed form the RN target does
   * not emit as components, so they can never match a node here.
   */
  /**
   * Whether `action` is one of the named UI-logic actions, identified by its **definition id** and only
   * falling back to the label.
   *
   * <p>The id is the source of truth (see {@link UI_LOGIC_ACTION_DEFINITION_IDS}); the label is a
   * mutable cache of the definition name that drifts by creation path and project age. Matching the id
   * FIRST is what fixes the drift; keeping the label as a fallback costs nothing and keeps a project
   * whose action carries a correct label but an unrecognised definition id — a domain that re-seeded its
   * system actions — working exactly as it did before.</p>
   *
   * <p>Guid comparison is case-insensitive and lowercased with `toLowerCase` (locale-independent in JS,
   * unlike `toLocaleLowerCase`): the designer persists whatever casing the row carried, and a Turkish
   * locale must not turn a hex 'I' into 'ı' and stop matching.</p>
   */
  private static isUiLogicAction(action: IAction | undefined, definitionNames: ReadonlySet<string>): boolean {
    if (!action) {
      return false;
    }
    const definitionId: string = (action.actionDefinitionId ?? "").trim().toLowerCase();
    if (definitionId !== "") {
      for (const name of definitionNames) {
        if (UI_LOGIC_ACTION_DEFINITION_IDS.get(name) === definitionId) {
          return true;
        }
      }
    }
    return definitionNames.has(action.label ?? "");
  }

  private static collectVisibilityActionTargets(context: ICodeGenerationContext): ReadonlySet<number> {
    const memoized: ReadonlySet<number> | undefined = VISIBILITY_ACTION_TARGET_MEMO.get(context);
    if (memoized !== undefined) {
      return memoized;
    }
    const targets: Set<number> = new Set<number>();
    const allActions: IAction[] = context.allActions ?? [];
    for (const action of allActions) {
      if (!ReactNativeElementGenerator.isUiLogicAction(action, VISIBILITY_ACTION_LABELS)) {
        continue;
      }
      const targetParam: IActionParameter | undefined = action.parameters?.find(
        (p: IActionParameter): boolean => p?.name === VISIBILITY_ACTION_TARGET_PARAM
      );
      const rawTargetId: string = String(targetParam?.symbol?.fixedValue ?? "");
      if (!/^-?\d+$/.test(rawTargetId)) {
        continue;
      }
      targets.add(Number(rawTargetId));
    }
    VISIBILITY_ACTION_TARGET_MEMO.set(context, targets);
    return targets;
  }

  /**
   * The `isEnabled_di<id>` variable a node's disable prop reads, or null when nothing can change its
   * editability.
   *
   * A node gets one when its designer editability is `disabled`/`custom`, **or** when a
   * `ChangeEnabledOf`/`ToggleEditabilityOf` action targets it — the same three cases
   * {@link resolveVisibilityVariable} covers, because web registers `isComp<id>Enabled` for exactly
   * those (`Analyser.SetUiEditabilityVariables` + `DetectUiLogicVariables`).
   *
   * The variable is **declared by the C# `ReactNativeScreenGenerator`**, off this reference. Returning a
   * name for a tag with no disable prop would therefore declare a state nothing reads, so the caller
   * gates on {@link disableProp} producing something — a Label cannot be disabled on React Native, and
   * inventing a way to grey one out is a design decision this seam has no business making.
   *
   * Note the deliberate asymmetry with {@link resolveVisibilityVariable}, which compares STRINGS: the C#
   * `EVisibilityType` carries a `JsonStringEnumConverter`, so it arrives as `"hidden"`/`"custom"`, while
   * `EEditabilityType`'s converter is commented out and it arrives as the ordinal. The two comparisons
   * look inconsistent because the wire formats are; matching them here would silently stop matching.
   */
  private static resolveEnabledVariable(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    repeaterDepth: number = 0
  ): string | null {
    const editabilityType: EEditabilityType | undefined = di.editability?.editabilityType;
    const isDeclaredControlled: boolean =
      editabilityType === EEditabilityType.Disabled || editabilityType === EEditabilityType.Custom;
    const isActionTarget: boolean = ReactNativeElementGenerator.collectEnabledActionTargets(context).has(toDiId(di.id));
    if (!isDeclaredControlled && !isActionTarget) {
      return null;
    }
    const slot: string = `isEnabled_di${diToken(di.id)}`;
    const isRowScoped: boolean = repeaterDepth > 0 && !isActionTarget && editabilityType === EEditabilityType.Custom;
    return isRowScoped ? ReactNativeElementGenerator.rowScopedSlotReference(slot, repeaterDepth) : slot;
  }

  /**
   * The `collapseKeys_di<id>` variable a Collapse's `activeKeys` binds to, or null when no action opens or
   * closes its panels. Unlike visibility and editability there is no *declared* designer counterpart — a
   * Collapse's initial open set comes from its panels' own `defaultOpen`, which the uncontrolled component
   * already handles — so being an action target is the whole condition.
   */
  private static resolveCollapseKeysVariable(context: ICodeGenerationContext, di: IDisplayItem): string | null {
    return ReactNativeElementGenerator.collectCollapseActionTargets(context).has(toDiId(di.id))
      ? `collapseKeys_di${diToken(di.id)}`
      : null;
  }

  /**
   * The `focusRef_di<id>` handle a component's `focusRef` binds to, or null when no `SetFocusTo` targets it.
   *
   * Focus is the one screen-state action that is not state at all: React Native moves the keyboard by calling
   * a method on a component instance, so the screen needs a ref rather than a variable. The seam is otherwise
   * identical to the others — this leg references the name, the C# leg declares it — which is what lets the
   * emitted `focusRef_di5.current?.focus()` stay honest about being a ref while looking like every other
   * lowering at the call site.
   */
  private static resolveFocusRefVariable(context: ICodeGenerationContext, di: IDisplayItem): string | null {
    return ReactNativeElementGenerator.collectFocusActionTargets(context).has(toDiId(di.id))
      ? `focusRef_di${diToken(di.id)}`
      : null;
  }

  /** Display-item ids a `SetFocusTo` targets, read off its `element` parameter. */
  private static collectFocusActionTargets(context: ICodeGenerationContext): ReadonlySet<number> {
    const memoized: ReadonlySet<number> | undefined = FOCUS_ACTION_TARGET_MEMO.get(context);
    if (memoized !== undefined) {
      return memoized;
    }
    const targets: Set<number> = new Set<number>();
    for (const action of context.allActions ?? []) {
      // Matched by definition id first, label second — the drift fix the visibility and editability collectors
      // already use. A label is a mutable cache of the definition name; matching on it alone is what made a
      // re-seeded domain's SetFocusTo silently stop being recognised.
      if (!ReactNativeElementGenerator.isUiLogicAction(action, FOCUS_ACTION_LABELS)) {
        continue;
      }
      const targetParam: IActionParameter | undefined = action.parameters?.find(
        (p: IActionParameter): boolean => p?.name === FOCUS_ACTION_TARGET_PARAM
      );
      const rawTargetId: string = String(targetParam?.symbol?.fixedValue ?? "");
      if (!/^-?\d+$/.test(rawTargetId)) {
        continue;
      }
      targets.add(Number(rawTargetId));
    }
    FOCUS_ACTION_TARGET_MEMO.set(context, targets);
    return targets;
  }

  /** Display-item ids targeted by a collapse-panel action, read exactly as {@link collectVisibilityActionTargets} reads the visibility ones. */
  private static collectCollapseActionTargets(context: ICodeGenerationContext): ReadonlySet<number> {
    const memoized: ReadonlySet<number> | undefined = COLLAPSE_ACTION_TARGET_MEMO.get(context);
    if (memoized !== undefined) {
      return memoized;
    }
    const targets: Set<number> = new Set<number>();
    for (const action of context.allActions ?? []) {
      // Definition id first, label second — see the note in collectFocusActionTargets.
      if (!ReactNativeElementGenerator.isUiLogicAction(action, COLLAPSE_ACTION_LABELS)) {
        continue;
      }
      const targetParam: IActionParameter | undefined = action.parameters?.find(
        (p: IActionParameter): boolean => p?.name === VISIBILITY_ACTION_TARGET_PARAM
      );
      const rawTargetId: string = String(targetParam?.symbol?.fixedValue ?? "");
      if (!/^-?\d+$/.test(rawTargetId)) {
        continue;
      }
      targets.add(Number(rawTargetId));
    }
    COLLAPSE_ACTION_TARGET_MEMO.set(context, targets);
    return targets;
  }

  /** Display-item ids targeted by an editability-mutating action, read exactly as {@link collectVisibilityActionTargets} reads the visibility ones. */
  private static collectEnabledActionTargets(context: ICodeGenerationContext): ReadonlySet<number> {
    const memoized: ReadonlySet<number> | undefined = ENABLED_ACTION_TARGET_MEMO.get(context);
    if (memoized !== undefined) {
      return memoized;
    }
    const targets: Set<number> = new Set<number>();
    for (const action of context.allActions ?? []) {
      if (!ReactNativeElementGenerator.isUiLogicAction(action, ENABLED_ACTION_LABELS)) {
        continue;
      }
      const targetParam: IActionParameter | undefined = action.parameters?.find(
        (p: IActionParameter): boolean => p?.name === VISIBILITY_ACTION_TARGET_PARAM
      );
      const rawTargetId: string = String(targetParam?.symbol?.fixedValue ?? "");
      if (!/^-?\d+$/.test(rawTargetId)) {
        continue;
      }
      targets.add(Number(rawTargetId));
    }
    ENABLED_ACTION_TARGET_MEMO.set(context, targets);
    return targets;
  }

  /**
   * Display-item ids a <c>SetValueOf</c> action writes on this screen, read off each action's
   * `targetDiName` parameter exactly as {@link collectVisibilityActionTargets} reads `componentName`.
   *
   * Used by the components that are NOT controlled inputs (a Label): they own no state of their own, so their
   * value prop emits a literal or a binding and a write has nothing to change. Being named here makes such a
   * component read `di<id>` instead, which is also the reference the C# leg picks up off the emitted JSX to
   * declare that state — the same fragment-is-the-reference-list contract the visibility guards use.
   */
  private static collectSetValueTargets(context: ICodeGenerationContext): ReadonlySet<number> {
    const memoized: ReadonlySet<number> | undefined = SET_VALUE_TARGET_MEMO.get(context);
    if (memoized !== undefined) {
      return memoized;
    }
    const targets: Set<number> = new Set<number>();
    for (const action of context.allActions ?? []) {
      if (!ReactNativeElementGenerator.isUiLogicAction(action, SET_VALUE_ACTION_LABELS)) {
        continue;
      }
      const targetParam: IActionParameter | undefined = action.parameters?.find(
        (p: IActionParameter): boolean => p?.name === SET_VALUE_ACTION_TARGET_PARAM
      );
      const rawTargetId: string = String(targetParam?.symbol?.fixedValue ?? "");
      if (!/^-?\d+$/.test(rawTargetId)) {
        continue;
      }
      targets.add(Number(rawTargetId));
    }
    SET_VALUE_TARGET_MEMO.set(context, targets);
    return targets;
  }

  /**
   * Resolves a component's per-component authorization (di.authorization) to an emit directive,
   * mirroring the web/native ClientCodeGenerationBase gate: only when authorization is set, hide or
   * disable is on, and allowAllRoles is off. Hide takes precedence over disable. Returns null when
   * nothing is emitted (no config, allowAllRoles, or hide with no roles → always visible).
   */
  private static resolveAuthorization(context: ICodeGenerationContext, di: IDisplayItem): IAuthDirective | null {
    const authorization: IAuthorizationDto | undefined = di.authorization;
    if (!authorization || authorization.allowAllRoles || (!authorization.hide && !authorization.disable)) {
      return null;
    }
    const roleIds: string[] = ReactNativeElementGenerator.resolveRoleIds(context, authorization.permittedRoles ?? []);
    const mode: TAuthorizationMode = authorization.hide ? "hide" : "disable";
    if (mode === "hide" && roleIds.length === 0) {
      return null;
    }
    // Use a per-component variable name so the hook call can be hoisted to component top-level.
    // "false" for disable-with-no-roles means always disabled; no hook call is needed.
    const authorizedExpr: string = roleIds.length === 0 ? "false" : `isAuthorized_di${diToken(di.id)}`;
    return { mode, authorizedExpr, roleIds };
  }

  /**
   * Builds the role-match set: the permitted role GUIDs plus the resolved role names (from the
   * project roles). Emitting both lets the runtime match a user's role by id OR name — mirrors
   * ClientCodeGenerationBase. Falls back to GUIDs only when project roles are absent in the context.
   */
  private static resolveRoleIds(context: ICodeGenerationContext, permittedRoles: readonly string[]): string[] {
    const guids: string[] = permittedRoles.filter((roleId: string): boolean => roleId !== "");
    const projectRoles: IProjectRole[] = context.project?.roles ?? [];
    const result: string[] = [...guids];
    for (const role of projectRoles) {
      if (guids.includes(role.id) && !result.includes(role.name)) {
        result.push(role.name);
      }
    }
    return result;
  }

  /**
   * Wraps a node's JSX in a `{guardExpr && (...)}` conditional render — the RN "hide", used by both the
   * authorization gate and the designer visibility gate.
   */
  /**
   * Splits a component's resolved style into the box the {@link SKELETON_COMPONENTS} skeleton wrapper takes
   * and what stays on the component, MUTATING the style object it is handed (which is the node's own local,
   * not yet partitioned or emitted).
   *
   * The contract is "inserting the wrapper changes no layout":
   *  - sizes are **copied**, so the component keeps painting its own background/border at full size;
   *  - a **percentage** width/height is moved and re-expressed as `100%` of the wrapper, because 50% of a
   *    wrapper that is itself 50% of the parent would be 25% of the parent;
   *  - a percentage min/max is moved outright — the wrapper enforces it and the child stretches to fill;
   *  - margins, flex share, `alignSelf` and absolute placement are **moved**: they position the box in the
   *    parent, which is now the wrapper's job, and applying them twice would double the offset;
   *  - when a flex key moved, the component gets `flexGrow: 1` — not `flex: 1`, whose `flexBasis: 0` would
   *    collapse it to nothing inside a wrapper that is still sizing itself from its content.
   */
  private static extractShimmerBoxStyle(style: Record<string, string | number>): Record<string, string | number> {
    const box: Record<string, string | number> = {};
    for (const key of SHIMMER_BOX_SIZE_KEYS) {
      const value: string | number | undefined = style[key];
      if (value === undefined) {
        continue;
      }
      box[key] = value;
      if (typeof value !== "string" || !value.endsWith("%")) {
        continue;
      }
      if (key === "width" || key === "height") {
        style[key] = "100%";
      } else {
        delete style[key];
      }
    }
    let movedFlex: boolean = false;
    for (const key of SHIMMER_BOX_MOVE_KEYS) {
      const value: string | number | undefined = style[key];
      if (value === undefined) {
        continue;
      }
      box[key] = value;
      delete style[key];
      if (SHIMMER_FLEX_KEYS.has(key)) {
        movedFlex = true;
      }
    }
    if (movedFlex) {
      style.flexGrow = 1;
    }
    // Ordinal-sorted like every other emitted style block: the keys arrive in two passes (copy, then move), so
    // without sorting the emitted order would encode which pass happened to claim each key.
    return ReactNativeElementGenerator.sortStyleKeys(box);
  }

  /**
   * Wraps a node's JSX in the page-init skeleton. `loading` is the negation of the screen-scope
   * {@link PAGE_INIT_FINISHED_VARIABLE}, so the placeholder is up for exactly the page-init window and a
   * pull-to-refresh redraw raises it again for free (the redraw remounts the screen body, restarting that state).
   */
  private static wrapShimmer(jsx: string, indent: string, styleProp: string): string {
    const inner: string = jsx
      .split("\n")
      .map((line: string): string => `${INDENT_STEP}${line}`)
      .join("\n");
    return [
      `${indent}<KShimmer loading={!${PAGE_INIT_FINISHED_VARIABLE}}${styleProp}>`,
      inner,
      `${indent}</KShimmer>`
    ].join("\n");
  }

  private static wrapHidden(jsx: string, indent: string, guardExpr: string): string {
    const inner: string = jsx
      .split("\n")
      .map((line: string): string => `${INDENT_STEP}${line}`)
      .join("\n");
    return `${indent}{${guardExpr} && (\n${inner}\n${indent})}`;
  }

  /**
   * The disable prop for a node, or "" when nothing disables it. Single source of truth for how each RN
   * tag renders "not interactive right now": TextInput uses `editable`, Pressable uses `disabled`;
   * non-interactive tags (Text/View) have no disable affordance, mirroring the web (a Label cannot be
   * disabled). The exhaustive switch (`never` arm) makes adding a new `TReactNativeTag` a compile error
   * here until its disable shape is decided — so the concern can never be silently forgotten for a new
   * tag.
   *
   * Two independent sources can disable a node and they are ANDed, not ranked: role authorization
   * (`isAuthorized_di<id>`) and designer editability (`isEnabled_di<id>` — a declared `disabled`/`custom`
   * editability, or a `ChangeEnabledOf`/`ToggleEditabilityOf` action). They share one prop slot, so
   * emitting them separately would produce a duplicate attribute where the second silently wins;
   * combining them here is what makes "authorization disables it AND an action re-enables it" resolve
   * the safe way round. Web does the same in `React_UIGenerator` (`editability={state.isCompXEnabled &&
   * state.isCompXAuthorizationEnabled}`). With a single source the emitted text is unchanged.
   */
  static disableProp(
    tag: TReactNativeTag,
    authorization: IAuthDirective | null,
    enabledVar: string | null = null
  ): string {
    return ReactNativeElementGenerator.resolveDisableRendering(tag, authorization, enabledVar).prop;
  }

  /**
   * How a node renders "not interactive right now": the tag's own disable prop when it has one, otherwise the
   * **boundary** — a `pointerEvents: "none"` + dimming delta merged into the node's own style, which is precisely
   * what the web target does for every component through `withCommonEvents`
   * (`editability === "disabled" → pointerEvents: "none", opacity: 0.7`).
   *
   * <p>The boundary exists because "does this tag declare a `disabled` prop" was always the wrong question. Web
   * blocks a disabled Row, Table, Tabs, Calendar or Icon without any such prop — `props.style` reaches the
   * element and `pointer-events: none` rides in on it, taking the whole subtree with it. Android reaches the same
   * result from the other side (`KtSystemFunctions.changeEnabledOf` sets `isEnabled` recursively). Extending the
   * per-tag prop switch fifty times would have reproduced web's behaviour one component at a time; merging a
   * style delta reproduces its mechanism, so a tag added later is covered the day it is added.</p>
   *
   * <p>Because RN ANDs role authorization into the same slot, every tag left without either form is also a
   * role-authorization hole — an unauthorized user keeps a live control. The remaining "" arm is therefore not a
   * TODO list: it is the set where web genuinely does not block either (its root element drops `props.style`, or
   * whitelists the keys it forwards), and matching web is the contract.</p>
   */
  private static resolveDisableRendering(
    tag: TReactNativeTag,
    authorization: IAuthDirective | null,
    enabledVar: string | null
  ): IDisableRendering {
    const isAuthDisable: boolean = authorization !== null && authorization.mode === "disable";
    if (!isAuthDisable && enabledVar === null) {
      return { prop: "", boundaryExpr: "", disabledExpr: "" };
    }
    // The positive form ("may the user interact?") and the negative one, kept as a pair so `editable`
    // and `disabled` can never disagree. `&&` binds looser than `!==`, so neither needs parenthesising.
    const enabledTerms: string[] = [];
    const disabledTerms: string[] = [];
    if (isAuthDisable) {
      enabledTerms.push(authorization!.authorizedExpr);
      disabledTerms.push(`!${authorization!.authorizedExpr}`);
    }
    if (enabledVar !== null) {
      enabledTerms.push(`${enabledVar} !== "disabled"`);
      disabledTerms.push(`${enabledVar} === "disabled"`);
    }
    const editableExpr: string = enabledTerms.join(" && ");
    const disabledExpr: string = disabledTerms.join(" || ");
    /** The tag carries its own disable prop — no style boundary, the prop already stops the interaction. */
    const withProp: (prop: string) => IDisableRendering = (prop: string): IDisableRendering => ({
      prop,
      boundaryExpr: "",
      disabledExpr
    });
    switch (tag) {
      case "TextInput":
        return withProp(` editable={${editableExpr}}`);
      case "Pressable":
        return withProp(` disabled={${disabledExpr}}`);
      case "Switch":
      case "Checkbox":
      case "FloatButton":
      case "Rating":
      case "Slider":
      case "Signature":
      case "ColorPicker":
      // The editor's text area is a real input, so an authorization disable has to stop typing in it rather than
      // merely dim it — KMDEditor's `disabled` closes its own TextInput.
      case "MDEditor":
      case "RangeSlider":
      case "Select":
      // A language selector is a select: an authorization disable has to close it, not just dim it.
      case "LanguageSelector":
      case "DatePicker":
      case "YearPicker":
      case "DateRange":
      case "RadioGroup":
      // The bar owns the selection, so disabling it is what stops every one of its buttons responding; the
      // buttons themselves read that from the group's context rather than each taking a directive.
      case "RadioButtonGroup":
      case "LocationSearch":
      // The web LocationPicker declares no `disabled` prop, so a disable directive is a no-op in the browser and
      // an unauthorized user can still move the pin. KLocationPicker takes one and stops the tap, which closes
      // that hole rather than reproducing it.
      case "LocationPicker":
      // Maps is a picker as well as a repeater: web's `isPicker` mode turns every map tap into a value write, so an
      // authorization disable that left it interactive would be the same hole as the LocationPicker one above.
      // KMaps takes `disabled` and drops both the map tap and the marker tap.
      case "Maps":
      // A Tree is interactive twice over — a node selection and a checkbox both write screen state a designer
      // action can read — so leaving it live under a disable directive would be the LocationPicker hole again.
      // KTree takes `disabled` and drops the selection, the checkboxes and the expand caret alike.
      case "Tree":
      // A Label is interactive whenever the designer gave it an onClick, and a disabled one that still fires it is
      // indefensible — so it takes a real prop rather than the style boundary. (Its `style` is a TextStyle, which
      // has no `pointerEvents` to ride on anyway.) This is a deliberate step AHEAD of web, where the click keeps
      // working because `onClick` sits on the unstyled container — a web defect, not a contract. Found on a device:
      // a clickable Label in a Table with a `custom` editability condition stayed clickable either way (RNM-0188).
      case "Text":
      // A tab header and a step marker are the thing the designer disabled; their CONTENT is not. Both components
      // take `style` for their content container, so the style boundary killed everything inside the tab/step while
      // leaving the header selectable — the exact opposite of the intent. The prop goes to the parent (KTab /
      // KStepper), which owns header selectability. Found on a device (RNM-0188).
      case "TabItem":
      case "StepperItem":
        return withProp(` disabled={${disabledExpr}}`);

      // ---- No disable prop of their own — web blocks these through the element's own style ----
      // A clickable Row/Col/Panel, a Table's row press and pagination, a Tabs bar, a Calendar's navigation, a
      // clickable Icon: all of them are live on RN today and dead on web, because `props.style` reaches the
      // component's root element and carries `pointer-events: none` into the whole subtree. The boundary is that
      // same delta, so these need no per-tag prop — and none of them is a role-authorization hole any more.
      case "View":
      case "ScrollView":
      case "Container":
      case "Panel":
      case "Icon":
      case "Image":
      case "Thumbnail":
      case "Timeline":
      case "Calendar":
      case "NodeTree":
      case "BigFileUpload":
      case "WebView":
      case "VideoPlayer":
      case "MicroApp":
      case "Modal":
      case "Drawer":
      case "FloatPanel":
      case "Tab":
      case "Stepper":
      case "Collapse":
      case "CollapsePanel":
      case "Menu":
      case "MenuItem":
      case "SubMenu":
      case "ContentMenu":
      case "ContentMenuItem":
      case "ContentMenuPanel":
      case "Breadcrumb":
      case "Pagination":
      case "Markdown":
      case "Recaptcha":
      case "Stopwatch":
      case "PaInteractionButtons":
      case "ProcessAutomation":
      // A single segment takes no disable directive of its own — the bar it belongs to owns that state (see
      // RadioButtonGroup) — but a standalone RadioButton has no bar, so the boundary is what stops it.
      case "RadioButton":
      case "Chat":
      // The legend toggles and tooltips these two own are interactions, and web blocks them with the rest of the
      // subtree. (`Chart` and `RadialChart` below do NOT: their web components whitelist the style keys they
      // forward, so `pointer-events` never reaches the canvas there.)
      case "GaugeChart":
      case "MixedChart":
      case "Table":
      case "Gallery":
      case "FlexGrid":
      case "Carousel":
      // Nothing interactive is lost on these, but web dims them with everything else, and the designer's authored
      // Disabled state styles have to apply to them on RN too or a disabled screen looks different on the two
      // targets.
      case "Badge":
      case "Divider":
      case "Spacer":
      case "ProgressBar":
      case "ProgressCircle":
      case "QRRenderer":
      case "BarcodeRenderer":
      case "Countdown":
      case "CountUp":
        return { prop: "", boundaryExpr: disabledExpr, disabledExpr };

      // ---- Nothing at all: web does not block these either, and matching web is the contract ----
      // Their web components drop or whitelist `props.style` on the root, so `pointer-events: none` never
      // reaches them in the browser: a boundary here would make RN stricter than web, not equal to it.
      case "PivotTable":
      case "Chart":
      case "RadialChart":
      // The web Marquee is not wrapped by `withCommonEvents` at all, so web ignores an editability — AND a
      // visibility — on one. That is a latent web defect with its own ticket, not an RN gap to lead on.
      case "Marquee":
      // The web Tooltip hands `style` to the antd popup, never to the anchor, so `pointer-events: none` never
      // reaches the wrapped child; KTooltip exposes no `style` prop either. The authored content inside
      // carries its own disable, exactly as it does outside a Tooltip.
      case "Tooltip":
        return { prop: "", boundaryExpr: "", disabledExpr };
      default: {
        const exhaustive: never = tag;
        return exhaustive;
      }
    }
  }

  /**
   * Fail-loud codegen guard: when a node is disabled, verify the emitting path actually applied it — the tag's
   * own prop spliced into the opening tag, or the style boundary composed into that tag's `style`.
   *
   * <p>A dropped disable is a silent authorization gap (a live control shipped to a user who may not use it), so
   * we throw rather than emit it. Scoped to the opening-tag line — the only place either form belongs — so a
   * child's JSX concatenated into a container's string can never mask a real omission. A no-op when neither form
   * is in play (nothing disables the node, or web does not block its counterpart either).</p>
   */
  static assertDisableApplied(
    tag: TReactNativeTag,
    di: IDisplayItem,
    rendering: IDisableRendering,
    disabledStyleTerm: string,
    jsx: string
  ): void {
    const openingLine: string = jsx.split("\n", 1)[0];
    // A tooltip-wrapped node satisfies both checks through its wrapper: `KTooltip` owns the press surface and
    // carries ` disabled={<expr>}` (ctx.disableWrapperProp), while the component's own prop / style boundary sits
    // on the inner line where the dimming belongs. The directive expression on the outermost tag is the invariant.
    const appliedOnWrapper: boolean =
      rendering.disabledExpr !== "" && openingLine.includes(` disabled={${rendering.disabledExpr}}`);
    if (appliedOnWrapper) {
      return;
    }
    if (rendering.prop !== "" && !openingLine.includes(rendering.prop.trim())) {
      throw new Error(
        `RN codegen: interactive <${tag}> (di${di.id}) dropped its disable guard — the strategy must splice ctx.disableProp()`
      );
    }
    if (disabledStyleTerm !== "" && !openingLine.includes(disabledStyleTerm)) {
      throw new Error(
        `RN codegen: <${tag}> (di${di.id}) dropped its disable boundary — the strategy must register its own style through ctx.registerStyle(ctx.styleName, …)`
      );
    }
  }

  /** State var name to its useState setter ("di100" becomes "setDi100"). */
  private static toSetterName(stateVar: string): string {
    return `set${stateVar.charAt(0).toUpperCase()}${stateVar.slice(1)}`;
  }

  /**
   * Merges a component's RN layout defaults under the resolved designer styles. Resolved
   * keys always win; the `flex` default (Col width sharing) is also skipped when the
   * designer set an explicit width. Output keys are re-sorted so emission stays byte-stable.
   */
  static applyBaseStyle(
    baseStyle: Record<string, string | number> | undefined,
    resolved: Record<string, string | number>,
    widthResolvedAtRender: boolean = false
  ): Record<string, string | number> {
    const merged: Record<string, string | number> = { ...resolved };
    if (baseStyle) {
      for (const [key, value] of Object.entries(baseStyle)) {
        if (merged[key] !== undefined) {
          continue;
        }
        if (key === "flex" && (merged.width !== undefined || widthResolvedAtRender)) {
          continue;
        }
        merged[key] = value;
      }
    }
    // Always re-sort — including the no-baseStyle case — so a key injected after the resolved style was
    // sorted (the block-flow `width` fill, a stack's `gap`) still emits in a byte-stable position.
    return ReactNativeElementGenerator.sortStyleKeys(merged);
  }

  /**
   * Key-sorts a style object with a locale-independent comparison, so a key injected after the
   * resolved style was sorted still emits in a byte-stable position. Every style that reaches the
   * StyleSheet passes through here.
   */
  private static sortStyleKeys(style: Record<string, string | number>): Record<string, string | number> {
    const sorted: Record<string, string | number> = {};
    for (const key of Object.keys(style).sort(compareOrdinal)) {
      sorted[key] = style[key];
    }
    return sorted;
  }

  /**
   * The value + change-handler expressions a controlled input binds to. Outside a table cell that is the screen's
   * own `di<id>` / `setDi<id>` state (declared by the C# assembler). Inside an editable column's editor it is the
   * cell buffer KTable passes to `renderEditor` — one value per (row, column) — with the coercion the input's
   * value type needs, since the buffer is text.
   */
  private static resolveControlledBinding(
    kind: TControlledValueKind,
    inCellEditor: boolean,
    styleName: string
  ): IControlledBinding {
    if (!inCellEditor) {
      return { valueExpr: styleName, setterExpr: ReactNativeElementGenerator.toSetterName(styleName) };
    }
    // A location value is a place — an object with an address and two coordinates — and the editable table's cell
    // buffer is a single string per row (`cell.value` / `cell.onChangeValue`), so there is nothing there to hold
    // one. Such a component binds the SCREEN-level state instead: every row's editor then shares one place, which
    // is a documented limitation rather than a screen that does not compile. A per-row place needs the cell buffer
    // to carry a typed value, which is its own change.
    // An upload result is the same shape of problem — an object holding the batch's ids and names — and binds the
    // screen state for the same reason.
    if (kind === "location" || kind === "upload") {
      return { valueExpr: styleName, setterExpr: ReactNativeElementGenerator.toSetterName(styleName) };
    }
    if (kind === "boolean") {
      return {
        valueExpr: `cell.value === "true"`,
        setterExpr: `(next: boolean): void => cell.onChangeValue(String(next))`
      };
    }
    if (kind === "number") {
      return {
        valueExpr: `Number(cell.value) || 0`,
        setterExpr: `(next: number): void => cell.onChangeValue(String(next))`
      };
    }
    return { valueExpr: "cell.value", setterExpr: "cell.onChangeValue" };
  }

  /**
   * The typography a node's children inherit: what it inherited, under its own. `undefined` — nothing to inherit —
   * stays `undefined` whatever the node sets itself, which keeps every screen outside a table cell byte-identical:
   * this target has never cascaded a container's colour, and only the table cell seeds a cascade to start from.
   * The alignment is carried by {@link IParentLayout.inheritedTextAlign} and is left out here.
   */
  private static cascadeTextStyle(
    inherited: Readonly<Record<string, string | number>> | undefined,
    own: Record<string, string | number>
  ): Readonly<Record<string, string | number>> | undefined {
    if (inherited === undefined) {
      return undefined;
    }
    const cascaded: Record<string, string | number> = { ...inherited };
    for (const [key, value] of Object.entries(own)) {
      if (key !== "textAlign") {
        cascaded[key] = value;
      }
    }
    return cascaded;
  }

  /** The parent layout a strategy's child is emitted under, with the inherited text the strategy overrides. */
  private static overrideInheritedText(layout: IParentLayout, options: IEmitChildOptions | undefined): IParentLayout {
    if (options?.inheritedTextAlign === undefined && options?.inheritedTextStyle === undefined) {
      return layout;
    }
    return {
      ...layout,
      inheritedTextAlign: options?.inheritedTextAlign ?? layout.inheritedTextAlign,
      inheritedTextStyle: options?.inheritedTextStyle ?? layout.inheritedTextStyle
    };
  }

  /**
   * Builds the {@link IStructuralChildContext} for a display item a data-repeater strategy renders itself (a
   * Table's header / row / column). Styles resolve through the same pipeline as a node's, so a column's
   * designer styling behaves identically to any other component's — it is just carried on the emitted column
   * object instead of on a JSX element.
   */
  private buildStructuralChild(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    styleEntries: IStyleSheetEntry[]
  ): IStructuralChildContext {
    const styleName: string = `di${di.id}`;
    return {
      di,
      registerStyle: (): string => {
        // A structural child is a value on the emitted column/header object, not an element, so it has
        // nowhere to host the background-image element — its bitmap fill is dropped here.
        const resolved: Record<string, string | number> = this.resolveStyle(context, di).style;
        const partitioned: Record<string, string | number> =
          ReactNativeElementGenerator.partitionStyle(resolved).viewStyle;
        // A table header / row / column is the table's own grid, where an authored height is a minimum — see
        // tableSectionBoxStyle. Applied here rather than at the three call sites in emitTable so a section added
        // later (a footer row) cannot be the one that forgets.
        const viewStyle: Record<string, string | number> = TABLE_SECTION_COMPONENTS.has(di.item?.name ?? "")
          ? tableSectionBoxStyle(partitioned)
          : partitioned;
        // A structural child needs the bare `styles.diN` reference, not a `style=` prop: it lands as a
        // value inside the emitted column/header object.
        const accessor: string | null = ReactNativeElementGenerator.registerStyleEntry(
          styleEntries,
          styleName,
          viewStyle
        );
        return accessor ?? "";
      },
      registerTextStyle: (): string => {
        const textStyle: Record<string, string | number> = ReactNativeElementGenerator.partitionStyle(
          this.resolveStyle(context, di).style
        ).textStyle;
        return ReactNativeElementGenerator.registerStyleEntry(styleEntries, `${styleName}Text`, textStyle) ?? "";
      },
      registerStateStyle: (state: TStyleStateDelta, pick: TStateStylePick): string => {
        const partitioned: IPartitionedStyle | null = this.resolveStateStyle(context, di, state);
        if (partitioned === null) {
          return "";
        }
        const entryName: string = `${styleName}${state.charAt(0).toUpperCase()}${state.substring(1)}${
          pick === "text" ? "Text" : ""
        }`;
        const accessor: string | null = ReactNativeElementGenerator.registerStyleEntry(
          styleEntries,
          entryName,
          ReactNativeElementGenerator.pickStateHalf(partitioned, pick)
        );
        return accessor ?? "";
      },
      visibilityGuard: (): string => {
        // A structural child is a value in a prop the repeater evaluates ONCE (a column object, a series
        // literal), not something rendered per row — so it stays screen-scoped even inside a repeater.
        const visibilityVar: string | null = ReactNativeElementGenerator.resolveVisibilityVariable(context, di);
        return visibilityVar === null ? "" : `${visibilityVar} !== "hidden"`;
      },
      resolveTextStyle: (): Record<string, string | number> =>
        ReactNativeElementGenerator.partitionStyle(this.resolveStyle(context, di).style).textStyle,
      getFixedPropertyValue: (propertyName: string): string =>
        ReactNativeElementGenerator.getFixedPropertyValue(di, propertyName),
      getTranslatedTextExpression: (propertyName: string): string =>
        ReactNativeElementGenerator.getTranslatedTextExpression(context, di, propertyName),
      getBoundValueMarker: (propertyName: string): string =>
        ReactNativeElementGenerator.getBoundValueMarker(di, propertyName),
      getActionBoundMarker: (propertyName: string): string =>
        ReactNativeElementGenerator.getActionBoundMarker(di, propertyName)
    };
  }

  /** Adds a StyleSheet entry when non-empty and returns the JSX style prop (or empty string). */
  private static registerStyle(
    styleEntries: IStyleSheetEntry[],
    name: string,
    style: Record<string, string | number>
  ): string {
    const accessor: string | null = ReactNativeElementGenerator.registerStyleEntry(styleEntries, name, style);
    return accessor === null ? "" : ` style={${accessor}}`;
  }

  /**
   * Adds a StyleSheet entry when non-empty and returns the expression that reads it back
   * (`styles.di5`), or null when there was nothing to register.
   *
   * <p>Separate from {@link registerStyle} because not every registered style becomes a `style=` prop: a
   * state delta becomes `pressedStyle={…}`, and a table column's style becomes a value inside the emitted
   * column object. All three need the same "skip when empty" rule and the same accessor spelling —
   * negative DI ids produce names like `di-2` which are not valid JS identifiers, so those read through
   * bracket notation (`styles["di-2"]`) or the generated screen would not parse.</p>
   */
  private static registerStyleEntry(
    styleEntries: IStyleSheetEntry[],
    name: string,
    style: Record<string, string | number>
  ): string | null {
    if (Object.keys(style).length === 0) {
      return null;
    }
    styleEntries.push({ name, style });
    const isIdentifier: boolean = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name);
    return isIdentifier ? `styles.${name}` : `styles["${name}"]`;
  }

  /**
   * The state-style props for one node: for every state the component declares support for, the delta the
   * designer authored, narrowed and partitioned through exactly the same pipeline as the base style.
   *
   * <p>Reusing the base pipeline is the point. The web target projects state styles through a second,
   * hand-written property walk (`React_UIGenerator.GenerateStateBlock` — 200 lines mirroring the base
   * emitter's decisions), which is why the two have already drifted. Here a delta is assembled back into an
   * `IKuikaStyle` and run through `StyleHelper` → {@link toReactNativeStyle} → {@link partitionStyle}, so a
   * `pressed` background resolves by the same rules as a resting one, gradient conversion and transform
   * rewriting included, and a new advanced style key reaches states the day it reaches the base.</p>
   */
  private buildStateStyleProps(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    styleName: string,
    styleEntries: IStyleSheetEntry[],
    specs: readonly IStateStylePropSpec[]
  ): string {
    const resolved: Map<TStyleStateDelta, IPartitionedStyle | null> = new Map<
      TStyleStateDelta,
      IPartitionedStyle | null
    >();
    const props: string[] = [];
    for (const spec of specs) {
      if (!resolved.has(spec.state)) {
        resolved.set(spec.state, this.resolveStateStyle(context, di, spec.state));
      }
      const partitioned: IPartitionedStyle | null | undefined = resolved.get(spec.state);
      if (partitioned === null || partitioned === undefined) {
        continue;
      }
      const half: Record<string, string | number> = ReactNativeElementGenerator.pickStateHalf(partitioned, spec.pick);
      const entryName: string = ReactNativeElementGenerator.stateStyleName(styleName, spec);
      const accessor: string | null = ReactNativeElementGenerator.registerStyleEntry(styleEntries, entryName, half);
      if (accessor !== null) {
        props.push(` ${spec.prop}={${accessor}}`);
      }
    }
    return props.join("");
  }

  /** The part of a partitioned state delta a spec asks for. */
  private static pickStateHalf(partitioned: IPartitionedStyle, pick: TStateStylePick): Record<string, string | number> {
    if (pick === "view") {
      return partitioned.viewStyle;
    }
    if (pick === "text") {
      return partitioned.textStyle;
    }
    return { ...partitioned.viewStyle, ...partitioned.textStyle };
  }

  /** The StyleSheet entry name for a state delta: `di5Pressed`, and `di5PressedText` for its typography half. */
  private static stateStyleName(styleName: string, spec: IStateStylePropSpec): string {
    const state: string = spec.state.charAt(0).toUpperCase() + spec.state.substring(1);
    const half: string = spec.pick === "text" ? "Text" : "";
    return `${styleName}${state}${half}`;
  }

  /**
   * The designer's style delta for one interaction state, narrowed to React Native, or null when the state
   * was never authored (or authored with nothing RN can express).
   *
   * <p>The six section deltas are reassembled into a throwaway {@link IKuikaStyle} — they are field-for-field
   * subsets of the section models, which is what makes the base pipeline reusable — and each section is
   * COPIED rather than referenced: `StyleHelper`'s converters mutate what they are handed (they null out a
   * consumed `borderId`, they delete a unit whose value is unset), and the design must not change because it
   * was projected. The identity fields those models require but a delta has no notion of (`borderId`,
   * `kuikaid`, …) are supplied empty, which is also what makes the project-level style collections not apply:
   * a state delta is the designer's own override, never a named project style.</p>
   *
   * <p><b>One known divergence from the web target.</b> A state shadow with a colour but zero blur AND zero
   * spread — a hard-edged offset shadow — is emitted by web's state block and dropped here, because the
   * shared `StyleHelper.getShadowStyle` guards on `b === 0 && s === 0` and that guard governs the base style
   * on both targets. Changing it would change the web target's output, which is not this card's scope;
   * duplicating the shadow rule for states is exactly the drift this method exists to avoid.</p>
   */
  private resolveStateStyle(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    state: TStyleStateDelta
  ): IPartitionedStyle | null {
    if (!di.styleObj) {
      return null;
    }
    const kuikaid: string = di.id.toString();
    const delta: IKuikaStyle = {};
    const border: IBorderStateStyles | undefined = pickStateDelta(di.styleObj.border, state);
    if (border !== undefined) {
      delta.border = { ...border, borderId: null, kuikaid } as IStylingBorder;
    }
    const shadow: IShadowStateStyles | undefined = pickStateDelta(di.styleObj.shadow, state);
    if (shadow !== undefined) {
      delta.shadow = { ...shadow, shadowId: null, shadowIds: null, kuikaid } as IStylingShadow;
    }
    const fill: IFillStateStyles | undefined = pickStateDelta(di.styleObj.fill, state);
    if (fill !== undefined) {
      delta.fill = { ...fill, fillId: null, fillIds: null, kuikaid } as IFillStyle;
    }
    const position: IPositionStateStyles | undefined = pickStateDelta(di.styleObj.position, state);
    if (position !== undefined) {
      delta.position = { ...position, positionId: null, positionIds: null, kuikaid } as IStylingPosition;
    }
    const text: ITypographyStateStyles | undefined = pickStateDelta(di.styleObj.text, state);
    if (text !== undefined) {
      delta.text = { ...text, typoId: null, typoIds: null, kuikaid } as IStylingTypography;
    }
    const effects: IEffectsStateStyles | undefined = pickStateDelta(di.styleObj.effects, state);
    if (effects !== undefined) {
      delta.effects = { ...effects, effectsId: null, kuikaid } as IStylingEffects;
    }
    if (Object.keys(delta).length === 0) {
      return null;
    }
    const htmlStyle: Record<string, unknown> = StyleHelper.convertToHtmlStyleFromStyleObj(
      context,
      delta,
      kuikaid,
      context.generateType
    ) as Record<string, unknown>;
    const rnStyle: Record<string, string | number> = ReactNativeElementGenerator.toReactNativeStyle(htmlStyle);
    if (Object.keys(rnStyle).length === 0) {
      return null;
    }
    return ReactNativeElementGenerator.partitionStyle(rnStyle);
  }

  /**
   * Resolves the DI's designer styles via StyleHelper, then narrows them to the RN-safe subset — and hands
   * back the one thing the narrowing cannot carry: a bitmap {@link IFillImage} background.
   *
   * <p>Reported together because they come out of ONE `StyleHelper` pass. The alternative — a second method
   * that re-runs the whole style pipe just to read `background-image` — would double the most expensive step
   * of emitting a node, and would leave two places that could disagree about what a fill resolves to.</p>
   */
  private resolveStyle(context: ICodeGenerationContext, di: IDisplayItem): IResolvedStyle {
    let resolved: Record<string, string | number> = {};
    let fillImage: IFillImage | null = null;
    let isBlockDisplay: boolean = false;
    let explicitTextAlign: TChartLegendAlign = null;
    if (di.styleObj) {
      const htmlStyle: Record<string, unknown> = StyleHelper.convertToHtmlStyleFromStyleObj(
        context,
        di.styleObj,
        di.id.toString(),
        context.generateType
      ) as Record<string, unknown>;
      resolved = ReactNativeElementGenerator.toReactNativeStyle(htmlStyle);
      fillImage = ReactNativeElementGenerator.resolveFillImage(htmlStyle);
      isBlockDisplay = htmlStyle.display === "block";
      explicitTextAlign = resolveExplicitTextAlignValue(htmlStyle.textAlign);
    }
    // Web parity (ServerSideElementGenerator.buildProps): in an absolute-design screen, every
    // non-table/divider component is forced to full width (web sets width: "100%" + display: block;
    // RN has no display, so only the width applies). An overlay is exempt for the same reason it is exempt
    // from the block-flow fill — it is a portal sized by its own property, not a child of this layout.
    const componentName: string = di.item?.name ?? "";
    if (
      context.sd?.isAbsoluteDesign === true &&
      !ABSOLUTE_DESIGN_WIDTH_EXCLUDED.has(componentName) &&
      !OVERLAY_COMPONENTS.has(componentName)
    ) {
      resolved.width = "100%";
    }
    return { style: resolved, fillImage, isBlockDisplay, explicitTextAlign };
  }

  /**
   * The designer's Fill → Image as something the RN runtime can paint, or null when the fill carries no
   * bitmap.
   *
   * <p>React Native has no `background-image`, so this is deliberately NOT a style key: the emitter turns it
   * into a `<KBackgroundImage />` element (see {@link buildFillImageJsx}). What arrives here is whatever
   * `StyleHelper.getFillStyle` wrote — a `[imageResourceId:GUID]` marker under `generateClientCode`, or a
   * `url(...)` wrapper otherwise — plus the panel's three sizing switches folded into `background-size` /
   * `background-repeat` / `background-position`. A GRADIENT arrives under the same key and is not one of
   * these: it IS expressible as a style value and {@link normalizeGradient} carries it, so it is skipped.</p>
   */
  private static resolveFillImage(htmlStyle: Record<string, unknown>): IFillImage | null {
    const raw: unknown = htmlStyle.backgroundImage;
    if (typeof raw !== "string" || raw === "") {
      return null;
    }
    const value: string | null = ReactNativeElementGenerator.toFillImageValue(raw);
    if (value === null) {
      return null;
    }
    const size: unknown = htmlStyle.backgroundSize;
    let fit: string = "cover";
    if (size === "contain") {
      fit = "contain";
    } else if (size === "unset") {
      // The panel's `stretch` clears the sizing (getFillStyle writes `unset` to three keys at once), which
      // on the web lets the image fill both axes independently — RN spells that `fill`.
      fit = "stretch";
    }
    const position: unknown = htmlStyle.backgroundPosition;
    return {
      value,
      fit,
      repeat: htmlStyle.backgroundRepeat === "repeat",
      position: typeof position === "string" ? position : ""
    };
  }

  /**
   * The image behind a `background-image` value, or null when it is not a bitmap. Accepts both spellings the
   * web pipe produces: the `[imageResourceId:GUID]` marker (the resource form, which the RN runtime resolves
   * against the backend exactly as KImage does) and a `url(...)` wrapper around a plain address.
   */
  private static toFillImageValue(raw: string): string | null {
    const resourceMarker: RegExpMatchArray | null = raw.match(/^\[imageResourceId:([^\]]+)\]$/);
    if (resourceMarker !== null) {
      return resourceMarker[1];
    }
    const urlWrapper: RegExpMatchArray | null = raw.match(/^url\((.*)\)$/);
    if (urlWrapper !== null) {
      return urlWrapper[1].replace(/^['"]|['"]$/g, "");
    }
    return null;
  }

  /**
   * The designer's Effects animations for this node, or null when it authored none.
   *
   * <p>Not read out of the style pipe, because they were never in it: `StyleHelper.getEffectsStyle` does not
   * touch `entryAnimation` / `animations`, and the web target emits them as `@keyframes` (`EffectsCssBuilder`)
   * rather than as declarations. So they are read off the effects section directly — including the
   * project-level effects styles the section references by id, merged the same way `getEffectsStyles` merges
   * them (project styles first, the component's own last and winning).</p>
   */
  private static resolveAnimation(context: ICodeGenerationContext, di: IDisplayItem): IAnimationSpec | null {
    const own: IStylingEffects | undefined = di.styleObj?.effects;
    if (own === undefined) {
      return null;
    }
    let entry: IEffectsEntryAnimation | undefined;
    let items: ILoopingAnimationItem[] | undefined;
    for (const effectsId of own.effectsIds ?? []) {
      const shared: IStylingEffects | undefined = context.project?.stylingEffects?.find(
        (candidate: IStylingEffects): boolean => candidate.effectsId?.toLowerCase() === effectsId?.toLowerCase()
      );
      if (shared !== undefined) {
        entry = shared.entryAnimation ?? entry;
        items = shared.animations ?? items;
      }
    }
    entry = own.entryAnimation ?? entry;
    items = own.animations ?? items;

    const loops: IAnimationLoop[] = (items ?? [])
      .map((item: ILoopingAnimationItem): IAnimationLoop | null => ReactNativeElementGenerator.toAnimationLoop(item))
      .filter((loop: IAnimationLoop | null): loop is IAnimationLoop => loop !== null);
    if (entry === undefined && loops.length === 0) {
      return null;
    }
    const spec: IAnimationSpec = {};
    if (entry !== undefined) {
      spec.entry = entry;
    }
    if (loops.length > 0) {
      spec.loops = loops;
    }
    return spec;
  }

  /**
   * One looping animation lowered for the runtime, or null when nothing in it can be driven — a track whose
   * every stop only carried a filter, for instance, leaves no channel behind and is dropped rather than
   * emitted as an animation that would run and change nothing.
   */
  private static toAnimationLoop(item: ILoopingAnimationItem): IAnimationLoop | null {
    const stops: IAnimationStop[] = (item.keyframes ?? []).map((stop: ILoopingKeyframeStop): IAnimationStop =>
      ReactNativeElementGenerator.toAnimationStop(stop)
    );
    const hasChannel: boolean = stops.some((stop: IAnimationStop): boolean => Object.keys(stop).length > 1);
    if (stops.length < 2 || !hasChannel) {
      return null;
    }
    const loop: IAnimationLoop = { durationMs: item.durationMs ?? 1000, stops };
    if (item.delayMs !== undefined && item.delayMs > 0) {
      loop.delayMs = item.delayMs;
    }
    if (item.timingFunction !== undefined) {
      loop.timingFunction = item.timingFunction;
    }
    // `infinite` is the designer's switch and wins; an explicit count only travels when it is finite.
    if (item.infinite !== true && item.iterationCount !== undefined && item.iterationCount > 0) {
      loop.iterationCount = item.iterationCount;
    }
    if (item.direction !== undefined && item.direction !== "normal") {
      loop.direction = item.direction;
    }
    return loop;
  }

  /**
   * One keyframe stop's transform chain flattened onto the animatable channels. `move`'s and `scale`'s z axis
   * is dropped (RN drives no depth), a uniform scale collapses to one `scale` channel, and a rotate carries
   * only its z component — an x/y rotation needs the 3D context RN does not have.
   */
  private static toAnimationStop(stop: ILoopingKeyframeStop): IAnimationStop {
    const result: IAnimationStop = { offset: stop.offset };
    if (stop.opacity !== undefined) {
      result.opacity = stop.opacity;
    }
    for (const transform of stop.transforms ?? []) {
      ReactNativeElementGenerator.applyAnimationTransform(result, transform);
    }
    return result;
  }

  /** Folds one transform item onto the stop's channels. */
  private static applyAnimationTransform(stop: IAnimationStop, transform: ITransformItem): void {
    if (transform.type === "move") {
      if (transform.x !== undefined) {
        stop.translateX = transform.x;
      }
      if (transform.y !== undefined) {
        stop.translateY = transform.y;
      }
      return;
    }
    if (transform.type === "scale") {
      const x: number | undefined = transform.x;
      const y: number | undefined = transform.y;
      if (transform.uniformScale === true || (x !== undefined && x === y)) {
        stop.scale = x ?? y;
        return;
      }
      if (x !== undefined) {
        stop.scaleX = x;
      }
      if (y !== undefined) {
        stop.scaleY = y;
      }
      return;
    }
    if (transform.type === "rotate") {
      if (transform.z !== undefined) {
        stop.rotateZ = transform.z;
      }
      return;
    }
    if (transform.x !== undefined) {
      stop.skewX = transform.x;
    }
    if (transform.y !== undefined) {
      stop.skewY = transform.y;
    }
  }

  /** The ` animation={{…}}` prop for a node's Effects animations, or "" when it authored none. */
  private static buildAnimationProp(spec: IAnimationSpec | null): string {
    return spec === null ? "" : ` animation={${JSON.stringify(spec)}}`;
  }

  /**
   * The `<KBackgroundImage />` element for a node's bitmap fill, indented as the box's first child — or ""
   * when the designer set none. Only the knobs the designer actually moved are emitted, so a plain
   * background stays a one-prop element.
   */
  private static buildFillImageJsx(fillImage: IFillImage | null, indent: string): string {
    if (fillImage === null) {
      return "";
    }
    const props: string[] = [`value={${JSON.stringify(fillImage.value)}}`];
    if (fillImage.fit !== "cover") {
      props.push(`fit={${JSON.stringify(fillImage.fit)}}`);
    }
    if (fillImage.repeat) {
      props.push("repeat");
    }
    if (fillImage.position !== "") {
      props.push(`position={${JSON.stringify(fillImage.position)}}`);
    }
    return `${indent}<KBackgroundImage ${props.join(" ")} />`;
  }

  /**
   * Whether a resolved style carries a font size React Native can actually measure with: a positive
   * NUMBER. A percentage/em string survives the px conversion as a string, which RN cannot evaluate either.
   */
  private static hasUsableFontSize(style: Record<string, string | number>): boolean {
    const fontSize: string | number | undefined = style.fontSize;
    return typeof fontSize === "number" && fontSize > 0;
  }

  /**
   * Narrows a CSS-in-JS object (StyleHelper output) to React Native StyleSheet values:
   * keeps only RN-valid keys, converts "12px" to 12, keeps "%" strings (RN supports them
   * for dimensions), and drops anything non-scalar. Pure and deterministic — keys are
   * sorted with a locale-independent comparison so generated output is byte-stable.
   *
   * <p><b>A font size React Native cannot measure with takes letterSpacing down with it.</b> Android's
   * `TextLayoutManager.buildSpannableFromFragments` reads `TextAttributeProps.letterSpacing` on <i>every</i>
   * text fragment — only to test it for NaN — and that getter opens with `require(fontSize > 0)`. So a Text
   * whose style carries `fontSize: 0` (or a `120%` / `1.2em` string, which survives the px conversion as a
   * string RN cannot evaluate) red-screens the app on mount with
   * `IllegalArgumentException: FontSize should be a positive value. Current value: 0` — regardless of whether
   * letter spacing was ever authored. Both keys are therefore dropped unless the font size is a positive
   * number: the same "narrow to what RN can evaluate" rule that already drops `display: grid` and
   * `position: fixed`. `letterSpacing` alone is dropped too, because the designer writes the two
   * independently (`StyleHelper.convertIStylingTypographyToHtmlStyle` emits each under its own `if`) —
   * harmless on web, where letter-spacing applies to whatever size cascades in, but there is no such cascade
   * for an RN StyleSheet entry. Substituting a made-up font size was rejected: it would silently resize text
   * the designer never sized.</p>
   */
  static toReactNativeStyle(htmlStyle: Record<string, unknown>): Record<string, string | number> {
    const result: Record<string, string | number> = {};
    const paintsGradientText: boolean = ReactNativeElementGenerator.paintsGradientText(htmlStyle);
    const keys: string[] = Object.keys(htmlStyle)
      .filter((key) => REACT_NATIVE_SAFE_STYLE_KEYS.includes(key))
      .sort(compareOrdinal);

    for (const key of keys) {
      if (key === "backgroundImage" && paintsGradientText) {
        continue;
      }
      const value: unknown = htmlStyle[key];
      const targetKey: string = RN_STYLE_KEY_RENAMES.get(key) ?? key;
      if (typeof value === "number") {
        result[targetKey] = value;
        continue;
      }
      if (typeof value !== "string" || value === "") {
        continue;
      }
      const normalized: string | number | null = ReactNativeElementGenerator.normalizeStyleValue(key, value);
      if (normalized !== null) {
        result[targetKey] = normalized;
      }
    }
    if (!ReactNativeElementGenerator.hasUsableFontSize(result)) {
      delete result.fontSize;
      delete result.letterSpacing;
    }
    ReactNativeElementGenerator.dropClippingLineHeight(result);
    ReactNativeElementGenerator.applyFontFamily(result);
    return result;
  }

  /**
   * Drops a `lineHeight` smaller than the font it lays out. React Native sizes each line box to `lineHeight`
   * exactly and clips the glyphs that overflow it, so such a value cuts the tops off the text and collapses
   * the box around it. Neither other target does that: the web keeps `overflow: visible` (the glyphs spill out
   * of a short line box, drawn whole), and native Android turns the value into a softened
   * `lineSpacingMultiplier` (`ClientUIGenerationBase.GetLineSpacing`) that never clips a single line. Dropping
   * it gives the font's natural line height — the closest RN can draw to both (KOA-18617: a 10px line height
   * flattened every sign-in Button). The comparison uses RN's default font size when the style names none,
   * because that is the size the Text will actually render at.
   */
  private static dropClippingLineHeight(style: Record<string, string | number>): void {
    const lineHeight: string | number | undefined = style.lineHeight;
    if (typeof lineHeight !== "number") {
      return;
    }
    const fontSize: number = typeof style.fontSize === "number" ? style.fontSize : REACT_NATIVE_DEFAULT_FONT_SIZE;
    if (lineHeight < fontSize) {
      delete style.lineHeight;
    }
  }

  /**
   * Whether this style is the web target's <b>gradient text</b> trick rather than a gradient fill: a
   * gradient painted as the element's background and then clipped to the glyphs
   * (`StyleHelper.getTextStyle` emits `backgroundImage` + `background-clip: text` when the designer
   * gives typography a gradient colour).
   *
   * <p>It has to be recognised, not just ignored. React Native has no `backgroundClip`, so the clip half
   * of the pair is dropped by the allow-list — and passing the surviving `backgroundImage` through would
   * flood the entire component with the gradient that was meant to tint only its letters, which is far
   * worse than the text simply keeping its inherited colour. Painting glyphs with a gradient on RN needs
   * a mask layer (a component change, not a style key), so until then the pair is dropped whole.</p>
   */
  private static paintsGradientText(htmlStyle: Record<string, unknown>): boolean {
    return htmlStyle.backgroundClip === "text" || htmlStyle.WebkitBackgroundClip === "text";
  }

  /**
   * The React Native value for one web CSS declaration, or null when RN has no equivalent and the
   * declaration must be dropped. Keys with no entry here fall through to {@link toStyleScalar}: a plain
   * scalar passes through, with `"12px"` narrowed to `12`.
   */
  private static normalizeStyleValue(key: string, value: string): string | number | null {
    switch (key) {
      case "textAlign":
        return ReactNativeElementGenerator.normalizeTextAlign(value);
      case "display":
        return ReactNativeElementGenerator.normalizeDisplay(value);
      case "overflow":
        return ReactNativeElementGenerator.normalizeOverflow(value);
      case "position":
        return ReactNativeElementGenerator.normalizePosition(value);
      case "backgroundImage":
        return ReactNativeElementGenerator.normalizeGradient(value);
      case "boxShadow":
        return ReactNativeElementGenerator.normalizeBoxShadow(value);
      case "filter":
        return ReactNativeElementGenerator.normalizeFilter(value);
      case "transform":
        return ReactNativeElementGenerator.normalizeTransform(value);
      case "backfaceVisibility":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_BACKFACE_VISIBILITY);
      case "mixBlendMode":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_BLEND_MODES);
      case "outlineStyle":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_OUTLINE_STYLES);
      case "pointerEvents":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_POINTER_EVENTS);
      case "textDecoration":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_TEXT_DECORATION_LINES);
      case "textTransform":
        return ReactNativeElementGenerator.normalizeKeyword(value, RN_TEXT_TRANSFORMS);
      default:
        return ReactNativeElementGenerator.toStyleScalar(value);
    }
  }

  /** `"12px"` → `12`; every other string through unchanged (RN takes `%` strings for dimensions). */
  private static toStyleScalar(value: string): string | number {
    const pxMatch: RegExpMatchArray | null = value.match(/^(-?\d+(?:\.\d+)?)px$/);
    if (pxMatch) {
      return Number(pxMatch[1]);
    }
    return value;
  }

  /**
   * A CSS keyword narrowed to the set React Native accepts for that property, or null when it is not one
   * of them. CSS keywords are case-insensitive and the web pipe passes the designer's casing through, so
   * the comparison is lowercased first — with `toLowerCase`, which is locale-independent, unlike
   * `toLocaleLowerCase` (on a tr-TR machine that maps `I` to `ı` and no keyword would ever match).
   * Internal runs of whitespace collapse to one space so the two-word `underline line-through` resolves
   * however it was spaced.
   */
  static normalizeKeyword(value: string, allowed: ReadonlySet<string>): string | null {
    const candidate: string = value.trim().toLowerCase().replace(/\s+/g, " ");
    return allowed.has(candidate) ? candidate : null;
  }

  /**
   * Folds `fontFamily` + `fontWeight` into the single weighted family name React Native can resolve,
   * in place.
   *
   * <p><b>`fontWeight` is removed once a family resolves, and that is the point, not an oversight.</b>
   * The registered face already *is* the requested weight (`poppins_semibold`), so leaving
   * `fontWeight: 600` alongside it makes Android's `Typeface.create(typeface, weight, italic)` apply
   * synthetic bold on top of a semibold face — visibly heavier than the designer drew, and heavier
   * than the web target renders the same screen. `fontStyle` is deliberately kept: the bundled set
   * ships only upright faces (as does the web target's woff2 set, whose `@font-face` blocks are all
   * `font-style: normal`), so italic is synthesised on both targets and stays in parity.</p>
   *
   * <p>When no family was authored, `fontWeight` is left exactly as it was — RN applies it against the
   * OS default family, which is the correct rendering for a screen that never chose a font.</p>
   */
  private static applyFontFamily(style: Record<string, string | number>): void {
    const fontFamily: string | number | undefined = style.fontFamily;
    if (typeof fontFamily !== "string") {
      return;
    }
    const resolved: string | null = ReactNativeElementGenerator.toReactNativeFontFamily(fontFamily, style.fontWeight);
    if (resolved === null) {
      delete style.fontFamily;
      return;
    }
    style.fontFamily = resolved;
    delete style.fontWeight;
  }

  /**
   * Maps web textAlign values to the React Native union: strips the "-webkit-" prefix
   * ("-webkit-left" → "left"), maps logical "start"/"end" to physical sides (RN has no
   * RTL-logical textAlign), and drops anything that doesn't land in the valid set.
   */
  static normalizeTextAlign(value: string): string | null {
    let candidate: string = value;
    if (candidate.startsWith("-webkit-")) {
      candidate = candidate.substring("-webkit-".length);
    }
    if (candidate === "start") {
      candidate = "left";
    } else if (candidate === "end") {
      candidate = "right";
    }
    if (TEXT_ALIGN_VALUES.includes(candidate)) {
      return candidate;
    }
    return null;
  }

  /**
   * Maps web `display` to the RN-valid subset. Only "flex" and "none" are supported;
   * all other web display modes (block, inline-block, grid, etc.) are dropped (null).
   */
  static normalizeDisplay(value: string): string | null {
    return RN_DISPLAY_VALUES.has(value) ? value : null;
  }

  /**
   * Maps web `overflow` to the RN-valid subset. "auto" is normalised to "scroll"
   * (RN ScrollView semantics). "visible", "hidden", and "scroll" pass through;
   * everything else is dropped (null). A "scroll" result signals to emitNode that the
   * container should become a ScrollView rather than a plain View.
   */
  static normalizeOverflow(value: string): string | null {
    const candidate: string = value === "auto" ? "scroll" : value;
    return RN_OVERFLOW_PASSTHROUGH.has(candidate) ? candidate : null;
  }

  /**
   * Maps web `position` to the RN-valid subset. Only "absolute" and "relative" pass
   * through; "fixed" and "sticky" (web-only) are dropped (null).
   */
  static normalizePosition(value: string): string | null {
    return RN_POSITION_VALUES.has(value) ? value : null;
  }

  /**
   * The designer's gradient fill as a value React Native can paint, or null.
   *
   * <p>Two things have to happen for a gradient the browser renders to also render on RN 0.85:</p>
   * <ol>
   *   <li><b>Conic gradients are dropped.</b> Kuika's colour picker offers linear, radial and conic
   *       (`GRADIENT_PREFIXES`), but RN's `processBackgroundImage` only parses the first two. A conic
   *       value would be accepted by the StyleSheet and then painted as nothing, so it is better refused
   *       here — the box keeps whatever solid `backgroundColor` it has.</li>
   *   <li><b>`rgb()`/`rgba()` colour stops are rewritten to hex.</b> RN splits a linear gradient's
   *       contents on <i>every</i> comma (`parseLinearGradientCSSString` → `gradientContent.split(",")`),
   *       so a functional colour is torn into `rgba(0` / `0` / `0` / `.5) 20%` and the whole gradient is
   *       discarded. The designer's picker does emit `rgba(...)` stops (`gradient-utils.ts`), so the
   *       equivalent `#RRGGBB`/`#RRGGBBAA` form — which carries no commas and the same colour — is
   *       emitted instead. A stop that cannot be converted drops the gradient rather than emitting one
   *       whose colours have quietly changed.</li>
   * </ol>
   */
  static normalizeGradient(value: string): string | null {
    const trimmed: string = value.trim();
    const isPaintable: boolean = RN_GRADIENT_PREFIXES.some((prefix: string): boolean => trimmed.startsWith(prefix));
    if (!isPaintable) {
      return null;
    }
    return ReactNativeElementGenerator.toCommaFreeColours(trimmed);
  }

  /**
   * Rewrites every `rgb()`/`rgba()` function in a CSS value to its hex equivalent, or null when one of
   * them is not a colour this can convert exactly. See {@link normalizeGradient} for why the commas
   * inside those functions are the problem.
   */
  private static toCommaFreeColours(value: string): string | null {
    let failed: boolean = false;
    const rewritten: string = value.replace(/rgba?\(([^()]*)\)/gi, (match: string, args: string): string => {
      const hex: string | null = ReactNativeElementGenerator.rgbFunctionToHex(args);
      if (hex === null) {
        failed = true;
        return match;
      }
      return hex;
    });
    return failed ? null : rewritten;
  }

  /**
   * `"0, 0, 0, .5"` → `"#00000080"`. Accepts the three channels as 0–255 numbers or percentages and the
   * optional alpha as a 0–1 number or a percentage, which is every form the designer's colour picker and
   * the MCP style path produce. Returns null for anything else — including `none`, a CSS variable, or a
   * channel out of range — so the caller can drop the declaration instead of shifting a colour.
   *
   * <p>Six hex digits when the colour is opaque, eight when it is not: `#RRGGBBAA` is what RN's
   * `normalizeColor` reads, and keeping the short form for the common case keeps generated styles small.</p>
   */
  private static rgbFunctionToHex(args: string): string | null {
    const parts: string[] = args
      .split(/[,/]/)
      .map((part: string): string => part.trim())
      .filter((part: string): boolean => part !== "");
    if (parts.length !== 3 && parts.length !== 4) {
      return null;
    }
    const channels: string[] = [];
    for (let index = 0; index < 3; index++) {
      const channel: number | null = ReactNativeElementGenerator.toColourChannel(parts[index], 255);
      if (channel === null) {
        return null;
      }
      channels.push(channel.toString(16).padStart(2, "0"));
    }
    let alphaHex: string = "";
    if (parts.length === 4) {
      const alpha: number | null = ReactNativeElementGenerator.toColourChannel(parts[3], 1);
      if (alpha === null) {
        return null;
      }
      if (alpha !== 255) {
        alphaHex = alpha.toString(16).padStart(2, "0");
      }
    }
    return `#${channels.join("")}${alphaHex}`;
  }

  /**
   * One `rgb()` argument as a 0–255 byte, or null when it is not a number in range. `fullScale` is the
   * value the channel reaches at 100% — 255 for a colour channel, 1 for alpha — so both are read by the
   * same rule. `Math.round` (not truncation) matches how browsers quantise a fractional channel.
   */
  private static toColourChannel(token: string, fullScale: number): number | null {
    const percentMatch: RegExpMatchArray | null = token.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))%$/);
    let raw: number;
    if (percentMatch === null) {
      if (!isCssNumber(token)) {
        return null;
      }
      raw = Number(token);
    } else {
      raw = (Number(percentMatch[1]) / 100) * fullScale;
    }
    if (raw < 0 || raw > fullScale) {
      return null;
    }
    return Math.round((raw / fullScale) * 255);
  }

  /**
   * The designer's box shadow as a value React Native can paint, or null.
   *
   * <p>RN 0.85 parses the CSS `box-shadow` string itself (`processBoxShadow`), and the grammar it accepts
   * is exactly the one the web pipe writes — `[inset] <offset-x> <offset-y> [blur] [spread] [colour]` —
   * so the declaration passes through rather than being rebuilt. What is checked is that it IS that
   * grammar: RN answers an unparseable shadow with "no shadow at all", and a value that reaches this
   * emitter from the MCP style path rather than from `StyleHelper.getShadowStyle` has not been through
   * any such shape. Colours are rewritten to hex for the same reason as in {@link normalizeGradient} —
   * `processBoxShadow` splits a shadow LIST on commas, and a functional colour carries commas.</p>
   */
  static normalizeBoxShadow(value: string): string | null {
    const trimmed: string = value.trim();
    if (trimmed === "" || trimmed.toLowerCase() === "none") {
      return null;
    }
    const commaFree: string | null = ReactNativeElementGenerator.toCommaFreeColours(trimmed);
    if (commaFree === null) {
      return null;
    }
    const shadows: string[] = splitTopLevelCommas(commaFree);
    if (shadows.length === 0) {
      return null;
    }
    for (const shadow of shadows) {
      if (!ReactNativeElementGenerator.isBoxShadow(shadow)) {
        return null;
      }
    }
    return shadows.join(", ");
  }

  /**
   * Whether one segment of a `box-shadow` list is a shadow RN will accept: two to four lengths, at most
   * one `inset` keyword, and at most one further token (the colour). The lengths must carry a unit unless
   * they are zero, which is CSS's own rule and RN's `parseLength`.
   */
  private static isBoxShadow(shadow: string): boolean {
    const tokens: string[] = splitTopLevelWhitespace(shadow);
    let lengths: number = 0;
    let insets: number = 0;
    let others: number = 0;
    for (const token of tokens) {
      if (token.toLowerCase() === "inset") {
        insets++;
      } else if (isReactNativeLength(token)) {
        lengths++;
      } else {
        others++;
      }
    }
    return lengths >= 2 && lengths <= 4 && insets <= 1 && others <= 1;
  }

  /**
   * The designer's filter chain as a value React Native can apply, or null — <b>all of it or none of it</b>.
   *
   * <p>That is not a simplification, it is RN's own behaviour: `processFilter` returns an empty list the
   * moment one function fails to parse, so a single out-of-range amount cancels the whole chain. Web CSS
   * does the same with an invalid `filter` declaration. Validating every function up front means the
   * emitted declaration either applies completely or was never written, instead of the generated app
   * silently losing a blur because a `brightness` next to it was negative.</p>
   *
   * <p>Order is preserved: `grayscale(100%) blur(4px)` is not the same image as `blur(4px) grayscale(100%)`.</p>
   */
  static normalizeFilter(value: string): string | null {
    const segments: ICssFunctionSegment[] = parseCssFunctionSegments(value);
    if (segments.length === 0) {
      return null;
    }
    const parts: string[] = [];
    for (const segment of segments) {
      const name: string = segment.name.toLowerCase();
      if (name === "drop-shadow") {
        if (!ReactNativeElementGenerator.isDropShadow(segment.args)) {
          return null;
        }
        const commaFree: string | null = ReactNativeElementGenerator.toCommaFreeColours(segment.args);
        if (commaFree === null) {
          return null;
        }
        parts.push(`drop-shadow(${commaFree})`);
        continue;
      }
      if (!ReactNativeElementGenerator.isFilterAmount(name, segment.args)) {
        return null;
      }
      parts.push(`${name}(${segment.args})`);
    }
    return parts.join(" ");
  }

  /**
   * Whether a filter function's amount is one React Native can read — the mirror of `_getFilterAmount`
   * in RN's `processFilter`. `hue-rotate` takes an angle (or a bare zero); `blur` takes a non-negative
   * length; the remaining functions take a non-negative number or percentage. An unknown function name
   * is not an amount at all.
   */
  private static isFilterAmount(name: string, args: string): boolean {
    if (!RN_FILTER_UNITS.has(name)) {
      return false;
    }
    const match: RegExpMatchArray | null = args.trim().match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(px|%|deg|rad)?$/);
    if (match === null) {
      return false;
    }
    const amount: number = Number(match[1]);
    const unit: string | undefined = match[2];
    if (name === "hue-rotate") {
      return amount === 0 || unit === "deg" || unit === "rad";
    }
    if (amount < 0) {
      return false;
    }
    if (name === "blur") {
      return unit === undefined || unit === "px";
    }
    return unit === undefined || unit === "%" || unit === "px";
  }

  /**
   * Whether a `drop-shadow(...)` argument list is one React Native can read: an x and a y offset, an
   * optional blur, and an optional colour. Unlike `box-shadow` there is no spread and no `inset`.
   */
  private static isDropShadow(args: string): boolean {
    const tokens: string[] = splitTopLevelWhitespace(args);
    let lengths: number = 0;
    let others: number = 0;
    for (const token of tokens) {
      if (isReactNativeLength(token)) {
        lengths++;
      } else {
        others++;
      }
    }
    return lengths >= 2 && lengths <= 3 && others <= 1;
  }

  /**
   * The designer's transform chain rewritten into the function set React Native actually applies, or null.
   *
   * <p>This is the one advanced style key that cannot pass through. RN understands a CSS transform
   * string, but a narrower vocabulary than CSS — and the mismatch is not a silent no-op. Its C++ side
   * (`conversions.h`) <b>discards the entire transform list</b> when one operation fails its type check,
   * so the `scale(x,y)` the web pipe writes for a two-axis scale cancels every other transform on the
   * component. Three functions are therefore rewritten into their per-axis equivalents, which say the
   * same thing in a vocabulary RN has:</p>
   *
   * <ul>
   *   <li>`scale(x,y)` → `scaleX(x) scaleY(y)` (and `scale(x)` when both axes agree — RN's own short form)</li>
   *   <li>`scale3d(x,y,z)` → `scaleX(x) scaleY(y)`. RN's renderer knows `scaleZ`, but its JS validator
   *       does not and throws on it in development; a depth scale with no 3D context for children
   *       changes nothing on screen anyway.</li>
   *   <li>`skew(x,y)` → `skewX(x) skewY(y)`</li>
   *   <li>`translate3d(x,y,z)` → `translate(x,y)`. RN's JS parser accepts three arguments here but its
   *       renderer requires exactly two, so passing the z through would discard the whole list. Depth
   *       without a children perspective — which RN has no key for either — moves nothing on screen, so
   *       dropping it costs the rendering nothing.</li>
   * </ul>
   *
   * <p>Anything outside the resulting vocabulary is dropped function by function rather than taking the
   * chain down with it. Lengths are checked for a unit: RN raises a DEV-only `invariant` on a non-zero
   * unitless translate, which red-screens the app instead of mis-rendering it.</p>
   */
  static normalizeTransform(value: string): string | null {
    const parts: string[] = [];
    for (const segment of parseCssFunctionSegments(value)) {
      parts.push(...ReactNativeElementGenerator.toReactNativeTransform(segment));
    }
    return parts.length === 0 ? null : parts.join(" ");
  }

  /** One CSS transform function as zero or more React Native transform functions. */
  private static toReactNativeTransform(segment: ICssFunctionSegment): string[] {
    const args: string[] = splitTopLevelCommas(segment.args.replace(/\s+/g, " ")).flatMap((part: string): string[] =>
      splitTopLevelWhitespace(part)
    );
    switch (segment.name) {
      case "scale":
        if (args.length === 1 && isCssNumber(args[0])) {
          return [`scale(${args[0]})`];
        }
        if (args.length === 2 && args.every(isCssNumber)) {
          if (args[0] === args[1]) {
            return [`scale(${args[0]})`];
          }
          return [`scaleX(${args[0]})`, `scaleY(${args[1]})`];
        }
        return [];
      case "scale3d":
        if (args.length !== 3 || !args.every(isCssNumber)) {
          return [];
        }
        return [`scaleX(${args[0]})`, `scaleY(${args[1]})`];
      case "skew":
        if (args.length === 1) {
          return [`skewX(${args[0]})`];
        }
        if (args.length === 2) {
          return [`skewX(${args[0]})`, `skewY(${args[1]})`];
        }
        return [];
      case "translate3d":
        if (args.length !== 3 || !args.every(isReactNativeLength)) {
          return [];
        }
        return [`translate(${args[0]}, ${args[1]})`];
      default:
        return ReactNativeElementGenerator.passThroughTransform(segment.name, args);
    }
  }

  /**
   * A transform function React Native already speaks, kept as-is once its arity and arguments check out.
   *
   * <p>Every rule here is one of `_validateTransform`'s DEV `invariant`s, which throw rather than
   * degrade: `translate*` take lengths, `rotate*` / `skew*` take an angle carrying `deg` or `rad`,
   * `scale*` take numbers, and `perspective` takes a non-zero length ("Transform with key of
   * perspective cannot be zero"). Emitting a value that trips one of them would red-screen the
   * generated app on mount, so a function that fails is dropped instead.</p>
   */
  private static passThroughTransform(name: string, args: string[]): string[] {
    const arity: number | undefined = RN_TRANSFORM_ARITY.get(name);
    if (arity === undefined || args.length !== arity) {
      return [];
    }
    const kept: string[] = [`${name}(${args.join(", ")})`];
    if (RN_ANGLE_TRANSFORMS.has(name)) {
      return args.every(isCssAngle) ? kept : [];
    }
    if (name.startsWith("scale")) {
      return args.every(isCssNumber) ? kept : [];
    }
    if (name.startsWith("translate")) {
      return args.every(isReactNativeLength) ? kept : [];
    }
    if (name === "perspective") {
      const depth: number = Number.parseFloat(args[0]);
      return isReactNativeLength(args[0]) && depth !== 0 ? kept : [];
    }
    return [];
  }

  /**
   * The designer's font *display* name lowered to the key the app registers with expo-font:
   * invariant-lowercase, spaces and hyphens folded to underscores, and the weight baked in
   * (`"Open Sans"` @ 600 → `"open_sans_semibold"`).
   *
   * <p>The TS mirror of `FontFamilyManager.GetFontFamilyKey` + `GetFontFileName`. RN resolves a
   * `fontFamily` only against a registered family and cannot synthesise a weight from one face, so
   * unlike the web target — where `font-family: "Open Sans"` plus `font-weight: 600` is enough —
   * the weight has to be part of the family name here. `ReactNativeProjectGenerator.ApplyFonts`
   * registers exactly these keys.</p>
   *
   * <p>`toLowerCase` (not `toLocaleLowerCase`) is deliberate: JS `toLowerCase` is locale-independent,
   * so "Nunito Sans" keys as `nunito_sans` on a tr-TR machine as well as an en-US one. A locale-aware
   * lowercase would map 'I' to 'ı' and no registered family would ever match.</p>
   *
   * <p>Returns null for an empty family, so the caller leaves `fontFamily` off the style entirely
   * rather than emitting a family nothing can resolve.</p>
   */
  static toReactNativeFontFamily(fontFamily: string, fontWeight: string | number | undefined): string | null {
    const normalized: string = fontFamily
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace("condenced", "condensed")
      .replace(/^_+|_+$/g, "");
    if (normalized === "") {
      return null;
    }
    const weightKey: string = fontWeight === undefined ? "" : String(fontWeight).trim().toLowerCase();
    const weightName: string = FONT_WEIGHT_NAMES.get(weightKey) ?? DEFAULT_FONT_WEIGHT_NAME;
    return `${normalized}_${weightName}`;
  }

  /**
   * Splits a resolved style into the View-legal box subset and the Text-only typography
   * subset. Pure and order-preserving (inputs are already key-sorted by toReactNativeStyle).
   */
  static partitionStyle(style: Record<string, string | number>): IPartitionedStyle {
    const viewStyle: Record<string, string | number> = {};
    const textStyle: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(style)) {
      if (TEXT_ONLY_STYLE_KEYS.includes(key)) {
        textStyle[key] = value;
      } else {
        viewStyle[key] = value;
      }
    }
    return { viewStyle, textStyle };
  }

  /**
   * A column's grid span as the CSS width the web grid gives it: `span / 24` as a percentage, the RN
   * mirror of antd's `.ant-col-<n>` width. Spans above a full row are clamped to 100% (antd treats
   * `span > 24` as a full row too). Rounded to six decimals so the emitted style is byte-stable —
   * `toFixed` is culture-invariant, so the same span yields the same string under every locale.
   */
  static spanToWidth(span: number): string {
    const units: number = Math.min(span, GRID_COLUMN_UNITS);
    const percent: number = (units / GRID_COLUMN_UNITS) * 100;
    return `${Number(percent.toFixed(6))}%`;
  }

  /**
   * Translates the designer's two alignment intents onto the flex keys that actually carry them in
   * React Native. Kuika's align section writes them independently — horizontal as `textAlign`,
   * vertical as `alignItems` — and the web reconciles the pair at runtime in
   * `KMainFunctions.handleAlignments` (`justifyContent` from `textAlign`, cross-axis distribution from
   * `alignItems`). RN has no such runtime step, and which flex key means "horizontal" depends on the
   * container's own axis: on a column container the cross axis is horizontal (`alignItems`) and the
   * main axis vertical (`justifyContent`); on a row container the two swap. Emitting the designer's
   * `alignItems` verbatim therefore silently aligned column containers on the wrong axis and dropped
   * the horizontal intent altogether (`textAlign` is not a `ViewStyle` key).
   *
   * `textAlign` is left in place for the typography partition — a `Text` still needs it, and children
   * inherit it (see {@link IParentLayout}). A flex key the designer set explicitly is never overwritten.
   */
  static resolveContentAlignment(
    style: Record<string, string | number>,
    isHorizontal: boolean
  ): Record<string, string | number> {
    const horizontal: string | undefined =
      typeof style.textAlign === "string" ? FLEX_ALIGNMENT_FROM_TEXT_ALIGN[style.textAlign] : undefined;
    const vertical: string | number | undefined = style.alignItems;
    if (horizontal === undefined && vertical === undefined) {
      return style;
    }

    const horizontalKey: string = isHorizontal ? "justifyContent" : "alignItems";
    const verticalKey: string = isHorizontal ? "alignItems" : "justifyContent";
    const result: Record<string, string | number> = { ...style };
    // Cleared first so a column container can move the value to `justifyContent` and take the freed
    // `alignItems` for the horizontal intent, rather than reading its own leftover as designer input.
    delete result.alignItems;
    if (vertical !== undefined && result[verticalKey] === undefined) {
      result[verticalKey] = vertical;
    }
    if (horizontal !== undefined && result[horizontalKey] === undefined) {
      result[horizontalKey] = horizontal;
    }
    return ReactNativeElementGenerator.sortStyleKeys(result);
  }

  /**
   * The column's Ant-Design grid spans, breakpoint by breakpoint — only the ones the designer actually set, in
   * {@link GRID_SPAN_PROPERTIES} order. Empty when the column configures no span at all, in which case the
   * caller keeps the `flex: 1` base default instead of a grid width. Over-full spans are clamped downstream
   * (`spanToWidth` here, `kuikaSpanToWidth` in the runtime), not here.
   */
  private static resolveColumnSpans(di: IDisplayItem): IColumnSpanEntry[] {
    const spans: IColumnSpanEntry[] = [];
    for (const breakpoint of GRID_SPAN_PROPERTIES) {
      const raw: string = ReactNativeElementGenerator.getFixedPropertyValue(di, breakpoint);
      const span: number = parseInt(raw, 10);
      if (!Number.isNaN(span) && span > 0) {
        spans.push({ breakpoint, span });
      }
    }
    return spans;
  }

  /**
   * The width expression a RESPONSIVE column needs, or "" when the column's width is the same at every
   * breakpoint and can therefore stay a plain StyleSheet value.
   *
   * <p>The test is "more than one distinct span", not a re-run of the runtime's cascade: whatever breakpoint the
   * device is in, a cascade over a single distinct value can only yield that value. So a column the designer
   * never made responsive — which is most of them, since the designer's own default fills only `xs` — emits
   * exactly the static width it did before, and only a genuinely responsive column pays for a runtime read. It
   * also keeps the cascade itself in ONE place (the runtime), instead of a copy here that could drift from it.</p>
   */
  private static columnWidthExpression(spans: readonly IColumnSpanEntry[]): string {
    const distinct: Set<number> = new Set<number>(spans.map((entry: IColumnSpanEntry): number => entry.span));
    if (distinct.size < 2) {
      return "";
    }
    const literal: string = spans
      .map((entry: IColumnSpanEntry): string => `${entry.breakpoint}: ${entry.span}`)
      .join(", ");
    return `${RN_COLUMN_WIDTH_HELPER}(${RN_BREAKPOINT_VARIABLE}, { ${literal} })`;
  }

  /**
   * The `style` prop for a node whose style is a StyleSheet entry plus, optionally, a runtime expression
   * evaluated per render (a responsive column's width). With no expression this is exactly
   * {@link registerStyle}, so a node that needs nothing dynamic emits the same single reference as before.
   */
  private static composeStyleProp(
    styleEntries: IStyleSheetEntry[],
    name: string,
    style: Record<string, string | number>,
    ...dynamicExprs: readonly string[]
  ): string {
    const accessor: string | null = ReactNativeElementGenerator.registerStyleEntry(styleEntries, name, style);
    const terms: string[] = [];
    if (accessor !== null) {
      terms.push(accessor);
    }
    for (const expr of dynamicExprs) {
      if (expr !== "") {
        terms.push(expr);
      }
    }
    if (terms.length === 0) {
      return "";
    }
    if (terms.length === 1) {
      return ` style={${terms[0]}}`;
    }
    return ` style={[${terms.join(", ")}]}`;
  }

  /**
   * Registers the screen's single shared "disabled" StyleSheet entry (idempotently) and returns the term a
   * disabled node's style array carries — `isEnabled_di5 === "disabled" ? styles.kuikaDisabled : null`.
   *
   * <p>One entry per screen rather than one per node: the delta is identical everywhere, and sharing it keeps the
   * emitted StyleSheet small and the referenced object stable.</p>
   */
  private static disabledStyleTerm(styleEntries: IStyleSheetEntry[], boundaryExpr: string): string {
    if (boundaryExpr === "") {
      return "";
    }
    const alreadyRegistered: boolean = styleEntries.some(
      (entry: IStyleSheetEntry): boolean => entry.name === DISABLED_STYLE_ENTRY_NAME
    );
    if (!alreadyRegistered) {
      styleEntries.push({ name: DISABLED_STYLE_ENTRY_NAME, style: { ...DISABLED_STYLE } });
    }
    return `${boundaryExpr} ? styles.${DISABLED_STYLE_ENTRY_NAME} : null`;
  }

  /**
   * Reads a stack's spacing ("Gap", model name `size`) property as the flex `gap` value: a finite
   * number > 0, or null when unset / non-numeric / non-positive. The web stacks clamp a negative
   * `size` to 0; a zero (or absent) gap is the RN default, so both collapse to "emit no gap" here —
   * keeping the output minimal and byte-stable. The value passes through unrounded to mirror the web
   * stacks passing `size` straight to antd `<Space>`.
   */
  private static resolveGap(di: IDisplayItem, propertyName: string): number | null {
    const raw: string = ReactNativeElementGenerator.getFixedPropertyValue(di, propertyName);
    if (raw === "") {
      return null;
    }
    const value: number = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return value;
  }

  /**
   * Decides how a container renders. The stacks are logically scroll containers (the RN mirror of the
   * web stacks' scrolling outer div):
   * - HorizontalStack → a horizontal `ScrollView` (web outer div `overflowX: auto`); `overflowX="hidden"`
   *   opts out to a clipping `View`.
   * - VerticalStack → a vertical `ScrollView` only when it is height-bounded (`height`/`maxHeight` set);
   *   otherwise a plain `View`. The screen body is already a vertical ScrollView, so an unbounded vertical
   *   ScrollView here would nest same-axis scrolls (RN warns and the inner one collapses to content height).
   *
   * Every other container keeps the prior behavior: a generic `overflow:scroll` becomes a plain vertical
   * ScrollView (single style block, no content-container split), and anything else is a `View`.
   */
  private static resolveContainerRender(
    componentName: string,
    di: IDisplayItem,
    viewStyle: Record<string, string | number>
  ): IContainerRender {
    if (componentName === "HorizontalStack") {
      const overflowX: string = ReactNativeElementGenerator.getFixedPropertyValue(di, "overflowX");
      if (overflowX === "hidden") {
        return { tag: "View", horizontal: false, splitContentContainer: false };
      }
      return { tag: "ScrollView", horizontal: true, splitContentContainer: true };
    }
    if (componentName === "VerticalStack") {
      const bounded: boolean = viewStyle.height !== undefined || viewStyle.maxHeight !== undefined;
      return { tag: bounded ? "ScrollView" : "View", horizontal: false, splitContentContainer: bounded };
    }
    if (viewStyle.overflow === "scroll") {
      return { tag: "ScrollView", horizontal: false, splitContentContainer: false };
    }
    return { tag: "View", horizontal: false, splitContentContainer: false };
  }

  /**
   * Partitions a container's box style into the scroll-viewport subset (`box` → ScrollView `style`) and
   * the child-layout subset (`content` → `contentContainerStyle`). Order-preserving, so each subset stays
   * key-sorted (the input already is) and emission is byte-stable.
   */
  static splitContentContainerStyle(style: Record<string, string | number>): ISplitContainerStyle {
    const box: Record<string, string | number> = {};
    const content: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(style)) {
      if (CONTENT_CONTAINER_STYLE_KEYS.includes(key)) {
        content[key] = value;
      } else {
        box[key] = value;
      }
    }
    if (content.justifyContent !== undefined && content.flexGrow === undefined) {
      content.flexGrow = 1;
    }
    return { box, content: ReactNativeElementGenerator.sortStyleKeys(content) };
  }

  /**
   * Whether a Table in `items` (at any depth) has a fixed header that may be on — fixed `true`, or bound, since a
   * binding can turn it on at runtime. Its header rides the body's scroll (KTable `useStickyTableHeader`), which
   * only a {@link SCREEN_SCROLL_VIEW_TAG} body can feed.
   */
  private static hasScrollFollowingTable(items: readonly IDisplayItem[]): boolean {
    return items.some((di: IDisplayItem): boolean => {
      if (di.item?.name === "Table") {
        const property: IDisplayItemProperty | undefined = di.item.properties?.find(
          (p: IDisplayItemProperty): boolean => p.name === "fixedHeader"
        );
        const isBound: boolean =
          property?.symbol !== undefined &&
          property.symbol !== null &&
          !ReactNativeElementGenerator.isLiteralValueCategory(property.symbol.category);
        if (isBound || ReactNativeElementGenerator.getFixedPropertyValue(di, "fixedHeader") === "true") {
          return true;
        }
      }
      return ReactNativeElementGenerator.hasScrollFollowingTable(di.children ?? []);
    });
  }

  private static getFixedPropertyValue(di: IDisplayItem, propertyName: string): string {
    const property: IDisplayItemProperty | undefined = di.item.properties?.find((p) => p.name === propertyName);
    // A *bound* property has no fixed value to emit: whatever sits in its `fixedValue` is a designer leftover
    // (a table cell keeps the canvas token `[datafield:status]`, a repeater keeps its sample rows). Returning it
    // printed that placeholder in the app — e.g. a cell button labelled "[datafield:status]". The binding itself
    // travels through getBoundValueMarker / getActionBoundMarker instead.
    if (!ReactNativeElementGenerator.isLiteralValueCategory(property?.symbol?.category)) {
      return "";
    }
    const fixedValue: unknown = property?.symbol?.fixedValue;
    if (fixedValue === undefined || fixedValue === null) {
      return "";
    }
    return ReactNativeElementGenerator.resolveValuePlaceholders(String(fixedValue), di);
  }

  /**
   * The designer tokens a fixed value may carry, resolved — the RN mirror of the substitution the web leg runs in
   * `ServerSideElementGenerator.getPropertyValueForCodeGen`.
   *
   * <p>`@componentInstanceId` is the DEFAULT fixed value of the `key` property on every component whose siblings
   * have to be told apart: StepperItem, TabItem, CollapsePanel, MenuItem, SubMenu (`0141_StepperItem.json` and
   * friends). It is a token, not a value — web replaces it with the display item's own id, which is unique by
   * construction. Emitting it verbatim gave every step, tab and panel on a screen the SAME key: React logged
   * "Encountered two children with the same key, `@componentInstanceId`", and a Stepper became unusable, since
   * `activeStepIndex` resolves the active step BY key and always found the first one — tapping step 2 moved
   * nothing.</p>
   *
   * <p>Only this one token is resolved. `@randomkey` and `@@autoIncremented(...)` belong to `0101_Step.json`,
   * which this target does not lower (the designer's Stepper uses StepperItem), and `@@get(...)` / `@@set(...)`
   * are web's `this.state` accessors — the RN leg binds `value`/`onValueChange` to its own screen state instead,
   * so those defaults are never read. Adding a resolver for a token no emitted component carries would be
   * unreachable code that still had to be kept correct.</p>
   */
  private static resolveValuePlaceholders(value: string, di: IDisplayItem): string {
    if (!value.includes(COMPONENT_INSTANCE_ID_TOKEN)) {
      return value;
    }
    return value.split(COMPONENT_INSTANCE_ID_TOKEN).join(String(di.id));
  }

  /**
   * Bind marker `__KBIND_<id>_<prop>__` for a property carrying a dynamic symbol, else ""
   * (fixed/none/default/uploadedImages — the categories whose value is already a literal).
   *
   * `uploadedImages` is a **literal, not a binding**: picking an image in the designer stores the
   * resource GUID in `symbol.fixedValue`, which is why the web emitter lists it alongside `fixed` in
   * `BaseElementGenerator` and simply returns that value. Treating it as dynamic made the C# assembler
   * fall through to its `binding not yet supported` marker, so every designer-selected image emitted an
   * empty value and rendered as the "no image" placeholder. Left as "" here, the fixed-value path emits
   * the GUID and `KImage`'s `resolveImageUri` turns it into a backend resource URL.
   */
  /**
   * Bind marker for a property whose symbol carries an `actionDefinitionId` — a **data repeater's row source**,
   * which the designer stores that way while leaving the category `fixed` (its `fixedValue` holds the canvas
   * sample rows). The web target keys the same read off the property definition's `isTableOptionsProp` flag
   * rather than off the symbol category (`React_UIGenerator`), so a category check alone silently ships the
   * sample data. Returns "" when the property names no action, and the caller falls back to the fixed value.
   */
  private static getActionBoundMarker(di: IDisplayItem, propertyName: string): string {
    const property: IDisplayItemProperty | undefined = di.item.properties?.find((p) => p.name === propertyName);
    const actionDefinitionId: string | undefined = property?.symbol?.actionDefinitionId;
    if (actionDefinitionId === undefined || actionDefinitionId.length === 0) {
      return "";
    }
    return `__KBIND_${di.id}_${propertyName}__`;
  }

  /**
   * Whether a symbol's value is already a literal — the categories whose `fixedValue` IS the value
   * (`fixed`/`none`/`uploadedImages`, or no symbol at all). Everything else is a *binding*, and its
   * `fixedValue` is stale designer leftovers: the designer stamps a canvas placeholder there (a table cell keeps
   * `[datafield:status]`, a data repeater keeps its sample rows), so emitting it as a literal ships that
   * placeholder to the user's screen.
   *
   * <p><b>`default` is NOT a literal.</b> A "Default" symbol (the picker's Now / Empty / Space / null /
   * Current Username / Client IP Address / Version Info …) carries its choice in `defaultValueName` and leaves
   * `fixedValue` unset — it is lowered to an expression by `ReactNativeSymbolResolver.ResolveDefaultValue`,
   * which needs a `__KBIND_` marker to substitute into. Listing it here returned "" from
   * `getBoundValueMarker`, so no marker was emitted, `getFixedPropertyValue` then found no `fixedValue`, and
   * the property was dropped entirely: a Label bound to `Now` emitted `<KLabel style={…} />` with no `value`
   * and rendered blank, with not even the assembler's "not yet supported" breadcrumb to show why. The web leg's
   * literal set (`BaseElementGenerator`) is `fixed` + `uploadedImages` only — this now matches it.</p>
   */
  private static isLiteralValueCategory(category: ESymbolCategory | undefined): boolean {
    return (
      category === undefined ||
      category === ESymbolCategory.none ||
      category === ESymbolCategory.fixed ||
      category === ESymbolCategory.uploadedImages
    );
  }

  /**
   * The screen-catalogue constant and the project default the emitted lookups read. Declared by the C#
   * `ReactNativeScreenGenerator`, which fills them from the SAME `ScreenExtensions.GetMultiLanguageData`
   * output the web target's `this.ml` carries — it emits them whenever the JSX it assembles references
   * {@link SCREEN_ML_CONST}, so the two cannot get out of step.
   */
  private static readonly SCREEN_ML_CONST: string = "SCREEN_ML";
  private static readonly DEFAULT_ML_CONST: string = "DEFAULT_ML";

  /**
   * The lookup expression for a multi-language text property, or "" when the property is not translated here.
   *
   * <p>Gated exactly as the web leg gates it (`React_UIGenerator`): the designer must have marked the
   * property multi-language AND left it a fixed value. The second half is not a formality — a BOUND property
   * has no phrase in the catalogue at all (its text comes from data at runtime), so translating it would
   * replace a live binding with an empty string.</p>
   */
  /**
   * Which properties each component allows translating, indexed once per component set.
   *
   * <p>Memoised because it is asked once per property per node — a table with fifty cells would otherwise
   * rescan the whole set fifty times — and keyed on the component-set ARRAY IDENTITY rather than on a component
   * id, so the index cannot outlive the set it was built from. A process-lifetime map keyed by id would be
   * stale for the next generation the long-running codegen server handles (and made these tests depend on the
   * order they ran in, which is how it was caught).</p>
   */
  private static readonly translatablePropertyCache: WeakMap<object, Map<string, Set<string>>> = new WeakMap<
    object,
    Map<string, Set<string>>
  >();

  /**
   * Whether `propertyName` is translatable on this component, read from the COMPONENT DEFINITION rather than
   * from the display item.
   *
   * <p>This is not a preference, it is where the flag lives. A saved screen's display-item properties do not
   * carry the definition-level flags at all — the web leg copies them onto the model at generation time from
   * the component set (`React_UIGenerator`, the `originalProp` loop, which assigns `IsSupportMultiLanguage`
   * among two dozen others). Reading the display item instead returned `undefined` for every property on every
   * real screen, so the gate never fired and nothing was ever translated, while every hand-built test fixture —
   * which sets the flag directly — passed. The display item is kept as a fallback for exactly those fixtures
   * and for a component the set does not describe.</p>
   */
  private static isTranslatableProperty(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    propertyName: string
  ): boolean {
    const componentId: string | undefined = di.item?.id;
    const components: IComponent[] | undefined = context.project?.componentSetObj?.components;
    const component: IComponent | undefined =
      componentId === undefined ? undefined : components?.find((c) => c.id === componentId);
    // A component the set does not describe keeps whatever the display item says, which is what the web leg
    // does too (its enrichment loop is skipped when the component is not found).
    if (component === undefined || componentId === undefined) {
      return di.item.properties?.find((p) => p.name === propertyName)?.isSupportMultiLanguage === true;
    }
    let bySet: Map<string, Set<string>> | undefined = ReactNativeElementGenerator.translatablePropertyCache.get(
      components as object
    );
    if (bySet === undefined) {
      bySet = new Map<string, Set<string>>();
      ReactNativeElementGenerator.translatablePropertyCache.set(components as object, bySet);
    }
    let translatable: Set<string> | undefined = bySet.get(componentId);
    if (translatable === undefined) {
      translatable = new Set<string>(
        (component.properties ?? []).filter((p) => p.isSupportMultiLanguage === true).map((p) => p.name)
      );
      bySet.set(componentId, translatable);
    }
    return translatable.has(propertyName);
  }

  private static getTranslatedTextExpression(
    context: ICodeGenerationContext,
    di: IDisplayItem,
    propertyName: string
  ): string {
    const property: IDisplayItemProperty | undefined = di.item.properties?.find((p) => p.name === propertyName);
    if (
      !ReactNativeElementGenerator.isTranslatableProperty(context, di, propertyName) ||
      property?.symbol?.category !== ESymbolCategory.fixed
    ) {
      return "";
    }
    return `ReactSystemFunctions.translate(${ReactNativeElementGenerator.SCREEN_ML_CONST}, ${toDiId(
      di.id
    )}, ${JSON.stringify(propertyName)}, ${ReactNativeElementGenerator.DEFAULT_ML_CONST})`;
  }

  private static getBoundValueMarker(di: IDisplayItem, propertyName: string): string {
    const property: IDisplayItemProperty | undefined = di.item.properties?.find((p) => p.name === propertyName);
    if (ReactNativeElementGenerator.isLiteralValueCategory(property?.symbol?.category)) {
      return "";
    }
    return `__KBIND_${di.id}_${propertyName}__`;
  }

  private static formatStyleEntry(entry: IStyleSheetEntry): string {
    const indent: string = INDENT_STEP;
    const propIndent: string = INDENT_STEP.repeat(2);
    const props: string = Object.entries(entry.style)
      .map(([key, value]) => `${propIndent}${key}: ${JSON.stringify(value)}`)
      .join(",\n");
    // Non-identifier keys (e.g. "di-2" for special DIs with negative ids) must be quoted.
    const isIdentifier: boolean = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(entry.name);
    const keyStr: string = isIdentifier ? entry.name : JSON.stringify(entry.name);
    if (props.length === 0) {
      return `${indent}${keyStr}: {}`;
    }
    return `${indent}${keyStr}: {\n${props}\n${indent}}`;
  }
}
